import React, { useState, useEffect } from 'react';
import { ManualModal } from './Manual';
import LeadsModal from './leads/LeadsModal';
import { useLeadsLive } from './leads/useLeads';
import { createClient } from '@supabase/supabase-js';

// Enlace de "¿Olvidaste tu contraseña?": se detecta ANTES de crear el cliente (que limpia la URL). Mientras la marca
// exista, la app pide elegir contraseña nueva en vez de entrar al tablero (no es un enlace de acceso directo).
const RECOVERY_KEY = 'enmarcha-recovery';
const RECOVERY_LINK_ERROR = (() => {
  try {
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    if (hash.get('type') === 'recovery' && hash.get('access_token')) localStorage.setItem(RECOVERY_KEY, '1');
    if (window.location.pathname === '/reset-password' && hash.get('error')) {
      return 'El enlace para cambiar la contraseña ha caducado o ya se usó. Pide uno nuevo aquí abajo.';
    }
  } catch (e) { /* sin almacenamiento: se usa el evento PASSWORD_RECOVERY */ }
  return '';
})();
function recoveryPending() {
  try { return localStorage.getItem(RECOVERY_KEY) === '1'; } catch (e) { return false; }
}
function setRecoveryPending(on) {
  try { if (on) localStorage.setItem(RECOVERY_KEY, '1'); else localStorage.removeItem(RECOVERY_KEY); } catch (e) { /* nada */ }
}

// "Recordarme en este dispositivo": si no se marca, la sesión se guarda solo en esta pestaña (sessionStorage) y se
// pierde al cerrarla. Se lee de los dos sitios para no cerrar las sesiones que ya estaban guardadas.
const REMEMBER_KEY = 'enmarcha-remember';
const authStorage = {
  getItem(key) {
    try { return sessionStorage.getItem(key) ?? localStorage.getItem(key); } catch (e) { return null; }
  },
  setItem(key, value) {
    try {
      if (localStorage.getItem(REMEMBER_KEY) === '0') { sessionStorage.setItem(key, value); localStorage.removeItem(key); }
      else { localStorage.setItem(key, value); sessionStorage.removeItem(key); }
    } catch (e) { /* sin almacenamiento */ }
  },
  removeItem(key) {
    try { sessionStorage.removeItem(key); localStorage.removeItem(key); } catch (e) { /* sin almacenamiento */ }
  }
};

const supabase = createClient(
  'https://dabyiebqjiporyuxqlbf.supabase.co',
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImRhYnlpZWJxamlwb3J5dXhxbGJmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzQ3MDcxNDEsImV4cCI6MjA5MDI4MzE0MX0.TOgdwF3xCpz5cPMWrOQ_byejI30JSuvox2qhdT6iHdk',
  { auth: { storage: authStorage } }
);

// Identificador aleatorio de este navegador, para avisar por correo de inicios de sesión desde dispositivos nuevos
// (register_login_device + process_login_alerts en Supabase). No contiene datos personales.
function deviceId() {
  try {
    let id = localStorage.getItem('enmarcha-device');
    if (!id || !/^[A-Za-z0-9-]{16,64}$/.test(id)) {
      id = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID()
        : Array.from(window.crypto.getRandomValues(new Uint8Array(16)), b => b.toString(16).padStart(2, '0')).join('');
      localStorage.setItem('enmarcha-device', id);
    }
    return id;
  } catch (e) { return null; }
}
let registeredDeviceFor = null;
function registerDevice(userId) {
  if (!userId || registeredDeviceFor === userId) return;
  const id = deviceId();
  if (!id) return;
  registeredDeviceFor = userId;
  supabase.rpc('register_login_device', { p_device_id: id }).then(({ error }) => {
    if (error) { registeredDeviceFor = null; console.error('No se pudo registrar el dispositivo:', error.message); }
  });
}

function useAuth() {
  const [user, setUser] = useState(null);
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [recovery, setRecovery] = useState(recoveryPending());
  // Doble verificación: null = comprobando; true = falta el PIN del correo (la base de datos no da datos hasta entonces).
  const [pinRequired, setPinRequired] = useState(null);

  useEffect(() => {
    // Tras entrar: si la sesión es de contraseña y aún no tiene PIN, se pide antes de cargar nada.
    const checkPin = async (sessionUser) => {
      const { data, error } = await supabase.rpc('login_pin_status');
      const required = !error && data === true;
      setPinRequired(required);
      if (!required) await loadUserData(sessionUser.id);
    };

    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);
        registerDevice(session.user.id);
        await checkPin(session.user);
      } else {
        // El enlace de recuperación no llegó a abrir sesión (caducado o ya usado): no forzar nada después.
        setRecoveryPending(false);
        setRecovery(false);
      }
      setLoading(false);
    };

    const loadUserData = async (userId) => {
      try {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .single();

        setProfile(profileData);

        if (profileData?.org_id) {
          const { data: orgData } = await supabase
            .from('organizations')
            .select('*')
            .eq('id', profileData.org_id)
            .single();

          setOrg(orgData);
        }
      } catch (error) {
        console.error('Error cargando datos del usuario:', error);
      }
    };

    getSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setRecoveryPending(true);
        setRecovery(true);
      }
      if (event === 'SIGNED_IN' || event === 'PASSWORD_RECOVERY') {
        setUser(session?.user);
        if (session?.user) {
          const sessionUser = session.user;
          registerDevice(sessionUser.id);
          // Fuera del aviso de Supabase: llamar a la API dentro de él puede quedarse bloqueado.
          setTimeout(() => { checkPin(sessionUser); }, 0);
        }
      } else if (event === 'SIGNED_OUT') {
        registeredDeviceFor = null;
        setPinRequired(null);
        setRecoveryPending(false);
        setRecovery(false);
        setUser(null);
        setOrg(null);
        setProfile(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const finishRecovery = () => { setRecoveryPending(false); setRecovery(false); };
  const pinVerified = async () => {
    setPinRequired(false);
    if (!user) return;
    const { data: profileData } = await supabase.from('profiles').select('*').eq('id', user.id).single();
    setProfile(profileData);
    if (profileData?.org_id) {
      const { data: orgData } = await supabase.from('organizations').select('*').eq('id', profileData.org_id).single();
      setOrg(orgData);
    }
  };
  return { user, org, profile, loading, recovery, finishRecovery, pinRequired, pinVerified };
}

function AuthProvider({ children }) {
  return children;
}

// Marca EnMarcha: azul marino #0F1B31 y dorado #CDA455. Los logos están en public/brand.
const BRAND = { navy: '#0F1B31', gold: '#CDA455', light: '#F4F6FA', font: "'Montserrat', sans-serif" };

// Tema claro/oscuro. Los colores de la app son variables CSS (public/index.html) con un valor por tema;
// la preferencia se guarda en el navegador (para pintar sin parpadeo) y en la cuenta (notification_settings.ui_theme).
const THEME_KEY = 'enmarcha-theme';
function currentTheme() {
  return document.documentElement.dataset.theme === 'light' ? 'light' : 'dark';
}
function applyTheme(theme) {
  document.documentElement.dataset.theme = theme;
  try { localStorage.setItem(THEME_KEY, theme); } catch (e) { /* sin almacenamiento: solo esta sesión */ }
}
function brandMarkSrc(theme) {
  return theme === 'light' ? '/brand/logo-mark.svg' : '/brand/logo-mark-light.svg';
}

// Marca asociada de la empresa (organizations.co_brand), p. ej. "EnMarcha × Flexicar". Se recuerda en el
// navegador para la pantalla de carga y la de acceso del siguiente inicio (public/theme-init.js).
const COBRANDS = {
  flexicar: { name: 'Flexicar', src: { dark: '/brand/partners/flexicar-light.svg', light: '/brand/partners/flexicar.svg' }, ratio: 489 / 120 }
};
function currentCobrand() {
  const c = document.documentElement.dataset.cobrand;
  return COBRANDS[c] ? c : null;
}
function applyCobrand(cobrand) {
  const c = COBRANDS[cobrand] ? cobrand : null;
  if (c) document.documentElement.dataset.cobrand = c; else delete document.documentElement.dataset.cobrand;
  try { if (c) localStorage.setItem('enmarcha-cobrand', c); else localStorage.removeItem('enmarcha-cobrand'); } catch (e) { /* solo esta sesión */ }
}
// "× Flexicar" junto al nombre EnMarcha (altura en em: se ajusta al tamaño del texto).
function CobrandSuffix({ cobrand, theme, height = '0.95em' }) {
  const cb = COBRANDS[cobrand];
  if (!cb) return null;
  return (
    <span style={{display: 'inline-flex', alignItems: 'center', gap: '0.55em', marginLeft: '0.55em'}}>
      <svg viewBox="0 0 20 20" aria-hidden="true" style={{width: '0.5em', height: '0.5em'}} stroke={BRAND.gold} strokeWidth="2.2" strokeLinecap="round">
        <line x1="2" y1="2" x2="18" y2="18" /><line x1="18" y1="2" x2="2" y2="18" />
      </svg>
      <img src={cb.src[theme === 'light' ? 'light' : 'dark']} alt={cb.name} style={{height, width: 'auto', display: 'block'}} />
    </span>
  );
}

// Pantalla de carga con el logo animado (estilos .em-loader en public/index.html; la misma que se ve
// antes de que arranque la app).
function BrandLoader({ text }) {
  return (
    <div className="em-loader" role="status" aria-label={text || 'Cargando EnMarcha'}>
      <img className="ld-dark" src="/brand/logo-loader-light.svg" alt="" />
      <img className="ld-light" src="/brand/logo-loader.svg" alt="" />
      <img className="ld-cob-dark" src="/brand/partners/loader-flexicar-light.svg" alt="" />
      <img className="ld-cob-light" src="/brand/partners/loader-flexicar.svg" alt="" />
      {text && <div className="em-loader-text">{text}</div>}
    </div>
  );
}

// Logo con nombre para las pantallas de acceso (fondo oscuro).
function BrandLockup() {
  return (
    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '1.5rem'}}>
      <img src={brandMarkSrc(currentTheme())} alt="" style={{height: '34px', width: 'auto'}} />
      <span style={{display: 'inline-flex', alignItems: 'center', fontFamily: BRAND.font, fontSize: '1.5rem', fontWeight: 300, letterSpacing: '0.09em', color: 'var(--em-wordmark)'}}>
        En<span style={{fontWeight: 600}}>Marcha</span>
        <CobrandSuffix cobrand={currentCobrand()} theme={currentTheme()} />
      </span>
    </div>
  );
}

function Register({ goTo }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [fullName, setFullName] = useState('');
  const [inviteToken, setInviteToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('invite');
    if (token) {
      setInviteToken(token);
      validateInviteToken(token);
    }
  }, []);

  const validateInviteToken = async (token) => {
    // Las invitaciones no se pueden leer directamente: solo se consulta la de este enlace.
    const { data } = await supabase.rpc('get_invitation_by_token', { p_token: token });
    const invite = Array.isArray(data) ? data[0] : null;

    if (invite) {
      setEmail(invite.email);
      setMessage(`Invitación válida: te unirás a ${invite.org_name} como ${roleLabel(invite.role)}.`);
    } else {
      setMessage('Token de invitación inválido o expirado');
      setInviteToken('');
    }
  };

  const register = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
          invite_token: inviteToken || undefined
        }
      }
    });

    if (error) {
      setMessage(/database error/i.test(error.message)
        ? 'No se ha podido completar el registro: la invitación no es válida para este correo o ha caducado.'
        : error.message);
    } else {
      setMessage('¡Registro exitoso! Revisa tu email para confirmar tu cuenta.');
    }
    setLoading(false);
  };

  return (
    <div style={{minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--b-0f172a)'}}>
      <div style={{backgroundColor: 'var(--b-1e293b)', padding: '2rem', borderRadius: '0.5rem', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', width: '100%', maxWidth: '400px'}}>
        <BrandLockup />
        <h2 style={{fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--t-f1f5f9)', textAlign: 'center', marginBottom: '1.5rem'}}>
          {inviteToken ? 'Aceptar Invitación' : 'Crear Cuenta'}
        </h2>
        
        {message && (
          <div style={{
            backgroundColor: message.includes('exitoso') || message.includes('válida') ? 'var(--b-065f46)' : 'var(--b-7f1d1d)', 
            color: 'var(--t-f1f5f9)', 
            padding: '0.75rem', 
            borderRadius: '0.375rem', 
            marginBottom: '1rem',
            fontSize: '0.875rem'
          }}>
            {message}
          </div>
        )}

        {!inviteToken ? (
          <p style={{color: 'var(--t-cbd5e1)', fontSize: '0.875rem', lineHeight: 1.6, textAlign: 'center'}}>
            🔒 El registro en EnMarcha solo es posible mediante <strong>invitación</strong>. Pide a la persona responsable de tu
            concesionario que te invite: recibirás un correo de <strong>avisos@enmarcha.cloud</strong> con el enlace para crear tu cuenta.
          </p>
        ) : (
        <form onSubmit={register}>
          <div style={{marginBottom: '1rem'}}>
            <label style={{display: 'block', color: 'var(--t-e2e8f0)', marginBottom: '0.5rem', fontSize: '0.875rem'}}>
              Nombre Completo
            </label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value.replace(/[<>]/g, ''))}
              maxLength={120}
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: 'var(--b-334155)',
                border: '1px solid var(--l-475569)',
                borderRadius: '0.375rem',
                color: 'var(--t-f1f5f9)',
                fontSize: '0.875rem'
              }}
              placeholder="Tu nombre completo"
              required
            />
          </div>
          
          <div style={{marginBottom: '1rem'}}>
            <label style={{display: 'block', color: 'var(--t-e2e8f0)', marginBottom: '0.5rem', fontSize: '0.875rem'}}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={!!inviteToken}
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: inviteToken ? 'var(--b-475569)' : 'var(--b-334155)',
                border: '1px solid var(--l-475569)',
                borderRadius: '0.375rem',
                color: 'var(--t-f1f5f9)',
                fontSize: '0.875rem',
                opacity: inviteToken ? 0.7 : 1
              }}
              placeholder="tu@email.com"
              required
            />
          </div>
          
          <div style={{marginBottom: '1.5rem'}}>
            <label style={{display: 'block', color: 'var(--t-e2e8f0)', marginBottom: '0.5rem', fontSize: '0.875rem'}}>
              Contraseña
            </label>
            <div style={{position: 'relative'}}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  paddingRight: '2.75rem',
                  backgroundColor: 'var(--b-334155)',
                  border: '1px solid var(--l-475569)',
                  borderRadius: '0.375rem',
                  color: 'var(--t-f1f5f9)',
                  fontSize: '0.875rem',
                  boxSizing: 'border-box'
                }}
                placeholder="Mínimo 10 caracteres"
                minLength={10}
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                style={{
                  position: 'absolute',
                  right: '0.5rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1.1rem',
                  padding: '0.25rem',
                  lineHeight: 1
                }}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>
          
          <button 
            type="submit" 
            disabled={loading}
            style={{
              width: '100%',
              backgroundColor: 'var(--b-f59e0b)',
              color: '#1f2937',
              padding: '0.75rem',
              borderRadius: '0.375rem',
              border: 'none',
              fontWeight: '500',
              fontSize: '0.875rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1
            }}
          >
            {loading ? 'Registrando...' : (inviteToken ? 'Aceptar Invitación' : 'Crear Cuenta')}
          </button>
        </form>
        )}
        
        <p style={{textAlign: 'center', marginTop: '1.5rem', color: 'var(--t-94a3b8)', fontSize: '0.875rem'}}>
          ¿Ya tienes una cuenta?{' '}
          <button 
            onClick={() => goTo('login')}
            style={{color: 'var(--t-f59e0b)', background: 'none', border: 'none', textDecoration: 'underline', cursor: 'pointer'}}
          >
            Iniciar sesión
          </button>
        </p>
      </div>
    </div>
  );
}

function ForgotPassword({ goTo, initialMessage = '' }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(initialMessage);

  const resetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + '/reset-password'
    });

    if (error) {
      setMessage(/rate limit|seconds/i.test(error.message)
        ? 'Ya te hemos enviado un enlace hace muy poco. Espera un minuto y vuelve a intentarlo.'
        : 'No se ha podido enviar el enlace. Comprueba el email e inténtalo de nuevo.');
    } else {
      setMessage('Si ese email tiene cuenta en EnMarcha, te hemos enviado un enlace para elegir una contraseña nueva. Caduca en 1 hora.');
    }
    setLoading(false);
  };

  return (
    <div style={{minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--b-0f172a)'}}>
      <div style={{backgroundColor: 'var(--b-1e293b)', padding: '2rem', borderRadius: '0.5rem', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', width: '100%', maxWidth: '400px'}}>
        <BrandLockup />
        <h2 style={{fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--t-f1f5f9)', textAlign: 'center', marginBottom: '1.5rem'}}>
          Restablecer Contraseña
        </h2>
        
        {message && (
          <div style={{
            backgroundColor: message.includes('hemos enviado un enlace para') ? 'var(--b-065f46)' : 'var(--b-7f1d1d)', 
            color: 'var(--t-f1f5f9)', 
            padding: '0.75rem', 
            borderRadius: '0.375rem', 
            marginBottom: '1rem',
            fontSize: '0.875rem'
          }}>
            {message}
          </div>
        )}

        <form onSubmit={resetPassword}>
          <div style={{marginBottom: '1.5rem'}}>
            <label style={{display: 'block', color: 'var(--t-e2e8f0)', marginBottom: '0.5rem', fontSize: '0.875rem'}}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: 'var(--b-334155)',
                border: '1px solid var(--l-475569)',
                borderRadius: '0.375rem',
                color: 'var(--t-f1f5f9)',
                fontSize: '0.875rem',
                boxSizing: 'border-box'
              }}
              placeholder="tu@email.com"
              required
            />
          </div>
          
          <button 
            type="submit" 
            disabled={loading}
            style={{
              width: '100%',
              backgroundColor: 'var(--b-f59e0b)',
              color: '#1f2937',
              padding: '0.75rem',
              borderRadius: '0.375rem',
              border: 'none',
              fontWeight: '500',
              fontSize: '0.875rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1
            }}
          >
            {loading ? 'Enviando...' : 'Enviar Enlace de Restablecimiento'}
          </button>
        </form>
        
        <p style={{textAlign: 'center', marginTop: '1.5rem', color: 'var(--t-94a3b8)', fontSize: '0.875rem'}}>
          ¿Recordaste tu contraseña?{' '}
          <button 
            onClick={() => goTo('login')}
            style={{color: 'var(--t-f59e0b)', background: 'none', border: 'none', textDecoration: 'underline', cursor: 'pointer'}}
          >
            Iniciar sesión
          </button>
        </p>
      </div>
    </div>
  );
}

// Segundo paso del inicio de sesión: PIN de 6 cifras enviado al correo (send_login_pin / verify_login_pin).
function maskEmail(email) {
  const [name, domain] = String(email || '').split('@');
  if (!domain) return email || '';
  return (name.length <= 2 ? name[0] : name.slice(0, 2)) + '•••@' + domain;
}

function LoginPinScreen({ email, onVerified }) {
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null); // { ok: bool, text }
  const [wait, setWait] = useState(0);
  const sentOnce = React.useRef(false);

  const send = async (manual) => {
    setMessage(null);
    const { data, error } = await supabase.rpc('send_login_pin');
    const r = error ? 'error' : data;
    if (r === 'no_requerido') { onVerified(); return; }
    if (r === 'enviado') {
      setWait(60);
      if (manual) setMessage({ ok: true, text: 'Te hemos enviado un código nuevo. Usa siempre el último que llegue.' });
    } else if (r === 'espera') {
      setWait(60);
      setMessage({ ok: true, text: 'Ya te hemos enviado un código hace un momento: revisa tu correo (y la carpeta de spam).' });
    } else if (r === 'limite') {
      setMessage({ ok: false, text: 'Has pedido demasiados códigos. Cierra sesión y vuelve a intentarlo dentro de un rato.' });
    } else {
      setMessage({ ok: false, text: 'No se ha podido enviar el código. Comprueba tu conexión y pulsa "Enviar otro código".' });
    }
  };

  useEffect(() => {
    if (sentOnce.current) return;
    sentOnce.current = true;
    send(false);
  }); // solo al abrir la pantalla (protegido con sentOnce)

  useEffect(() => {
    if (wait <= 0) return undefined;
    const t = setTimeout(() => setWait(w => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const verify = async (e) => {
    e.preventDefault();
    if (pin.length !== 6) { setMessage({ ok: false, text: 'El código tiene 6 cifras.' }); return; }
    setLoading(true);
    setMessage(null);
    const { data, error } = await supabase.rpc('verify_login_pin', { p_pin: pin });
    setLoading(false);
    const r = error ? 'error' : data;
    if (r === 'ok') { onVerified(); return; }
    setPin('');
    setMessage({ ok: false, text:
      r === 'incorrecto' ? 'Ese código no es correcto. Revisa el último correo e inténtalo de nuevo.'
      : r === 'caducado' ? 'El código ha caducado. Pulsa "Enviar otro código".'
      : r === 'bloqueado' ? 'Demasiados intentos con este código. Pulsa "Enviar otro código".'
      : 'No se ha podido comprobar el código. Inténtalo de nuevo.' });
  };

  const linkBtn = {color: 'var(--t-f59e0b)', background: 'none', border: 'none', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.875rem', padding: 0};

  return (
    <div style={{minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--b-0f172a)', padding: '1rem', boxSizing: 'border-box'}}>
      <div style={{backgroundColor: 'var(--b-1e293b)', padding: '2rem', borderRadius: '0.5rem', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', width: '100%', maxWidth: '400px', boxSizing: 'border-box'}}>
        <BrandLockup />
        <h2 style={{fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--t-f1f5f9)', textAlign: 'center', marginBottom: '0.75rem'}}>Revisa tu correo</h2>
        <p style={{color: 'var(--t-94a3b8)', fontSize: '0.875rem', textAlign: 'center', margin: '0 0 1.5rem', lineHeight: 1.5}}>
          Por seguridad, escribe el código de 6 cifras que te hemos enviado a <strong style={{color: 'var(--t-e2e8f0)'}}>{maskEmail(email)}</strong>. Caduca en 10 minutos.
        </p>
        {message && (
          <div style={{backgroundColor: message.ok ? 'var(--b-065f46)' : 'var(--b-7f1d1d)', color: 'var(--t-f1f5f9)', padding: '0.75rem', borderRadius: '0.375rem', marginBottom: '1rem', fontSize: '0.875rem', lineHeight: 1.5}}>
            {message.text}
          </div>
        )}
        <form onSubmit={verify}>
          <label htmlFor="login-pin" style={{display: 'block', color: 'var(--t-e2e8f0)', marginBottom: '0.5rem', fontSize: '0.875rem'}}>Código</label>
          <input
            id="login-pin"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            placeholder="••••••"
            aria-describedby="login-pin-help"
            style={{width: '100%', boxSizing: 'border-box', padding: '0.75rem', backgroundColor: 'var(--b-334155)', border: '1px solid var(--l-475569)',
              borderRadius: '0.375rem', color: 'var(--t-f1f5f9)', fontSize: '1.6rem', letterSpacing: '0.5em', textAlign: 'center',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', marginBottom: '0.5rem'}}
          />
          <p id="login-pin-help" style={{color: 'var(--t-94a3b8)', fontSize: '0.75rem', margin: '0 0 1.25rem', lineHeight: 1.5}}>
            Llega desde avisos@enmarcha.cloud con tu código de seguridad. Si no lo ves, mira en spam.
          </p>
          <button type="submit" disabled={loading} style={{width: '100%', backgroundColor: 'var(--b-f59e0b)', color: '#1f2937', padding: '0.75rem',
            borderRadius: '0.375rem', border: 'none', fontWeight: '600', fontSize: '0.875rem', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1}}>
            {loading ? 'Comprobando...' : 'Entrar'}
          </button>
        </form>
        <div style={{display: 'flex', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap', marginTop: '1.5rem'}}>
          <button type="button" onClick={() => send(true)} disabled={wait > 0}
            style={{...linkBtn, opacity: wait > 0 ? 0.55 : 1, cursor: wait > 0 ? 'default' : 'pointer', textDecoration: wait > 0 ? 'none' : 'underline'}}>
            {wait > 0 ? `Enviar otro código (${wait} s)` : 'Enviar otro código'}
          </button>
          <button type="button" onClick={() => supabase.auth.signOut()} style={linkBtn}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

// Pantalla obligatoria tras abrir el enlace de "¿Olvidaste tu contraseña?" (o el botón "No he sido yo" del aviso de
// inicio de sesión). Al guardar, cierra la sesión en los demás dispositivos.
function SetNewPassword({ onDone, fromAlert }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [done, setDone] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setMessage('');
    if (password.length < 10) { setMessage('La contraseña tiene que tener al menos 10 caracteres.'); return; }
    if (password !== confirm) { setMessage('Las dos contraseñas no coinciden.'); return; }
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      setLoading(false);
      const m = error.message || '';
      setMessage(/different|same/i.test(m) ? 'La contraseña nueva tiene que ser distinta de la anterior.'
        : /weak|pwned|leaked|characters/i.test(m) ? 'Esa contraseña es demasiado débil o aparece en filtraciones conocidas. Elige otra.'
        : /reauth/i.test(m) ? 'Por seguridad, cierra sesión y usa "¿Olvidaste tu contraseña?" para cambiarla.'
        : 'No se ha podido guardar la contraseña. Inténtalo de nuevo.');
      return;
    }
    try { await supabase.auth.signOut({ scope: 'others' }); } catch (err) { /* la contraseña ya está cambiada */ }
    setLoading(false);
    setDone(true);
  };

  const finish = () => {
    try { window.history.replaceState(null, '', '/'); } catch (err) { /* nada */ }
    onDone();
  };

  const inputStyle = {
    width: '100%', padding: '0.75rem', backgroundColor: 'var(--b-334155)', border: '1px solid var(--l-475569)',
    borderRadius: '0.375rem', color: 'var(--t-f1f5f9)', fontSize: '0.875rem', boxSizing: 'border-box'
  };
  const labelStyle = {display: 'block', color: 'var(--t-e2e8f0)', marginBottom: '0.5rem', fontSize: '0.875rem'};
  const primary = {
    width: '100%', backgroundColor: 'var(--b-f59e0b)', color: '#1f2937', padding: '0.75rem', borderRadius: '0.375rem',
    border: 'none', fontWeight: '600', fontSize: '0.875rem', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1
  };
  const linkBtn = {color: 'var(--t-f59e0b)', background: 'none', border: 'none', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.875rem'};

  return (
    <div style={{minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--b-0f172a)', padding: '1rem', boxSizing: 'border-box'}}>
      <div style={{backgroundColor: 'var(--b-1e293b)', padding: '2rem', borderRadius: '0.5rem', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', width: '100%', maxWidth: '400px'}}>
        <BrandLockup />
        {done ? (
          <>
            <h2 style={{fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--t-f1f5f9)', textAlign: 'center', marginBottom: '1rem'}}>Contraseña cambiada</h2>
            <div style={{backgroundColor: 'var(--b-065f46)', color: 'var(--t-f1f5f9)', padding: '0.75rem', borderRadius: '0.375rem', marginBottom: '1.5rem', fontSize: '0.875rem', lineHeight: 1.5}}>
              Ya puedes entrar con tu contraseña nueva. Por seguridad, se ha cerrado la sesión en todos tus demás dispositivos.
            </div>
            <button type="button" onClick={finish} style={primary}>Entrar en EnMarcha</button>
          </>
        ) : (
          <>
            <h2 style={{fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--t-f1f5f9)', textAlign: 'center', marginBottom: '0.75rem'}}>Elige tu contraseña nueva</h2>
            <p style={{color: 'var(--t-94a3b8)', fontSize: '0.875rem', textAlign: 'center', margin: '0 0 1.5rem', lineHeight: 1.5}}>
              {fromAlert
                ? 'Si alguien ha entrado en tu cuenta sin permiso, cambia la contraseña: se cerrará la sesión en todos los demás dispositivos.'
                : 'Para terminar, escribe la contraseña que usarás a partir de ahora.'}
            </p>
            {message && (
              <div style={{backgroundColor: 'var(--b-7f1d1d)', color: 'var(--t-f1f5f9)', padding: '0.75rem', borderRadius: '0.375rem', marginBottom: '1rem', fontSize: '0.875rem'}}>
                {message}
              </div>
            )}
            <form onSubmit={save}>
              <div style={{marginBottom: '1rem'}}>
                <label htmlFor="new-password" style={labelStyle}>Contraseña nueva</label>
                <div style={{position: 'relative'}}>
                  <input id="new-password" type={show ? 'text' : 'password'} value={password} autoComplete="new-password"
                    onChange={(e) => setPassword(e.target.value)} minLength={10} required placeholder="Mínimo 10 caracteres"
                    style={{...inputStyle, paddingRight: '2.75rem'}} />
                  <button type="button" onClick={() => setShow(!show)} aria-label={show ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    style={{position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', padding: '0.25rem', lineHeight: 1}}>
                    {show ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>
              <div style={{marginBottom: '1.5rem'}}>
                <label htmlFor="new-password-confirm" style={labelStyle}>Repite la contraseña</label>
                <input id="new-password-confirm" type={show ? 'text' : 'password'} value={confirm} autoComplete="new-password"
                  onChange={(e) => setConfirm(e.target.value)} minLength={10} required style={inputStyle} />
              </div>
              <button type="submit" disabled={loading} style={primary}>
                {loading ? 'Guardando...' : 'Guardar contraseña nueva'}
              </button>
            </form>
            <p style={{textAlign: 'center', marginTop: '1.5rem', marginBottom: 0}}>
              {fromAlert
                ? <button type="button" onClick={finish} style={linkBtn}>Volver sin cambiarla</button>
                : <button type="button" onClick={() => supabase.auth.signOut()} style={linkBtn}>Cancelar y cerrar sesión</button>}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function Login({ goTo }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(() => {
    try { return localStorage.getItem(REMEMBER_KEY) === '1'; } catch (e) { return false; }
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const signIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    // Antes de iniciar sesión: decide dónde se guardará (authStorage).
    try { localStorage.setItem(REMEMBER_KEY, remember ? '1' : '0'); } catch (err) { /* sin almacenamiento */ }

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) {
      setMessage('El email o la contraseña no son correctos. Comprueba ambos e inténtalo de nuevo.');
    }
    setLoading(false);
  };

  return (
    <div style={{minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--b-0f172a)'}}>
      <div style={{backgroundColor: 'var(--b-1e293b)', padding: '2rem', borderRadius: '0.5rem', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', width: '100%', maxWidth: '400px'}}>
        <BrandLockup />
        <h2 style={{fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--t-f1f5f9)', textAlign: 'center', marginBottom: '1.5rem'}}>
          Iniciar Sesión
        </h2>
        
        {message && (
          <div style={{backgroundColor: 'var(--b-7f1d1d)', color: 'var(--t-f1f5f9)', padding: '0.75rem', borderRadius: '0.375rem', marginBottom: '1rem', fontSize: '0.875rem'}}>
            {message}
          </div>
        )}

        <form onSubmit={signIn}>
          <div style={{marginBottom: '1rem'}}>
            <label style={{display: 'block', color: 'var(--t-e2e8f0)', marginBottom: '0.5rem', fontSize: '0.875rem'}}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: 'var(--b-334155)',
                border: '1px solid var(--l-475569)',
                borderRadius: '0.375rem',
                color: 'var(--t-f1f5f9)',
                fontSize: '0.875rem',
                boxSizing: 'border-box'
              }}
              placeholder="tu@email.com"
              required
            />
          </div>
          
          <div style={{marginBottom: '1rem'}}>
            <label style={{display: 'block', color: 'var(--t-e2e8f0)', marginBottom: '0.5rem', fontSize: '0.875rem'}}>
              Contraseña
            </label>
            <div style={{position: 'relative'}}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  paddingRight: '2.75rem',
                  backgroundColor: 'var(--b-334155)',
                  border: '1px solid var(--l-475569)',
                  borderRadius: '0.375rem',
                  color: 'var(--t-f1f5f9)',
                  fontSize: '0.875rem',
                  boxSizing: 'border-box'
                }}
                placeholder="Tu contraseña"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                style={{
                  position: 'absolute',
                  right: '0.5rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1.1rem',
                  padding: '0.25rem',
                  lineHeight: 1
                }}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>
          
          <div style={{marginBottom: '1.5rem'}}>
            <label htmlFor="remember-device" style={{display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--t-e2e8f0)', fontSize: '0.875rem', cursor: 'pointer'}}>
              <input
                id="remember-device"
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                style={{width: '1rem', height: '1rem', accentColor: BRAND.gold, cursor: 'pointer'}}
              />
              Recordarme en este dispositivo
            </label>
            <p style={{margin: '0.35rem 0 0 1.5rem', color: 'var(--t-94a3b8)', fontSize: '0.75rem', lineHeight: 1.5}}>
              Márcalo solo en tus dispositivos. Si no lo marcas, la sesión se cierra al cerrar la pestaña.
            </p>
            <div style={{textAlign: 'right', marginTop: '0.75rem'}}>
              <button 
                type="button"
                onClick={() => goTo('forgot')}
                style={{color: 'var(--t-f59e0b)', background: 'none', border: 'none', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.875rem', padding: 0}}
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
          </div>
          
          <button 
            type="submit" 
            disabled={loading}
            style={{
              width: '100%',
              backgroundColor: 'var(--b-f59e0b)',
              color: '#1f2937',
              padding: '0.75rem',
              borderRadius: '0.375rem',
              border: 'none',
              fontWeight: '500',
              fontSize: '0.875rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1
            }}
          >
            {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
          </button>
        </form>
        
        <p style={{textAlign: 'center', marginTop: '1.5rem', color: 'var(--t-94a3b8)', fontSize: '0.875rem'}}>
          ¿No tienes una cuenta?{' '}
          <button 
            onClick={() => goTo('register')}
            style={{color: 'var(--t-f59e0b)', background: 'none', border: 'none', textDecoration: 'underline', cursor: 'pointer'}}
          >
            Registrarse
          </button>
        </p>
      </div>
    </div>
  );
}

function daysInColumn(vehicle) {
  const since = vehicle.column_changed_at || vehicle.created_at;
  if (!since) return 0;
  return Math.floor((new Date() - new Date(since)) / (1000 * 60 * 60 * 24));
}

// Texto a mostrar: por debajo de 24h se expresa en horas enteras, a partir de 24h en días enteros.
function timeInColumnLabel(vehicle) {
  const since = vehicle.column_changed_at || vehicle.created_at;
  if (!since) return '0 h';
  const ms = new Date() - new Date(since);
  const hours = Math.floor(ms / (1000 * 60 * 60));
  if (hours < 24) return `${hours} h`;
  const days = Math.floor(hours / 24);
  return `${days} día${days === 1 ? '' : 's'}`;
}

function lerpColor(colorA, colorB, t) {
  const a = colorA.match(/\w\w/g).map(x => parseInt(x, 16));
  const b = colorB.match(/\w\w/g).map(x => parseInt(x, 16));
  const rgb = a.map((c, i) => Math.round(c + (b[i] - c) * t));
  return '#' + rgb.map(c => c.toString(16).padStart(2, '0')).join('');
}

function progressColor(t) {
  const clamped = Math.max(0, Math.min(1, t));
  if (clamped < 0.5) return lerpColor('#22c55e', '#eab308', clamped / 0.5);
  return lerpColor('#eab308', '#ef4444', (clamped - 0.5) / 0.5);
}

// Color de la viñeta de tiempo: en la columna "Stock" es un degradado verde→rojo
// relativo al resto de vehículos de esa misma columna; el resto de columnas usan
// el aviso naranja/rojo habitual según el límite configurado (col.alert_days).
function timeBadgeColor(vehicle, col, columnVehicles) {
  const days = daysInColumn(vehicle);
  const overLimit = col.alert_days != null && days > col.alert_days;

  if (col.label?.trim().toLowerCase() === 'stock') {
    const allDays = columnVehicles.map(daysInColumn);
    const min = Math.min(...allDays);
    const max = Math.max(...allDays);
    const t = max > min ? (days - min) / (max - min) : 0;
    return overLimit ? '#ef4444' : progressColor(t);
  }

  return overLimit ? '#ef4444' : 'var(--t-f59e0b)';
}

const ROLE_LABELS = {
  creador: 'Creador',
  responsable: 'Responsable',
  comercial: 'Comercial',
  captador: 'Captador',
  logistica: 'Logística',
  encargado_preparacion: 'Encargado Preparación',
  encargado_garantias: 'Encargado Garantías',
  administracion: 'Administración'
};

// Roles con control total del tablero (mover a cualquier columna, editar/borrar vehículos, etc.)
const ADMIN_TIER_ROLES = ['creador', 'responsable'];
// Quién puede ver el botón de Métricas
const METRICS_ROLES = ['creador', 'responsable', 'comercial', 'captador', 'encargado_preparacion', 'encargado_garantias', 'logistica'];
// Quién puede abrir el panel de gestión de usuarios/permisos
const MANAGE_USERS_ROLES = ['creador', 'responsable'];
// Quién puede marcar un vehículo como entregado (solo desde la columna "Entrega")
const DELIVER_ROLES = ['captador', 'comercial', 'creador', 'responsable'];
const READY_FOR_DELIVERY_LABEL = 'entrega';
const TRANSPORTE_LABEL = 'transporte';
// La columna "Listo entrega" se renombró a "Entrega" (ahora va tras Taller y Limpieza entrega,
// antes de Transporte). Se aceptan ambos nombres por compatibilidad.
const isDeliveryColumnLabel = (label) => ['entrega', 'listo entrega'].includes(label?.trim().toLowerCase());
const RESERVED_LABEL = 'reservados';
// Roles que pueden llevar una venta asignada / computar una entrega
const SALE_ROLES = ['comercial', 'captador'];
const LOCATION_OPTIONS = ['Tienda', 'Parque', 'Centro Comercial', 'ITV'];
const PREPARACION_LABEL = 'preparación';
const REVISION_F2_LABEL = 'revisión f2';
const VALIDATION_ALERT_LABEL = 'S/EN VALIDAR';
const LIMPIEZA_ENTREGA_LABEL = 'limpieza entrega';
const TALLER_LABEL = 'taller';
// Ubicaciones específicas mientras el vehículo está en la columna "Taller" (talleres externos / ITV).
const TALLER_LOCATION_OPTIONS = ['DMG Getafe', 'Norauto', 'DMG Villaverde', 'Olalla Car', 'LRC Taller', 'KIA', 'ITV'];
// Ubicaciones al salir de Taller hacia Limpieza entrega.
const TALLER_DEPARTURE_LOCATION_OPTIONS = ['Tienda', 'Parque', 'Centro Comercial'];
// Quién puede mover un vehículo de Preparación a Taller.
const TALLER_MOVE_ROLES = ['encargado_preparacion', 'creador', 'responsable'];
// Por debajo de este ancho (tablets) la barra superior de escritorio muestra solo iconos para que quepa.
const COMPACT_HEADER_MAX_WIDTH = 1120;
const PREP_SHEET_EDIT_ROLES = ['encargado_preparacion', 'creador', 'responsable'];
// Quién puede iniciar el traslado de Taller a Limpieza entrega.
const TALLER_DEPARTURE_ROLES = ['encargado_preparacion', 'creador', 'responsable', 'comercial', 'captador'];
// Quién autoriza un traslado pendiente (y cuyo propio traslado se ejecuta al instante).
const TALLER_PRIVILEGED_ROLES = ['encargado_preparacion', 'creador', 'responsable'];
const STOCK_LABEL = 'stock';
const BUYER_TYPE_LABELS = { tienda: 'Tienda', profesionales: 'Profesionales', gestion_venta: 'Gestión de Venta', fuera: 'Vehículo de fuera' };
const BUYER_TYPE_OPTIONS = ['tienda', 'profesionales', 'gestion_venta', 'fuera'];
// "Vehículo de fuera" solo se puede elegir con el coche en Revisión F2 (no pasa a nuestro stock).
function buyerTypeOptionsFor(columnLabel) {
  const inF2 = columnLabel?.trim().toLowerCase() === 'revisión f2';
  return BUYER_TYPE_OPTIONS.filter(t => t !== 'fuera' || inF2);
}

// Vehículo de fuera: coche de otra tienda Flexicar que viene solo a la revisión F2 y se queda en esa columna.
// 1) taller: revisado y ya en la tienda (encargado de preparación / responsable / creador)
// 2) compra: el captador avisa a la tienda de origen y confirma que compra -> comisión "Recepción compra fuera" (25 €)
// 3) transporte: la tienda de origen lo recoge -> sale del tablero (captador / preparación / responsable / creador)
const FUERA_COLLECT_ROLES = ['encargado_preparacion', 'creador', 'responsable'];
const FUERA_COMMISSION_AMOUNT = 25;
function isFuera(vehicle) {
  return vehicle?.buyer_type === 'fuera';
}
function fueraState(vehicle, check) {
  if (vehicle.fuera_collected_at) return 'recogido';
  if (vehicle.fuera_confirmed_at) return 'transporte';
  if (!check || !check.step1_done) return 'taller';
  return 'compra';
}
function fueraAuthorized(state, vehicle, role, currentUserId) {
  if (state === 'taller') return F2_STEP1_ROLES.includes(role);
  if (state === 'compra') return F2_STEP3_ROLES.includes(role) || (!!vehicle.purchased_by && vehicle.purchased_by === currentUserId);
  if (state === 'transporte') return FUERA_COLLECT_ROLES.includes(role) || (!!vehicle.purchased_by && vehicle.purchased_by === currentUserId);
  return false;
}
function fueraPendingBadge(vehicle, check) {
  const state = fueraState(vehicle, check);
  if (state === 'taller') return 'pte. taller';
  if (state === 'compra') return `pte. ${vehicle.purchased_profile?.full_name || 'captador'}`;
  if (state === 'transporte') return 'pte. transporte';
  return null;
}
function fueraPendingWhoText(vehicle, state) {
  if (state === 'taller') return 'el encargado de preparación, el responsable o el creador';
  if (state === 'compra') return vehicle.purchased_profile?.full_name || 'el captador con la compra asignada';
  if (state === 'transporte') return `${vehicle.purchased_profile?.full_name || 'el captador'}, el encargado de preparación, el responsable o el creador`;
  return '';
}
function FueraBadge({ store }) {
  return (
    <>
      <span title="Vehículo de fuera: viene de otra tienda Flexicar solo para la revisión F2" style={{backgroundColor: 'var(--b-1e3a8a)', color: 'var(--t-bfdbfe)', padding: '2px 6px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.03em'}}>
        FUERA
      </span>
      {store && (
        <span title="Tienda de origen: a ella se factura la revisión" style={{backgroundColor: 'var(--b-1f2937)', color: 'var(--t-bfdbfe)', border: '1px solid var(--l-1e3a8a)', padding: '1px 6px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 600}}>
          🏬 {store}
        </span>
      )}
    </>
  );
}

// Tienda de origen de un vehículo de fuera: texto libre con sugerencias de las tiendas ya usadas.
const FUERA_STORE_MAX = 80;
function fueraStoreError(store) {
  const v = (store || '').trim();
  if (!v) return 'Indica de qué tienda es el vehículo de fuera.';
  if (v.length > FUERA_STORE_MAX) return `La tienda no puede pasar de ${FUERA_STORE_MAX} caracteres.`;
  if (/[<>]/.test(v)) return 'La tienda no puede llevar < ni >.';
  return null;
}
function useKnownFueraStores(enabled) {
  const [stores, setStores] = useState([]);
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    supabase.from('vehicles').select('fuera_store').eq('buyer_type', 'fuera').not('fuera_store', 'is', null).limit(1000)
      .then(({ data }) => {
        if (cancelled) return;
        const seen = new Map();
        (data || []).forEach(r => { const v = (r.fuera_store || '').trim(); if (v && !seen.has(v.toLowerCase())) seen.set(v.toLowerCase(), v); });
        setStores([...seen.values()].sort((a, b) => a.localeCompare(b, 'es')));
      });
    return () => { cancelled = true; };
  }, [enabled]);
  return stores;
}
function FueraStoreInput({ id, value, onChange, disabled, stores }) {
  return (
    <>
      <input id={id} list={`${id}-list`} style={{...S.input, boxSizing: 'border-box'}} maxLength={FUERA_STORE_MAX} value={value} disabled={disabled}
        onChange={e => onChange(e.target.value)} placeholder="Ej.: Flexicar Leganés" autoComplete="off" />
      <datalist id={`${id}-list`}>
        {(stores || []).map(st => <option key={st} value={st} />)}
      </datalist>
    </>
  );
}
// Gestión de Venta: lleva la viñeta "GV" hasta que se confirma el cierre de gestión, y en Stock se ve en
// la vista Tienda. Al pasar a Preparación el sistema asigna el cierre a quien compró el coche y le avisa.
const GV_CONFIRM_ROLES = ['creador', 'responsable'];
function isGestionVenta(vehicle) {
  return vehicle?.buyer_type === 'gestion_venta';
}
function gvPending(vehicle) {
  return isGestionVenta(vehicle) && !vehicle.gv_closed_at;
}
function GvBadge() {
  return (
    <span title="Gestión de Venta: hay que hacer el cierre de la gestión antes de la validación" style={{backgroundColor: 'var(--b-166534)', color: 'var(--t-bbf7d0)', padding: '2px 6px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.03em'}}>
      GV
    </span>
  );
}
// Quién gestiona la etiqueta Tienda/Profesionales y los coches de Profesionales en Stock.
const BUYER_TYPE_ROLES = ['creador', 'responsable'];
const BUYER_TYPE_CHANGE_COLUMN_LABELS = ['revisión f2', 'limpieza fotos', 'pendiente fotos', 'pendiente publicar', STOCK_LABEL];

function isLockedProfesionales(vehicle, columns) {
  if (vehicle?.buyer_type !== 'profesionales') return false;
  return columns.find(c => c.id === vehicle.column_id)?.label?.trim().toLowerCase() === STOCK_LABEL;
}

// En Stock solo se muestran los vehículos de la vista elegida (Tienda o Profesionales).
function visibleInStockView(vehicle, col, stockBuyerView) {
  if (col?.label?.trim().toLowerCase() !== STOCK_LABEL) return true;
  return (vehicle.buyer_type === 'profesionales' ? 'profesionales' : 'tienda') === stockBuyerView;
}

function locationOptionsForColumnLabel(label) {
  return label?.trim().toLowerCase() === TALLER_LABEL ? TALLER_LOCATION_OPTIONS : LOCATION_OPTIONS;
}

// Traslado Taller -> Limpieza entrega (ubicación y, opcionalmente, fecha de entrega):
// 'form'              -> aún no hay solicitud.
// 'pending-authorize' -> lo pidió un comercial/captador: falta que lo autorice encargado_preparacion/responsable/creador.
function tallerTransferState(transfer) {
  return transfer ? 'pending-authorize' : 'form';
}

function tallerTransferAuthorized(state, role) {
  if (state === 'form') return TALLER_DEPARTURE_ROLES.includes(role);
  return TALLER_PRIVILEGED_ROLES.includes(role);
}

function tallerPendingBadge(transfer) {
  return transfer ? 'pte. autorización' : null;
}

const DELIVERY_TIME_SLOTS = (() => {
  const slots = [];
  for (let h = 10; h < 20; h++) {
    slots.push(`${String(h).padStart(2, '0')}:00`);
    slots.push(`${String(h).padStart(2, '0')}:30`);
  }
  slots.push('20:00');
  return slots;
})();
const WEEKDAY_NAMES_ES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

function deliveryAuthorLabel(d) {
  if (d.delivered_by) return d.profiles?.full_name || 'Sin nombre';
  return d.is_professional_sale ? 'Entrega profesionales' : 'Cruzada (otra tienda)';
}

// Ventas en menos de 60 días desde el alta, compradas por un captador (comisión extra).
// Se imputan al mes de la entrega.
function quickSalesFor(deliveries, profileId, year, month) {
  return deliveries.filter(d => {
    if (!d.quick_sale || d.purchased_by !== profileId || !d.delivered_at) return false;
    const dt = new Date(d.delivered_at);
    return dt.getFullYear() === year && dt.getMonth() + 1 === month;
  });
}

function quickSaleDays(d) {
  if (!d.vehicle_created_at || !d.delivered_at) return null;
  return Math.floor((new Date(d.delivered_at) - new Date(d.vehicle_created_at)) / 86400000);
}

function fallthroughsCount(fallthroughs, profileId, year, month) {
  return fallthroughs.filter(f => {
    const dt = new Date(f.created_at);
    return f.profile_id === profileId && dt.getFullYear() === year && dt.getMonth() + 1 === month;
  }).length;
}

function deliveriesCount(deliveries, profileIdOrNull, year, month) {
  return deliveries.filter(d => {
    if (!d.delivered_at || d.is_professional_sale) return false;
    const dt = new Date(d.delivered_at);
    if (dt.getFullYear() !== year || dt.getMonth() + 1 !== month) return false;
    return profileIdOrNull === null ? true : d.delivered_by === profileIdOrNull;
  }).length;
}

// Unidad de compra: cuando un vehículo llega a "Stock" y pierde su asignación de compra
// (ver handle_vehicle_column_change). Las compras de empresa (is_company, sin purchased_by)
// cuentan en el total de la tienda pero no en el objetivo individual de nadie.
function purchasesCount(purchases, profileIdOrNull, year, month) {
  return purchases.filter(p => {
    if (p.is_company) return false; // las compras de empresa son una métrica aparte: no cuentan aquí
    if (!p.purchased_at) return false;
    const dt = new Date(p.purchased_at);
    if (dt.getFullYear() !== year || dt.getMonth() + 1 !== month) return false;
    return profileIdOrNull === null ? true : p.purchased_by === profileIdOrNull;
  }).length;
}

function companyPurchasesCount(purchases, year, month) {
  return purchases.filter(p => {
    if (!p.is_company || !p.purchased_at) return false;
    const dt = new Date(p.purchased_at);
    return dt.getFullYear() === year && dt.getMonth() + 1 === month;
  }).length;
}

function monthlyTargetFor(monthlyTargets, profileIdOrNull, year, month, metric = 'ventas') {
  const row = monthlyTargets.find(t => (t.profile_id || null) === profileIdOrNull && t.year === year && t.month === month && (t.metric || 'ventas') === metric);
  return row ? row.target : null;
}

function targetProgressLabel(count, target) {
  if (target == null) return String(count);
  const pct = target > 0 ? Math.round((count / target) * 100) : 0;
  return `${count}/${target} (${pct}%)`;
}
const PREP_SECTIONS = [
  { key: 'itv', label: 'ITV' },
  { key: 'mecanica', label: 'Mecánica' },
  { key: 'pintura', label: 'Pintura' },
  { key: 'observaciones', label: 'Observaciones' }
];

function prepSheetSectionComplete(sheet, key) {
  if (!sheet) return false;
  if (key === 'itv') return !sheet.itv_needed || sheet.itv_done;
  if (key === 'pintura') return (sheet.pintura || []).length === 0 || sheet.pintura_done;
  const items = sheet[key] || [];
  return items.length === 0 || items.every(i => i.done);
}

function prepSheetComplete(sheet) {
  if (!sheet) return false;
  return PREP_SECTIONS.every(s => prepSheetSectionComplete(sheet, s.key));
}

function assignedSaleLabel(vehicle) {
  if (vehicle.assigned_profile?.full_name) return vehicle.assigned_profile.full_name;
  if (vehicle.sale_cross_store) return 'Cruzada (otra tienda)';
  return null;
}
function assignedPurchaseLabel(vehicle) {
  if (vehicle.purchased_profile?.full_name) return vehicle.purchased_profile.full_name;
  if (vehicle.purchase_company) return 'Compras empresa';
  return null;
}

// Proceso de 2-3 pasos al pasar un vehículo de "Revisión F2" a "Limpieza fotos".
const F2_STEP1_ROLES = ['encargado_preparacion', 'creador', 'responsable'];
const F2_STEP3_ROLES = ['creador', 'responsable'];

function f2StepState(check) {
  if (!check || !check.step1_done) return 'step1';
  if (!check.step2_by) return 'step2';
  if (check.step2_discount_60 === false && !check.step3_authorized) return 'step3';
  return 'done';
}

function f2StepAuthorized(state, vehicle, role, currentUserId) {
  if (state === 'step1') return F2_STEP1_ROLES.includes(role);
  if (state === 'step2') {
    // El creador y el responsable pueden completar este paso aunque no sean
    // el usuario con la compra asignada.
    if (F2_STEP3_ROLES.includes(role)) return true;
    if (vehicle.purchase_company) return role === 'encargado_preparacion';
    return vehicle.purchased_by === currentUserId;
  }
  if (state === 'step3') return F2_STEP3_ROLES.includes(role);
  return false;
}

function f2PendingBadge(vehicle, check) {
  if (isFuera(vehicle)) return fueraPendingBadge(vehicle, check);
  const state = f2StepState(check);
  if (state === 'step1') return 'pte. taller';
  if (state === 'step2') {
    if (vehicle.purchase_company) return 'pte. Encargado Preparación';
    return `pte. ${vehicle.purchased_profile?.full_name || 'comprador'}`;
  }
  if (state === 'step3') return 'pte. encargado';
  return null;
}

function f2PendingWhoText(vehicle, state) {
  if (state === 'step1') return 'el encargado de preparación, el responsable o el creador';
  if (state === 'step2') {
    if (vehicle.purchase_company) return 'el encargado de preparación';
    return vehicle.purchased_profile?.full_name ? vehicle.purchased_profile.full_name : 'el usuario con la compra asignada';
  }
  if (state === 'step3') return 'el responsable o el creador';
  return '';
}
// Quién puede crear vehículos nuevos
// Quién puede confirmar la revisión de un vehículo añadido "Sin F2" (fuera de Revisión F2).
const SIN_F2_CONFIRM_ROLES = ['encargado_preparacion', 'responsable', 'creador'];
const CREATE_VEHICLE_ROLES = ['creador', 'responsable', 'comercial', 'captador', 'encargado_preparacion', 'encargado_garantias'];

function roleLabel(role) {
  return ROLE_LABELS[role] || role || 'Viewer';
}

function Dashboard() {
  const { user, org, profile, loading } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [showAddVehicleModal, setShowAddVehicleModal] = useState(false);
  const [showManageCols, setShowManageCols] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  // Tablet (y pantallas estrechas): vista de escritorio con la barra superior compacta (solo iconos).
  const [isCompact, setIsCompact] = useState(window.innerWidth >= 768 && window.innerWidth < COMPACT_HEADER_MAX_WIDTH);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileActiveCol, setMobileActiveCol] = useState(0);
  const [showMetrics, setShowMetrics] = useState(false);
  const [selVeh, setSelVeh] = useState(null);
  const [showUsers, setShowUsers] = useState(false);
  const [pending, setPending] = useState(null);
  const [draggedVehicle, setDraggedVehicle] = useState(null);
  const [mySubscriptions, setMySubscriptions] = useState(new Set());
  const [followFilter, setFollowFilter] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedVehicleId, setHighlightedVehicleId] = useState(null);
  const [prepSheets, setPrepSheets] = useState({});
  const [showNotifPrefs, setShowNotifPrefs] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [showDeliveryCalendar, setShowDeliveryCalendar] = useState(false);
  const [deliveries, setDeliveries] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [monthlyTargets, setMonthlyTargets] = useState([]);
  const [showTargets, setShowTargets] = useState(false);
  const [f2Checks, setF2Checks] = useState({});
  const [tallerTransfers, setTallerTransfers] = useState({});
  const [purchaseFallthroughs, setPurchaseFallthroughs] = useState([]);
  const [stockBuyerView, setStockBuyerView] = useState('tienda');
  const [showQuickSales, setShowQuickSales] = useState(false);
  const [showCommissions, setShowCommissions] = useState(false);
  const [commissionsProfileId, setCommissionsProfileId] = useState(null);
  const toggleStockBuyerView = () => setStockBuyerView(v => v === 'tienda' ? 'profesionales' : 'tienda');
  // Módulo de Leads (src/leads/): datos en vivo para el botón 📇 y la ventana. ?leads=<id> (enlace de los
  // correos de aviso) abre ese lead al entrar.
  const leadsData = useLeadsLive({ supabase, orgId: org?.id, userId: profile?.id });
  const [showLeads, setShowLeads] = useState(false);
  const [leadsVehicleId, setLeadsVehicleId] = useState(null);
  const [leadsDeepLink, setLeadsDeepLink] = useState(() => new URLSearchParams(window.location.search).get('leads'));
  useEffect(() => {
    if (!leadsDeepLink || leadsData.loading) return;
    if (leadsData.access.ok) setShowLeads(true);
    else setLeadsDeepLink(null);
    const url = new URL(window.location.href);
    url.searchParams.delete('leads');
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);
  }, [leadsDeepLink, leadsData.loading, leadsData.access.ok]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsCompact(window.innerWidth >= 768 && window.innerWidth < COMPACT_HEADER_MAX_WIDTH);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!highlightedVehicleId) return;
    const el = document.getElementById(`vehicle-card-${highlightedVehicleId}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    const t = setTimeout(() => setHighlightedVehicleId(null), 2200);
    return () => clearTimeout(t);
  }, [highlightedVehicleId]);

  // Mantiene la ficha abierta (selVeh) sincronizada con los datos frescos tras cada loadAll(),
  // para que acciones como confirmar validación o marcar tareas se reflejen sin cerrar y reabrir.
  useEffect(() => {
    setSelVeh(prev => {
      if (!prev) return prev;
      const fresh = vehicles.find(v => v.id === prev.id);
      return fresh || prev;
    });
  }, [vehicles]);

  useEffect(() => {
    if (org?.id) loadAll();
  }, [org?.id]);

  // Cambios en vivo: cuando alguien mueve un coche, comenta o cambia algo, la base de datos manda un aviso ligero
  // (sin datos) al canal privado de la empresa (trigger trg_rt_signal → realtime.send) y el tablero se recarga solo
  // (agrupando los avisos de una misma acción). Al volver a la pestaña o recuperar la conexión, también se recarga
  // por si se perdió algún aviso mientras estaba en segundo plano.
  const loadAllRef = React.useRef(null);
  loadAllRef.current = loadAll;
  useEffect(() => {
    if (!org?.id) return undefined;
    let timer = null;
    const refresh = () => {
      clearTimeout(timer);
      timer = setTimeout(() => { if (loadAllRef.current) loadAllRef.current(); }, 400);
    };
    const channel = supabase.channel(`org:${org.id}:tablero`, { config: { private: true } });
    channel.on('broadcast', { event: 'changed' }, ({ payload }) => {
      // Comentarios: los recoge la ficha abierta (VehicleDetailPanel); el resto recarga el tablero.
      if (payload && payload.table === 'vehicle_comments') {
        window.dispatchEvent(new CustomEvent('enmarcha:comment-change', { detail: { vehicle_id: payload.vehicle_id } }));
      } else {
        refresh();
      }
    });
    let subscribedBefore = false;
    let cancelled = false;
    // Canal privado: necesita el token de la sesión antes de entrar.
    Promise.resolve(supabase.realtime.setAuth()).catch(() => {}).finally(() => {
      if (cancelled) return;
      channel.subscribe((status) => {
        if (status !== 'SUBSCRIBED') return;
        if (subscribedBefore) refresh(); // reconectado: ponerse al día
        subscribedBefore = true;
      });
    });
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', refresh);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      supabase.removeChannel(channel);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', refresh);
    };
  }, [org?.id]);

  async function loadAll() {
    setLoadingData(true);
    const queries = [
      supabase.from('columns').select('*').eq('org_id', org.id).order('position'),
      supabase.from('vehicles').select('*, org_id, column_id, assigned_profile:assigned_to(full_name), purchased_profile:purchased_by(full_name)').eq('org_id', org.id),
      supabase.from('vehicle_subscriptions').select('vehicle_id').eq('profile_id', profile.id),
      supabase.from('vehicle_prep_sheets').select('*').eq('org_id', org.id),
      supabase.from('vehicle_deliveries').select('id, brand, model, plate, delivered_by, delivered_at, is_professional_sale, purchased_by, quick_sale, vehicle_created_at').eq('org_id', org.id),
      supabase.from('vehicle_purchases').select('id, purchased_by, is_company, purchased_at').eq('org_id', org.id).is('cancelled_at', null),
      supabase.from('monthly_targets').select('id, profile_id, year, month, target, metric').eq('org_id', org.id),
      supabase.from('vehicle_f2_checks').select('*').eq('org_id', org.id),
      supabase.from('vehicle_taller_transfers').select('*').eq('org_id', org.id).eq('authorized', false),
      // RLS solo devuelve filas a creador/responsable; para el resto llega vacío.
      supabase.from('purchase_fallthroughs').select('id, profile_id, created_at').eq('org_id', org.id)
    ];

    if (!canMoveAll && profile?.id) {
      queries.push(supabase.from('column_permissions').select('column_id').eq('profile_id', profile.id));
    }

    const results = await Promise.all(queries);
    setColumns(results[0].data || []);
    // Los vehículos de fuera ya recogidos por su tienda de origen salen del tablero (se conservan en la base de datos).
    setVehicles((results[1].data || []).filter(v => !v.fuera_collected_at));
    setMySubscriptions(new Set((results[2].data || []).map(s => s.vehicle_id)));
    const sheetsMap = {};
    (results[3].data || []).forEach(s => { sheetsMap[s.vehicle_id] = s; });
    setPrepSheets(sheetsMap);
    setDeliveries(results[4].data || []);
    setPurchases(results[5].data || []);
    setMonthlyTargets(results[6].data || []);
    const f2Map = {};
    (results[7].data || []).forEach(c => { f2Map[c.vehicle_id] = c; });
    setF2Checks(f2Map);
    const tallerMap = {};
    (results[8].data || []).forEach(t => { tallerMap[t.vehicle_id] = t; });
    setTallerTransfers(tallerMap);
    setPurchaseFallthroughs(results[9].data || []);
    if (results[10]) setColPerms(results[10].data?.map(p => p.column_id));
    setLoadingData(false);
    setInitialLoadDone(true);
  }

  function canMoveInCol(colId) {
    if (canMoveAll) return true;
    return colPerms.includes(colId);
  }

  // opts.advance ('venta' | 'compra'): viene del botón "Avanzar"; la pregunta del cambio de columna sale al final
  // del paso (después de rellenar lo que pida) o, si no hay nada que rellenar, directamente.
  function requestMove(vehicle, targetColumnId, opts) {
    if (vehicle.column_id === targetColumnId) return;
    if (isLockedProfesionales(vehicle, columns)) {
      window.alert('Este vehículo está etiquetado como Profesionales y no se puede mover desde Stock. El creador o el responsable pueden cambiar la etiqueta a Tienda o marcarlo como entregado desde su ficha.');
      return;
    }
    setPending({vehicle, targetColumnId, advance: opts?.advance || null});
  }

  function handleDropOnColumn(targetColumnId) {
    if (draggedVehicle) requestMove(draggedVehicle, targetColumnId);
    setDraggedVehicle(null);
  }

  async function confirmMove() {
    if (!pending) return;
    try {
      await supabase.from('vehicles').update({
        column_id: pending.targetColumnId
      }).eq('id', pending.vehicle.id);
      loadAll();
    } catch (error) {
      console.error('Error al mover vehículo:', error);
    } finally {
      setPending(null);
    }
  }

  const getAvailableRoles = () => {
    const staffRoles = ['comercial', 'captador', 'logistica', 'encargado_preparacion', 'encargado_garantias', 'administracion', 'responsable'];
    if (profile?.role === 'creador') return ['creador', ...staffRoles];
    if (profile?.role === 'responsable') return staffRoles;
    return [];
  };

  const role = profile?.role;
  const canMoveAll = ADMIN_TIER_ROLES.includes(role);
  // El permiso real de arrastrar a una columna concreta se comprueba por columna (canMoveInCol);
  // aquí solo permitimos iniciar el arrastre para que ese chequeo pueda aplicarse en el drop.
  const canDragCards = true;
  const [colPerms, setColPerms] = useState([]);
  // null = comprobando; true = aún no ha elegido su código de seguridad de los correos (obligatorio).
  const [needsSecurityCode, setNeedsSecurityCode] = useState(null);
  const [theme, setTheme] = useState(currentTheme());

  useEffect(() => {
    if (!profile?.id) return;
    let cancelled = false;
    supabase.from('notification_settings').select('antiphishing_code, ui_theme').eq('profile_id', profile.id).maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        // Si la consulta falla no se bloquea la app: se volverá a pedir en el siguiente inicio.
        setNeedsSecurityCode(!error && !(data?.antiphishing_code || '').trim());
        if (data?.ui_theme && data.ui_theme !== currentTheme()) { applyTheme(data.ui_theme); setTheme(data.ui_theme); }
      });
    return () => { cancelled = true; };
  }, [profile?.id]);

  useEffect(() => {
    if (org) applyCobrand(org.co_brand);
  }, [org?.id, org?.co_brand]);

  const toggleTheme = () => {
    const next = theme === 'light' ? 'dark' : 'light';
    applyTheme(next);
    setTheme(next);
    if (profile?.id) {
      supabase.from('notification_settings')
        .upsert({ profile_id: profile.id, ui_theme: next, updated_at: new Date().toISOString() }, { onConflict: 'profile_id' })
        .then(() => {});
    }
  };

  if (loading || (loadingData && !initialLoadDone) || (profile?.id && needsSecurityCode === null)) {
    return (
      <BrandLoader text="Cargando tablero" />
    );
  }

  const targetNow = new Date();
  const targetYear = targetNow.getFullYear();
  const targetMonth = targetNow.getMonth() + 1;
  const storeTargetThisMonth = monthlyTargetFor(monthlyTargets, null, targetYear, targetMonth, 'ventas');
  const storePurchaseTargetThisMonth = monthlyTargetFor(monthlyTargets, null, targetYear, targetMonth, 'compras');

  // Nadie puede usar la app sin elegir antes su código de seguridad de los correos (antiphishing).
  if (needsSecurityCode) {
    return <SecurityCodeRequiredModal currentUserId={profile.id} onSaved={() => setNeedsSecurityCode(false)} onSignOut={signOut} />;
  }

  // El responsable no puede usar la app sin fijar antes los objetivos de entregas y de
  // compras de la tienda para el mes. El creador puede fijarlos también, pero nunca se le bloquea.
  if (role === 'responsable' && (storeTargetThisMonth == null || storePurchaseTargetThisMonth == null)) {
    return (
      <StoreTargetModal
        blocking
        year={targetYear}
        month={targetMonth}
        defaultVentas={storeTargetThisMonth}
        defaultCompras={storePurchaseTargetThisMonth}
        onSaved={loadAll}
      />
    );
  }

  async function signOut() {
    await supabase.auth.signOut();
  }

  const visibleColumns = followFilter
    ? columns.filter(col => vehicles.some(v => v.column_id === col.id && mySubscriptions.has(v.id)))
    : columns;

  // Rango de columnas (por posición) donde se muestra la etiqueta del comercial asignado:
  // desde "Reservados" hasta "Entrega", ambos inclusive (incluye Taller y Limpieza entrega).
  const reservadosCol = columns.find(c => c.label?.trim().toLowerCase() === RESERVED_LABEL);
  const entregaCol = columns.find(c => isDeliveryColumnLabel(c.label));
  const inSaleAssignmentRange = (col) => {
    if (!reservadosCol || !entregaCol || !col) return false;
    return col.position >= reservadosCol.position && col.position <= entregaCol.position;
  };

  const mobileActiveColumn = visibleColumns[mobileActiveCol];
  const mobileColVehicles = mobileActiveColumn
    ? vehicles.filter(v => v.column_id === mobileActiveColumn.id && (!followFilter || mySubscriptions.has(v.id)) && visibleInStockView(v, mobileActiveColumn, stockBuyerView)).sort((a, b) => daysInColumn(b) - daysInColumn(a))
    : [];
  const mobileAlertCount = mobileActiveColumn?.alert_days != null
    ? mobileColVehicles.filter(v => daysInColumn(v) > mobileActiveColumn.alert_days).length
    : 0;
  const mobileShowAssignedBadge = inSaleAssignmentRange(mobileActiveColumn);
  const mobileIsPreparacionCol = mobileActiveColumn?.label?.trim().toLowerCase() === PREPARACION_LABEL;
  const mobileIsEntregaCol = isDeliveryColumnLabel(mobileActiveColumn?.label);
  const mobileIsRevisionF2Col = mobileActiveColumn?.label?.trim().toLowerCase() === REVISION_F2_LABEL;
  const mobileIsTallerCol = mobileActiveColumn?.label?.trim().toLowerCase() === TALLER_LABEL;

  const searchResults = searchQuery.trim().length >= 2
    ? vehicles.filter(v => {
        const q = searchQuery.trim().toLowerCase();
        return `${v.brand} ${v.model}`.toLowerCase().includes(q) || (v.plate || '').toLowerCase().includes(q);
      }).slice(0, 8)
    : [];

  function goToVehicle(vehicleId) {
    const v = vehicles.find(x => x.id === vehicleId);
    if (!v) return;
    setShowSearch(false);
    setSearchQuery('');
    // Si el filtro de Seguimientos ocultaría el resultado, se desactiva al navegar a él.
    const willFollowFilter = followFilter && mySubscriptions.has(vehicleId);
    if (followFilter !== willFollowFilter) setFollowFilter(willFollowFilter);
    if (isMobile) {
      const targetList = willFollowFilter
        ? columns.filter(col => vehicles.some(vv => vv.column_id === col.id && mySubscriptions.has(vv.id)))
        : columns;
      const idx = targetList.findIndex(c => c.id === v.column_id);
      if (idx >= 0) setMobileActiveCol(idx);
    }
    setHighlightedVehicleId(vehicleId);
  }

  // Roles "de plantilla" solo pueden incorporar vehículos nuevos en las columnas previas a "Stock"
  // (las etapas de entrada: revisión, fotos, etc.). Se usa la posición de "Stock" como referencia,
  // no los nombres de columna, porque esos se pueden renombrar desde Gestionar columnas.
  const stockColForCreate = columns.find(c => c.label?.trim().toLowerCase() === 'stock');
  const revisionF2ColForCreate = columns.find(c => c.label?.trim().toLowerCase() === REVISION_F2_LABEL);
  const limpiezaFotosColForCreate = columns.find(c => c.label?.trim().toLowerCase() === 'limpieza fotos');
  const addVehicleColumns = ADMIN_TIER_ROLES.includes(role)
    ? columns
    : role === 'encargado_preparacion'
      ? columns.filter(c => c.id === revisionF2ColForCreate?.id || c.id === limpiezaFotosColForCreate?.id)
      : (role === 'comercial' || role === 'captador')
        ? columns.filter(c => c.id === revisionF2ColForCreate?.id)
        : (stockColForCreate ? columns.filter(c => c.position < stockColForCreate.position) : []);

  function afterAssign() {
    loadAll();
    setPending(null);
  }

  return (
    <div style={{height: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: 'var(--b-0f172a)', color: 'var(--t-f1f5f9)', width: '100%', maxWidth: '100vw', overflowX: 'hidden'}}>
      {/* Header */}
      <div style={{flexShrink: 0, backgroundColor: 'var(--b-1e293b)', borderBottom: '1px solid var(--l-334155)', padding: isMobile ? '0.75rem' : '1rem'}}>
        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', minWidth: 0}}>
          <div style={{display: 'flex', alignItems: 'center', gap: isCompact ? '0.6rem' : '1rem', minWidth: 0}}>
            <img src={brandMarkSrc(theme)} alt="EnMarcha" style={{height: isMobile ? '26px' : '30px', width: 'auto', flexShrink: 0}} />
            <div>
              <div style={{display: 'flex', alignItems: 'center', fontFamily: BRAND.font, fontSize: isMobile ? '1rem' : '1.125rem', fontWeight: 300, letterSpacing: '0.08em', color: 'var(--em-wordmark)'}}>
                En<span style={{fontWeight: 600}}>Marcha</span>
                <CobrandSuffix cobrand={org?.co_brand} theme={theme} />
              </div>
              <div style={{fontSize: '0.75rem', color: 'var(--t-94a3b8)'}}>Flexicar Fuenlabrada</div>
            </div>
          </div>

          {/* Desktop Menu */}
          {!isMobile && (
            <div style={{display: 'flex', alignItems: 'center', gap: isCompact ? '0.15rem' : '1rem', flexShrink: 0}}>
              {followFilter && (
                <button
                  onClick={() => { setFollowFilter(false); setMobileActiveCol(0); }}
                  style={{
                    backgroundColor: 'var(--b-f59e0b)', color: '#1f2937', border: 'none',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem',
                    padding: '0.5rem 0.75rem', borderRadius: '0.375rem', fontSize: '0.875rem', fontWeight: 600
                  }}
                  title="Volver a ver todos los vehículos"
                >
                  {isCompact ? '🔔 ✕' : '🔔 Solo seguimientos ✕'}
                </button>
              )}

              <button
                onClick={toggleTheme}
                style={{
                  backgroundColor: 'transparent', color: 'var(--t-94a3b8)', border: 'none',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: isCompact ? '0.55rem' : '0.5rem', borderRadius: '0.375rem', fontSize: isCompact ? '1.1rem' : '0.875rem'
                }}
                title={theme === 'light' ? 'Cambiar a modo oscuro' : 'Cambiar a modo claro'}
                aria-label={theme === 'light' ? 'Cambiar a modo oscuro' : 'Cambiar a modo claro'}
              >
                {theme === 'light' ? (isCompact ? '🌙' : '🌙 Oscuro') : (isCompact ? '☀️' : '☀️ Claro')}
              </button>

              <button
                onClick={() => setShowManual(true)}
                style={{
                  backgroundColor: 'transparent', color: 'var(--t-94a3b8)', border: 'none',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: isCompact ? '0.55rem' : '0.5rem', borderRadius: '0.375rem', fontSize: isCompact ? '1.1rem' : '0.875rem'
                }}
                title="Manual de uso de EnMarcha"
                aria-label="Manual de uso de EnMarcha"
              >
                {isCompact ? '📖' : '📖 Ayuda'}
              </button>

              <button
                onClick={() => setShowNotifPrefs(true)}
                style={{
                  backgroundColor: 'transparent', color: 'var(--t-94a3b8)', border: 'none',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: isCompact ? '0.55rem' : '0.5rem', borderRadius: '0.375rem', fontSize: isCompact ? '1.1rem' : '0.875rem'
                }}
                title="Gestionar mis notificaciones por email"
                aria-label="Gestionar mis notificaciones por email"
              >
                {isCompact ? '⚙️' : '⚙️ Mis notificaciones'}
              </button>

              <div style={{position: 'relative'}}>
                <button
                  onClick={() => setShowSearch(s => !s)}
                  style={{
                    backgroundColor: showSearch ? 'var(--b-334155)' : 'transparent', color: 'var(--t-94a3b8)', border: 'none',
                    cursor: 'pointer', padding: '0.5rem', borderRadius: '0.375rem', fontSize: '1rem', display: 'flex'
                  }}
                  title="Buscar por modelo o matrícula"
                aria-label="Buscar por modelo o matrícula"
                >
                  🔍
                </button>
                {showSearch && (
                  <div style={{
                    position: 'absolute', top: '100%', right: 0, marginTop: '8px',
                    backgroundColor: 'var(--b-1e293b)', border: '1px solid var(--l-334155)', borderRadius: '8px',
                    padding: '10px', width: '280px', zIndex: 200, boxShadow: '0 10px 25px rgba(0,0,0,0.4)'
                  }}>
                    <input
                      autoFocus
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Modelo o matrícula..."
                      style={{...S.input, marginBottom: searchQuery.trim().length >= 2 ? '8px' : 0}}
                    />
                    {searchQuery.trim().length >= 2 && (
                      <div style={{maxHeight: '260px', overflowY: 'auto'}}>
                        {searchResults.length === 0 ? (
                          <p style={{color: 'var(--t-6b7280)', fontSize: '0.8rem', margin: 0, padding: '4px'}}>Sin resultados</p>
                        ) : searchResults.map(v => {
                          const col = columns.find(c => c.id === v.column_id);
                          return (
                            <button
                              key={v.id}
                              onClick={() => goToVehicle(v.id)}
                              style={{display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '8px', borderRadius: '6px', cursor: 'pointer'}}
                            >
                              <div style={{fontSize: '0.85rem', fontWeight: 600, color: 'var(--t-f1f5f9)'}}>{v.brand} {v.model}</div>
                              <div style={{fontSize: '0.75rem', color: 'var(--t-9ca3af)'}}>{v.plate} · {col?.label || '—'}</div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {MANAGE_USERS_ROLES.includes(role) && (
                <button
                  onClick={() => setShowUsers(true)}
                  style={{
                    backgroundColor: 'transparent', color: 'var(--t-94a3b8)', border: 'none',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem',
                    padding: isCompact ? '0.55rem' : '0.5rem', borderRadius: '0.375rem', fontSize: isCompact ? '1.1rem' : '0.875rem'
                  }}
                  title="Gestionar usuarios"
                aria-label="Gestionar usuarios"
                >
                  {isCompact ? '👥' : '👥 Usuarios'}
                </button>
              )}

              {leadsData.access.ok && (
                <button
                  onClick={() => setShowLeads(true)}
                  style={{
                    backgroundColor: 'transparent', color: 'var(--t-94a3b8)', border: 'none',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', position: 'relative',
                    padding: isCompact ? '0.55rem' : '0.5rem', borderRadius: '0.375rem', fontSize: isCompact ? '1.1rem' : '0.875rem'
                  }}
                  title={leadsData.unassignedCount ? `Leads: ${leadsData.unassignedCount} sin coger` : 'Leads'}
                  aria-label={leadsData.unassignedCount ? `Leads: ${leadsData.unassignedCount} sin coger` : 'Leads'}
                >
                  {isCompact ? '📇' : '📇 Leads'}
                  {leadsData.unassignedCount > 0 && (
                    <span style={{
                      backgroundColor: 'var(--b-991b1b)', color: 'var(--t-f9fafb)', borderRadius: '999px', fontSize: '0.65rem',
                      fontWeight: 700, minWidth: '18px', height: '18px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      padding: '0 5px', ...(isCompact ? { position: 'absolute', top: '1px', right: '0px' } : {})
                    }}>
                      {leadsData.unassignedCount}
                    </span>
                  )}
                </button>
              )}

              {(COMMISSION_ROLES.includes(role) || CAPTADOR_COMMISSION_ROLES.includes(role) || ADMIN_TIER_ROLES.includes(role)) && (
                <button
                  onClick={() => setShowCommissions(true)}
                  style={{
                    backgroundColor: 'transparent', color: 'var(--t-94a3b8)', border: 'none',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem',
                    padding: isCompact ? '0.55rem' : '0.5rem', borderRadius: '0.375rem', fontSize: isCompact ? '1.1rem' : '0.875rem'
                  }}
                  title="Ver y completar comisiones"
                aria-label="Ver y completar comisiones"
                >
                  {isCompact ? '💶' : '💶 Comisiones'}
                </button>
              )}

              {role === 'creador' && (
                <button
                  onClick={() => setShowManageCols(true)}
                  style={{
                    backgroundColor: 'transparent', color: 'var(--t-94a3b8)', border: 'none',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem',
                    padding: isCompact ? '0.55rem' : '0.5rem', borderRadius: '0.375rem', fontSize: isCompact ? '1.1rem' : '0.875rem'
                  }}
                  title="Gestionar columnas"
                aria-label="Gestionar columnas"
                >
                  {isCompact ? '🏛️' : '🏛️ Columnas'}
                </button>
              )}

              {CREATE_VEHICLE_ROLES.includes(role) && (
                <button
                  onClick={() => setShowAddVehicleModal(true)}
                  style={{
                    backgroundColor: 'var(--b-f59e0b)', color: '#1f2937', border: 'none',
                    padding: '0.5rem 1rem', borderRadius: '0.375rem', fontWeight: '500',
                    cursor: 'pointer', fontSize: '0.875rem'
                  }}
                >
                  {isCompact ? '+ Añadir' : '+ Añadir Vehículo'}
                </button>
              )}

              <button
                onClick={signOut}
                style={{
                  backgroundColor: 'transparent', color: 'var(--t-94a3b8)', border: 'none',
                  cursor: 'pointer', padding: '0.5rem', borderRadius: '0.375rem',
                  fontSize: '0.875rem', whiteSpace: 'nowrap'
                }}
                title="Cerrar sesión"
                aria-label="Cerrar sesión"
              >
                {isCompact ? 'Salir' : 'Cerrar sesión'}
              </button>
            </div>
          )}

          {/* Mobile: filtro de seguimientos activo, búsqueda y menú */}
          {isMobile && (
            <div style={{display: 'flex', alignItems: 'center', gap: '0.25rem'}}>
              {followFilter && (
                <button
                  onClick={() => { setFollowFilter(false); setMobileActiveCol(0); }}
                  style={{
                    backgroundColor: 'var(--b-f59e0b)', color: '#1f2937', border: 'none',
                    fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', padding: '0.4rem 0.5rem', borderRadius: '0.375rem'
                  }}
                  title="Quitar el filtro de seguimientos"
                >
                  🔔 ✕
                </button>
              )}
              {leadsData.access.ok && (
                <button
                  onClick={() => setShowLeads(true)}
                  style={{
                    backgroundColor: 'transparent', color: 'var(--t-f1f5f9)', border: 'none', position: 'relative',
                    fontSize: '1.1rem', cursor: 'pointer', padding: '0.4rem', borderRadius: '0.375rem'
                  }}
                  title="Leads"
                  aria-label={leadsData.unassignedCount ? `Leads: ${leadsData.unassignedCount} sin coger` : 'Leads'}
                >
                  📇
                  {leadsData.unassignedCount > 0 && (
                    <span style={{
                      position: 'absolute', top: '0px', right: '-2px', backgroundColor: 'var(--b-991b1b)', color: 'var(--t-f9fafb)',
                      borderRadius: '999px', fontSize: '0.6rem', fontWeight: 700, minWidth: '16px', height: '16px',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px'
                    }}>
                      {leadsData.unassignedCount}
                    </span>
                  )}
                </button>
              )}
              <div style={{position: 'relative'}}>
                <button
                  onClick={() => setShowSearch(s => !s)}
                  style={{
                    backgroundColor: showSearch ? 'var(--b-334155)' : 'transparent', color: 'var(--t-f1f5f9)', border: 'none',
                    fontSize: '1.1rem', cursor: 'pointer', padding: '0.4rem', borderRadius: '0.375rem'
                  }}
                >
                  🔍
                </button>
                {showSearch && (
                  <div style={{
                    position: 'absolute', top: '100%', right: 0, marginTop: '8px',
                    backgroundColor: 'var(--b-1e293b)', border: '1px solid var(--l-334155)', borderRadius: '8px',
                    padding: '10px', width: '260px', zIndex: 200, boxShadow: '0 10px 25px rgba(0,0,0,0.4)'
                  }}>
                    <input
                      autoFocus
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Modelo o matrícula..."
                      style={{...S.input, marginBottom: searchQuery.trim().length >= 2 ? '8px' : 0}}
                    />
                    {searchQuery.trim().length >= 2 && (
                      <div style={{maxHeight: '240px', overflowY: 'auto'}}>
                        {searchResults.length === 0 ? (
                          <p style={{color: 'var(--t-6b7280)', fontSize: '0.8rem', margin: 0, padding: '4px'}}>Sin resultados</p>
                        ) : searchResults.map(v => {
                          const col = columns.find(c => c.id === v.column_id);
                          return (
                            <button
                              key={v.id}
                              onClick={() => goToVehicle(v.id)}
                              style={{display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '8px', borderRadius: '6px', cursor: 'pointer'}}
                            >
                              <div style={{fontSize: '0.85rem', fontWeight: 600, color: 'var(--t-f1f5f9)'}}>{v.brand} {v.model}</div>
                              <div style={{fontSize: '0.75rem', color: 'var(--t-9ca3af)'}}>{v.plate} · {col?.label || '—'}</div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                style={{
                  backgroundColor: 'transparent', color: 'var(--t-f1f5f9)', border: 'none',
                  fontSize: '1.5rem', cursor: 'pointer', padding: '0.5rem'
                }}
              >
                ☰
              </button>
            </div>
          )}
        </div>

        {/* Mobile Menu */}
        {isMobile && mobileMenuOpen && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 50
          }}>
            <div style={{
              position: 'fixed', top: 0, right: 0, bottom: 0, width: '280px',
              backgroundColor: 'var(--b-1e293b)', padding: '1rem', overflowY: 'auto'
            }}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem'}}>
                <h3 style={{margin: 0, color: 'var(--t-f1f5f9)'}}>Menú</h3>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  style={{backgroundColor: 'transparent', color: 'var(--t-f1f5f9)', border: 'none', fontSize: '1.5rem', cursor: 'pointer'}}
                >
                  ✕
                </button>
              </div>

              <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
                <div style={{color: 'var(--t-9ca3af)', fontSize: '0.8rem', padding: '0 0.25rem'}}>Mi rol: {roleLabel(role)}</div>

                {MANAGE_USERS_ROLES.includes(role) && (
                  <button
                    onClick={() => { setShowUsers(true); setMobileMenuOpen(false); }}
                    style={{
                      backgroundColor: 'var(--b-334155)', color: 'var(--t-f1f5f9)', border: 'none',
                      padding: '0.75rem', borderRadius: '0.375rem', textAlign: 'left',
                      cursor: 'pointer', fontSize: '0.875rem'
                    }}
                  >
                    👥 Usuarios
                  </button>
                )}

                {leadsData.access.ok && (
                  <button
                    onClick={() => { setShowLeads(true); setMobileMenuOpen(false); }}
                    style={{
                      backgroundColor: 'var(--b-334155)', color: 'var(--t-f1f5f9)', border: 'none',
                      padding: '0.75rem', borderRadius: '0.375rem', textAlign: 'left',
                      cursor: 'pointer', fontSize: '0.875rem'
                    }}
                  >
                    📇 Leads{leadsData.unassignedCount > 0 ? ` (${leadsData.unassignedCount} sin coger)` : ''}
                  </button>
                )}

                {(COMMISSION_ROLES.includes(role) || CAPTADOR_COMMISSION_ROLES.includes(role) || ADMIN_TIER_ROLES.includes(role)) && (
                  <button
                    onClick={() => { setShowCommissions(true); setMobileMenuOpen(false); }}
                    style={{
                      backgroundColor: 'var(--b-334155)', color: 'var(--t-f1f5f9)', border: 'none',
                      padding: '0.75rem', borderRadius: '0.375rem', textAlign: 'left',
                      cursor: 'pointer', fontSize: '0.875rem'
                    }}
                  >
                    💶 Comisiones
                  </button>
                )}

                {role === 'creador' && (
                  <button
                    onClick={() => { setShowManageCols(true); setMobileMenuOpen(false); }}
                    style={{
                      backgroundColor: 'var(--b-334155)', color: 'var(--t-f1f5f9)', border: 'none',
                      padding: '0.75rem', borderRadius: '0.375rem', textAlign: 'left',
                      cursor: 'pointer', fontSize: '0.875rem'
                    }}
                  >
                    🏛️ Columnas
                  </button>
                )}

                <button
                  onClick={() => { toggleTheme(); setMobileMenuOpen(false); }}
                  style={{
                    backgroundColor: 'var(--b-334155)', color: 'var(--t-f1f5f9)', border: 'none',
                    padding: '0.75rem', borderRadius: '0.375rem', textAlign: 'left',
                    cursor: 'pointer', fontSize: '0.875rem'
                  }}
                >
                  {theme === 'light' ? '🌙 Modo oscuro' : '☀️ Modo claro'}
                </button>

                <button
                  onClick={() => { setShowManual(true); setMobileMenuOpen(false); }}
                  style={{
                    backgroundColor: 'var(--b-334155)', color: 'var(--t-f1f5f9)', border: 'none',
                    padding: '0.75rem', borderRadius: '0.375rem', textAlign: 'left',
                    cursor: 'pointer', fontSize: '0.875rem'
                  }}
                >
                  📖 Ayuda
                </button>

                <button
                  onClick={() => { setShowNotifPrefs(true); setMobileMenuOpen(false); }}
                  style={{
                    backgroundColor: 'var(--b-334155)', color: 'var(--t-f1f5f9)', border: 'none',
                    padding: '0.75rem', borderRadius: '0.375rem', textAlign: 'left',
                    cursor: 'pointer', fontSize: '0.875rem'
                  }}
                >
                  ⚙️ Mis notificaciones
                </button>

                {CREATE_VEHICLE_ROLES.includes(role) && (
                  <button
                    onClick={() => { setShowAddVehicleModal(true); setMobileMenuOpen(false); }}
                    style={{
                      backgroundColor: 'var(--b-f59e0b)', color: '#1f2937', border: 'none',
                      padding: '0.75rem', borderRadius: '0.375rem', textAlign: 'left',
                      cursor: 'pointer', fontWeight: '500', fontSize: '0.875rem'
                    }}
                  >
                    + Añadir Vehículo
                  </button>
                )}

                <button
                  onClick={signOut}
                  style={{
                    backgroundColor: 'var(--b-7f1d1d)', color: 'var(--t-f1f5f9)', border: 'none',
                    padding: '0.75rem', borderRadius: '0.375rem', textAlign: 'left',
                    cursor: 'pointer', fontSize: '0.875rem'
                  }}
                >
                  Cerrar Sesión
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Kanban Board */}
      <div style={{flex: 1, minHeight: 0, overflowX: 'auto', overflowY: 'hidden', padding: '16px', display: 'flex', gap: '12px', alignItems: 'stretch'}}>
        {followFilter && visibleColumns.length === 0 ? (
          <div style={{width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--t-6b7280)', fontStyle: 'italic'}}>
            No tienes ningún vehículo en seguimiento. Activa la campanita 🔔 en la ficha de un coche para empezar (el filtro se quita en ⚙️ Mis notificaciones o con el botón 🔔 ✕ de arriba).
          </div>
        ) : isMobile ? (
          // Mobile: Tab selector + single column
          <div style={{width: '100%', display: 'flex', flexDirection: 'column', minHeight: 0}}>
            {/* Active Column Content */}
            {mobileActiveColumn && (
              <div style={{
                backgroundColor: 'var(--b-1e293b)',
                borderRadius: '8px',
                padding: '16px',
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px',
                  paddingBottom: '8px',
                  flexShrink: 0,
                  borderBottom: `2px solid ${mobileActiveColumn.color || 'var(--l-374151)'}`
                }}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0}}>
                    {/* Nombre de la columna = desplegable para cambiar de columna (selector nativo del móvil). */}
                    <div style={{
                      position: 'relative', display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0,
                      backgroundColor: 'var(--b-334155)', border: '1px solid var(--l-475569)', borderRadius: '8px', padding: '4px 10px'
                    }}>
                      <h3 style={{
                        margin: 0,
                        color: 'var(--t-f1f5f9)',
                        fontSize: '1.125rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {mobileActiveColumn.label}
                      </h3>
                      <span aria-hidden="true" style={{color: 'var(--t-f59e0b)', fontSize: '0.8rem', flexShrink: 0}}>▼</span>
                      <select
                        id="mobile-column-select"
                        aria-label="Cambiar de columna"
                        value={mobileActiveCol}
                        onChange={e => setMobileActiveCol(Number(e.target.value))}
                        style={{position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', fontSize: '16px'}}
                      >
                        {visibleColumns.map((col, index) => (
                          <option key={col.id} value={index}>
                            {col.label} ({vehicles.filter(v => v.column_id === col.id && (!followFilter || mySubscriptions.has(v.id)) && visibleInStockView(v, col, stockBuyerView)).length})
                          </option>
                        ))}
                      </select>
                    </div>
                    {mobileActiveColumn.label?.trim().toLowerCase() === STOCK_LABEL && (
                      <StockViewToggle view={stockBuyerView} onToggle={toggleStockBuyerView} />
                    )}
                    {mobileActiveColumn?.alert_days != null && (
                      <span
                        title={mobileAlertCount > 0 ? `${mobileAlertCount} vehículo(s) superan el límite de días de esta columna` : 'Ningún vehículo supera el límite de días de esta columna'}
                        style={{
                          backgroundColor: mobileAlertCount > 0 ? '#ef4444' : '#16a34a', color: '#fff', padding: '2px 6px',
                          borderRadius: '10px', fontSize: '0.7rem', fontWeight: 700, flexShrink: 0
                        }}
                      >
                        {mobileAlertCount > 0 ? `⚠️ ${mobileAlertCount}` : '✅ 0'}
                      </span>
                    )}
                  </div>
                  <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                    {mobileIsEntregaCol && (
                      <button
                        onClick={() => setShowDeliveryCalendar(true)}
                        title="Ver calendario semanal de entregas"
                        style={{background: 'none', border: 'none', color: 'var(--t-9ca3af)', cursor: 'pointer', fontSize: '1rem', padding: 0}}
                      >
                        📅
                      </button>
                    )}
                    <span style={{
                      backgroundColor: mobileActiveColumn.color || 'var(--b-374151)',
                      color: 'var(--t-f1f5f9)',
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '0.75rem'
                    }}>
                      {mobileColVehicles.length}
                    </span>
                  </div>
                </div>

                {/* Vehicles in mobile format */}
                <div style={{display: 'flex', flexDirection: 'column', gap: '12px', overflowY: 'auto', minHeight: 0}}>
                  {mobileColVehicles.map(vehicle => (
                      <div
                        key={vehicle.id}
                        id={`vehicle-card-${vehicle.id}`}
                        onClick={() => setSelVeh(vehicle)}
                        style={{
                          backgroundColor: 'var(--b-334155)',
                          padding: '12px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          border: '2px solid transparent',
                          boxShadow: vehicle.id === highlightedVehicleId ? '0 0 0 3px #CDA455, 0 0 24px 6px rgba(205,164,85,0.55)' : 'none',
                          transition: 'box-shadow 0.3s'
                        }}
                      >
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                          <h4 style={{margin: '0 0 4px', color: 'var(--t-f1f5f9)', fontSize: '1rem'}}>{vehicle.brand} {vehicle.model}</h4>
                          {mySubscriptions.has(vehicle.id) && <span title="Estás siguiendo este vehículo">🔔</span>}
                        </div>
                        <div style={{color: 'var(--t-9ca3af)', fontSize: '0.8rem', marginBottom: '6px'}}>{vehicle.plate}</div>

                        {(gvPending(vehicle) || isFuera(vehicle) || vehicle.sin_f2 || (mobileShowAssignedBadge && assignedSaleLabel(vehicle)) || assignedPurchaseLabel(vehicle) || (mobileIsRevisionF2Col && f2PendingBadge(vehicle, f2Checks?.[vehicle.id])) || (mobileIsTallerCol && tallerTransfers?.[vehicle.id]) || vehicle.location || (vehicle.paid && !vehicle.uploaded_to_validation) || (mobileIsPreparacionCol && prepSheetComplete(prepSheets?.[vehicle.id]))) && (
                          <div style={{display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '6px'}}>
                            {gvPending(vehicle) && <GvBadge />}
                            {isFuera(vehicle) && <FueraBadge store={vehicle.fuera_store} />}
                            {vehicle.sin_f2 && (
                              <span title="Añadido sin pasar por Revisión F2: pendiente de confirmar la revisión y la subida de F1/F2 al CRM" style={{backgroundColor: 'var(--b-7f1d1d)', color: 'var(--t-fecaca)', padding: '2px 6px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 700}}>
                                🚫 Sin F2
                              </span>
                            )}
                            {mobileShowAssignedBadge && assignedSaleLabel(vehicle) && (
                              <span style={{backgroundColor: 'var(--b-1e3a8a)', color: 'var(--t-93c5fd)', padding: '2px 6px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 600}}>
                                👤 {assignedSaleLabel(vehicle)}
                              </span>
                            )}
                            {assignedPurchaseLabel(vehicle) && (
                              <span style={{backgroundColor: 'var(--b-78350f)', color: 'var(--t-fbbf24)', padding: '2px 6px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 600}}>
                                🛒 {assignedPurchaseLabel(vehicle)}
                              </span>
                            )}
                            {mobileIsRevisionF2Col && f2PendingBadge(vehicle, f2Checks?.[vehicle.id]) && (
                              <span title="Proceso de salida de Revisión F2" style={{backgroundColor: 'var(--b-581c87)', color: 'var(--t-e9d5ff)', padding: '2px 6px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 600}}>
                                ⏳ {f2PendingBadge(vehicle, f2Checks?.[vehicle.id])}
                              </span>
                            )}
                            {mobileIsTallerCol && tallerTransfers?.[vehicle.id] && (
                              <span title="Traslado a Limpieza entrega en curso" style={{backgroundColor: 'var(--b-1e40af)', color: 'var(--t-bfdbfe)', padding: '2px 6px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 600}}>
                                🚦 {tallerPendingBadge(tallerTransfers[vehicle.id])}
                              </span>
                            )}
                            {vehicle.location && (
                              <span style={{backgroundColor: 'var(--b-374151)', color: 'var(--t-d1d5db)', padding: '2px 6px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 600}}>
                                📍 {vehicle.location}
                              </span>
                            )}
                            {vehicle.paid && !vehicle.uploaded_to_validation && (
                              <span title="Pendiente de subir a validar" style={{backgroundColor: 'var(--b-7f1d1d)', color: 'var(--t-fecaca)', padding: '2px 6px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 700}}>
                                ⚠️ {VALIDATION_ALERT_LABEL}
                              </span>
                            )}
                            {mobileIsPreparacionCol && prepSheetComplete(prepSheets?.[vehicle.id]) && (
                              <span title="Hoja de preparación completada" style={{backgroundColor: 'var(--b-166534)', color: 'var(--t-4ade80)', padding: '2px 6px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 700}}>
                                🔧 Recoger Taller
                              </span>
                            )}
                          </div>
                        )}

                        <div style={{display: 'flex', flexDirection: 'column', gap: '6px'}}>
                          <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                            <span style={{color: timeBadgeColor(vehicle, mobileActiveColumn, mobileColVehicles), fontSize: '0.875rem'}}>
                              {mobileActiveColumn.alert_days != null && daysInColumn(vehicle) > mobileActiveColumn.alert_days ? '⚠️' : '⏰'} {timeInColumnLabel(vehicle)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))
                  }
                </div>

                {mobileColVehicles.length === 0 && (
                  <div style={{
                    textAlign: 'center',
                    color: 'var(--t-6b7280)',
                    fontStyle: 'italic',
                    padding: '2rem'
                  }}>
                    Sin vehículos
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          // Desktop: Traditional Kanban columns
          visibleColumns.map(col => (
            <KanbanCol key={col.id} col={col}
              vehicles={vehicles.filter(v => v.column_id === col.id && (!followFilter || mySubscriptions.has(v.id)) && visibleInStockView(v, col, stockBuyerView)).sort((a, b) => daysInColumn(b) - daysInColumn(a))}
              stockBuyerView={stockBuyerView}
              onToggleStockBuyerView={toggleStockBuyerView}
              canDrop={canMoveInCol(col.id)}
              canDragCards={canDragCards}
              draggedVehicle={draggedVehicle}
              onDragStartVehicle={setDraggedVehicle}
              onDropColumn={() => handleDropOnColumn(col.id)}
              onSelectVehicle={setSelVeh}
              mySubscriptions={mySubscriptions}
              highlightedVehicleId={highlightedVehicleId}
              showAssignedBadge={inSaleAssignmentRange(col)}
              prepSheets={prepSheets}
              f2Checks={f2Checks}
              tallerTransfers={tallerTransfers}
              onOpenDeliveryCalendar={() => setShowDeliveryCalendar(true)}
            />
          ))
        )}
      </div>

      {/* Status Bar */}
      <div style={{
        backgroundColor: 'var(--b-1e293b)',
        borderTop: '1px solid var(--l-334155)',
        padding: '8px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '0.75rem',
        color: 'var(--t-94a3b8)'
      }}>
        <div style={{display: 'flex', gap: '16px'}}>
          <span title="Vehículos de la tienda (sin contar los de fuera, que no son nuestros)">🚗 Stock: <strong style={{color: 'var(--t-f9fafb)'}}>{vehicles.filter(v => !isFuera(v)).length}</strong></span>
          <span>📦 Entregados: <strong style={{color: 'var(--t-f9fafb)'}}>
            {targetProgressLabel(deliveriesCount(deliveries, null, targetYear, targetMonth), storeTargetThisMonth)}
          </strong></span>
          {SALE_ROLES.includes(role) && monthlyTargetFor(monthlyTargets, profile.id, targetYear, targetMonth, 'ventas') != null && (
            <span>🎯 Tu objetivo de ventas: <strong style={{color: 'var(--t-f9fafb)'}}>
              {targetProgressLabel(deliveriesCount(deliveries, profile.id, targetYear, targetMonth), monthlyTargetFor(monthlyTargets, profile.id, targetYear, targetMonth, 'ventas'))}
            </strong></span>
          )}
          {SALE_ROLES.includes(role) && monthlyTargetFor(monthlyTargets, profile.id, targetYear, targetMonth, 'compras') != null && (
            <span>🛒 Tu objetivo de compras: <strong style={{color: 'var(--t-f9fafb)'}}>
              {targetProgressLabel(purchasesCount(purchases, profile.id, targetYear, targetMonth), monthlyTargetFor(monthlyTargets, profile.id, targetYear, targetMonth, 'compras'))}
            </strong></span>
          )}
          {role === 'captador' && (
            <button
              onClick={() => setShowQuickSales(true)}
              title="Ver tus vehículos vendidos en menos de 60 días desde la compra"
              style={{background: 'none', border: 'none', color: 'var(--t-fbbf24)', cursor: 'pointer', fontSize: '0.75rem', textDecoration: 'underline', padding: 0}}
            >
              ⚡ Ventas &lt;60 días este mes: <strong>{quickSalesFor(deliveries, profile.id, targetYear, targetMonth).length}</strong>
            </button>
          )}
          {['creador', 'responsable'].includes(role) && (
            <button
              onClick={() => setShowTargets(true)}
              style={{background: 'none', border: 'none', color: 'var(--t-94a3b8)', cursor: 'pointer', fontSize: '0.75rem', textDecoration: 'underline', padding: 0}}
            >
              🎯 Objetivos
            </button>
          )}
          <span>⚠️ Alertas: <strong style={{color: '#ef4444'}}>{vehicles.filter(v => {
            const col = columns.find(c => c.id === v.column_id);
            return col?.alert_days != null && daysInColumn(v) > col.alert_days;
          }).length}</strong></span>
        </div>

        <span>Total: <strong style={{color: 'var(--t-f9fafb)'}}>{vehicles.length}</strong></span>

        {METRICS_ROLES.includes(role) && (
          <button
            onClick={() => setShowMetrics(true)}
            style={{
              background: 'rgba(255, 255, 255, 0.07)',
              border: 'none',
              color: 'var(--t-f1f5f9)',
              padding: '4px 8px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.75rem'
            }}
          >
            📊 Métricas
          </button>
        )}
      </div>

      {/* Modals */}
      {pending && (() => {
        const fromCol = columns.find(c => c.id === pending.vehicle.column_id);
        const toCol = columns.find(c => c.id === pending.targetColumnId);
        const fromLabel = fromCol?.label?.trim().toLowerCase();
        const toLabel = toCol?.label?.trim().toLowerCase();
        const isStockToReservados = fromLabel === 'stock' && toLabel === RESERVED_LABEL;
        const isReservadosToStock = fromLabel === RESERVED_LABEL && toLabel === 'stock';
        const isLeavingRevisionF2 = fromLabel === REVISION_F2_LABEL && toLabel !== REVISION_F2_LABEL;
        const isReservadosToPreparacion = fromLabel === RESERVED_LABEL && toLabel === PREPARACION_LABEL;
        const isPreparacionToTaller = fromLabel === PREPARACION_LABEL && toLabel === TALLER_LABEL;
        const isLeavingPreparacion = fromLabel === PREPARACION_LABEL && toLabel !== PREPARACION_LABEL && toLabel !== TALLER_LABEL;
        const isTallerToLimpiezaEntrega = fromLabel === TALLER_LABEL && toLabel === LIMPIEZA_ENTREGA_LABEL;
        const isMovingToEntrega = isDeliveryColumnLabel(toLabel) && !isDeliveryColumnLabel(fromLabel);
        // Llega a Limpieza entrega por otra vía que no es Taller (ni volviendo desde Entrega/Transporte):
        // se ofrece fijar ya la fecha de entrega, opcional.
        const isMovingToLimpiezaEntrega = toLabel === LIMPIEZA_ENTREGA_LABEL && !isTallerToLimpiezaEntrega
          && !isDeliveryColumnLabel(fromLabel) && fromLabel !== TRANSPORTE_LABEL;
        const sheet = prepSheets[pending.vehicle.id];
        const leavingIncomplete = isLeavingPreparacion && !isMovingToEntrega && sheet && !prepSheetComplete(sheet) && !pending.confirmedIncomplete;
        const tallerTransfer = tallerTransfers[pending.vehicle.id];
        const tallerLeavingIncomplete = isTallerToLimpiezaEntrega && !tallerTransfer && sheet && !prepSheetComplete(sheet) && !pending.confirmedIncomplete;

        if (isStockToReservados) {
          return <AssignSaleModal vehicle={pending.vehicle} orgId={org.id} mode="move" toCol={toCol} onClose={() => setPending(null)} onAssigned={afterAssign} />;
        }
        if (isReservadosToStock) {
          return (
            <ReservationCanceledConfirmModal
              vehicle={pending.vehicle}
              currentUserId={profile?.id}
              currentUserName={profile?.full_name}
              onConfirm={confirmMove}
              onClose={() => setPending(null)}
            />
          );
        }
        if (isLeavingRevisionF2) {
          return (
            <F2ProcessGateModal
              vehicle={pending.vehicle}
              check={f2Checks[pending.vehicle.id]}
              role={role}
              currentUserId={profile?.id}
              onDone={afterAssign}
              onClose={() => setPending(null)}
            />
          );
        }
        if (isPreparacionToTaller) {
          if (!TALLER_MOVE_ROLES.includes(role)) {
            return (
              <div style={S.overlay}>
                <div style={{...S.modal, maxWidth: '400px'}}>
                  <button onClick={() => setPending(null)} style={S.closeBtn}>✕</button>
                  <h3 style={S.title}>No autorizado</h3>
                  <p style={{color: 'var(--t-e5e7eb)', fontSize: '0.875rem'}}>
                    Solo el encargado de preparación, el responsable o el creador pueden mover este vehículo a Taller.
                  </p>
                  <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: '16px'}}>
                    <button onClick={() => setPending(null)} style={S.secondaryButton}>Cerrar</button>
                  </div>
                </div>
              </div>
            );
          }
          return <MoveToTallerModal vehicle={pending.vehicle} confirmColumnMove={!!pending.advance} fromLabel={fromCol?.label} toLabel={toCol?.label} onClose={() => setPending(null)} onDone={afterAssign} />;
        }
        if (isTallerToLimpiezaEntrega) {
          if (tallerLeavingIncomplete) {
            return (
              <div style={S.overlay}>
                <div style={{...S.modal, maxWidth: '420px'}}>
                  <button onClick={() => setPending(null)} style={S.closeBtn}>✕</button>
                  <h3 style={S.title}>⚠️ Hoja de preparación incompleta</h3>
                  <p style={{color: 'var(--t-fecaca)', fontSize: '0.875rem', marginBottom: '16px'}}>
                    Este vehículo no tiene todas las secciones de la hoja de preparación marcadas como completadas.
                    ¿Seguro que quieres mover <strong>{pending.vehicle.brand} {pending.vehicle.model}</strong> ({pending.vehicle.plate}) de <strong>{fromCol?.label}</strong> a <strong>{toCol?.label}</strong> de todas formas?
                  </p>
                  <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
                    <button type="button" onClick={() => setPending(null)} style={S.secondaryButton}>Cancelar</button>
                    <button type="button" onClick={() => setPending(p => ({...p, confirmedIncomplete: true}))} style={S.button}>Mover de todas formas</button>
                  </div>
                </div>
              </div>
            );
          }
          return (
            <TallerDepartureGateModal
              vehicle={pending.vehicle}
              transfer={tallerTransfer}
              role={role}
              currentUserId={profile?.id}
              onDone={afterAssign}
              onClose={() => setPending(null)}
            />
          );
        }
        // Si la fecha ya se fijó (p. ej. en Limpieza entrega) no se vuelve a pedir.
        if (isMovingToEntrega && !pending.vehicle.delivery_scheduled_at) {
          return <ScheduleDeliveryModal vehicle={pending.vehicle} toCol={toCol} confirmColumnMove={!!pending.advance} fromLabel={fromCol?.label} onClose={() => setPending(null)} onDone={afterAssign} onMoveWithoutDate={confirmMove} />;
        }
        if (isReservadosToPreparacion) {
          const canDoPrepMove = pending.vehicle.assigned_to === user?.id || ['creador', 'encargado_preparacion', 'responsable'].includes(role);
          if (!canDoPrepMove) {
            return (
              <div style={S.overlay}>
                <div style={{...S.modal, maxWidth: '400px'}}>
                  <button onClick={() => setPending(null)} style={S.closeBtn}>✕</button>
                  <h3 style={S.title}>No autorizado</h3>
                  <p style={{color: 'var(--t-e5e7eb)', fontSize: '0.875rem'}}>
                    Solo el comercial asignado a la venta, el encargado de preparación, el responsable o el creador pueden pasar este vehículo a Preparación.
                  </p>
                  <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: '16px'}}>
                    <button onClick={() => setPending(null)} style={S.secondaryButton}>Cerrar</button>
                  </div>
                </div>
              </div>
            );
          }
          return <PrepChecklistModal vehicle={pending.vehicle} confirmColumnMove={!!pending.advance} fromLabel={fromCol?.label} toLabel={toCol?.label} onClose={() => setPending(null)} onDone={afterAssign} />;
        }
        if (leavingIncomplete) {
          return (
            <div style={S.overlay}>
              <div style={{...S.modal, maxWidth: '420px'}}>
                <button onClick={() => setPending(null)} style={S.closeBtn}>✕</button>
                <h3 style={S.title}>⚠️ Hoja de preparación incompleta</h3>
                <p style={{color: 'var(--t-fecaca)', fontSize: '0.875rem', marginBottom: '16px'}}>
                  Este vehículo no tiene todas las secciones de la hoja de preparación marcadas como completadas.
                  ¿Seguro que quieres mover <strong>{pending.vehicle.brand} {pending.vehicle.model}</strong> ({pending.vehicle.plate}) de <strong>{fromCol?.label}</strong> a <strong>{toCol?.label}</strong> de todas formas?
                </p>
                <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
                  <button type="button" onClick={() => setPending(null)} style={S.secondaryButton}>Cancelar</button>
                  <button type="button" onClick={isMovingToLimpiezaEntrega ? () => setPending(p => ({...p, confirmedIncomplete: true})) : confirmMove} style={S.button}>Mover de todas formas</button>
                </div>
              </div>
            </div>
          );
        }
        if (isMovingToLimpiezaEntrega) {
          return <ScheduleDeliveryModal vehicle={pending.vehicle} toCol={toCol} optional confirmColumnMove={!!pending.advance} fromLabel={fromCol?.label} onClose={() => setPending(null)} onDone={afterAssign} onMoveWithoutDate={confirmMove} />;
        }
        if (pending.advance) {
          return (
            <SaleAdvanceConfirmModal vehicle={pending.vehicle} title={`Avanzar en el flujo de ${pending.advance}`}
              fromLabel={fromCol?.label} toLabel={toCol?.label} onConfirm={confirmMove} onClose={() => setPending(null)} />
          );
        }
        return <ConfirmMoveModal vehicle={pending.vehicle} fromCol={fromCol} toCol={toCol} onConfirm={confirmMove} onClose={() => setPending(null)} />;
      })()}
      {showAddVehicleModal && <AddVehicleModal columns={addVehicleColumns} stockPosition={stockColForCreate?.position} orgId={org.id} currentUserId={profile?.id} onClose={() => setShowAddVehicleModal(false)} onSave={loadAll} />}
      {selVeh && (
        <VehicleDetailPanel
          vehicle={selVeh}
          columns={columns}
          role={role}
          currentUserId={user?.id}
          currentUserName={profile?.full_name}
          linkedLeads={leadsData.leads.filter(l => l.vehicle_id === selVeh.id)}
          onOpenLeads={() => { setLeadsVehicleId(selVeh.id); setShowLeads(true); }}
          prepSheet={prepSheets[selVeh.id]}
          f2Check={f2Checks[selVeh.id]}
          tallerTransfer={tallerTransfers[selVeh.id]}
          moveTargetColumns={isLockedProfesionales(selVeh, columns) ? [] : columns.filter(c => c.id !== selVeh.column_id && canMoveInCol(c.id))}
          onRequestMove={(targetColumnId, opts) => { requestMove(selVeh, targetColumnId, opts); setSelVeh(null); }}
          onClose={() => setSelVeh(null)}
          onRefresh={loadAll}
          onDeleted={() => setSelVeh(null)}
        />
      )}
      {showMetrics && <MetricsPanel vehicles={vehicles} columns={columns} orgId={org.id} onClose={() => setShowMetrics(false)} />}
      {showManageCols && <ManageKanbanCols columns={columns} onClose={() => setShowManageCols(false)} orgId={org.id} onRefresh={loadAll} />}
      {showNotifPrefs && (
        <NotificationPreferencesModal
          columns={columns}
          currentUserId={user?.id}
          role={role}
          followFilter={followFilter}
          followCount={vehicles.filter(v => mySubscriptions.has(v.id)).length}
          onToggleFollowFilter={() => { setFollowFilter(f => !f); setMobileActiveCol(0); setShowNotifPrefs(false); }}
          onClose={() => setShowNotifPrefs(false)}
        />
      )}
      {showManual && <ManualModal role={role} onClose={() => setShowManual(false)} />}
      {showDeliveryCalendar && <DeliveryCalendarModal vehicles={vehicles} columns={columns} role={role} onRefresh={loadAll} onClose={() => setShowDeliveryCalendar(false)} />}
      {showTargets && (
        <TargetsModal
          orgId={org.id}
          year={targetYear}
          month={targetMonth}
          storeTarget={storeTargetThisMonth}
          storePurchaseTarget={storePurchaseTargetThisMonth}
          deliveries={deliveries}
          purchases={purchases}
          purchaseFallthroughs={purchaseFallthroughs}
          monthlyTargets={monthlyTargets}
          onOpenCommissions={(profileId) => { setCommissionsProfileId(profileId); setShowTargets(false); setShowCommissions(true); }}
          onClose={() => setShowTargets(false)}
          onSaved={loadAll}
        />
      )}
      {showCommissions && (
        <CommissionsModal
          orgId={org.id}
          role={role}
          currentUserId={profile.id}
          currentUserName={profile.full_name}
          initialProfileId={commissionsProfileId}
          onClose={() => { setShowCommissions(false); setCommissionsProfileId(null); }}
        />
      )}
      {showLeads && leadsData.access.ok && (
        <LeadsModal
          supabase={supabase}
          data={leadsData}
          role={role}
          currentUserId={profile.id}
          vehicles={vehicles}
          columns={columns}
          isMobile={isMobile}
          initialLeadId={/^[0-9a-f-]{36}$/i.test(leadsDeepLink || '') ? leadsDeepLink : null}
          initialVehicleId={leadsVehicleId}
          onClose={() => { setShowLeads(false); setLeadsDeepLink(null); setLeadsVehicleId(null); }}
        />
      )}
      {showQuickSales && (
        <QuickSalesModal
          deliveries={deliveries.filter(d => d.quick_sale && d.purchased_by === profile.id)}
          onClose={() => setShowQuickSales(false)}
        />
      )}
      {showUsers && (
        <UsersPanel
          role={role}
          currentUserId={user?.id}
          columns={columns}
          orgId={org.id}
          getAvailableRoles={getAvailableRoles}
          onClose={() => setShowUsers(false)}
        />
      )}
    </div>
  );
}

function StockViewToggle({ view, onToggle }) {
  const isProf = view === 'profesionales';
  return (
    <button
      type="button"
      onClick={onToggle}
      title={isProf ? 'Viendo coches de Profesionales. Pulsa para ver los de Tienda.' : 'Viendo coches de Tienda. Pulsa para ver los de Profesionales.'}
      style={{
        backgroundColor: isProf ? 'var(--b-4c1d95)' : 'var(--b-334155)',
        color: isProf ? 'var(--t-ddd6fe)' : 'var(--t-e2e8f0)',
        border: `1px solid ${isProf ? '#7c3aed' : 'var(--l-475569)'}`,
        borderRadius: '10px', padding: '2px 8px', fontSize: '0.7rem', fontWeight: 600,
        cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap'
      }}
    >
      🔁 {BUYER_TYPE_LABELS[view]}
    </button>
  );
}

function KanbanCol({ col, vehicles, canDrop, canDragCards, draggedVehicle, onDragStartVehicle, onDropColumn, onSelectVehicle, mySubscriptions, highlightedVehicleId, showAssignedBadge, prepSheets, f2Checks, tallerTransfers, onOpenDeliveryCalendar, stockBuyerView, onToggleStockBuyerView }) {
  const isStockCol = col.label?.trim().toLowerCase() === STOCK_LABEL;
  const isPreparacionCol = col.label?.trim().toLowerCase() === PREPARACION_LABEL;
  const isEntregaCol = isDeliveryColumnLabel(col.label);
  const isRevisionF2Col = col.label?.trim().toLowerCase() === REVISION_F2_LABEL;
  const isTallerCol = col.label?.trim().toLowerCase() === TALLER_LABEL;
  const [dragOver, setDragOver] = useState(false);
  const alertCount = col.alert_days != null ? vehicles.filter(v => daysInColumn(v) > col.alert_days).length : 0;

  return (
    <div
      onDragOver={(e) => { if (canDrop && draggedVehicle) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOver(true); } }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        if (canDrop) onDropColumn();
      }}
      style={{
        backgroundColor: 'var(--b-1e293b)',
        borderRadius: '8px',
        padding: '16px',
        minWidth: '300px',
        maxWidth: '300px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        outline: dragOver && canDrop ? '2px solid var(--l-f59e0b)' : '2px solid transparent',
        transition: 'outline-color 0.15s'
      }}
    >
      {/* Column Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        paddingBottom: '8px',
        flexShrink: 0,
        borderBottom: `2px solid ${col.color || 'var(--l-374151)'}`
      }}>
        <div style={{display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0}}>
          <h3 style={{
            margin: 0,
            color: 'var(--t-f1f5f9)',
            fontSize: '1.125rem',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {col.label}
          </h3>
          {isStockCol && <StockViewToggle view={stockBuyerView} onToggle={onToggleStockBuyerView} />}
          {col.alert_days != null && (
            <span
              title={alertCount > 0 ? `${alertCount} vehículo(s) superan el límite de días de esta columna` : 'Ningún vehículo supera el límite de días de esta columna'}
              style={{
                backgroundColor: alertCount > 0 ? '#ef4444' : '#16a34a',
                color: '#fff',
                padding: '2px 6px',
                borderRadius: '10px',
                fontSize: '0.7rem',
                fontWeight: 700,
                flexShrink: 0
              }}
            >
              {alertCount > 0 ? `⚠️ ${alertCount}` : '✅ 0'}
            </span>
          )}
        </div>
        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
          {isEntregaCol && (
            <button
              onClick={onOpenDeliveryCalendar}
              title="Ver calendario semanal de entregas"
              style={{background: 'none', border: 'none', color: 'var(--t-9ca3af)', cursor: 'pointer', fontSize: '1rem', padding: 0}}
            >
              📅
            </button>
          )}
          <span style={{
            backgroundColor: col.color || 'var(--b-374151)',
            color: 'var(--t-f1f5f9)',
            padding: '4px 8px',
            borderRadius: '12px',
            fontSize: '0.75rem'
          }}>
            {vehicles.length}
          </span>
        </div>
      </div>

      {/* Vehicles */}
      <div style={{display: 'flex', flexDirection: 'column', gap: '12px', overflowY: 'auto', minHeight: 0, flex: 1}}>
        {vehicles.map(vehicle => (
          <div
            key={vehicle.id}
            id={`vehicle-card-${vehicle.id}`}
            draggable={canDragCards}
            onDragStart={(e) => { e.dataTransfer.setData('text/plain', vehicle.id); e.dataTransfer.effectAllowed = 'move'; onDragStartVehicle(vehicle); }}
            onDragEnd={() => onDragStartVehicle(null)}
            onClick={() => onSelectVehicle(vehicle)}
            style={{
              backgroundColor: 'var(--b-334155)',
              padding: '12px',
              borderRadius: '6px',
              cursor: canDragCards ? 'grab' : 'pointer',
              border: '2px solid transparent',
              boxShadow: vehicle.id === highlightedVehicleId ? '0 0 0 3px #CDA455, 0 0 24px 6px rgba(205,164,85,0.55)' : 'none',
              transition: 'box-shadow 0.3s'
            }}
          >
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px'}}>
              <h4 style={{margin: 0, color: 'var(--t-f1f5f9)', fontSize: '0.875rem'}}>{vehicle.brand} {vehicle.model}</h4>
              {mySubscriptions?.has(vehicle.id) && <span title="Estás siguiendo este vehículo">🔔</span>}
            </div>
            <div style={{color: 'var(--t-9ca3af)', fontSize: '0.75rem', marginBottom: '6px'}}>{vehicle.plate}</div>

            {(gvPending(vehicle) || isFuera(vehicle) || vehicle.sin_f2 || (showAssignedBadge && assignedSaleLabel(vehicle)) || assignedPurchaseLabel(vehicle) || (isRevisionF2Col && f2PendingBadge(vehicle, f2Checks?.[vehicle.id])) || (isTallerCol && tallerTransfers?.[vehicle.id]) || vehicle.location || (vehicle.paid && !vehicle.uploaded_to_validation) || (isPreparacionCol && prepSheetComplete(prepSheets?.[vehicle.id]))) && (
              <div style={{display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '6px'}}>
                {gvPending(vehicle) && <GvBadge />}
                {isFuera(vehicle) && <FueraBadge store={vehicle.fuera_store} />}
                {vehicle.sin_f2 && (
                  <span title="Añadido sin pasar por Revisión F2: pendiente de confirmar la revisión y la subida de F1/F2 al CRM" style={{backgroundColor: 'var(--b-7f1d1d)', color: 'var(--t-fecaca)', padding: '2px 6px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 700}}>
                    🚫 Sin F2
                  </span>
                )}
                {showAssignedBadge && assignedSaleLabel(vehicle) && (
                  <span style={{backgroundColor: 'var(--b-1e3a8a)', color: 'var(--t-93c5fd)', padding: '2px 6px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 600}}>
                    👤 {assignedSaleLabel(vehicle)}
                  </span>
                )}
                {assignedPurchaseLabel(vehicle) && (
                  <span style={{backgroundColor: 'var(--b-78350f)', color: 'var(--t-fbbf24)', padding: '2px 6px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 600}}>
                    🛒 {assignedPurchaseLabel(vehicle)}
                  </span>
                )}
                {isRevisionF2Col && f2PendingBadge(vehicle, f2Checks?.[vehicle.id]) && (
                  <span title="Proceso de salida de Revisión F2" style={{backgroundColor: 'var(--b-581c87)', color: 'var(--t-e9d5ff)', padding: '2px 6px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 600}}>
                    ⏳ {f2PendingBadge(vehicle, f2Checks?.[vehicle.id])}
                  </span>
                )}
                {isTallerCol && tallerTransfers?.[vehicle.id] && (
                  <span title="Traslado a Limpieza entrega en curso" style={{backgroundColor: 'var(--b-1e40af)', color: 'var(--t-bfdbfe)', padding: '2px 6px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 600}}>
                    🚦 {tallerPendingBadge(tallerTransfers[vehicle.id])}
                  </span>
                )}
                {vehicle.location && (
                  <span style={{backgroundColor: 'var(--b-374151)', color: 'var(--t-d1d5db)', padding: '2px 6px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 600}}>
                    📍 {vehicle.location}
                  </span>
                )}
                {vehicle.paid && !vehicle.uploaded_to_validation && (
                  <span title="Pendiente de subir a validar" style={{backgroundColor: 'var(--b-7f1d1d)', color: 'var(--t-fecaca)', padding: '2px 6px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 700}}>
                    ⚠️ {VALIDATION_ALERT_LABEL}
                  </span>
                )}
                {isPreparacionCol && prepSheetComplete(prepSheets?.[vehicle.id]) && (
                  <span title="Hoja de preparación completada" style={{backgroundColor: 'var(--b-166534)', color: 'var(--t-4ade80)', padding: '2px 6px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 700}}>
                    🔧 Recoger Taller
                  </span>
                )}
              </div>
            )}

            <div style={{color: timeBadgeColor(vehicle, col, vehicles), fontSize: '0.75rem', fontWeight: 700, marginBottom: '4px'}}>
              {col.alert_days != null && daysInColumn(vehicle) > col.alert_days ? '⚠️' : '⏰'} {timeInColumnLabel(vehicle)}
            </div>
          </div>
        ))}
      </div>

      {vehicles.length === 0 && (
        <div style={{
          textAlign: 'center',
          color: 'var(--t-6b7280)',
          fontStyle: 'italic',
          padding: '2rem'
        }}>
          Sin vehículos
        </div>
      )}
    </div>
  );
}

// Pregunta final antes de cambiar el coche de columna (pasos abiertos desde los botones "Avanzar"): se muestra
// después de marcar o rellenar lo que pide el paso, justo antes de guardarlo.
function ColumnMoveConfirm({ fromLabel, toLabel, lead, saving, onBack, onConfirm }) {
  return (
    <>
      <div style={{display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', backgroundColor: 'var(--b-1f2937)',
        border: '1px solid var(--l-374151)', borderRadius: '8px', padding: '10px 12px', marginBottom: '12px', fontSize: '0.9rem'}}>
        <span style={{color: 'var(--t-cbd5e1)'}}>{fromLabel}</span>
        <span aria-hidden="true" style={{color: 'var(--t-f59e0b)'}}>→</span>
        <strong style={{color: 'var(--t-f1f5f9)'}}>{toLabel}</strong>
      </div>
      <p style={{color: 'var(--t-e5e7eb)', fontSize: '0.875rem', margin: '0 0 16px'}}>
        {lead || 'Con esto'} el coche pasa a la siguiente columna. ¿Confirmas y continúas a <strong>{toLabel}</strong>?
      </p>
      <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
        <button type="button" onClick={onBack} disabled={saving} style={S.secondaryButton}>Volver</button>
        <button type="button" onClick={onConfirm} disabled={saving} style={{...S.button, opacity: saving ? 0.6 : 1}}>
          {saving ? 'Guardando...' : `Sí, continuar a ${toLabel}`}
        </button>
      </div>
    </>
  );
}
// Guarda el último paso de una ventana: con confirmColumnMove, antes enseña la pregunta del cambio de columna.
function useColumnMoveConfirm(confirmColumnMove) {
  const [finalAction, setFinalAction] = useState(null);
  const finish = (fn) => (confirmColumnMove ? setFinalAction(() => fn) : fn());
  return { finalAction, finish, back: () => setFinalAction(null) };
}

function ConfirmMoveModal({ vehicle, fromCol, toCol, onConfirm, onClose }) {
  return (
    <div style={S.overlay}>
      <div style={{...S.modal, maxWidth: '400px'}}>
        <button onClick={onClose} style={S.closeBtn}>✕</button>
        <h3 style={S.title}>Mover vehículo</h3>
        <p style={{color: 'var(--t-e5e7eb)', fontSize: '0.875rem', marginBottom: '20px'}}>
          ¿Mover <strong>{vehicle.brand} {vehicle.model}</strong> ({vehicle.plate}) de <strong>{fromCol?.label || '—'}</strong> a <strong>{toCol?.label || '—'}</strong>?
        </p>
        <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
          <button type="button" onClick={onClose} style={S.secondaryButton}>Cancelar</button>
          <button type="button" onClick={onConfirm} style={S.button}>Confirmar</button>
        </div>
      </div>
    </div>
  );
}

// Confirmación específica al devolver un vehículo de "Reservados" a "Stock":
// dos checks obligatorios antes de permitir el movimiento.
function ReservationCanceledConfirmModal({ vehicle, currentUserId, currentUserName, onConfirm, onClose }) {
  const [canceledInCrm, setCanceledInCrm] = useState(false);
  const [availableAgain, setAvailableAgain] = useState(false);
  const [saving, setSaving] = useState(false);
  const canConfirm = canceledInCrm && availableAgain;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setSaving(true);
    await supabase.from('vehicle_comments').insert([{
      vehicle_id: vehicle.id,
      user_id: currentUserId,
      author_name: currentUserName || null,
      content: '✅ Reserva cancelada en el CRM · ✅ Vehículo figura como disponible',
      is_system: true // acompaña al movimiento a Stock, que ya tiene su propio aviso
    }]);
    await onConfirm();
    setSaving(false);
  };

  return (
    <div style={S.overlay}>
      <div style={{...S.modal, maxWidth: '400px'}}>
        <button onClick={onClose} style={S.closeBtn}>✕</button>
        <h3 style={S.title}>Volver a Stock</h3>
        <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem', marginBottom: '16px'}}>
          {vehicle.brand} {vehicle.model} ({vehicle.plate})
        </p>
        <div style={{display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px'}}>
          <label style={{display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--t-e5e7eb)', fontSize: '0.875rem', cursor: 'pointer'}}>
            <input type="checkbox" checked={canceledInCrm} onChange={e => setCanceledInCrm(e.target.checked)} />
            Reserva cancelada en el CRM
          </label>
          <label style={{display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--t-e5e7eb)', fontSize: '0.875rem', cursor: 'pointer'}}>
            <input type="checkbox" checked={availableAgain} onChange={e => setAvailableAgain(e.target.checked)} />
            Vehículo figura como disponible
          </label>
        </div>
        <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
          <button type="button" onClick={onClose} style={S.secondaryButton}>Cancelar</button>
          <button type="button" onClick={handleConfirm} disabled={!canConfirm || saving} style={{...S.button, opacity: (!canConfirm || saving) ? 0.6 : 1}}>
            {saving ? 'Guardando...' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Procesos con varios pasos y varios usuarios (Revisión F2, vehículos de fuera, Taller → Limpieza entrega, cierre GV):
// lista numerada con el estado de cada paso (✅ hecho · ⏳ en curso · ○ pendiente · – no hace falta) y, si el paso actual
// no le toca a quien mira, quién tiene que hacerlo.
function ProcessSteps({ steps }) {
  return (
    <ol style={{listStyle: 'none', padding: 0, margin: '0 0 16px', display: 'flex', flexDirection: 'column', gap: '6px'}}>
      {steps.map((st, i) => {
        const icon = st.status === 'done' ? '✅' : st.status === 'current' ? '⏳' : st.status === 'skipped' ? '–' : '○';
        const color = st.status === 'done' ? 'var(--t-86efac)' : st.status === 'current' ? 'var(--t-f1f5f9)' : 'var(--t-6b7280)';
        return (
          <li key={i} style={{display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '0.85rem', lineHeight: 1.45, color, fontWeight: st.status === 'current' ? 600 : 400}}>
            <span aria-hidden="true" style={{width: '1.2em', textAlign: 'center', flexShrink: 0}}>{icon}</span>
            <span>
              {i + 1}. {st.label}
              {st.status === 'skipped' && <span style={{fontWeight: 400}}> · {st.skippedText || 'no hace falta'}</span>}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
function ProcessPending({ who }) {
  return (
    <p style={{color: 'var(--t-fbbf24)', fontSize: '0.875rem', margin: '0 0 16px'}}>
      Pendiente de que {who} complete este paso.
    </p>
  );
}

// Proceso de 2-3 pasos al pasar un vehículo de "Revisión F2" a "Limpieza fotos".
// Muestra el formulario del paso pendiente si quien lo abre está autorizado a completarlo,
// o un aviso de quién tiene que hacerlo en caso contrario.
function F2ProcessGateModal(props) {
  return isFuera(props.vehicle) ? <FueraProcessModal {...props} /> : <F2StandardProcessModal {...props} />;
}

// confirmColumnMove: abierto desde la ficha ("Avanzar en el flujo de compra"); antes del paso que saca el coche de
// Revisión F2 se pregunta si se quiere continuar a la siguiente columna (nextLabel).
function F2StandardProcessModal({ vehicle, check, role, currentUserId, onDone, onClose, confirmColumnMove, nextLabel = 'Limpieza fotos' }) {
  const state = f2StepState(check);
  const authorized = f2StepAuthorized(state, vehicle, role, currentUserId);

  const [location, setLocation] = useState('');
  const [reviewedF2, setReviewedF2] = useState(false);
  const [uploadedCrm, setUploadedCrm] = useState(false);
  const [discount60, setDiscount60] = useState(false); // sin marcar: el comprador lo marca si de verdad ha llegado al 60%
  const [confirmMove, setConfirmMove] = useState(null); // 'step2' | 'step3': falta confirmar el cambio de columna
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const submitStep1 = async () => {
    if (!location) return;
    setSaving(true);
    setMessage('');
    const { error } = await supabase.rpc('submit_f2_step1', { p_vehicle_id: vehicle.id, p_location: location });
    if (error) { setMessage('Error: ' + error.message); setSaving(false); return; }
    await onDone();
  };

  const submitStep2 = async () => {
    if (!reviewedF2 || !uploadedCrm) return;
    setSaving(true);
    setMessage('');
    const { error } = await supabase.rpc('submit_f2_step2', { p_vehicle_id: vehicle.id, p_discount_60: discount60 });
    if (error) { setMessage('Error: ' + error.message); setSaving(false); return; }
    await onDone();
  };

  const submitStep3 = async () => {
    setSaving(true);
    setMessage('');
    const { error } = await supabase.rpc('authorize_f2_purchase', { p_vehicle_id: vehicle.id });
    if (error) { setMessage('Error: ' + error.message); setSaving(false); return; }
    await onDone();
  };

  const st = ['step1', 'step2', 'step3', 'done'];
  const idx = st.indexOf(state);
  const buyerName = vehicle.purchase_company ? 'el encargado de preparación (Compras empresa)' : (vehicle.purchased_profile?.full_name || 'el comprador');
  const noAuthNeeded = !!check?.step2_by && check.step2_discount_60 !== false;
  const steps = [
    { label: check?.step1_done ? `Revisado en taller · ${check.step1_location || ''}` : 'Revisado en taller y dónde está (Tienda o Parque)', status: idx > 0 ? 'done' : 'current' },
    { label: `Confirmación de ${buyerName}: F2 revisada, F1 y F2 en el CRM y descuento al cliente`, status: idx > 1 ? 'done' : idx === 1 ? 'current' : 'pending' },
    { label: 'Autorización del responsable o del creador (solo si no se llega al 60 %)', status: noAuthNeeded ? 'skipped' : idx > 2 ? 'done' : idx === 2 ? 'current' : 'pending',
      skippedText: 'no hace falta: se llegó al 60 %' }
  ];

  return (
    <div style={S.overlay}>
      <div style={{...S.modal, maxWidth: '460px'}}>
        <button onClick={onClose} style={S.closeBtn}>✕</button>
        <h3 style={S.title}>Revisión F2 · {BUYER_TYPE_LABELS[vehicle.buyer_type] || 'Tienda'}</h3>
        <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem', margin: '8px 0 14px'}}>
          {vehicle.brand} {vehicle.model} ({vehicle.plate}). Al completar los pasos pasa solo a Limpieza fotos.
        </p>
        <ProcessSteps steps={steps} />

        {message && (
          <div style={{backgroundColor: 'var(--b-7f1d1d)', color: 'var(--t-f1f5f9)', padding: '10px', borderRadius: '6px', marginBottom: '16px', fontSize: '0.875rem'}}>
            {message}
          </div>
        )}

        {confirmMove && authorized ? (
          <ColumnMoveConfirm fromLabel="Revisión F2" toLabel={nextLabel} lead={confirmMove === 'step3' ? 'Al autorizar la compra' : 'Con esto'}
            saving={saving} onBack={() => setConfirmMove(null)} onConfirm={confirmMove === 'step3' ? submitStep3 : submitStep2} />
        ) : state === 'done' ? (
          <>
            <p style={{color: 'var(--t-86efac)', fontSize: '0.875rem', margin: '0 0 16px'}}>Proceso completo.</p>
            <div style={{display: 'flex', justifyContent: 'flex-end'}}>
              <button type="button" onClick={onClose} style={S.secondaryButton}>Cerrar</button>
            </div>
          </>
        ) : !authorized ? (
          <>
            <ProcessPending who={f2PendingWhoText(vehicle, state)} />
            <div style={{display: 'flex', justifyContent: 'flex-end'}}>
              <button type="button" onClick={onClose} style={S.secondaryButton}>Cerrar</button>
            </div>
          </>
        ) : state === 'step1' ? (
          <>
            <label style={S.label} htmlFor="f2-step1-location">¿Dónde está el vehículo?</label>
            <select id="f2-step1-location" style={{...S.select, marginBottom: '16px'}} value={location} onChange={e => setLocation(e.target.value)}>
              <option value="">Selecciona...</option>
              <option value="Tienda">Tienda</option>
              <option value="Parque">Parque</option>
            </select>
            <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
              <button type="button" onClick={onClose} style={S.secondaryButton}>Cancelar</button>
              <button type="button" onClick={submitStep1} disabled={!location || saving} style={{...S.button, opacity: (!location || saving) ? 0.6 : 1}}>
                {saving ? 'Guardando...' : 'Confirmar revisión en taller'}
              </button>
            </div>
          </>
        ) : state === 'step2' ? (
          <>
            <div style={{display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '14px'}}>
              <label style={{display: 'flex', alignItems: 'flex-start', gap: '8px', color: 'var(--t-e5e7eb)', fontSize: '0.875rem', cursor: 'pointer'}}>
                <input id="f2-reviewed" type="checkbox" checked={reviewedF2} onChange={e => setReviewedF2(e.target.checked)} style={{marginTop: '3px'}} />
                He revisado la F2.
              </label>
              <label style={{display: 'flex', alignItems: 'flex-start', gap: '8px', color: 'var(--t-e5e7eb)', fontSize: '0.875rem', cursor: 'pointer'}}>
                <input id="f2-crm" type="checkbox" checked={uploadedCrm} onChange={e => setUploadedCrm(e.target.checked)} style={{marginTop: '3px'}} />
                He subido la F1 y la F2 al CRM.
              </label>
              <label style={{display: 'flex', alignItems: 'flex-start', gap: '8px', color: 'var(--t-e5e7eb)', fontSize: '0.875rem', cursor: 'pointer'}}>
                <input id="f2-discount" type="checkbox" checked={discount60} onChange={e => setDiscount60(e.target.checked)} style={{marginTop: '3px'}} />
                He bajado al cliente al menos el 60 % de lo presupuestado por el taller.
              </label>
            </div>
            <p style={{color: 'var(--t-9ca3af)', fontSize: '0.8rem', margin: '0 0 14px'}}>
              {discount60
                ? 'Al confirmar, el coche pasa a Limpieza fotos.'
                : 'Si no llega al 60 %, el responsable o el creador tendrán que autorizar la compra antes de continuar.'}
            </p>
            <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
              <button type="button" onClick={onClose} style={S.secondaryButton}>Cancelar</button>
              <button type="button" onClick={() => (confirmColumnMove && discount60 ? setConfirmMove('step2') : submitStep2())}
                disabled={!reviewedF2 || !uploadedCrm || saving} style={{...S.button, opacity: (!reviewedF2 || !uploadedCrm || saving) ? 0.6 : 1}}>
                {saving ? 'Guardando...' : 'Confirmar'}
              </button>
            </div>
          </>
        ) : (
          <>
            <p style={{color: 'var(--t-e5e7eb)', fontSize: '0.875rem', margin: '0 0 16px'}}>
              {vehicle.purchased_profile?.full_name || 'El comprador'} no ha llegado al 60 % presupuestado por el taller. ¿Autorizas la compra igualmente? Pasará a Limpieza fotos.
            </p>
            <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
              <button type="button" onClick={onClose} style={S.secondaryButton}>Cancelar</button>
              <button type="button" onClick={() => (confirmColumnMove ? setConfirmMove('step3') : submitStep3())} disabled={saving} style={{...S.button, opacity: saving ? 0.6 : 1}}>
                {saving ? 'Guardando...' : 'Autorizar compra'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Proceso de un vehículo de fuera (se queda siempre en Revisión F2; nunca se mueve de columna).
function FueraProcessModal({ vehicle, check, role, currentUserId, onDone, onClose }) {
  const state = fueraState(vehicle, check);
  const authorized = fueraAuthorized(state, vehicle, role, currentUserId);
  const [ok1, setOk1] = useState(false);
  const [ok2, setOk2] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const run = async (fn, args) => {
    setSaving(true);
    setMessage('');
    const { error } = await supabase.rpc(fn, args);
    if (error) { setMessage('Error: ' + error.message); setSaving(false); return; }
    await onDone(state === 'transporte' ? 'recogido' : null);
  };

  const order = ['taller', 'compra', 'transporte', 'recogido'];
  const pos = order.indexOf(state);
  const steps = ['Revisado en taller y en la tienda', 'Tienda de origen avisada y compra confirmada', 'Recogido por la tienda de origen']
    .map((label, i) => ({ label, status: pos > i ? 'done' : pos === i ? 'current' : 'pending' }));
  const checkRow = {display: 'flex', alignItems: 'flex-start', gap: '8px', color: 'var(--t-e5e7eb)', fontSize: '0.875rem', cursor: 'pointer'};
  const noBuyer = state === 'compra' && !vehicle.purchased_by;
  const canAct = authorized && !noBuyer && state !== 'recogido';

  return (
    <div style={S.overlay}>
      <div style={{...S.modal, maxWidth: '460px'}}>
        <button onClick={onClose} style={S.closeBtn}>✕</button>
        <h3 style={S.title}>Vehículo de fuera · Revisión F2</h3>
        <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem', margin: '8px 0 14px'}}>
          {vehicle.brand} {vehicle.model} ({vehicle.plate}). No pasa a nuestro stock: se queda en Revisión F2 hasta que su tienda lo recoja.
        </p>
        {vehicle.fuera_store && (
          <p style={{color: 'var(--t-bfdbfe)', fontSize: '0.875rem', margin: '-6px 0 14px'}}>🏬 Tienda de origen: <strong>{vehicle.fuera_store}</strong> (se le factura la revisión)</p>
        )}
        <ProcessSteps steps={steps} />

        {message && (
          <div style={{backgroundColor: 'var(--b-7f1d1d)', color: 'var(--t-f1f5f9)', padding: '10px', borderRadius: '6px', marginBottom: '16px', fontSize: '0.875rem'}}>
            {message}
          </div>
        )}

        {state === 'recogido' ? (
          <p style={{color: 'var(--t-86efac)', fontSize: '0.875rem'}}>Proceso terminado: el vehículo ya no está en la tienda.</p>
        ) : noBuyer ? (
          <p style={{color: 'var(--t-fbbf24)', fontSize: '0.875rem', marginBottom: '16px'}}>
            Falta asignar la compra a un captador. Hazlo desde la ficha del vehículo.
          </p>
        ) : !authorized ? (
          <ProcessPending who={fueraPendingWhoText(vehicle, state)} />
        ) : state === 'taller' ? (
          <div style={{display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px'}}>
            <label style={checkRow}>
              <input id="fuera-taller" type="checkbox" checked={ok1} onChange={e => setOk1(e.target.checked)} style={{marginTop: '3px'}} />
              El vehículo se ha revisado en el taller y ya está en la tienda.
            </label>
          </div>
        ) : state === 'compra' ? (
          <div style={{display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '12px'}}>
            <label style={checkRow}>
              <input id="fuera-avisada" type="checkbox" checked={ok1} onChange={e => setOk1(e.target.checked)} style={{marginTop: '3px'}} />
              He avisado a {vehicle.fuera_store || 'la tienda de origen'} del resultado de la revisión.
            </label>
            <label style={checkRow}>
              <input id="fuera-compra" type="checkbox" checked={ok2} onChange={e => setOk2(e.target.checked)} style={{marginTop: '3px'}} />
              {vehicle.fuera_store || 'La tienda de origen'} ha confirmado que compra el vehículo.
            </label>
            <p style={{color: 'var(--t-9ca3af)', fontSize: '0.8rem', margin: '4px 0 0'}}>
              Quedará pendiente de transporte hasta que la tienda de origen lo recoja.
              {CAPTADOR_COMMISSIONS_ENABLED && ` ${vehicle.purchased_profile?.full_name || 'El captador'} sumará ${eur(FUERA_COMMISSION_AMOUNT)} en sus comisiones (Recepción compra fuera).`}
            </p>
          </div>
        ) : (
          <div style={{display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px'}}>
            <p style={{color: 'var(--t-d1d5db)', fontSize: '0.85rem', margin: 0}}>
              La tienda de origen tiene que pedir el transporte para recoger el coche aquí.
            </p>
            <label style={checkRow}>
              <input id="fuera-recogido" type="checkbox" checked={ok1} onChange={e => setOk1(e.target.checked)} style={{marginTop: '3px'}} />
              El vehículo se ha recogido y ya no está en nuestra tienda.
            </label>
            <p style={{color: 'var(--t-9ca3af)', fontSize: '0.8rem', margin: 0}}>Al confirmarlo, el vehículo sale del tablero.</p>
          </div>
        )}

        <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
          <button type="button" onClick={onClose} style={S.secondaryButton}>{canAct ? 'Cancelar' : 'Cerrar'}</button>
          {canAct && state === 'taller' && (
            <button type="button" onClick={() => run('submit_f2_step1', { p_vehicle_id: vehicle.id, p_location: 'Tienda' })} disabled={!ok1 || saving} style={{...S.button, opacity: (!ok1 || saving) ? 0.6 : 1}}>
              {saving ? 'Guardando...' : 'Confirmar revisión'}
            </button>
          )}
          {canAct && state === 'compra' && (
            <button type="button" onClick={() => run('confirm_fuera_purchase', { p_vehicle_id: vehicle.id })} disabled={!ok1 || !ok2 || saving} style={{...S.button, opacity: (!ok1 || !ok2 || saving) ? 0.6 : 1}}>
              {saving ? 'Guardando...' : 'Confirmar compra'}
            </button>
          )}
          {canAct && state === 'transporte' && (
            <button type="button" onClick={() => run('confirm_fuera_collected', { p_vehicle_id: vehicle.id })} disabled={!ok1 || saving} style={{...S.button, opacity: (!ok1 || saving) ? 0.6 : 1}}>
              {saving ? 'Guardando...' : 'Confirmar recogida'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Preparación -> Taller: obligatorio fijar la ubicación específica del taller/ITV.
function MoveToTallerModal({ vehicle, onClose, onDone, confirmColumnMove, fromLabel, toLabel }) {
  const moveConfirm = useColumnMoveConfirm(confirmColumnMove);
  const [location, setLocation] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const submit = async () => {
    if (!location) return;
    setSaving(true);
    setMessage('');
    const { error } = await supabase.rpc('move_to_taller', { p_vehicle_id: vehicle.id, p_location: location });
    if (error) { setMessage('Error: ' + error.message); setSaving(false); return; }
    await onDone();
  };

  return (
    <div style={S.overlay}>
      <div style={{...S.modal, maxWidth: '420px'}}>
        <button onClick={onClose} style={S.closeBtn}>✕</button>
        <h3 style={S.title}>Preparación → Taller</h3>
        <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem', marginBottom: '16px'}}>
          {vehicle.brand} {vehicle.model} ({vehicle.plate})
        </p>

        {message && (
          <div style={{backgroundColor: 'var(--b-7f1d1d)', color: 'var(--t-f1f5f9)', padding: '10px', borderRadius: '6px', marginBottom: '16px', fontSize: '0.875rem'}}>
            {message}
          </div>
        )}

        {moveConfirm.finalAction ? (
          <ColumnMoveConfirm fromLabel={fromLabel} toLabel={toLabel} saving={saving} onBack={moveConfirm.back} onConfirm={moveConfirm.finalAction} />
        ) : (
          <>
            <label style={S.label} htmlFor="move-taller-location">Ubicación</label>
            <select id="move-taller-location" style={{...S.select, marginBottom: '16px'}} value={location} onChange={e => setLocation(e.target.value)}>
              <option value="">Selecciona...</option>
              {TALLER_LOCATION_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>

            <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
              <button type="button" onClick={onClose} style={S.secondaryButton}>Cancelar</button>
              <button type="button" onClick={() => moveConfirm.finish(submit)} disabled={!location || saving} style={{...S.button, opacity: (!location || saving) ? 0.6 : 1}}>
                {saving ? 'Guardando...' : 'Mover a Taller'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Taller -> Limpieza entrega: se pide la ubicación y, opcionalmente, la fecha de entrega. Si lo hace
// un comercial/captador queda pendiente de que encargado_preparacion/responsable/creador lo autoricen.
// Si no se fija aquí la fecha, se pide al mover el coche a "Entrega".
function TallerDepartureGateModal({ vehicle, transfer, role, onDone, onClose, confirmColumnMove, fromLabel = 'Taller', toLabel = 'Limpieza entrega' }) {
  const moveConfirm = useColumnMoveConfirm(confirmColumnMove);
  const state = tallerTransferState(transfer);
  const authorized = tallerTransferAuthorized(state, role);
  const [location, setLocation] = useState('');
  const prevDelivery = vehicle.delivery_scheduled_at ? new Date(vehicle.delivery_scheduled_at) : null;
  const [deliveryDate, setDeliveryDate] = useState(prevDelivery ? dateInputValue(prevDelivery) : '');
  const [deliveryTime, setDeliveryTime] = useState(prevDelivery ? timeValue(prevDelivery) : '');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // La fecha de entrega es opcional; si se rellena, hacen falta día y hora.
  const deliveryPartial = Boolean(deliveryDate) !== Boolean(deliveryTime);
  const deliveryAt = deliveryDate && deliveryTime ? new Date(`${deliveryDate}T${deliveryTime}:00`) : null;
  const requestDeparture = () => {
    if (deliveryAt && deliveryAt.getDay() === 0) {
      setMessage('La entrega debe programarse de lunes a sábado, no domingo.');
      return;
    }
    const go = () => run('request_taller_departure', { p_vehicle_id: vehicle.id, p_scheduled_at: deliveryAt ? deliveryAt.toISOString() : null, p_location: location });
    // Solo el encargado de preparación, el responsable o el creador lo mueven directamente; el resto lo solicita.
    if (TALLER_PRIVILEGED_ROLES.includes(role)) moveConfirm.finish(go); else go();
  };

  const run = async (fn, args) => {
    setSaving(true);
    setMessage('');
    const { error } = await supabase.rpc(fn, args);
    if (error) { setMessage('Error: ' + error.message); setSaving(false); return; }
    await onDone();
  };

  return (
    <div style={S.overlay}>
      <div style={{...S.modal, maxWidth: '440px'}}>
        <button onClick={onClose} style={S.closeBtn}>✕</button>
        <h3 style={S.title}>Taller → Limpieza entrega</h3>
        <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem', margin: '8px 0 14px'}}>
          {vehicle.brand} {vehicle.model} ({vehicle.plate}). Al autorizarse pasa solo a Limpieza entrega.
        </p>
        <ProcessSteps steps={[
          { label: transfer
              ? `Traslado solicitado · ${transfer.location}${transfer.scheduled_at ? ` · entrega ${new Date(transfer.scheduled_at).toLocaleString('es-ES', {day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'})}` : ''}`
              : 'Solicitud: ubicación y, si se sabe, fecha de entrega',
            status: transfer ? 'done' : 'current' },
          { label: 'Autorización del encargado de preparación, el responsable o el creador',
            status: transfer ? 'current' : (TALLER_PRIVILEGED_ROLES.includes(role) ? 'skipped' : 'pending'),
            skippedText: 'no hace falta si lo haces tú' }
        ]} />

        {message && (
          <div style={{backgroundColor: 'var(--b-7f1d1d)', color: 'var(--t-f1f5f9)', padding: '10px', borderRadius: '6px', marginBottom: '16px', fontSize: '0.875rem'}}>
            {message}
          </div>
        )}

        {moveConfirm.finalAction ? (
          <ColumnMoveConfirm fromLabel={fromLabel} toLabel={toLabel} lead={state === 'pending-authorize' ? 'Al autorizarlo' : 'Con esto'}
            saving={saving} onBack={moveConfirm.back} onConfirm={moveConfirm.finalAction} />
        ) : !authorized ? (
          <>
            <ProcessPending who={state === 'form'
              ? 'el encargado de preparación, el responsable, el creador o un comercial/captador'
              : 'el encargado de preparación, el responsable o el creador'} />
            <div style={{display: 'flex', justifyContent: 'flex-end'}}>
              <button type="button" onClick={onClose} style={S.secondaryButton}>Cerrar</button>
            </div>
          </>
        ) : state === 'pending-authorize' ? (
          <>
            <p style={{color: 'var(--t-e5e7eb)', fontSize: '0.875rem', marginBottom: '16px'}}>
              ¿Autorizas el traslado a Limpieza entrega (ubicación <strong>{transfer.location}</strong>)?
            </p>
            <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
              <button type="button" onClick={onClose} style={S.secondaryButton}>Cancelar</button>
              <button type="button" onClick={() => moveConfirm.finish(() => run('authorize_taller_departure', { p_vehicle_id: vehicle.id }))} disabled={saving} style={{...S.button, opacity: saving ? 0.6 : 1}}>
                {saving ? 'Guardando...' : 'Autorizar traslado'}
              </button>
            </div>
          </>
        ) : (
          <>
            <label style={S.label} htmlFor="taller-departure-location">Ubicación</label>
            <select id="taller-departure-location" style={{...S.select, marginBottom: '12px'}} value={location} onChange={e => setLocation(e.target.value)} required>
              <option value="">Selecciona...</option>
              {TALLER_DEPARTURE_LOCATION_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
            <label style={S.label} htmlFor="taller-departure-date">Fecha de entrega (opcional, lunes a sábado)</label>
            <div style={{display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap'}}>
              <input id="taller-departure-date" type="date" style={{...S.input, flex: '1 1 140px', marginBottom: 0}} value={deliveryDate}
                min={new Date().toISOString().slice(0, 10)} onChange={e => setDeliveryDate(e.target.value)} />
              <select id="taller-departure-time" aria-label="Hora de entrega" style={{...S.select, flex: '1 1 110px', marginBottom: 0}} value={deliveryTime} onChange={e => setDeliveryTime(e.target.value)}>
                <option value="">Hora...</option>
                {DELIVERY_TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <p style={{color: 'var(--t-9ca3af)', fontSize: '0.75rem', margin: '0 0 16px'}}>
              Si fijas ya la fecha de entrega, no se volverá a pedir al mover el coche a Entrega. Si no, se pedirá en ese momento.
              {!TALLER_PRIVILEGED_ROLES.includes(role) && ' Como no eres encargado de preparación, responsable ni creador, quedará pendiente de autorización.'}
            </p>
            <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
              <button type="button" onClick={onClose} style={S.secondaryButton}>Cancelar</button>
              <button type="button" onClick={requestDeparture}
                disabled={!location || deliveryPartial || saving} style={{...S.button, opacity: (!location || deliveryPartial || saving) ? 0.6 : 1}}>
                {saving ? 'Guardando...' : (TALLER_PRIVILEGED_ROLES.includes(role) ? 'Mover a Limpieza entrega' : 'Solicitar traslado')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// blocking: true para el responsable (no puede cerrar sin guardar) | false para el creador (opcional, en cualquier momento)
function StoreTargetModal({ blocking, year, month, defaultVentas, defaultCompras, onClose, onSaved }) {
  const [ventasValue, setVentasValue] = useState(defaultVentas != null ? String(defaultVentas) : '');
  const [comprasValue, setComprasValue] = useState(defaultCompras != null ? String(defaultCompras) : '');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    const [ventasRes, comprasRes] = await Promise.all([
      supabase.rpc('set_monthly_target', { p_profile_id: null, p_year: year, p_month: month, p_target: Number(ventasValue), p_metric: 'ventas' }),
      supabase.rpc('set_monthly_target', { p_profile_id: null, p_year: year, p_month: month, p_target: Number(comprasValue), p_metric: 'compras' })
    ]);
    const error = ventasRes.error || comprasRes.error;
    if (error) {
      setMessage('Error al guardar el objetivo: ' + error.message);
      setSaving(false);
      return;
    }
    onSaved();
  };

  return (
    <div style={S.overlay}>
      <div style={{...S.modal, maxWidth: '380px'}}>
        {!blocking && <button onClick={onClose} style={S.closeBtn}>✕</button>}
        <h3 style={S.title}>🎯 Objetivos de la tienda — {MONTH_NAMES_ES[month - 1]} {year}</h3>
        {blocking && (
          <p style={{color: 'var(--t-fecaca)', fontSize: '0.8rem', marginBottom: '16px'}}>
            Antes de seguir usando la app este mes, indica el objetivo de entregas y el de compras de la tienda.
          </p>
        )}
        {message && (
          <div style={{backgroundColor: 'var(--b-7f1d1d)', color: 'var(--t-f1f5f9)', padding: '10px', borderRadius: '6px', marginBottom: '16px', fontSize: '0.875rem'}}>
            {message}
          </div>
        )}
        <form onSubmit={save}>
          <label style={S.label}>Objetivo de entregas este mes</label>
          <input style={S.input} type="number" min="0" value={ventasValue} onChange={e => setVentasValue(e.target.value)} required />
          <label style={S.label}>Objetivo de compras este mes</label>
          <input style={S.input} type="number" min="0" value={comprasValue} onChange={e => setComprasValue(e.target.value)} required />
          <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '16px'}}>
            {!blocking && <button type="button" onClick={onClose} style={S.secondaryButton}>Cancelar</button>}
            <button type="submit" disabled={saving || ventasValue === '' || comprasValue === ''} style={{...S.button, opacity: saving ? 0.7 : 1}}>
              {saving ? 'Guardando...' : 'Guardar objetivos'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Panel único de objetivos: el objetivo mensual de la tienda (entregas y compras) y el
// objetivo individual de cada comercial/captador, con su progresión de cumplimiento (este
// mes y el mes anterior). Solo para creador/responsable, abierto desde "🎯 Objetivos" del Kanban.
function TargetsSection({ metric, icon, title, countFor, year, month, prevYear, prevMonth, storeTarget, users, loadingUsers, monthlyTargets, onSaved, extraStoreInfo }) {
  const [storeInput, setStoreInput] = useState(storeTarget != null ? String(storeTarget) : '');
  const [savingStore, setSavingStore] = useState(false);
  const [inputs, setInputs] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [message, setMessage] = useState('');

  const saveStore = async (e) => {
    e.preventDefault();
    setSavingStore(true);
    setMessage('');
    const { error } = await supabase.rpc('set_monthly_target', {
      p_profile_id: null, p_year: year, p_month: month, p_target: Number(storeInput), p_metric: metric
    });
    setSavingStore(false);
    if (error) { setMessage('Error al guardar: ' + error.message); return; }
    onSaved();
  };

  const saveOne = async (profileId) => {
    const val = inputs[profileId];
    if (val === undefined || val === '') return;
    setSavingId(profileId);
    setMessage('');
    const { error } = await supabase.rpc('set_monthly_target', {
      p_profile_id: profileId, p_year: year, p_month: month, p_target: Number(val), p_metric: metric
    });
    setSavingId(null);
    if (error) { setMessage('Error al guardar: ' + error.message); return; }
    setInputs(i => ({...i, [profileId]: ''}));
    onSaved();
  };

  return (
    <div style={{marginBottom: '28px'}}>
      {message && (
        <div style={{backgroundColor: 'var(--b-7f1d1d)', color: 'var(--t-f1f5f9)', padding: '10px', borderRadius: '6px', marginBottom: '16px', fontSize: '0.875rem'}}>
          {message}
        </div>
      )}

      <div style={{backgroundColor: 'var(--b-374151)', borderRadius: '8px', padding: '14px', marginBottom: '20px'}}>
        <p style={{color: 'var(--t-f1f5f9)', fontSize: '0.875rem', fontWeight: 600, margin: '0 0 4px'}}>{icon} Objetivo de {title} de la tienda</p>
        <p style={{color: 'var(--t-9ca3af)', fontSize: '0.8rem', margin: '0 0 4px'}}>
          Progresión actual: <strong style={{color: 'var(--t-f1f5f9)'}}>{targetProgressLabel(countFor(null, year, month), storeTarget)}</strong>
        </p>
        {extraStoreInfo && (
          <p style={{color: 'var(--t-9ca3af)', fontSize: '0.75rem', margin: '0 0 10px', fontStyle: 'italic'}}>{extraStoreInfo}</p>
        )}
        <form onSubmit={saveStore} style={{display: 'flex', gap: '8px', alignItems: 'center', marginTop: '10px'}}>
          <input
            type="number" min="0"
            style={{...S.input, marginBottom: 0, width: '100px'}}
            value={storeInput}
            onChange={e => setStoreInput(e.target.value)}
            required
          />
          <button type="submit" disabled={savingStore || storeInput === ''} style={{...S.button, padding: '6px 14px', fontSize: '0.8rem', opacity: savingStore ? 0.7 : 1}}>
            {savingStore ? 'Guardando...' : 'Guardar'}
          </button>
        </form>
      </div>

      <p style={{color: 'var(--t-f1f5f9)', fontSize: '0.875rem', fontWeight: 600, margin: '0 0 10px'}}>Objetivos individuales de {title} — comerciales y captadores</p>

      {loadingUsers ? (
        <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem'}}>Cargando...</p>
      ) : users.length === 0 ? (
        <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem', fontStyle: 'italic'}}>No hay usuarios con rol comercial o captador en la organización.</p>
      ) : (
        <div style={{overflowX: 'auto'}}>
          <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem'}}>
            <thead>
              <tr style={{color: 'var(--t-9ca3af)', textAlign: 'left'}}>
                <th style={{padding: '6px'}}>Usuario</th>
                <th style={{padding: '6px'}}>Este mes</th>
                <th style={{padding: '6px'}}>{MONTH_NAMES_ES[prevMonth - 1]} (mes anterior)</th>
                <th style={{padding: '6px'}}>Nuevo objetivo</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const curCount = countFor(u.id, year, month);
                const curUserTarget = monthlyTargetFor(monthlyTargets, u.id, year, month, metric);
                const prevCount = countFor(u.id, prevYear, prevMonth);
                const prevTarget = monthlyTargetFor(monthlyTargets, u.id, prevYear, prevMonth, metric);
                return (
                  <tr key={u.id} style={{borderTop: '1px solid var(--l-374151)'}}>
                    <td style={{padding: '6px', color: 'var(--t-f1f5f9)'}}>{u.full_name || 'Sin nombre'} <span style={{color: 'var(--t-6b7280)'}}>({roleLabel(u.role)})</span></td>
                    <td style={{padding: '6px', color: 'var(--t-e5e7eb)'}}>{targetProgressLabel(curCount, curUserTarget)}</td>
                    <td style={{padding: '6px', color: 'var(--t-e5e7eb)'}}>{targetProgressLabel(prevCount, prevTarget)}</td>
                    <td style={{padding: '6px'}}>
                      <div style={{display: 'flex', gap: '6px'}}>
                        <input
                          type="number" min="0"
                          style={{...S.input, marginBottom: 0, width: '80px'}}
                          value={inputs[u.id] ?? ''}
                          onChange={e => setInputs(i => ({...i, [u.id]: e.target.value}))}
                          placeholder={curUserTarget != null ? String(curUserTarget) : ''}
                        />
                        <button
                          type="button"
                          onClick={() => saveOne(u.id)}
                          disabled={savingId === u.id || !inputs[u.id]}
                          style={{...S.secondaryButton, padding: '4px 10px', fontSize: '0.75rem'}}
                        >
                          {savingId === u.id ? '...' : 'Guardar'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ───────────── Comisiones de comerciales (condiciones Flexicar 2026) ─────────────
const COMMISSION_ROLES = ['comercial'];
// El captador cobra por "Recepción compra fuera" (vehículos de otras tiendas que vienen a la revisión F2).
// DESACTIVADO hasta montar las comisiones del captador (son distintas de las del comercial): mientras esté en
// false, las líneas se guardan en commission_fuera_receptions pero no se ven en ningún sitio.
const CAPTADOR_COMMISSIONS_ENABLED = false;
const CAPTADOR_COMMISSION_ROLES = CAPTADOR_COMMISSIONS_ENABLED ? ['captador'] : [];
const COMMISSION_MIN_SALES = 8;
const COMMISSION_MIN_CAPTACIONES = 3;
const COMMISSION_VARIABLE_FLOOR = 363.17;

const eur = (n) => (Number(n) || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
const monthIndex = (ts) => { const d = new Date(ts); return d.getFullYear() * 12 + d.getMonth(); };

function saleUnitCommission(rank) {
  if (rank < 8) return 0;
  if (rank <= 14) return 55;
  if (rank <= 20) return 60;
  return 65;
}
function warrantyRateFor(penetration) {
  if (penetration >= 0.8) return 0.10;
  if (penetration >= 0.6) return 0.06;
  return 0;
}
function financeRateFor(totalMargin) {
  if (totalMargin > 60000) return 0.02;
  if (totalMargin > 40000) return 0.015;
  if (totalMargin > 25000) return 0.01;
  if (totalMargin > 15000) return 0.005;
  return 0;
}
// Margen de financiación = lo que paga la financiera: importe financiado × puntuación %.
function financeMarginOf(op) {
  if (!op.financed) return 0;
  return (Number(op.financed_amount) || 0) * (Number(op.finance_points) || 0) / 100;
}
function commissionOpMissingData(op) {
  if (op.has_warranty == null || op.financed == null) return true;
  if (op.has_warranty && op.warranty_amount == null) return true;
  if (op.financed && (op.financed_amount == null || op.finance_points == null)) return true;
  return false;
}

// Comisión de cada operación según su mes de validación (ranking, penetración y tramo de ese mes).
function validationMonthContext(ops, mIdx) {
  const validated = ops
    .filter(o => o.validated_at && monthIndex(o.validated_at) === mIdx)
    .sort((a, b) => new Date(a.validated_at) - new Date(b.validated_at));
  const n = validated.length;
  const withWarranty = validated.filter(o => o.has_warranty).length;
  const penetration = n ? withWarranty / n : 0;
  const wRate = warrantyRateFor(penetration);
  const financeTotal = validated.reduce((s, o) => s + financeMarginOf(o), 0);
  const fRate = financeRateFor(financeTotal);
  const perOp = {};
  validated.forEach((o, i) => {
    const unit = saleUnitCommission(i + 1);
    const warranty = o.has_warranty ? wRate * (Number(o.warranty_amount) || 0) : 0;
    const finance = fRate * financeMarginOf(o);
    const special = Number(o.special_commission) || 0;
    perOp[o.id] = { rank: i + 1, unit, warranty, finance, special, total: unit + warranty + finance + special };
  });
  return { n, penetration, wRate, financeTotal, fRate, perOp };
}

// Devengo del mes: 100% si validación y entrega caen en el mismo mes (o la entrega fue antes);
// si no, 40% en el mes de validación y 60% en el de entrega.
// Una operación anulada no computa en ningún mes (ni ventas, ni penetración, ni tramos).
function computeCommissions({ ops: allOps, purchases, penalties, profileId, year, month, insuranceWaived = false }) {
  const ops = allOps.filter(o => !o.cancelled_at);
  const mIdx = year * 12 + (month - 1);
  const ctxCache = {};
  const ctx = (idx) => (ctxCache[idx] = ctxCache[idx] || validationMonthContext(ops, idx));
  const current = ctx(mIdx);

  const lines = [];
  ops.forEach(o => {
    if (!o.validated_at) return;
    const vIdx = monthIndex(o.validated_at);
    const c = ctx(vIdx).perOp[o.id];
    if (!c) return;
    const dIdx = o.delivered_at ? monthIndex(o.delivered_at) : null;
    if (vIdx === mIdx) {
      const full = dIdx !== null && dIdx <= vIdx;
      lines.push({ op: o, kind: full ? 'Validada y entregada (100%)' : 'Validada (40%)', share: full ? 1 : 0.4, base: c });
    } else if (vIdx < mIdx && dIdx === mIdx) {
      lines.push({ op: o, kind: 'Entregada (60% restante)', share: 0.6, base: c });
    }
  });
  lines.forEach(l => {
    l.amount = l.base.total * l.share;
    l.parts = {
      unit: l.base.unit * l.share,
      warranty: l.base.warranty * l.share,
      finance: l.base.finance * l.share,
      special: (l.base.special || 0) * l.share
    };
  });
  const salesAmount = lines.reduce((s, l) => s + l.amount, 0);
  // Desglose de lo devengado este mes por origen de la comisión.
  const sumParts = (ls) => ['unit', 'warranty', 'finance', 'special'].reduce((acc, k) => {
    acc[k] = ls.reduce((s, l) => s + l.parts[k], 0);
    return acc;
  }, { amount: ls.reduce((s, l) => s + l.amount, 0), count: ls.length });
  const salesBreakdown = sumParts(lines);
  // Separado por el mes en que se validó: lo generado este mes y el 60% heredado de cada mes anterior.
  const currentLines = lines.filter(l => monthIndex(l.op.validated_at) === mIdx);
  const salesCurrent = sumParts(currentLines);
  const inheritedByMonth = {};
  lines.filter(l => monthIndex(l.op.validated_at) < mIdx).forEach(l => {
    const k = monthIndex(l.op.validated_at);
    (inheritedByMonth[k] = inheritedByMonth[k] || []).push(l);
  });
  const salesInherited = Object.keys(inheritedByMonth).map(Number).sort((a, b) => a - b).map(k => ({
    monthIdx: k,
    year: Math.floor(k / 12),
    month: (k % 12) + 1,
    ...sumParts(inheritedByMonth[k])
  }));

  const captacionList = purchases
    .filter(p => p.purchased_by === profileId && !p.is_company && p.purchased_at && monthIndex(p.purchased_at) === mIdx)
    .sort((a, b) => new Date(a.purchased_at) - new Date(b.purchased_at))
    .map((p, i) => ({ ...p, rank: i + 1, amount: i + 1 > COMMISSION_MIN_CAPTACIONES ? 90 : 0 }));
  const captaciones = captacionList.length;
  const captacionesAmount = captacionList.reduce((s, p) => s + p.amount, 0);

  const policies = ops.filter(o => o.insurance && !o.cancelled_at && o.delivered_at && monthIndex(o.delivered_at) === mIdx).length;
  const policyTarget = Math.max(2, Math.ceil(current.n * 0.15));
  const insuranceOk = policies >= policyTarget;
  const insuranceAmount = insuranceOk ? policies * 20 : 0;
  // El creador/responsable pueden eliminar la penalización de seguros de un mes.
  const insurancePenaltyCalculated = insuranceOk ? 0 : (policyTarget - policies) * 100;
  const insurancePenalty = insuranceWaived ? 0 : insurancePenaltyCalculated;

  const monthPenalties = penalties.filter(p => !p.voided_at && p.year === year && p.month === month);
  const penaltiesAmount = monthPenalties.reduce((s, p) => s + (Number(p.amount) || 0), 0);

  const requirements = [
    { label: `Mínimo ${COMMISSION_MIN_SALES} ventas validadas en el mes`, ok: current.n >= COMMISSION_MIN_SALES, detail: `${current.n}/${COMMISSION_MIN_SALES}` },
    { label: `Mínimo ${COMMISSION_MIN_CAPTACIONES} captaciones en el mes`, ok: captaciones >= COMMISSION_MIN_CAPTACIONES, detail: `${captaciones}/${COMMISSION_MIN_CAPTACIONES}` }
  ];
  const requirementsOk = requirements.every(r => r.ok);

  const gross = salesAmount + captacionesAmount + insuranceAmount - insurancePenalty - penaltiesAmount;
  const variable = requirementsOk ? gross : 0;
  const payable = Math.max(0, variable - COMMISSION_VARIABLE_FLOOR);

  return {
    current, lines, salesAmount, salesBreakdown, salesCurrent, salesInherited, captacionList, captaciones, captacionesAmount,
    policies, policyTarget, insuranceOk, insuranceAmount, insurancePenalty, insurancePenaltyCalculated, insuranceWaived,
    monthPenalties, penaltiesAmount, requirements, requirementsOk, gross, variable, payable
  };
}

function CommissionOpEditor({ op, isAdmin, onSaved, onCancel }) {
  const toNum = (v) => (v === '' || v == null ? null : Number(v));
  const [store, setStore] = useState(op.store || '');
  const [hasWarranty, setHasWarranty] = useState(op.has_warranty);
  const [warrantyAmount, setWarrantyAmount] = useState(op.warranty_amount ?? '');
  const [financed, setFinanced] = useState(op.financed);
  const [financedAmount, setFinancedAmount] = useState(op.financed_amount ?? '');
  const [financePoints, setFinancePoints] = useState(op.finance_points ?? '');
  const [insurance, setInsurance] = useState(op.insurance);
  const [special, setSpecial] = useState(op.special_commission ?? '');
  const [specialNote, setSpecialNote] = useState(op.special_commission_note || '');
  const toDateInput = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const fromDateInput = (s) => (s ? new Date(`${s}T12:00:00`).toISOString() : null);
  const [deliveredAt, setDeliveredAt] = useState(toDateInput(op.delivered_at));
  const [brand, setBrand] = useState(op.brand || '');
  const [model, setModel] = useState(op.model || '');
  const [plate, setPlate] = useState(op.plate || '');
  const [reservedAt, setReservedAt] = useState(toDateInput(op.reserved_at));
  const [validatedAt, setValidatedAt] = useState(toDateInput(op.validated_at));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const yesNo = (value, setter, idPrefix) => (
    <div style={{display: 'flex', gap: '6px', marginBottom: '10px'}}>
      {[{ v: true, l: 'Sí' }, { v: false, l: 'No' }].map(o => (
        <button key={o.l} id={`${idPrefix}-${o.l}`} type="button" onClick={() => setter(o.v)}
          style={{...S.secondaryButton, flex: 1, padding: '6px', backgroundColor: value === o.v ? 'var(--b-334155)' : 'transparent'}}>
          {o.l}
        </button>
      ))}
    </div>
  );

  const save = async () => {
    setSaving(true);
    setMessage('');
    if (isAdmin) {
      // Primero vehículo y fechas (son los que pueden rechazarse), para no guardar nada a medias.
      const keep = (orig, input) => (orig && toDateInput(orig) === input ? orig : fromDateInput(input));
      const { error: detailsError } = await supabase.rpc('admin_update_commission_details', {
        p_id: op.id,
        p_brand: brand,
        p_model: model,
        p_plate: plate,
        p_reserved_at: keep(op.reserved_at, reservedAt),
        p_validated_at: keep(op.validated_at, validatedAt),
        p_delivered_at: keep(op.delivered_at, deliveredAt)
      });
      if (detailsError) { setSaving(false); setMessage('Error: ' + detailsError.message); return; }
    }
    const { error } = await supabase.rpc('update_commission_operation', {
      p_id: op.id,
      p_store: store,
      p_purchase_price: op.purchase_price ?? null,
      p_has_warranty: hasWarranty,
      p_warranty_amount: toNum(warrantyAmount),
      p_financed: financed,
      p_financed_amount: toNum(financedAmount),
      p_finance_points: toNum(financePoints),
      p_insurance: insurance,
      p_delivered_at: fromDateInput(deliveredAt)
    });
    if (error) { setSaving(false); setMessage('Error: ' + error.message); return; }
    const specialChanged = (toNum(special) ?? 0) !== (Number(op.special_commission) || 0) || specialNote.trim() !== (op.special_commission_note || '');
    if (isAdmin && specialChanged) {
      const { error: specialError } = await supabase.rpc('set_commission_special', {
        p_id: op.id, p_amount: toNum(special), p_note: specialNote
      });
      if (specialError) { setSaving(false); setMessage('Error: ' + specialError.message); return; }
    }
    setSaving(false);
    onSaved();
  };

  const margin = financeMarginOf({ financed, financed_amount: toNum(financedAmount), finance_points: toNum(financePoints) });
  const locked = op.validated_at && !isAdmin;

  return (
    <div style={{backgroundColor: 'var(--b-1e293b)', borderRadius: '6px', padding: '12px', marginTop: '8px'}}>
      {message && <p style={{color: 'var(--t-fca5a5)', fontSize: '0.8rem', margin: '0 0 8px'}}>{message}</p>}
      {locked && <p style={{color: 'var(--t-fbbf24)', fontSize: '0.8rem', margin: '0 0 8px'}}>Operación validada: solo el creador o el responsable pueden modificarla.</p>}
      {isAdmin && (
        <div style={{borderBottom: '1px solid var(--l-334155)', marginBottom: '12px', paddingBottom: '4px'}}>
          <p style={{color: 'var(--t-cbd5e1)', fontSize: '0.75rem', fontWeight: 600, margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.03em'}}>Vehículo y fechas (creador / responsable)</p>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '0 12px'}}>
            {[['brand', 'Marca', brand, setBrand], ['model', 'Modelo', model, setModel], ['plate', 'Matrícula', plate, setPlate]].map(([k, l, v, set]) => (
              <div key={k}>
                <label style={S.label} htmlFor={`op-${k}-${op.id}`}>{l}</label>
                <input id={`op-${k}-${op.id}`} style={S.input} value={v} onChange={e => set(e.target.value)} />
              </div>
            ))}
            <div>
              <label style={S.label} htmlFor={`op-r-${op.id}`}>Fecha de reserva</label>
              <input id={`op-r-${op.id}`} style={S.input} type="date" value={reservedAt} onChange={e => setReservedAt(e.target.value)} />
            </div>
            <div>
              <label style={S.label} htmlFor={`op-v-${op.id}`}>Fecha de validación</label>
              <input id={`op-v-${op.id}`} style={S.input} type="date" value={validatedAt} onChange={e => setValidatedAt(e.target.value)} />
            </div>
            <div>
              <label style={S.label} htmlFor={`op-dd-${op.id}`}>Fecha de entrega</label>
              <input id={`op-dd-${op.id}`} style={S.input} type="date" value={deliveredAt} min={validatedAt || undefined} onChange={e => setDeliveredAt(e.target.value)} disabled={!validatedAt} />
            </div>
          </div>
          <p style={{color: 'var(--t-9ca3af)', fontSize: '0.72rem', margin: '-4px 0 8px'}}>Deja la fecha de validación vacía para quitarla. La entrega no puede ser anterior a la validación.</p>
        </div>
      )}
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0 12px'}}>
        <div>
          <label style={S.label} htmlFor={`op-store-${op.id}`}>Tienda del coche</label>
          <input id={`op-store-${op.id}`} style={S.input} value={store} onChange={e => setStore(e.target.value)} disabled={locked} />
        </div>
      </div>

      <label style={S.label}>¿Lleva garantía?</label>
      {yesNo(hasWarranty, locked ? () => {} : setHasWarranty, `op-w-${op.id}`)}
      {hasWarranty && (
        <>
          <label style={S.label} htmlFor={`op-wa-${op.id}`}>Importe de la garantía (€)</label>
          <input id={`op-wa-${op.id}`} style={S.input} type="number" min="0" step="0.01" value={warrantyAmount} onChange={e => setWarrantyAmount(e.target.value)} disabled={locked} />
        </>
      )}

      <label style={S.label}>¿Va financiado?</label>
      {yesNo(financed, locked ? () => {} : setFinanced, `op-f-${op.id}`)}
      {financed && (
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0 12px'}}>
          <div>
            <label style={S.label} htmlFor={`op-fa-${op.id}`}>Total financiado por el cliente (€)</label>
            <input id={`op-fa-${op.id}`} style={S.input} type="number" min="0" step="0.01" value={financedAmount} onChange={e => setFinancedAmount(e.target.value)} disabled={locked} />
          </div>
          <div>
            <label style={S.label} htmlFor={`op-fp-${op.id}`}>Puntuación de la financiación (%)</label>
            <input id={`op-fp-${op.id}`} style={S.input} type="number" min="0" step="0.01" value={financePoints} onChange={e => setFinancePoints(e.target.value)} disabled={locked} />
          </div>
          <p style={{gridColumn: '1 / -1', color: 'var(--t-9ca3af)', fontSize: '0.75rem', margin: '-4px 0 10px'}}>
            Margen de esta financiación: <strong style={{color: 'var(--t-f1f5f9)'}}>{eur(margin)}</strong>
          </p>
        </div>
      )}

      <label style={S.label}>¿El cliente ha contratado seguro?</label>
      {yesNo(insurance, locked ? () => {} : setInsurance, `op-i-${op.id}`)}

      {op.source === 'manual' && !isAdmin && (
        <p style={{color: 'var(--t-9ca3af)', fontSize: '0.75rem', margin: '0 0 10px'}}>
          Las fechas de validación y entrega de esta venta las indica el creador o el responsable.
        </p>
      )}

      {isAdmin ? (
        <div style={{borderTop: '1px solid var(--l-334155)', marginTop: '4px', paddingTop: '10px'}}>
          <p style={{color: 'var(--t-cbd5e1)', fontSize: '0.75rem', fontWeight: 600, margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.03em'}}>⭐ Comisión especial (creador / responsable)</p>
          <div style={{display: 'grid', gridTemplateColumns: 'minmax(110px, 160px) 1fr', gap: '0 12px'}}>
            <div>
              <label style={S.label} htmlFor={`op-sp-${op.id}`}>Importe (€)</label>
              <input id={`op-sp-${op.id}`} style={S.input} type="number" step="0.01" value={special} onChange={e => setSpecial(e.target.value)} />
            </div>
            <div>
              <label style={S.label} htmlFor={`op-spn-${op.id}`}>Motivo (opcional)</label>
              <input id={`op-spn-${op.id}`} style={S.input} value={specialNote} onChange={e => setSpecialNote(e.target.value)} placeholder="p. ej. incentivo de campaña" />
            </div>
          </div>
          <p style={{color: 'var(--t-9ca3af)', fontSize: '0.72rem', margin: '-4px 0 10px'}}>Se suma a la comisión de esta venta: 40 % al validar y 60 % al entregar (100 % si ambas en el mismo mes). Déjalo vacío o a 0 para quitarla.</p>
        </div>
      ) : (Number(op.special_commission) || 0) !== 0 && (
        <p style={{color: 'var(--t-fde68a)', fontSize: '0.8rem', margin: '0 0 10px'}}>
          ⭐ Comisión especial: {eur(op.special_commission)}{op.special_commission_note ? ` · ${op.special_commission_note}` : ''}
        </p>
      )}

      <div style={{display: 'flex', gap: '8px', justifyContent: 'flex-end'}}>
        <button type="button" onClick={onCancel} disabled={saving} style={S.secondaryButton}>Cerrar</button>
        {!locked && (
          <button type="button" onClick={save} disabled={saving} style={{...S.button, opacity: saving ? 0.7 : 1}}>
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        )}
      </div>
    </div>
  );
}

const PENALTY_TYPES = [
  { key: 'incidencia', label: 'Incidencia / reclamación', amount: 300 },
  { key: 'resenas', label: 'Reseñas Google (nota ≤ 4,0)', amount: 100 },
  { key: 'otra', label: 'Otra', amount: null }
];

const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// Hoja de comisiones lista para imprimir o guardar como PDF desde el diálogo del navegador.
// Heredadas pendientes de entrega en el mes mIdx: validadas en un mes anterior, sin anular y que en ese mes aún no se
// han entregado (sin entrega o entregadas después). No cobran nada hasta entregarse (entonces, el 60 %).
function pendingInheritedOps(ops, mIdx) {
  return ops.filter(o => !o.cancelled_at && o.validated_at && monthIndex(o.validated_at) < mIdx
    && (!o.delivered_at || monthIndex(o.delivered_at) > mIdx));
}

function printCommissionSheet({ userName, year, month, result, ops, note = '' }) {
  const lineByOp = {};
  result.lines.forEach(l => { lineByOp[l.op.id] = l; });
  const fmtDate = (ts) => (ts ? new Date(ts).toLocaleDateString('es-ES') : '—');
  const mIdx = year * 12 + (month - 1);
  const inMonth = (o, keys) => keys.some(k => o[k] && monthIndex(o[k]) === mIdx);
  const reservedOnly = ops.filter(o => !lineByOp[o.id] && !o.cancelled_at && inMonth(o, ['reserved_at']) && !inMonth(o, ['validated_at', 'delivered_at']))
    .sort((x, y) => new Date(x.reserved_at) - new Date(y.reserved_at));
  const reservedRows = reservedOnly.map(o => `<tr>
      <td>${escapeHtml(o.plate)}</td>
      <td>${escapeHtml(`${o.brand || ''} ${o.model || ''}`)}</td>
      <td>${escapeHtml(o.store || '—')}</td>
      <td>${fmtDate(o.reserved_at)}</td>
      <td class="n">${eur(0)}</td>
    </tr>`).join('');
  // Las anuladas no salen en la hoja. Las columnas de comisión muestran lo devengado este mes
  // (ya aplicado el 40/60), para que la fila de totales cuadre con el resumen.
  // Ordenadas por fecha de validación (de la más antigua a la más reciente, como el orden de las unidades);
  // las que aún no están validadas van al final, por fecha de reserva.
  const byValidation = (x, y) => {
    if (x.validated_at && y.validated_at) return new Date(x.validated_at) - new Date(y.validated_at);
    if (x.validated_at) return -1;
    if (y.validated_at) return 1;
    return new Date(x.reserved_at) - new Date(y.reserved_at);
  };
  const saleOps = ops.filter(o => !o.cancelled_at && (lineByOp[o.id] || inMonth(o, ['validated_at', 'delivered_at']))).sort(byValidation);
  // Dos tablas: las ventas del mes en curso (validadas este mes o sin validar), que son las que computan en el mes
  // (unidades, penetración de garantías, margen de financiación), y las heredadas de meses anteriores, que solo
  // cobran aquí el 60 % de la entrega y no suman en el cómputo del mes.
  const isInheritedOp = (o) => o.validated_at && monthIndex(o.validated_at) < mIdx;
  const currentSaleOps = saleOps.filter(o => !isInheritedOp(o));
  const deliveredInheritedOps = saleOps.filter(isInheritedOp);
  // Validadas antes y aún sin entregar: pasan a este mes como heredadas pendientes de entrega (sin importe).
  const pendingInherited = pendingInheritedOps(ops, mIdx).filter(o => !saleOps.includes(o)).sort(byValidation);
  const pendingIds = new Set(pendingInherited.map(o => o.id));
  const inheritedSaleOps = [...deliveredInheritedOps, ...pendingInherited];
  const buildSalesTable = (list, totalLabel) => {
    const totals = { warrantyAmount: 0, financedAmount: 0, margin: 0, unit: 0, warranty: 0, finance: 0, special: 0, amount: 0 };
    const rows = list.map(o => {
      const l = lineByOp[o.id];
      const b = l?.base;
      const pt = l?.parts;
      if (o.has_warranty) totals.warrantyAmount += Number(o.warranty_amount) || 0;
      if (o.financed) { totals.financedAmount += Number(o.financed_amount) || 0; totals.margin += financeMarginOf(o); }
      if (pt) { totals.unit += pt.unit; totals.warranty += pt.warranty; totals.finance += pt.finance; totals.special += pt.special; totals.amount += l.amount; }
      return `<tr>
        <td>${escapeHtml(o.plate)}</td>
        <td>${escapeHtml(`${o.brand || ''} ${o.model || ''}`)}</td>
        <td>${escapeHtml(o.store || '—')}</td>
        <td>${fmtDate(o.reserved_at)}</td>
        <td>${fmtDate(o.validated_at)}</td>
        <td>${fmtDate(o.delivered_at)}</td>
        <td class="n">${o.has_warranty ? eur(o.warranty_amount) : (o.has_warranty === false ? 'No' : '—')}</td>
        <td class="n">${o.financed ? `${eur(o.financed_amount)} · ${escapeHtml(o.finance_points)}%<br><span class="sub">margen ${eur(financeMarginOf(o))}</span>` : (o.financed === false ? 'Contado' : '—')}</td>
        <td>${o.insurance ? 'Sí' : (o.insurance === false ? 'No' : '—')}</td>
        <td class="n">${pt ? `${b.rank}ª · ${eur(pt.unit)}` : '—'}</td>
        <td class="n">${pt ? eur(pt.warranty) : '—'}</td>
        <td class="n">${pt ? eur(pt.finance) : '—'}</td>
        <td class="n">${pt && pt.special ? `${eur(pt.special)}${o.special_commission_note ? `<br><span class="sub">${escapeHtml(o.special_commission_note)}</span>` : ''}` : '—'}</td>
        <td>${l ? escapeHtml(l.kind) : (pendingIds.has(o.id) ? 'Pendiente de entrega (cobrará el 60 % al entregarse)' : 'Sin devengo este mes')}</td>
        <td class="n"><strong>${l ? eur(l.amount) : '—'}</strong></td>
      </tr>`;
    }).join('');
    const totalsRow = list.length ? `<tr class="sum">
      <td colspan="6">${totalLabel}</td>
      <td class="n">${eur(totals.warrantyAmount)}</td>
      <td class="n">${eur(totals.financedAmount)}<br><span class="sub">margen ${eur(totals.margin)}</span></td>
      <td></td>
      <td class="n">${eur(totals.unit)}</td>
      <td class="n">${eur(totals.warranty)}</td>
      <td class="n">${eur(totals.finance)}</td>
      <td class="n">${eur(totals.special)}</td>
      <td></td>
      <td class="n">${eur(totals.amount)}</td>
    </tr>` : '';
    return { rows, totalsRow };
  };
  const curValidated = currentSaleOps.filter(o => o.validated_at).length;
  const curUnvalidated = currentSaleOps.length - curValidated;
  const currentTable = buildSalesTable(currentSaleOps,
    `Total ventas del mes: ${currentSaleOps.length}${curUnvalidated ? `<br><span class="sub">${curValidated} validadas · ${curUnvalidated} sin validar</span>` : ''}`);
  const inheritedTable = buildSalesTable(inheritedSaleOps,
    `Total heredadas: ${inheritedSaleOps.length}<br><span class="sub">${deliveredInheritedOps.length} entregadas este mes · ${pendingInherited.length} pendientes de entrega; no computan en el mes</span>`);
  const deliveriesSummary = (saleOps.length || pendingInherited.length)
    ? `<p class="sub"><strong>Total entregas: ${saleOps.length}</strong> · ${currentSaleOps.length} del mes en curso · ${deliveredInheritedOps.length} ${deliveredInheritedOps.length === 1 ? 'heredada' : 'heredadas'} de meses anteriores${pendingInherited.length ? ` · además, ${pendingInherited.length} ${pendingInherited.length === 1 ? 'heredada pendiente' : 'heredadas pendientes'} de entrega` : ''}</p>`
    : '';
  const c = result.current;
  const explain = [
    `Ventas validadas en el mes: ${c.n}. Unidades 1ª–7ª incluidas en el fijo; 8ª–14ª a 55 €, 15ª–20ª a 60 €, 21ª en adelante a 65 €.`,
    `Penetración de garantías: ${c.n ? Math.round(c.penetration * 1000) / 10 : 0} % (${Math.round(c.penetration * c.n)} de ${c.n}) → ${c.wRate * 100} % del importe de cada garantía (≥ 80 %: 10 %; 60–80 %: 6 %).`,
    `Margen de financiación del mes: ${eur(c.financeTotal)} → ${(c.fRate * 100).toFixed(2)} % sobre el margen de cada operación financiada.`,
    'Devengo: 100 % si se valida y entrega en el mismo mes; si no, 40 % en el mes de validación y 60 % en el de entrega. Las columnas de comisión de la tabla de ventas muestran lo que se cobra este mes.'
  ].map(t => `<li>${escapeHtml(t)}</li>`).join('');
  const purchaseRows = result.captacionList.map(p => `<tr>
      <td>${p.rank}ª</td>
      <td>${escapeHtml(p.plate || '—')}</td>
      <td>${escapeHtml(`${p.brand || ''} ${p.model || ''}`)}</td>
      <td>${fmtDate(p.purchased_at)}</td>
      <td class="n">${p.amount ? eur(p.amount) : 'Incluida (1ª–3ª)'}</td>
    </tr>`).join('');
  const penaltyRows = [
    ...(result.insurancePenalty > 0 ? [[`Seguros: ${result.policies} de ${result.policyTarget} pólizas del objetivo`, result.insurancePenalty]] : []),
    ...result.monthPenalties.map(p => [p.reason || 'Penalización', Number(p.amount) || 0])
  ].map(([t, a]) => `<tr><td>${escapeHtml(t)}</td><td class="n">−${eur(a)}</td></tr>`).join('');
  const salesBlockRows = (title, b, wLabel, fLabel) => [
    [title, eur(b.amount), 'head'],
    ['· Unidades', eur(b.unit), 'part'],
    [`· Garantías (${wLabel})`, eur(b.warranty), 'part'],
    [`· Financiación (${fLabel})`, eur(b.finance), 'part'],
    ['· Comisiones especiales', eur(b.special), 'part']
  ];
  const summary = [
    ...salesBlockRows(`Generado en ${MONTH_NAMES_ES[month - 1]} (${result.salesCurrent.count} ${result.salesCurrent.count === 1 ? 'venta' : 'ventas'}: 100% o 40%)`, result.salesCurrent,
      `${result.current.wRate * 100}% del importe`, `${(result.current.fRate * 100).toFixed(2)}% del margen`),
    ...result.salesInherited.flatMap(g => salesBlockRows(`60% heredado de ${MONTH_NAMES_ES[g.month - 1]} ${g.year} (${g.count} ${g.count === 1 ? 'venta' : 'ventas'})`, g,
      `según ${MONTH_NAMES_ES[g.month - 1]}`, `según ${MONTH_NAMES_ES[g.month - 1]}`)),
    [`Captaciones (${result.captaciones})`, eur(result.captacionesAmount)],
    [`Seguros (${result.policies} pólizas)`, eur(result.insuranceAmount)],
    ['Penalizaciones', `−${eur(result.insurancePenalty + result.penaltiesAmount)}`],
    ['Total variable generado', eur(result.gross)],
    ['Mínimo provincial', `−${eur(COMMISSION_VARIABLE_FLOOR)}`],
    ['A cobrar', eur(result.payable)]
  ].map(([t, v, kind], i, arr) => `<tr class="${i === arr.length - 1 ? 'total' : (kind || '')}"><td>${escapeHtml(t)}</td><td class="n">${v}</td></tr>`).join('');
  const reqs = result.requirements.map(r => `<li class="${r.ok ? 'ok' : 'ko'}">${r.ok ? '✓' : '✗'} ${escapeHtml(r.label)} (${escapeHtml(r.detail)})</li>`).join('');

  const html = `<!doctype html><html lang="es"><head><meta charset="utf-8">
<title>Comisiones ${escapeHtml(userName)} - ${MONTH_NAMES_ES[month - 1]} ${year}</title>
<style>
  body { font-family: Arial, sans-serif; color: #111; margin: 24px; font-size: 12px; }
  h1 { font-size: 18px; margin: 0 0 4px; } h2 { font-size: 14px; margin: 20px 0 6px; }
  .sub { color: #555; margin: 0 0 12px; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border-bottom: 1px solid #ddd; padding: 5px 6px; text-align: left; vertical-align: top; }
  th { background: #f3f4f6; font-size: 11px; }
  .n { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
  .summary { max-width: 420px; } .total td { font-weight: bold; font-size: 14px; border-top: 2px solid #111; }
  .sum td { font-weight: bold; border-top: 2px solid #111; background: #f9fafb; }
  .head td { font-weight: bold; background: #f3f4f6; }
  .part td { color: #444; padding-left: 16px; }
  .sum .sub { font-weight: normal; margin: 0; }
  ul { padding-left: 16px; margin: 0; } .ok { color: #166534; } .ko { color: #b91c1c; }
  table th, table td { font-size: 11px; }
  @page { size: A4 landscape; margin: 12mm; }
  @media print { body { margin: 0; } }
</style></head><body>
<h1>Hoja de comisiones — ${escapeHtml(userName)}</h1>
<p class="sub">${MONTH_NAMES_ES[month - 1]} ${year} · Generada el ${new Date().toLocaleDateString('es-ES')}</p>
${note ? `<p style="border:2px dashed #7c3aed;padding:6px 8px;color:#5b21b6;font-weight:bold">${escapeHtml(note)}</p>` : ''}
<h2>Requisitos</h2><ul>${reqs}</ul>
<h2>Resumen</h2><table class="summary">${summary}</table>
<h2>Cómo se calcula este mes</h2><ul>${explain}</ul>
<h2>Ventas del mes en curso</h2>
<table><thead><tr><th>Matrícula</th><th>Vehículo</th><th>Tienda</th><th>Reserva</th><th>Validación</th><th>Entrega</th><th>Garantía</th><th>Financiación</th><th>Seguro</th><th class="n">Unidad</th><th class="n">Com. garantía</th><th class="n">Com. financ.</th><th class="n">Com. especial</th><th>Devengo</th><th class="n">Este mes</th></tr></thead>
<tbody>${currentTable.rows || '<tr><td colspan="15">Sin ventas validadas este mes</td></tr>'}</tbody>${currentTable.totalsRow ? `<tfoot>${currentTable.totalsRow}</tfoot>` : ''}</table>
${inheritedSaleOps.length ? `<h2>Heredadas de meses anteriores</h2>
<p class="sub">Validadas en un mes anterior: las entregadas este mes cobran aquí el 60 % de la entrega; las pendientes de entrega lo cobrarán el mes en que se entreguen. No suman en el cómputo de este mes.</p>
<table><thead><tr><th>Matrícula</th><th>Vehículo</th><th>Tienda</th><th>Reserva</th><th>Validación</th><th>Entrega</th><th>Garantía</th><th>Financiación</th><th>Seguro</th><th class="n">Unidad</th><th class="n">Com. garantía</th><th class="n">Com. financ.</th><th class="n">Com. especial</th><th>Devengo</th><th class="n">Este mes</th></tr></thead>
<tbody>${inheritedTable.rows}</tbody><tfoot>${inheritedTable.totalsRow}</tfoot></table>` : ''}
${deliveriesSummary}
${reservedRows ? `<h2>Reservas del mes (sin comisión)</h2>
<p class="sub">Reservar no genera comisión: estas ventas contarán en el mes en que se validen.</p>
<table class="summary"><thead><tr><th>Matrícula</th><th>Vehículo</th><th>Tienda</th><th>Reserva</th><th class="n">Comisión</th></tr></thead>
<tbody>${reservedRows}</tbody></table>` : ''}
<h2>Compras (captaciones)</h2>
<table><thead><tr><th>Nº</th><th>Matrícula</th><th>Vehículo</th><th>Fecha</th><th class="n">Comisión</th></tr></thead>
<tbody>${purchaseRows || '<tr><td colspan="5">Sin compras este mes</td></tr>'}</tbody></table>
<h2>Penalizaciones</h2>
<table class="summary">${penaltyRows || '<tr><td>Ninguna</td></tr>'}</table>
</body></html>`;

  const w = window.open('', '_blank');
  if (!w) { window.alert('El navegador ha bloqueado la ventana. Permite las ventanas emergentes para imprimir la hoja.'); return; }
  w.document.open();
  w.document.write(html);
  w.document.close();
  // Se imprime desde aquí (sin <script> dentro de la ventana, para cumplir la política de seguridad CSP).
  printWhenReady(w);
}

function printWhenReady(w) {
  setTimeout(() => { try { w.focus(); w.print(); } catch (e) { /* ventana cerrada */ } }, 300);
}

// Comisiones del captador: 25 € por cada "Recepción compra fuera" confirmada en el mes. La tienda de origen
// la escriben a mano el propio captador, el responsable o el creador (set_fuera_commission_store).
function CaptadorCommissions({ orgId, profileId, year, month, canEditStore }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [message, setMessage] = useState('');

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.from('commission_fuera_receptions')
      .select('id, brand, model, plate, origin_store, amount, earned_at')
      .eq('org_id', orgId).eq('profile_id', profileId).is('cancelled_at', null)
      .order('earned_at', { ascending: true });
    if (error) setMessage('Error al cargar: ' + error.message);
    setRows(data || []);
    setDrafts({});
    setLoading(false);
  };
  useEffect(() => { load(); }, [orgId, profileId]); // recarga al cambiar de usuario

  const mIdx = year * 12 + (month - 1);
  const monthRows = rows.filter(r => monthIndex(r.earned_at) === mIdx);
  const total = monthRows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
  const missingStore = monthRows.filter(r => !r.origin_store).length;

  const saveStore = async (r) => {
    setBusyId(r.id);
    setMessage('');
    const { error } = await supabase.rpc('set_fuera_commission_store', { p_id: r.id, p_store: drafts[r.id] ?? r.origin_store ?? '' });
    setBusyId(null);
    if (error) { setMessage('Error: ' + error.message); return; }
    load();
  };

  if (loading) return <p style={{color: 'var(--t-9ca3af)'}}>Cargando...</p>;
  return (
    <>
      {message && (
        <div style={{backgroundColor: 'var(--b-7f1d1d)', color: 'var(--t-f1f5f9)', padding: '10px', borderRadius: '6px', marginBottom: '16px', fontSize: '0.875rem'}}>{message}</div>
      )}
      <div style={{backgroundColor: 'var(--b-374151)', borderRadius: '8px', padding: '14px', marginBottom: '16px'}}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: '8px'}}>
          <span style={{color: 'var(--t-cbd5e1)', fontSize: '0.8rem'}}>Recepción compra fuera · {monthRows.length} {monthRows.length === 1 ? 'vehículo' : 'vehículos'} × {eur(FUERA_COMMISSION_AMOUNT)}</span>
          <span style={{color: total > 0 ? 'var(--t-4ade80)' : 'var(--t-f1f5f9)', fontSize: '1.6rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums'}}>{eur(total)}</span>
        </div>
        {missingStore > 0 && (
          <p style={{color: 'var(--t-fbbf24)', fontSize: '0.8rem', margin: '8px 0 0'}}>
            ⚠️ Falta la tienda de origen en {missingStore === 1 ? '1 vehículo' : `${missingStore} vehículos`}.
          </p>
        )}
      </div>
      {monthRows.length === 0 ? (
        <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem'}}>Este mes no hay recepciones de compras de fuera.</p>
      ) : (
        <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
          {monthRows.map(r => {
            const draft = drafts[r.id] ?? r.origin_store ?? '';
            const changed = draft.trim() !== (r.origin_store || '');
            return (
              <div key={r.id} style={{border: '1px solid var(--l-374151)', borderRadius: '8px', padding: '10px 12px'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap'}}>
                  <div>
                    <div style={{color: 'var(--t-f1f5f9)', fontSize: '0.875rem', fontWeight: 600}}>{r.brand} {r.model} · {r.plate}</div>
                    <div style={{color: 'var(--t-9ca3af)', fontSize: '0.75rem', marginTop: '2px'}}>
                      Recepción compra fuera · {new Date(r.earned_at).toLocaleDateString('es-ES')}
                    </div>
                  </div>
                  <div style={{color: 'var(--t-4ade80)', fontWeight: 700, fontVariantNumeric: 'tabular-nums'}}>{eur(r.amount)}</div>
                </div>
                <div style={{display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px', flexWrap: 'wrap'}}>
                  <label htmlFor={`origin-store-${r.id}`} style={{color: 'var(--t-cbd5e1)', fontSize: '0.8rem'}}>Tienda de origen</label>
                  {canEditStore ? (
                    <>
                      <input id={`origin-store-${r.id}`} value={draft} maxLength={80} placeholder="Ej.: Flexicar Leganés"
                        onChange={e => setDrafts(d => ({ ...d, [r.id]: e.target.value }))}
                        style={{...S.input, marginBottom: 0, flex: '1 1 180px', width: 'auto', padding: '6px 8px'}} />
                      <button type="button" onClick={() => saveStore(r)} disabled={!changed || busyId === r.id}
                        style={{...S.button, padding: '6px 12px', opacity: (!changed || busyId === r.id) ? 0.5 : 1}}>
                        {busyId === r.id ? 'Guardando...' : 'Guardar'}
                      </button>
                    </>
                  ) : (
                    <span style={{color: r.origin_store ? 'var(--t-f1f5f9)' : 'var(--t-fbbf24)', fontSize: '0.85rem'}}>{r.origin_store || 'Sin indicar'}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <p style={{color: 'var(--t-6b7280)', fontSize: '0.75rem', marginTop: '14px'}}>
        Se suma una línea cada vez que se confirma la compra de un vehículo de fuera asignado a este captador. Estos vehículos no cuentan en los objetivos.
      </p>
    </>
  );
}

// Editar una compra de comisiones (automática al llegar a Stock o añadida a mano).
function CommissionPurchaseEditor({ purchase, isAdmin, buyers, onSaved, onCancel }) {
  const d = new Date(purchase.purchased_at);
  const pad = (n) => String(n).padStart(2, '0');
  const [form, setForm] = useState({
    brand: purchase.brand || '', model: purchase.model || '', plate: purchase.plate || '', store: purchase.store || '',
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, buyer: purchase.purchased_by || ''
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const set = (patch) => setForm(f => ({ ...f, ...patch }));
  const idp = `pur-${purchase.kind}-${purchase.id}`;
  const input = {...S.input, boxSizing: 'border-box', marginBottom: '8px'};
  const save = async (e) => {
    e.preventDefault();
    if (!form.plate.trim()) { setMessage('La matrícula es obligatoria.'); return; }
    if (purchase.kind === 'manual' && !form.store.trim()) { setMessage('La tienda es obligatoria.'); return; }
    if (/[<>]/.test(form.brand + form.model + form.plate + form.store)) { setMessage('No se permiten los caracteres < o >.'); return; }
    setSaving(true);
    setMessage('');
    const { error } = await supabase.rpc('update_commission_purchase', {
      p_kind: purchase.kind, p_id: purchase.id, p_brand: form.brand, p_model: form.model, p_plate: form.plate,
      p_purchased_at: new Date(`${form.date}T12:00:00`).toISOString(),
      p_profile_id: isAdmin && form.buyer ? form.buyer : null,
      p_store: purchase.kind === 'manual' ? form.store : null
    });
    setSaving(false);
    if (error) { setMessage('Error: ' + error.message); return; }
    onSaved();
  };
  return (
    <form onSubmit={save} style={{backgroundColor: 'var(--b-1f2937)', borderRadius: '6px', padding: '10px', marginTop: '6px'}}>
      {message && <div style={{color: 'var(--t-fca5a5)', fontSize: '0.8rem', marginBottom: '8px'}}>{message}</div>}
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0 10px'}}>
        <div><label style={S.label} htmlFor={`${idp}-brand`}>Marca</label>
          <input id={`${idp}-brand`} style={input} maxLength={60} value={form.brand} onChange={e => set({ brand: e.target.value })} /></div>
        <div><label style={S.label} htmlFor={`${idp}-model`}>Modelo</label>
          <input id={`${idp}-model`} style={input} maxLength={60} value={form.model} onChange={e => set({ model: e.target.value })} /></div>
        <div><label style={S.label} htmlFor={`${idp}-plate`}>Matrícula</label>
          <input id={`${idp}-plate`} style={input} maxLength={20} value={form.plate} onChange={e => set({ plate: e.target.value })} /></div>
        <div><label style={S.label} htmlFor={`${idp}-date`}>Fecha de la compra</label>
          <input id={`${idp}-date`} type="date" style={input} value={form.date} onChange={e => set({ date: e.target.value })} required /></div>
        {purchase.kind === 'manual' && (
          <div><label style={S.label} htmlFor={`${idp}-store`}>Tienda</label>
            <input id={`${idp}-store`} style={input} maxLength={80} value={form.store} onChange={e => set({ store: e.target.value })} /></div>
        )}
        {isAdmin && (
          <div><label style={S.label} htmlFor={`${idp}-buyer`}>Comprador</label>
            <select id={`${idp}-buyer`} style={{...S.select, marginBottom: '8px'}} value={form.buyer} onChange={e => set({ buyer: e.target.value })}>
              {(buyers || []).map(b => <option key={b.id} value={b.id}>{b.full_name}</option>)}
            </select></div>
        )}
      </div>
      <p style={{color: 'var(--t-9ca3af)', fontSize: '0.72rem', margin: '0 0 8px'}}>La compra cuenta en el mes de su fecha{isAdmin ? ' y para el comprador indicado' : ''}.</p>
      <div style={{display: 'flex', gap: '8px', justifyContent: 'flex-end'}}>
        <button type="button" onClick={onCancel} disabled={saving} style={{...S.secondaryButton, padding: '4px 12px', fontSize: '0.8rem'}}>Cancelar</button>
        <button type="submit" disabled={saving} style={{...S.button, padding: '4px 12px', fontSize: '0.8rem', opacity: saving ? 0.6 : 1}}>{saving ? 'Guardando...' : 'Guardar compra'}</button>
      </div>
    </form>
  );
}

function CommissionsModal({ orgId, role, currentUserId, currentUserName, initialProfileId, onClose }) {
  const isAdmin = ADMIN_TIER_ROLES.includes(role);
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [comerciales, setComerciales] = useState([]);
  const [profileId, setProfileId] = useState(initialProfileId || (isAdmin ? '' : currentUserId));
  const [penaltyType, setPenaltyType] = useState('incidencia');
  const [penaltyAmount, setPenaltyAmount] = useState('');
  const [ops, setOps] = useState([]);
  const [penalties, setPenalties] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [waivers, setWaivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingPurchase, setEditingPurchase] = useState(null); // 'auto:<id>' | 'manual:<id>'
  const [message, setMessage] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [reassigning, setReassigning] = useState(null); // { id, to, withSale }
  // Simulación (solo creador/responsable): ventas que se quitan del cálculo para ver cómo quedaría.
  // No se guarda en ningún sitio.
  const [simulating, setSimulating] = useState(false);
  const [excludedIds, setExcludedIds] = useState(() => new Set());
  const toggleExcluded = (id) => setExcludedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const stopSimulation = () => { setSimulating(false); setExcludedIds(new Set()); };
  const [addMode, setAddMode] = useState(null); // 'sale' | 'purchase'
  const [orgName, setOrgName] = useState('');
  const emptyNewOp = () => ({ brand: '', model: '', plate: '', store: isAdmin ? orgName : '', reserved: new Date().toISOString().slice(0, 10) });
  const [newOp, setNewOp] = useState(emptyNewOp);
  const [manualPurchases, setManualPurchases] = useState([]);

  useEffect(() => {
    supabase.from('organizations').select('name').eq('id', orgId).maybeSingle()
      .then(({ data }) => setOrgName(data?.name || ''));
  }, [orgId]);

  const openAddForm = (mode) => {
    setAddMode(m => (m === mode ? null : mode));
    setNewOp(emptyNewOp());
  };
  const [penaltyReason, setPenaltyReason] = useState('');

  useEffect(() => {
    if (!isAdmin) return;
    supabase.from('profiles').select('id, full_name, role').eq('org_id', orgId).in('role', [...COMMISSION_ROLES, ...CAPTADOR_COMMISSION_ROLES]).order('full_name')
      .then(({ data }) => {
        setComerciales(data || []);
        if (data && data.length > 0) setProfileId(p => p || data[0].id);
      });
  }, [orgId, isAdmin]);

  const load = async () => {
    if (!profileId) return;
    setLoading(true);
    const [opsRes, penRes, purRes, waiverRes, manualPurRes] = await Promise.all([
      supabase.from('commission_operations').select('*').eq('org_id', orgId).eq('profile_id', profileId).order('reserved_at', { ascending: false }),
      supabase.from('commission_penalties').select('*').eq('org_id', orgId).eq('profile_id', profileId),
      supabase.from('vehicle_purchases').select('id, vehicle_id, brand, model, plate, purchased_by, is_company, purchased_at, cancelled_at').eq('org_id', orgId).eq('purchased_by', profileId),
      supabase.from('commission_insurance_waivers').select('id, year, month').eq('org_id', orgId).eq('profile_id', profileId).is('voided_at', null),
      supabase.from('commission_manual_purchases').select('*').eq('org_id', orgId).eq('profile_id', profileId)
    ]);
    setManualPurchases(manualPurRes.data || []);
    setOps(opsRes.data || []);
    setPenalties(penRes.data || []);
    setPurchases(purRes.data || []);
    setWaivers(waiverRes.data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [profileId]);

  const shiftMonth = (delta) => {
    const idx = year * 12 + (month - 1) + delta;
    setYear(Math.floor(idx / 12));
    setMonth((idx % 12) + 1);
  };

  const mIdx = year * 12 + (month - 1);
  const insuranceWaived = waivers.some(w => w.year === year && w.month === month);
  const purchasesWithCancelled = [
    ...purchases.map(p => ({ ...p, kind: 'auto' })),
    ...manualPurchases.map(p => ({ ...p, purchased_by: p.profile_id, is_company: false, manual: true, kind: 'manual' }))
  ];
  // Las compras anuladas no cuentan; se enseñan aparte en su mes para poder recuperarlas.
  const allPurchases = purchasesWithCancelled.filter(p => !p.cancelled_at);
  const cancelledPurchases = purchasesWithCancelled
    .filter(p => p.cancelled_at && p.purchased_by === profileId && p.purchased_at && monthIndex(p.purchased_at) === mIdx)
    .sort((a, b) => new Date(a.purchased_at) - new Date(b.purchased_at));
  const realResult = computeCommissions({ ops, purchases: allPurchases, penalties, profileId, year, month, insuranceWaived });
  const simActive = isAdmin && simulating && excludedIds.size > 0;
  const result = simActive
    ? computeCommissions({ ops: ops.filter(o => !excludedIds.has(o.id)), purchases: allPurchases, penalties, profileId, year, month, insuranceWaived })
    : realResult;
  const lineByOp = {};
  result.lines.forEach(l => { lineByOp[l.op.id] = l; });

  // Cada operación solo aparece en los meses en que se reservó, se validó, se entregó o se anuló.
  const touchesMonth = (o) => ['reserved_at', 'validated_at', 'delivered_at', 'cancelled_at'].some(k => o[k] && monthIndex(o[k]) === mIdx);
  // Reservar no genera comisión: lo que en este mes solo se ha reservado va en su propio apartado.
  const onlyReservedThisMonth = (o) => !lineByOp[o.id] && !['validated_at', 'delivered_at', 'cancelled_at'].some(k => o[k] && monthIndex(o[k]) === mIdx);
  const monthOps = ops.filter(touchesMonth);
  const visibleOps = monthOps.filter(o => o.cancelled_at || !onlyReservedThisMonth(o));
  const reservedOps = monthOps.filter(o => !o.cancelled_at && onlyReservedThisMonth(o));
  const pendingData = monthOps.filter(o => !o.cancelled_at && commissionOpMissingData(o)).length;
  const reservedIds = new Set(reservedOps.map(o => o.id));
  // Heredadas: validadas en un mes anterior que este mes cobran el 60% de la entrega.
  const isInherited = (o) => o.validated_at && monthIndex(o.validated_at) < mIdx;
  const cancelledOps = visibleOps.filter(o => o.cancelled_at);
  const currentOps = visibleOps.filter(o => !o.cancelled_at && !isInherited(o));
  const inheritedOps = visibleOps.filter(o => !o.cancelled_at && isInherited(o));
  // Validadas en un mes anterior y aún sin entregar: se ven en este mes como heredadas pendientes de entrega.
  const visibleIds = new Set(visibleOps.map(o => o.id));
  const pendingInherited = pendingInheritedOps(ops, mIdx).filter(o => !visibleIds.has(o.id))
    .sort((a, b) => new Date(a.validated_at) - new Date(b.validated_at));
  const pendingInheritedIds = new Set(pendingInherited.map(o => o.id));
  const monthLabelOf = (ts) => { const d = new Date(ts); return `${MONTH_NAMES_ES[d.getMonth()]} ${d.getFullYear()}`; };

  const runRpc = async (id, fn, args, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusyId(id);
    setMessage('');
    const { error } = await supabase.rpc(fn, args);
    setBusyId(null);
    if (error) { setMessage('Error: ' + error.message); return; }
    load();
  };

  const addManualEntry = async (e) => {
    e.preventDefault();
    setMessage('');
    const date = new Date(`${newOp.reserved}T12:00:00`).toISOString();
    const common = { p_profile_id: profileId, p_brand: newOp.brand, p_model: newOp.model, p_plate: newOp.plate, p_store: newOp.store };
    const { error } = addMode === 'purchase'
      ? await supabase.rpc('add_manual_commission_purchase', { ...common, p_purchased_at: date })
      : await supabase.rpc('add_manual_commission_operation', { ...common, p_reserved_at: date });
    if (error) { setMessage('Error: ' + error.message); return; }
    setAddMode(null);
    load();
  };

  const selectedPenaltyType = PENALTY_TYPES.find(t => t.key === penaltyType);
  const penaltyValue = selectedPenaltyType.amount ?? (Number(penaltyAmount) || 0);

  const addPenalty = async () => {
    if (penaltyValue <= 0) { setMessage('Indica el importe de la penalización.'); return; }
    if (!window.confirm(`¿Añadir una penalización de ${eur(penaltyValue)} (${selectedPenaltyType.label}) en ${MONTH_NAMES_ES[month - 1]} ${year}?`)) return;
    setMessage('');
    const note = penaltyReason.trim();
    const { error } = await supabase.rpc('add_commission_penalty', {
      p_profile_id: profileId, p_year: year, p_month: month, p_amount: penaltyValue,
      p_reason: note ? `${selectedPenaltyType.label}: ${note}` : selectedPenaltyType.label
    });
    if (error) { setMessage('Error: ' + error.message); return; }
    setPenaltyReason('');
    setPenaltyAmount('');
    load();
  };

  const selectedUserName = isAdmin
    ? (comerciales.find(c => c.id === profileId)?.full_name || 'Comercial')
    : (currentUserName || 'Comercial');
  const selectedRole = isAdmin ? comerciales.find(c => c.id === profileId)?.role : role;
  const captadorView = CAPTADOR_COMMISSION_ROLES.includes(selectedRole);

  const statusChips = (o) => {
    const chips = [];
    if (o.cancelled_at) chips.push(['Anulada', 'var(--b-7f1d1d)', 'var(--t-fecaca)']);
    else {
      if (commissionOpMissingData(o)) chips.push(['Faltan datos', 'var(--b-78350f)', 'var(--t-fde68a)']);
      chips.push(o.validated_at ? [`Validada ${new Date(o.validated_at).toLocaleDateString('es-ES')}`, 'var(--b-14532d)', 'var(--t-bbf7d0)'] : ['Pte. validación', 'var(--b-374151)', 'var(--t-d1d5db)']);
      chips.push(o.delivered_at ? [`Entregada ${new Date(o.delivered_at).toLocaleDateString('es-ES')}`, 'var(--b-1e3a8a)', 'var(--t-bfdbfe)'] : ['Pte. entrega', 'var(--b-374151)', 'var(--t-d1d5db)']);
    }
    if (o.source === 'manual') chips.push(['Manual', 'var(--b-4c1d95)', 'var(--t-ddd6fe)']);
    return chips;
  };

  // Reasignar a otro comercial: solo sin validar y durante el mes en que se hizo la venta
  // (vendida en septiembre y validada en octubre -> solo se puede reasignar en septiembre).
  const canReassignOp = (o) => isAdmin && !o.cancelled_at && !o.validated_at && !o.delivered_at
    && monthIndex(o.reserved_at) === monthIndex(new Date());
  const reassignOp = async () => {
    const target = comerciales.find(c => c.id === reassigning.to);
    const who = target?.full_name || 'este comercial';
    const text = reassigning.withSale
      ? `¿Reasignar la comisión Y la venta a ${who}? El comercial anterior dejará de tener la venta asignada.`
      : `¿Reasignar solo la comisión a ${who}? La venta seguirá apareciendo asignada al comercial actual.`;
    if (!window.confirm(text)) return;
    await runRpc(reassigning.id, 'reassign_commission_operation', { p_id: reassigning.id, p_profile_id: reassigning.to, p_assign_sale: reassigning.withSale });
    setReassigning(null);
  };

  // Compras: creador y responsable editan, anulan y recuperan cualquiera; el resto, solo las manuales que añadió.
  const canManagePurchase = (p) => isAdmin || (p.kind === 'manual' && p.created_by === currentUserId && p.profile_id === currentUserId);
  const renderPurchaseRow = (p) => {
    const key = `${p.kind}:${p.id}`;
    const cancelled = !!p.cancelled_at;
    const smallBtn = {background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem', padding: '2px 4px'};
    return (
      <div key={key} style={{borderTop: '1px solid var(--l-374151)', padding: '6px 0', opacity: cancelled ? 0.6 : 1}}>
        <div style={{display: 'flex', justifyContent: 'space-between', gap: '8px', fontSize: '0.8rem', flexWrap: 'wrap'}}>
          <span style={{color: 'var(--t-e5e7eb)', minWidth: 0}}>
            {cancelled ? '—' : `${p.rank}ª`} · <strong style={{color: 'var(--t-fbbf24)'}}>{p.plate || '—'}</strong> · {p.brand} {p.model} · {new Date(p.purchased_at).toLocaleDateString('es-ES')}
            {p.manual && <span style={{color: 'var(--t-c4b5fd)'}}> · Manual{p.store ? ` (${p.store})` : ''}</span>}
            {cancelled && <span style={{color: 'var(--t-fca5a5)'}}> · Anulada: no computa</span>}
          </span>
          <span style={{display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap'}}>
            <span style={{color: !cancelled && p.amount ? 'var(--t-4ade80)' : 'var(--t-9ca3af)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums'}}>
              {cancelled ? eur(0) : (p.amount ? eur(p.amount) : 'Incluida (1ª–3ª)')}
            </span>
            {canManagePurchase(p) && !cancelled && (
              <>
                <button type="button" onClick={() => setEditingPurchase(editingPurchase === key ? null : key)} style={{...smallBtn, color: 'var(--t-cbd5e1)'}}>
                  {editingPurchase === key ? 'Cerrar' : '✏️ Editar'}
                </button>
                <button type="button" disabled={busyId === key}
                  onClick={() => runRpc(key, 'set_commission_purchase_cancelled', { p_kind: p.kind, p_id: p.id, p_cancelled: true }, '¿Anular esta compra? Dejará de contar para comisiones y objetivos.')}
                  style={{...smallBtn, color: 'var(--t-fca5a5)'}}>Anular</button>
              </>
            )}
            {canManagePurchase(p) && cancelled && (
              <button type="button" disabled={busyId === key}
                onClick={() => runRpc(key, 'set_commission_purchase_cancelled', { p_kind: p.kind, p_id: p.id, p_cancelled: false }, '¿Recuperar esta compra? Volverá a contar para comisiones y objetivos.')}
                style={{...smallBtn, color: 'var(--t-86efac)'}}>↺ Recuperar</button>
            )}
          </span>
        </div>
        {editingPurchase === key && (
          <CommissionPurchaseEditor purchase={p} isAdmin={isAdmin} buyers={comerciales}
            onCancel={() => setEditingPurchase(null)} onSaved={() => { setEditingPurchase(null); load(); }} />
        )}
      </div>
    );
  };

  const renderOpCard = (o) => {
    const line = lineByOp[o.id];
    const inherited = isInherited(o);
    const excluded = simActive && excludedIds.has(o.id);
    const canSimulate = isAdmin && simulating && o.validated_at && !o.cancelled_at;
    const accent = reservedIds.has(o.id) || o.cancelled_at ? 'var(--l-475569)' : (inherited ? 'var(--l-f59e0b)' : '#22c55e');
    return (
      <div key={o.id} style={{backgroundColor: 'var(--b-334155)', borderRadius: '6px', padding: '10px 12px', borderLeft: `4px solid ${accent}`, opacity: o.cancelled_at || excluded ? 0.55 : 1}}>
        {canSimulate && (
          <label style={{display: 'flex', alignItems: 'center', gap: '6px', color: excludedIds.has(o.id) ? 'var(--t-fca5a5)' : 'var(--t-86efac)', fontSize: '0.75rem', fontWeight: 600, marginBottom: '6px', cursor: 'pointer'}}>
            <input id={`sim-${o.id}`} type="checkbox" checked={!excludedIds.has(o.id)} onChange={() => toggleExcluded(o.id)} />
            {excludedIds.has(o.id) ? 'Excluida de la simulación' : 'Contar en el cálculo'}
          </label>
        )}
        <div style={{display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap'}}>
          <div style={{minWidth: 0}}>
            {inherited && !reservedIds.has(o.id) && (
              <div style={{color: 'var(--t-fbbf24)', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: '2px'}}>
                ↩️ Heredada de {monthLabelOf(o.validated_at)}
              </div>
            )}
            <div style={{color: 'var(--t-f1f5f9)', fontWeight: 600, fontSize: '0.9rem'}}>{o.brand} {o.model} · <span style={{color: 'var(--t-fbbf24)'}}>{o.plate}</span></div>
            <div style={{color: 'var(--t-9ca3af)', fontSize: '0.75rem'}}>
              Reserva {new Date(o.reserved_at).toLocaleDateString('es-ES')} · {o.store || 'Tienda sin indicar'}
              {o.has_warranty && ` · Garantía ${eur(o.warranty_amount)}`}
              {o.financed && ` · Financia ${eur(o.financed_amount)} a ${o.finance_points}%`}
              {o.has_warranty === false && ' · Sin garantía'}
              {o.financed === false && ' · Contado'}
              {o.insurance && ' · 🛡️ Seguro'}
              {(Number(o.special_commission) || 0) !== 0 && ` · ⭐ Especial ${eur(o.special_commission)}`}
            </div>
            <div style={{display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '6px'}}>
              {statusChips(o).map(([t, bg, fg]) => (
                <span key={t} style={{backgroundColor: bg, color: fg, padding: '1px 6px', borderRadius: '10px', fontSize: '0.68rem', fontWeight: 600}}>{t}</span>
              ))}
            </div>
          </div>
          <div style={{textAlign: 'right'}}>
            {o.cancelled_at && (
              <>
                <div style={{color: 'var(--t-9ca3af)', fontWeight: 700, fontVariantNumeric: 'tabular-nums'}}>{eur(0)}</div>
                <div style={{color: 'var(--t-fca5a5)', fontSize: '0.7rem'}}>Anulada: no computa</div>
              </>
            )}
            {excluded && (
              <>
                <div style={{color: 'var(--t-9ca3af)', fontWeight: 700, fontVariantNumeric: 'tabular-nums'}}>{eur(0)}</div>
                <div style={{color: 'var(--t-fca5a5)', fontSize: '0.7rem'}}>Fuera de la simulación</div>
              </>
            )}
            {!line && pendingInheritedIds.has(o.id) && (
              <>
                <div style={{color: 'var(--t-9ca3af)', fontWeight: 700, fontVariantNumeric: 'tabular-nums'}}>{eur(0)}</div>
                <div style={{color: 'var(--t-fbbf24)', fontSize: '0.7rem'}}>Pendiente de entrega: cobrará el 60 % al entregarse</div>
              </>
            )}
            {!line && reservedIds.has(o.id) && (
              <>
                <div style={{color: 'var(--t-9ca3af)', fontWeight: 700, fontVariantNumeric: 'tabular-nums'}}>{eur(0)}</div>
                <div style={{color: 'var(--t-9ca3af)', fontSize: '0.7rem'}}>Solo reservada: sin comisión</div>
              </>
            )}
            {line && (
              <>
                <div style={{color: line.amount < 0 ? 'var(--t-fca5a5)' : 'var(--t-4ade80)', fontWeight: 700, fontVariantNumeric: 'tabular-nums'}}>{eur(line.amount)}</div>
                <div style={{color: 'var(--t-9ca3af)', fontSize: '0.7rem'}}>{line.kind}</div>
                <div style={{color: 'var(--t-6b7280)', fontSize: '0.68rem'}}>
                  {line.base.rank}ª venta: {eur(line.base.unit)} + gar. {eur(line.base.warranty)} + fin. {eur(line.base.finance)}{line.base.special ? ` + esp. ${eur(line.base.special)}` : ''}
                </div>
              </>
            )}
          </div>
        </div>
        {o.cancelled_at && (isAdmin || (o.source === 'manual' && !o.validated_at)) && (
          <div style={{display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px'}}>
            <button type="button" disabled={busyId === o.id}
              onClick={() => runRpc(o.id, 'restore_commission_operation', { p_id: o.id }, '¿Recuperar esta venta? Volverá a contar para las comisiones.')}
              style={{...S.secondaryButton, padding: '3px 10px', fontSize: '0.75rem', color: 'var(--t-86efac)'}}>
              ↺ Recuperar
            </button>
          </div>
        )}
        {!o.cancelled_at && (
          <div style={{display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px'}}>
            <button type="button" onClick={() => setEditingId(editingId === o.id ? null : o.id)} style={{...S.secondaryButton, padding: '3px 10px', fontSize: '0.75rem'}}>
              {editingId === o.id ? 'Ocultar datos' : (commissionOpMissingData(o) ? '✏️ Completar datos' : '✏️ Ver / editar datos')}
            </button>
            {isAdmin && !(o.validated_at && o.delivered_at) && (
              <button type="button" disabled={busyId === o.id}
                onClick={() => runRpc(o.id, 'set_commission_validated', { p_id: o.id, p_validated: !o.validated_at }, o.validated_at ? '¿Quitar la validación de esta operación?' : '¿Marcar esta operación como validada hoy?')}
                style={{...S.secondaryButton, padding: '3px 10px', fontSize: '0.75rem', color: o.validated_at ? 'var(--t-fca5a5)' : 'var(--t-86efac)'}}>
                {o.validated_at ? 'Quitar validación' : '✓ Validar'}
              </button>
            )}
            {canReassignOp(o) && (
              <button type="button" disabled={busyId === o.id}
                onClick={() => setReassigning(reassigning?.id === o.id ? null : { id: o.id, to: '', withSale: false })}
                style={{...S.secondaryButton, padding: '3px 10px', fontSize: '0.75rem'}}>
                👤 Reasignar
              </button>
            )}
            {(isAdmin || (o.source === 'manual' && !o.validated_at)) && (
              <button type="button" disabled={busyId === o.id}
                onClick={() => runRpc(o.id, 'cancel_commission_operation', { p_id: o.id }, '¿Anular esta operación? Dejará de contar para comisiones.')}
                style={{...S.secondaryButton, padding: '3px 10px', fontSize: '0.75rem', color: 'var(--t-fca5a5)'}}>
                Anular
              </button>
            )}
          </div>
        )}
        {reassigning?.id === o.id && canReassignOp(o) && (
          <div style={{display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center', marginTop: '8px', backgroundColor: 'var(--b-1f2937)', padding: '8px', borderRadius: '6px'}}>
            <label htmlFor={`reassign-${o.id}`} style={{color: 'var(--t-cbd5e1)', fontSize: '0.75rem'}}>Pasar a:</label>
            <select id={`reassign-${o.id}`} style={{...S.select, marginBottom: 0, flex: '1 1 160px', padding: '4px 8px', fontSize: '0.8rem'}}
              value={reassigning.to} onChange={e => setReassigning(r => ({ ...r, to: e.target.value }))}>
              <option value="">Selecciona comercial...</option>
              {comerciales.filter(c => c.id !== o.profile_id).map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
            </select>
            <button type="button" onClick={reassignOp} disabled={!reassigning.to || busyId === o.id}
              style={{...S.button, padding: '4px 12px', fontSize: '0.8rem', opacity: (!reassigning.to || busyId === o.id) ? 0.5 : 1}}>
              Reasignar
            </button>
            {o.vehicle_id && (
              <label style={{display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--t-e5e7eb)', fontSize: '0.75rem', flexBasis: '100%', cursor: 'pointer'}}>
                <input id={`reassign-sale-${o.id}`} type="checkbox" checked={!!reassigning.withSale}
                  onChange={e => setReassigning(r => ({ ...r, withSale: e.target.checked }))} />
                Asignar también la venta del coche a este comercial (el anterior deja de seguir el coche)
              </label>
            )}
            <span style={{color: 'var(--t-9ca3af)', fontSize: '0.7rem', flexBasis: '100%'}}>
              Solo se puede reasignar antes de validarse y durante el mes de la venta ({monthLabelOf(o.reserved_at)}).
              {o.vehicle_id ? ' Sin marcar la casilla, solo cambia la comisión y la venta sigue con el comercial actual.' : ''}
            </span>
          </div>
        )}
        {editingId === o.id && (
          <CommissionOpEditor op={o} isAdmin={isAdmin} onCancel={() => setEditingId(null)} onSaved={() => { setEditingId(null); load(); }} />
        )}
      </div>
    );
  };

  const row = (label, value, strong) => (
    <div style={{display: 'flex', justifyContent: 'space-between', gap: '12px', fontSize: '0.8rem', padding: '3px 0'}}>
      <span style={{color: 'var(--t-cbd5e1)'}}>{label}</span>
      <span style={{color: strong ? 'var(--t-f9fafb)' : 'var(--t-e5e7eb)', fontWeight: strong ? 700 : 400, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap'}}>{value}</span>
    </div>
  );

  // Bloque de ventas con subtotal y su desglose (unidades, garantías, financiación, especiales).
  const salesBlock = (title, b, color, rates) => (
    <div style={{borderLeft: `3px solid ${color}`, paddingLeft: '8px', margin: '4px 0 8px'}}>
      <div style={{display: 'flex', justifyContent: 'space-between', gap: '12px', fontSize: '0.8rem', padding: '3px 0'}}>
        <span style={{color, fontWeight: 700}}>{title}</span>
        <span style={{color: 'var(--t-f9fafb)', fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap'}}>{eur(b.amount)}</span>
      </div>
      {row('· Unidades', eur(b.unit))}
      {row(`· Garantías (${rates.warranty})`, eur(b.warranty))}
      {row(`· Financiación (${rates.finance})`, eur(b.finance))}
      {row('· ⭐ Comisiones especiales', eur(b.special))}
    </div>
  );

  return (
    <div style={S.overlay}>
      <div style={{...S.modal, maxWidth: '760px'}}>
        <button onClick={onClose} style={S.closeBtn}>✕</button>
        <h3 style={S.title}>💶 Comisiones</h3>

        <div style={{display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px'}}>
          {isAdmin && (
            <select style={{...S.select, marginBottom: 0, width: 'auto', minWidth: '200px'}} value={profileId} onChange={e => { setProfileId(e.target.value); setEditingId(null); stopSimulation(); }}>
              {comerciales.length === 0 && <option value="">No hay comerciales ni captadores</option>}
              {comerciales.map(c => <option key={c.id} value={c.id}>{c.full_name || 'Sin nombre'}{CAPTADOR_COMMISSION_ROLES.includes(c.role) ? ' (Captador)' : ''}</option>)}
            </select>
          )}
          <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
            <button type="button" onClick={() => shiftMonth(-1)} style={{...S.secondaryButton, padding: '4px 10px'}}>‹</button>
            <span style={{color: 'var(--t-f1f5f9)', fontWeight: 600, minWidth: '130px', textAlign: 'center'}}>{MONTH_NAMES_ES[month - 1]} {year}</span>
            <button type="button" onClick={() => shiftMonth(1)} style={{...S.secondaryButton, padding: '4px 10px'}}>›</button>
          </div>
          {isAdmin && profileId && !loading && !captadorView && (
            <button
              type="button"
              onClick={() => (simulating ? stopSimulation() : setSimulating(true))}
              style={{...S.secondaryButton, marginLeft: 'auto', padding: '4px 12px', backgroundColor: simulating ? 'var(--b-4c1d95)' : 'transparent', color: simulating ? 'var(--t-ddd6fe)' : S.secondaryButton.color}}
              title="Elige qué ventas cuentan para ver cómo quedarían las comisiones. No se guarda nada."
            >
              {simulating ? '✕ Salir de la simulación' : '🧮 Simular'}
            </button>
          )}
          {profileId && !loading && !captadorView && (
            <button
              type="button"
              onClick={() => printCommissionSheet({
                userName: selectedUserName, year, month, result,
                ops: simActive ? ops.filter(o => !excludedIds.has(o.id)) : ops,
                note: simActive ? `SIMULACIÓN — no es la liquidación real. Ventas excluidas: ${ops.filter(o => excludedIds.has(o.id)).map(o => o.plate).join(', ')}` : ''
              })}
              style={{...S.secondaryButton, marginLeft: isAdmin ? 0 : 'auto', padding: '4px 12px'}}
              title="Abre la hoja lista para imprimir; en el diálogo elige 'Guardar como PDF' para descargarla"
            >
              🖨️ Imprimir / PDF
            </button>
          )}
        </div>

        {message && (
          <div style={{backgroundColor: 'var(--b-7f1d1d)', color: 'var(--t-f1f5f9)', padding: '10px', borderRadius: '6px', marginBottom: '16px', fontSize: '0.875rem'}}>{message}</div>
        )}

        {!profileId ? null : captadorView ? (
          <CaptadorCommissions orgId={orgId} profileId={profileId} year={year} month={month}
            canEditStore={isAdmin || profileId === currentUserId} />
        ) : loading ? (
          <p style={{color: 'var(--t-9ca3af)'}}>Cargando...</p>
        ) : (
          <>
            {simulating && (
              <div style={{backgroundColor: 'var(--b-2e1065)', border: '1px solid var(--l-6d28d9)', borderRadius: '8px', padding: '10px 14px', marginBottom: '12px'}}>
                <p style={{color: 'var(--t-ddd6fe)', fontSize: '0.85rem', fontWeight: 700, margin: '0 0 4px'}}>🧮 Modo simulación</p>
                {simActive ? (
                  <>
                    <p style={{color: 'var(--t-e9d5ff)', fontSize: '0.8rem', margin: '0 0 4px'}}>
                      Sin {excludedIds.size === 1 ? '1 venta' : `${excludedIds.size} ventas`}: penetración de garantías {Math.round(realResult.current.penetration * 100)}% → <strong>{Math.round(result.current.penetration * 100)}%</strong> ·
                      {' '}a cobrar {eur(realResult.payable)} → <strong>{eur(result.payable)}</strong>
                      {' '}(<span style={{color: result.payable - realResult.payable >= 0 ? 'var(--t-86efac)' : 'var(--t-fca5a5)'}}>{result.payable - realResult.payable >= 0 ? '+' : ''}{eur(result.payable - realResult.payable)}</span>)
                    </p>
                    <button type="button" onClick={() => setExcludedIds(new Set())} style={{background: 'none', border: 'none', color: 'var(--t-c4b5fd)', cursor: 'pointer', fontSize: '0.75rem', padding: 0, textDecoration: 'underline'}}>
                      Volver a contar todas
                    </button>
                  </>
                ) : (
                  <p style={{color: 'var(--t-e9d5ff)', fontSize: '0.8rem', margin: 0}}>Desmarca "Contar en el cálculo" en las ventas que quieras quitar. Las cifras de abajo se recalculan al momento; no se guarda nada.</p>
                )}
              </div>
            )}

            <div style={{backgroundColor: 'var(--b-374151)', borderRadius: '8px', padding: '14px', marginBottom: '16px', outline: simActive ? '2px dashed #8b5cf6' : 'none'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: '8px', marginBottom: '10px'}}>
                <span style={{color: 'var(--t-cbd5e1)', fontSize: '0.8rem'}}>{simActive ? 'A cobrar este mes (simulado)' : 'A cobrar este mes (variable)'}</span>
                <span style={{color: result.payable > 0 ? 'var(--t-4ade80)' : 'var(--t-f1f5f9)', fontSize: '1.6rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums'}}>{eur(result.payable)}</span>
              </div>
              {result.requirements.map(r => (
                <div key={r.label} style={{fontSize: '0.8rem', color: r.ok ? 'var(--t-86efac)' : 'var(--t-fca5a5)'}}>{r.ok ? '✓' : '✗'} {r.label} ({r.detail})</div>
              ))}
              {!result.requirementsOk && (
                <p style={{color: 'var(--t-fca5a5)', fontSize: '0.75rem', margin: '6px 0 0'}}>Sin cumplir los requisitos no se cobra variable. Abajo ves lo que generarías si los cumplieras.</p>
              )}
              <div style={{borderTop: '1px solid var(--l-4b5563)', marginTop: '10px', paddingTop: '8px'}}>
                {row(`Ventas validadas este mes: ${result.current.n} · garantías ${Math.round(result.current.penetration * 100)}% (${result.current.wRate * 100}%) · financiación ${eur(result.current.financeTotal)} de margen (${(result.current.fRate * 100).toFixed(2)}%)`, '')}
                {salesBlock(
                  `🆕 Generado en ${MONTH_NAMES_ES[month - 1]} (${result.salesCurrent.count} ${result.salesCurrent.count === 1 ? 'venta' : 'ventas'}: 100% o 40%)`,
                  result.salesCurrent, 'var(--t-86efac)',
                  { warranty: `${result.current.wRate * 100}% del importe`, finance: `${(result.current.fRate * 100).toFixed(2)}% del margen` }
                )}
                {result.salesInherited.map(g => (
                  <React.Fragment key={g.monthIdx}>
                    {salesBlock(
                      `↩️ 60% heredado de ${MONTH_NAMES_ES[g.month - 1]} ${g.year} (${g.count} ${g.count === 1 ? 'venta' : 'ventas'})`,
                      g, 'var(--t-fbbf24)', { warranty: `según ${MONTH_NAMES_ES[g.month - 1]}`, finance: `según ${MONTH_NAMES_ES[g.month - 1]}` }
                    )}
                  </React.Fragment>
                ))}
                {row(`Captaciones: ${result.captaciones} (90 € desde la 4ª)`, eur(result.captacionesAmount))}
                {row(`Seguros: ${result.policies} pólizas (objetivo ${result.policyTarget})`, eur(result.insuranceAmount))}
                {(result.insurancePenalty + result.penaltiesAmount) > 0 && row('Penalizaciones (detalle abajo)', `−${eur(result.insurancePenalty + result.penaltiesAmount)}`)}
                {row('Total variable generado', eur(result.gross), true)}
                {row(`Mínimo provincial (solo se cobra el exceso)`, `−${eur(COMMISSION_VARIABLE_FLOOR)}`)}
              </div>
            </div>

            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '8px'}}>
              <p style={{color: 'var(--t-f1f5f9)', fontSize: '0.9rem', fontWeight: 600, margin: 0}}>
                Operaciones {pendingData > 0 && <span style={{color: 'var(--t-fde68a)', fontWeight: 400, fontSize: '0.8rem'}}>· {pendingData} con datos pendientes</span>}
              </p>
              <div style={{display: 'flex', gap: '6px', flexWrap: 'wrap'}}>
                <button type="button" onClick={() => openAddForm('sale')} style={{...S.secondaryButton, padding: '4px 10px', fontSize: '0.8rem'}}>
                  {isAdmin ? '＋ Añadir venta' : '＋ Venta de otra tienda'}
                </button>
                <button type="button" onClick={() => openAddForm('purchase')} style={{...S.secondaryButton, padding: '4px 10px', fontSize: '0.8rem'}}>
                  {isAdmin ? '＋ Añadir compra' : '＋ Compra de otra tienda'}
                </button>
              </div>
            </div>

            {addMode && (
              <form onSubmit={addManualEntry} style={{backgroundColor: 'var(--b-1e293b)', borderRadius: '6px', padding: '12px', marginBottom: '12px'}}>
                <p style={{color: 'var(--t-f1f5f9)', fontSize: '0.85rem', fontWeight: 600, margin: '0 0 4px'}}>
                  {addMode === 'purchase' ? 'Añadir compra (captación)' : 'Añadir venta'}
                </p>
                <p style={{color: 'var(--t-9ca3af)', fontSize: '0.75rem', margin: '0 0 8px'}}>
                  {isAdmin
                    ? `Para operaciones que no se han registrado solas en el tablero. Se añade a las comisiones de ${selectedUserName}.`
                    : `Solo para ${addMode === 'purchase' ? 'compras' : 'ventas'} de otra tienda Flexicar. Las de ${orgName || 'tu tienda'} las añade el creador o el responsable.`}
                </p>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0 10px'}}>
                  {[['brand', 'Marca'], ['model', 'Modelo'], ['plate', 'Matrícula'], ['store', isAdmin ? 'Tienda del coche' : 'Otra tienda (p. ej. Flexicar Alcorcón)']].map(([k, l]) => (
                    <div key={k}>
                      <label style={S.label} htmlFor={`new-op-${k}`}>{l}</label>
                      <input id={`new-op-${k}`} style={S.input} value={newOp[k]} onChange={e => setNewOp(n => ({...n, [k]: e.target.value}))} required={k === 'plate' || k === 'store'} />
                    </div>
                  ))}
                  <div>
                    <label style={S.label} htmlFor="new-op-reserved">{addMode === 'purchase' ? 'Fecha de la compra' : 'Fecha de reserva'}</label>
                    <input id="new-op-reserved" style={S.input} type="date" value={newOp.reserved} onChange={e => setNewOp(n => ({...n, reserved: e.target.value}))} required />
                  </div>
                </div>
                <div style={{display: 'flex', gap: '8px', justifyContent: 'flex-end'}}>
                  <button type="button" onClick={() => setAddMode(null)} style={S.secondaryButton}>Cancelar</button>
                  <button type="submit" style={S.button}>Añadir</button>
                </div>
              </form>
            )}

            <p style={{color: 'var(--t-86efac)', fontSize: '0.8rem', fontWeight: 700, margin: '0 0 6px'}}>
              🆕 Vendidas en {MONTH_NAMES_ES[month - 1]} · {currentOps.length}
            </p>
            {currentOps.length === 0 ? (
              <p style={{color: 'var(--t-9ca3af)', fontStyle: 'italic', fontSize: '0.875rem', margin: '0 0 16px'}}>No hay ventas validadas en este mes.</p>
            ) : (
              <div style={{display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px'}}>
                {currentOps.map(renderOpCard)}
              </div>
            )}

            {inheritedOps.length > 0 && (
              <div style={{marginBottom: '20px'}}>
                <p style={{color: 'var(--t-fbbf24)', fontSize: '0.8rem', fontWeight: 700, margin: '0 0 2px'}}>
                  ↩️ Heredadas de meses anteriores · {inheritedOps.length}
                </p>
                <p style={{color: 'var(--t-9ca3af)', fontSize: '0.72rem', margin: '0 0 6px'}}>
                  Se validaron en un mes anterior y se entregan en {MONTH_NAMES_ES[month - 1]}: aquí cobran el 60% restante. No cuentan para las ventas, la penetración ni el tramo de este mes.
                </p>
                <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                  {inheritedOps.map(renderOpCard)}
                </div>
              </div>
            )}

            {pendingInherited.length > 0 && (
              <div style={{marginBottom: '20px'}}>
                <p style={{color: 'var(--t-fbbf24)', fontSize: '0.8rem', fontWeight: 700, margin: '0 0 2px'}}>
                  ⏳ Heredadas pendientes de entrega · {pendingInherited.length}
                </p>
                <p style={{color: 'var(--t-9ca3af)', fontSize: '0.72rem', margin: '0 0 6px'}}>
                  Se validaron en un mes anterior y aún no se han entregado: cobrarán el 60 % restante el mes en que se entreguen. No cuentan para las ventas, la penetración ni el tramo de este mes.
                </p>
                <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                  {pendingInherited.map(renderOpCard)}
                </div>
              </div>
            )}

            {reservedOps.length > 0 && (
              <div style={{borderTop: '1px solid var(--l-374151)', paddingTop: '12px', marginBottom: '16px'}}>
                <p style={{color: 'var(--t-f1f5f9)', fontSize: '0.9rem', fontWeight: 600, margin: '0 0 4px'}}>
                  📝 Reservas de {MONTH_NAMES_ES[month - 1]} (sin comisión) · {reservedOps.length}
                </p>
                <p style={{color: 'var(--t-9ca3af)', fontSize: '0.75rem', margin: '0 0 8px'}}>
                  Reservar no genera comisión: estas ventas empezarán a contar en el mes en que se validen. Puedes ir completando sus datos.
                </p>
                <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                  {reservedOps.map(renderOpCard)}
                </div>
              </div>
            )}

            <div style={{borderTop: '1px solid var(--l-374151)', paddingTop: '12px', marginBottom: '16px'}}>
              <p style={{color: 'var(--t-f1f5f9)', fontSize: '0.9rem', fontWeight: 600, margin: '0 0 8px'}}>
                Compras (captaciones) de {MONTH_NAMES_ES[month - 1]} · {result.captaciones}
              </p>
              {result.captacionList.length === 0 ? (
                <p style={{color: 'var(--t-9ca3af)', fontSize: '0.8rem', fontStyle: 'italic', margin: '0 0 4px'}}>Sin compras registradas este mes (cuentan al llegar el coche a Stock).</p>
              ) : result.captacionList.map(p => renderPurchaseRow(p))}
              {cancelledPurchases.map(p => renderPurchaseRow(p))}
            </div>

            {(isAdmin || result.monthPenalties.length > 0 || result.insurancePenalty > 0) && (
              <div style={{borderTop: '1px solid var(--l-374151)', paddingTop: '12px'}}>
                <p style={{color: 'var(--t-f1f5f9)', fontSize: '0.9rem', fontWeight: 600, margin: '0 0 8px'}}>Penalizaciones de {MONTH_NAMES_ES[month - 1]}</p>
                {result.insurancePenaltyCalculated > 0 && (!result.insuranceWaived || isAdmin) && (
                  <div style={{display: 'flex', justifyContent: 'space-between', gap: '8px', fontSize: '0.8rem', padding: '3px 0', opacity: result.insuranceWaived ? 0.6 : 1}}>
                    <span style={{color: 'var(--t-e5e7eb)'}}>
                      🛡️ Seguros: {result.policies} de {result.policyTarget} pólizas del objetivo (100 € por cada una que falta)
                      {result.insuranceWaived && <em style={{color: 'var(--t-9ca3af)'}}> · eliminada</em>}
                    </span>
                    <span style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                      <span style={{color: result.insuranceWaived ? 'var(--t-9ca3af)' : 'var(--t-fca5a5)', whiteSpace: 'nowrap', textDecoration: result.insuranceWaived ? 'line-through' : 'none'}}>
                        −{eur(result.insurancePenaltyCalculated)}
                      </span>
                      {isAdmin && (
                        <button type="button"
                          onClick={() => runRpc('waiver', 'set_insurance_penalty_waived',
                            { p_profile_id: profileId, p_year: year, p_month: month, p_waived: !result.insuranceWaived },
                            result.insuranceWaived ? '¿Volver a aplicar la penalización de seguros de este mes?' : '¿Eliminar la penalización de seguros de este mes?')}
                          style={{background: 'none', border: 'none', color: 'var(--t-9ca3af)', cursor: 'pointer', fontSize: '0.75rem'}}>
                          {result.insuranceWaived ? 'Restaurar' : 'Eliminar'}
                        </button>
                      )}
                    </span>
                  </div>
                )}
                {result.monthPenalties.length === 0 && (result.insurancePenaltyCalculated === 0 || (result.insuranceWaived && !isAdmin)) && (
                  <p style={{color: 'var(--t-9ca3af)', fontSize: '0.8rem', fontStyle: 'italic', margin: 0}}>Ninguna.</p>
                )}
                {result.monthPenalties.map(p => (
                  <div key={p.id} style={{display: 'flex', justifyContent: 'space-between', gap: '8px', fontSize: '0.8rem', padding: '3px 0'}}>
                    <span style={{color: 'var(--t-e5e7eb)'}}>{p.reason || 'Incidencia / reclamación'}</span>
                    <span style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                      <span style={{color: 'var(--t-fca5a5)'}}>−{eur(p.amount)}</span>
                      {isAdmin && (
                        <button type="button" onClick={() => runRpc(p.id, 'void_commission_penalty', { p_id: p.id }, '¿Eliminar esta penalización?')}
                          style={{background: 'none', border: 'none', color: 'var(--t-9ca3af)', cursor: 'pointer', fontSize: '0.75rem'}}>Eliminar</button>
                      )}
                    </span>
                  </div>
                ))}
                {isAdmin && (
                  <div style={{display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap'}}>
                    <select id="penalty-type" style={{...S.select, marginBottom: 0, width: 'auto', flex: '1 1 180px'}} value={penaltyType} onChange={e => setPenaltyType(e.target.value)}>
                      {PENALTY_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}{t.amount ? ` (${t.amount} €)` : ''}</option>)}
                    </select>
                    {selectedPenaltyType.amount == null && (
                      <input id="penalty-amount" type="number" min="0" step="0.01" style={{...S.input, marginBottom: 0, width: '110px'}} placeholder="Importe €" value={penaltyAmount} onChange={e => setPenaltyAmount(e.target.value)} />
                    )}
                    <input id="penalty-reason" style={{...S.input, marginBottom: 0, flex: '2 1 180px'}} placeholder="Nota (opcional)" value={penaltyReason} onChange={e => setPenaltyReason(e.target.value)} />
                    <button type="button" onClick={addPenalty} style={{...S.secondaryButton, whiteSpace: 'nowrap', color: 'var(--t-fca5a5)'}}>＋ Añadir</button>
                  </div>
                )}
              </div>
            )}

            {cancelledOps.length > 0 && (
              <div style={{borderTop: '1px solid var(--l-374151)', paddingTop: '12px', marginTop: '16px'}}>
                <p style={{color: 'var(--t-fca5a5)', fontSize: '0.85rem', fontWeight: 700, margin: '0 0 2px'}}>
                  🚫 Anuladas · {cancelledOps.length}
                </p>
                <p style={{color: 'var(--t-9ca3af)', fontSize: '0.72rem', margin: '0 0 6px'}}>
                  No computan para las comisiones ni salen en la hoja impresa. Se pueden recuperar si se anularon por error.
                </p>
                <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                  {cancelledOps.map(renderOpCard)}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function QuickSaleList({ items }) {
  if (items.length === 0) return <span style={{color: 'var(--t-6b7280)'}}>—</span>;
  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '2px'}}>
      {items.map(d => (
        <span key={d.id} style={{color: 'var(--t-e5e7eb)'}}>
          <strong style={{color: 'var(--t-fbbf24)'}}>{d.plate}</strong> · {quickSaleDays(d) ?? '?'} días
        </span>
      ))}
    </div>
  );
}

// Vista del captador: sus ventas en menos de 60 días, agrupadas por mes de entrega.
function QuickSalesModal({ deliveries, onClose }) {
  const byMonth = {};
  deliveries.forEach(d => {
    const key = (d.delivered_at || '').slice(0, 7);
    if (key) (byMonth[key] = byMonth[key] || []).push(d);
  });
  const months = Object.keys(byMonth).sort().reverse();

  return (
    <div style={S.overlay}>
      <div style={{...S.modal, maxWidth: '480px'}}>
        <button onClick={onClose} style={S.closeBtn}>✕</button>
        <h3 style={S.title}>⚡ Ventas en menos de 60 días</h3>
        <p style={{color: 'var(--t-9ca3af)', fontSize: '0.8rem', marginBottom: '16px'}}>
          Vehículos que compraste y se entregaron antes de 60 días desde su alta. Cuentan en el mes de la entrega.
        </p>
        {months.length === 0 ? (
          <p style={{color: 'var(--t-9ca3af)', fontStyle: 'italic', fontSize: '0.875rem'}}>Todavía no tienes ninguna.</p>
        ) : months.map(key => {
          const [y, m] = key.split('-').map(Number);
          return (
            <div key={key} style={{marginBottom: '16px'}}>
              <p style={{color: 'var(--t-f1f5f9)', fontSize: '0.875rem', fontWeight: 600, margin: '0 0 6px'}}>
                {MONTH_NAMES_ES[m - 1]} {y} — {byMonth[key].length}
              </p>
              {byMonth[key].map(d => (
                <div key={d.id} style={{display: 'flex', justifyContent: 'space-between', gap: '12px', fontSize: '0.8rem', padding: '4px 0', borderTop: '1px solid var(--l-374151)'}}>
                  <span style={{color: 'var(--t-e5e7eb)'}}><strong style={{color: 'var(--t-fbbf24)'}}>{d.plate}</strong> · {d.brand} {d.model}</span>
                  <span style={{color: 'var(--t-9ca3af)', whiteSpace: 'nowrap'}}>{quickSaleDays(d) ?? '?'} días · {new Date(d.delivered_at).toLocaleDateString('es-ES')}</span>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TargetsModal({ orgId, year, month, storeTarget, storePurchaseTarget, deliveries, purchases, purchaseFallthroughs, monthlyTargets, onOpenCommissions, onClose, onSaved }) {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  const prevMonth = month === 1 ? 12 : month - 1;
  const prevYear = month === 1 ? year - 1 : year;

  useEffect(() => {
    supabase.from('profiles').select('id, full_name, role').eq('org_id', orgId).in('role', SALE_ROLES).order('full_name')
      .then(({ data }) => {
        setUsers(data || []);
        setLoadingUsers(false);
      });
  }, [orgId]);

  const companyThisMonth = companyPurchasesCount(purchases, year, month);

  return (
    <div style={S.overlay}>
      <div style={{...S.modal, maxWidth: '680px'}}>
        <button onClick={onClose} style={S.closeBtn}>✕</button>
        <h3 style={S.title}>🎯 Objetivos — {MONTH_NAMES_ES[month - 1]} {year}</h3>

        <TargetsSection
          metric="ventas" icon="📦" title="ventas"
          countFor={(profileId, y, m) => deliveriesCount(deliveries, profileId, y, m)}
          year={year} month={month} prevYear={prevYear} prevMonth={prevMonth}
          storeTarget={storeTarget} users={users} loadingUsers={loadingUsers}
          monthlyTargets={monthlyTargets} onSaved={onSaved}
        />

        <TargetsSection
          metric="compras" icon="🛒" title="compras"
          countFor={(profileId, y, m) => purchasesCount(purchases, profileId, y, m)}
          year={year} month={month} prevYear={prevYear} prevMonth={prevMonth}
          storeTarget={storePurchaseTarget} users={users} loadingUsers={loadingUsers}
          monthlyTargets={monthlyTargets} onSaved={onSaved}
          extraStoreInfo={`Compras central (empresa) este mes: ${companyThisMonth} — métrica aparte: no cuentan en el total de la tienda ni en el objetivo individual de nadie.`}
        />

        <div style={{marginBottom: '28px'}}>
          <p style={{color: 'var(--t-f1f5f9)', fontSize: '0.875rem', fontWeight: 600, margin: '0 0 10px'}}>💶 Comisiones por comercial</p>
          {loadingUsers ? (
            <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem'}}>Cargando...</p>
          ) : users.filter(u => COMMISSION_ROLES.includes(u.role)).length === 0 ? (
            <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem', fontStyle: 'italic'}}>No hay usuarios con comisiones en la organización.</p>
          ) : (
            <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
              {users.filter(u => COMMISSION_ROLES.includes(u.role)).map(u => (
                <button key={u.id} type="button" onClick={() => onOpenCommissions(u.id)} style={{...S.secondaryButton, padding: '6px 12px', fontSize: '0.8rem'}}>
                  💶 {u.full_name || 'Sin nombre'}
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{marginBottom: '28px'}}>
          <p style={{color: 'var(--t-f1f5f9)', fontSize: '0.875rem', fontWeight: 600, margin: '0 0 4px'}}>⚡ Ventas en menos de 60 días — captadores</p>
          <p style={{color: 'var(--t-9ca3af)', fontSize: '0.75rem', margin: '0 0 10px'}}>
            Vehículos comprados por el captador y entregados antes de 60 días desde su alta. Cuentan en el mes de la entrega.
          </p>
          {loadingUsers ? (
            <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem'}}>Cargando...</p>
          ) : users.filter(u => u.role === 'captador').length === 0 ? (
            <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem', fontStyle: 'italic'}}>No hay usuarios con rol captador en la organización.</p>
          ) : (
            <div style={{overflowX: 'auto'}}>
              <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem'}}>
                <thead>
                  <tr style={{color: 'var(--t-9ca3af)', textAlign: 'left'}}>
                    <th style={{padding: '6px'}}>Captador</th>
                    <th style={{padding: '6px'}}>Este mes</th>
                    <th style={{padding: '6px'}}>{MONTH_NAMES_ES[prevMonth - 1]} (mes anterior)</th>
                  </tr>
                </thead>
                <tbody>
                  {users.filter(u => u.role === 'captador').map(u => {
                    const cur = quickSalesFor(deliveries, u.id, year, month);
                    const prev = quickSalesFor(deliveries, u.id, prevYear, prevMonth);
                    return (
                      <tr key={u.id} style={{borderTop: '1px solid var(--l-374151)', verticalAlign: 'top'}}>
                        <td style={{padding: '6px', color: 'var(--t-f1f5f9)'}}>{u.full_name || 'Sin nombre'}</td>
                        <td style={{padding: '6px'}}><strong style={{color: 'var(--t-f1f5f9)'}}>{cur.length}</strong><QuickSaleList items={cur} /></td>
                        <td style={{padding: '6px'}}><strong style={{color: 'var(--t-f1f5f9)'}}>{prev.length}</strong><QuickSaleList items={prev} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div>
          <p style={{color: 'var(--t-f1f5f9)', fontSize: '0.875rem', fontWeight: 600, margin: '0 0 4px'}}>❌ Compras no realizadas</p>
          <p style={{color: 'var(--t-9ca3af)', fontSize: '0.75rem', margin: '0 0 10px'}}>
            Vehículos marcados como "No se compra" en Revisión F2, por usuario con la compra asignada.
          </p>
          {loadingUsers ? (
            <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem'}}>Cargando...</p>
          ) : users.length === 0 ? (
            <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem', fontStyle: 'italic'}}>No hay usuarios con rol comercial o captador en la organización.</p>
          ) : (
            <div style={{overflowX: 'auto'}}>
              <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem'}}>
                <thead>
                  <tr style={{color: 'var(--t-9ca3af)', textAlign: 'left'}}>
                    <th style={{padding: '6px'}}>Usuario</th>
                    <th style={{padding: '6px'}}>Este mes</th>
                    <th style={{padding: '6px'}}>{MONTH_NAMES_ES[prevMonth - 1]} (mes anterior)</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} style={{borderTop: '1px solid var(--l-374151)'}}>
                      <td style={{padding: '6px', color: 'var(--t-f1f5f9)'}}>{u.full_name || 'Sin nombre'} <span style={{color: 'var(--t-6b7280)'}}>({roleLabel(u.role)})</span></td>
                      <td style={{padding: '6px', color: 'var(--t-e5e7eb)', fontVariantNumeric: 'tabular-nums'}}>{fallthroughsCount(purchaseFallthroughs, u.id, year, month)}</td>
                      <td style={{padding: '6px', color: 'var(--t-e5e7eb)', fontVariantNumeric: 'tabular-nums'}}>{fallthroughsCount(purchaseFallthroughs, u.id, prevYear, prevMonth)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const CROSS_STORE_VALUE = '__cross__';

// mode: 'move' (Stock -> Reservados, incluye el cambio de columna) | 'reassign' (solo creador, cambia el asignado sin mover)
// ===== Venta: ficha de operación y flujo de venta =====
// La ficha se rellena al pasar de Stock a Reservados (quien hace el traslado) y la pueden cambiar el comercial de la venta,
// el responsable y el creador hasta Limpieza entrega (save_sale_operation). Sus datos se vuelcan a Comisiones.
const SALE_NAME_CHANGE_AMOUNT = 390;
// Transporte cobrado: importe fijo. Ni el cambio de nombre ni el transporte computan en comisiones.
const SALE_TRANSPORT_AMOUNT = 450;
const SALE_OP_ROLES = ['creador', 'responsable'];
// Importes de garantía por tramo de precio al contado (1, 2 y 3 años).
const WARRANTY_PRICES = { 1: [890, 1340, 1790], 2: [1150, 1590, 1990], 3: [1550, 1990, 2390] };
const WARRANTY_TIER_LABELS = { 1: 'hasta 30.000 € al contado', 2: 'de 30.000 a 40.000 € al contado', 3: 'más de 40.000 € al contado' };

// Con PVP financiado: por debajo de 25.000 € va directo al tramo de hasta 30.000 € al contado, y en dos franjas
// hay que preguntar el precio al contado (misma regla que sale_warranty_tier en la BD).
function saleWarrantyQuestion(pvpType, pvp) {
  if (pvpType !== 'financiado' || !(pvp > 0)) return null;
  if (pvp < 25000) return null;
  if (pvp < 28000) return 'over30';
  if (pvp >= 35000 && pvp < 37000) return 'over40';
  return null;
}
function saleWarrantyTier(pvpType, pvp, over30, over40) {
  if (!(pvp > 0)) return null;
  if (pvpType === 'contado') return pvp <= 30000 ? 1 : pvp <= 40000 ? 2 : 3;
  if (pvpType !== 'financiado') return null;
  if (pvp < 25000) return 1;
  if (pvp < 28000) return over30 === true ? 2 : over30 === false ? 1 : null;
  if (pvp < 35000) return 2;
  if (pvp < 37000) return over40 === true ? 3 : over40 === false ? 2 : null;
  return 3;
}
function emptySaleOp() {
  return { pvp: '', pvpType: '', warranty: null, over30: null, over40: null, years: 0, courtesy: null, courtesyAmount: '', courtesyBy: '',
    transport: null, transportDestination: '', transportCharged: null };
}
function saleOpFromRow(row) {
  if (!row) return emptySaleOp();
  return {
    pvpType: row.pvp_type, pvp: String(row.pvp ?? ''), over30: row.cash_over_30k, over40: row.cash_over_40k,
    warranty: (row.warranty_years || 0) > 0, years: row.warranty_years || 0, courtesy: !!row.courtesy,
    courtesyAmount: row.courtesy ? String(row.courtesy_amount ?? '') : '', courtesyBy: row.courtesy_authorized_by || '',
    transport: !!row.transport, transportDestination: row.transport_destination || '', transportCharged: row.transport ? row.transport_charged : null
  };
}
function saleOpError(op) {
  const pvp = Number(op.pvp);
  if (!op.pvpType) return 'Indica si el coche va financiado o al contado.';
  if (!(pvp > 0)) return 'Escribe el PVP.';
  if (op.warranty == null) return 'Indica si lleva garantía.';
  if (op.warranty) {
    const q = saleWarrantyQuestion(op.pvpType, pvp);
    if (q === 'over30' && op.over30 == null) return 'Responde si el precio al contado es de más de 30.000 €.';
    if (q === 'over40' && op.over40 == null) return 'Responde si el precio al contado es de más de 40.000 €.';
    if (!(Number(op.years) >= 1)) return 'Elige los años de garantía.';
  }
  if (op.courtesy == null) return 'Indica si hay cortesía comercial.';
  if (op.courtesy) {
    if (!(Number(op.courtesyAmount) > 0)) return 'Escribe el importe de la cortesía.';
    if (!op.courtesyBy.trim()) return 'Indica quién ha autorizado la cortesía.';
    if (/[<>]/.test(op.courtesyBy)) return 'El nombre de quien autoriza no puede llevar < ni >.';
  }
  if (op.transport == null) return 'Indica si lleva transporte.';
  if (op.transport) {
    if (!op.transportDestination.trim()) return 'Escribe la tienda de destino del transporte.';
    if (/[<>]/.test(op.transportDestination)) return 'La tienda de destino no puede llevar < ni >.';
    if (op.transportCharged == null) return 'Indica si se ha cobrado el transporte.';
  }
  return null;
}
function saleOperationParams(op) {
  return {
    p_pvp_type: op.pvpType, p_pvp: Number(op.pvp),
    p_cash_over_30k: op.warranty ? op.over30 : null, p_cash_over_40k: op.warranty ? op.over40 : null,
    p_warranty_years: op.warranty ? (Number(op.years) || 0) : 0,
    p_courtesy: !!op.courtesy, p_courtesy_amount: op.courtesy ? Number(op.courtesyAmount) : null,
    p_courtesy_authorized_by: op.courtesy ? op.courtesyBy.trim() : null,
    p_transport: !!op.transport, p_transport_destination: op.transport ? op.transportDestination.trim() : null,
    p_transport_charged: op.transport ? op.transportCharged : null
  };
}
async function saveSaleOperation(vehicleId, op) {
  return supabase.rpc('save_sale_operation', { p_vehicle_id: vehicleId, ...saleOperationParams(op) });
}

// Botones de elección (Contado/Financiado, Sí/No).
function ChoiceButtons({ options, value, onChange, disabled, name }) {
  return (
    <div role="radiogroup" aria-label={name} style={{display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px'}}>
      {options.map(o => {
        const on = value === o.value;
        return (
          <button key={String(o.value)} type="button" role="radio" aria-checked={on} disabled={disabled} onClick={() => onChange(o.value)}
            style={{...S.secondaryButton, padding: '7px 14px', fontWeight: on ? 700 : 400,
              backgroundColor: on ? 'var(--b-3f3320)' : 'transparent', borderColor: on ? 'var(--l-f59e0b)' : 'var(--l-4b5563)',
              color: on ? 'var(--t-f59e0b)' : 'var(--t-f1f5f9)', opacity: disabled ? 0.6 : 1}}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function SaleOperationForm({ value, onChange, disabled, idPrefix = 'sale-op' }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const pvp = Number(value.pvp);
  const question = value.warranty ? saleWarrantyQuestion(value.pvpType, pvp) : null;
  const tier = saleWarrantyTier(value.pvpType, pvp, value.over30, value.over40);
  const prices = tier ? WARRANTY_PRICES[tier] : null;
  const yesNo = [{ value: true, label: 'Sí' }, { value: false, label: 'No' }];
  const hint = {color: 'var(--t-9ca3af)', fontSize: '0.75rem', margin: '-6px 0 12px'};
  const input = {...S.input, boxSizing: 'border-box'};

  return (
    <div>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px', backgroundColor: 'var(--b-1f2937)',
        border: '1px solid var(--l-374151)', borderRadius: '6px', padding: '8px 10px', marginBottom: '14px'}}>
        <span style={{color: 'var(--t-cbd5e1)', fontSize: '0.85rem'}}>Cambio de nombre</span>
        <strong style={{color: 'var(--t-f1f5f9)', fontVariantNumeric: 'tabular-nums'}}>{eur(SALE_NAME_CHANGE_AMOUNT)}</strong>
      </div>

      <label style={S.label}>1. ¿El coche va financiado o al contado?</label>
      <ChoiceButtons name="Forma de pago" disabled={disabled}
        options={[{ value: 'financiado', label: 'Financiado' }, { value: 'contado', label: 'Al contado' }]}
        value={value.pvpType || null}
        onChange={v => set({ pvpType: v, over30: null, over40: null })} />

      <label style={S.label} htmlFor={`${idPrefix}-pvp`}>2. PVP (€)</label>
      <input id={`${idPrefix}-pvp`} style={input} type="number" inputMode="decimal" min="1" step="1" value={value.pvp} disabled={disabled}
        onChange={e => set({ pvp: e.target.value, over30: null, over40: null })} placeholder="Ej.: 24990" />

      <label style={S.label}>3. ¿Lleva garantía?</label>
      <ChoiceButtons name="Garantía" disabled={disabled} options={yesNo} value={value.warranty}
        onChange={v => set(v ? { warranty: true } : { warranty: false, years: 0, over30: null, over40: null })} />
      {value.warranty && (
        <div style={{borderLeft: '2px solid var(--l-374151)', paddingLeft: '12px', marginBottom: '4px'}}>
          {!(pvp > 0) || !value.pvpType ? (
            <p style={{...hint, margin: '0 0 12px'}}>Indica si va financiado o al contado y escribe el PVP para ver los importes de la garantía.</p>
          ) : (
            <>
              {question && (
                <>
                  <label style={S.label}>¿El precio al contado es de más de {question === 'over30' ? '30.000' : '40.000'} €?</label>
                  <ChoiceButtons name="Precio al contado" disabled={disabled} options={yesNo}
                    value={question === 'over30' ? value.over30 : value.over40}
                    onChange={v => set(question === 'over30' ? { over30: v } : { over40: v })} />
                </>
              )}
              {prices && (
                <>
                  <label style={S.label} htmlFor={`${idPrefix}-warranty`}>Años de garantía</label>
                  <select id={`${idPrefix}-warranty`} style={S.select} value={String(value.years || '')} disabled={disabled}
                    onChange={e => set({ years: Number(e.target.value) || 0 })}>
                    <option value="">Elige...</option>
                    {[1, 2, 3].map(y => <option key={y} value={y}>{y} {y === 1 ? 'año' : 'años'} · {eur(prices[y - 1])}</option>)}
                  </select>
                  <p style={hint}>Importes del tramo {WARRANTY_TIER_LABELS[tier]}.</p>
                </>
              )}
            </>
          )}
        </div>
      )}

      <label style={S.label}>4. ¿Hay cortesía comercial?</label>
      <ChoiceButtons name="Cortesía comercial" disabled={disabled} options={yesNo} value={value.courtesy}
        onChange={v => set(v ? { courtesy: true } : { courtesy: false, courtesyAmount: '', courtesyBy: '' })} />
      {value.courtesy && (
        <div style={{borderLeft: '2px solid var(--l-374151)', paddingLeft: '12px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0 12px'}}>
          <div>
            <label style={S.label} htmlFor={`${idPrefix}-courtesy-amount`}>Importe de la cortesía (€)</label>
            <input id={`${idPrefix}-courtesy-amount`} style={input} type="number" inputMode="decimal" min="1" step="1" disabled={disabled}
              value={value.courtesyAmount} onChange={e => set({ courtesyAmount: e.target.value })} />
          </div>
          <div>
            <label style={S.label} htmlFor={`${idPrefix}-courtesy-by`}>¿Quién la ha autorizado?</label>
            <input id={`${idPrefix}-courtesy-by`} style={input} maxLength={80} disabled={disabled}
              value={value.courtesyBy} onChange={e => set({ courtesyBy: e.target.value })} placeholder="Nombre" />
          </div>
        </div>
      )}

      <label style={S.label}>5. ¿Lleva transporte?</label>
      <ChoiceButtons name="Transporte" disabled={disabled} options={yesNo} value={value.transport}
        onChange={v => set(v ? { transport: true } : { transport: false, transportDestination: '', transportCharged: null })} />
      {value.transport && (
        <div style={{borderLeft: '2px solid var(--l-374151)', paddingLeft: '12px'}}>
          <label style={S.label} htmlFor={`${idPrefix}-transport-dest`}>Tienda de destino</label>
          <input id={`${idPrefix}-transport-dest`} style={input} maxLength={80} disabled={disabled}
            value={value.transportDestination} onChange={e => set({ transportDestination: e.target.value })} placeholder="Ej.: Flexicar Getafe" />
          <label style={S.label}>¿Se ha cobrado el transporte?</label>
          <ChoiceButtons name="Transporte cobrado" disabled={disabled} options={yesNo} value={value.transportCharged}
            onChange={v => set({ transportCharged: v })} />
          {value.transportCharged && (
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px', backgroundColor: 'var(--b-1f2937)',
              border: '1px solid var(--l-374151)', borderRadius: '6px', padding: '8px 10px', marginBottom: '12px'}}>
              <span style={{color: 'var(--t-cbd5e1)', fontSize: '0.85rem'}}>Transporte cobrado</span>
              <strong style={{color: 'var(--t-f1f5f9)', fontVariantNumeric: 'tabular-nums'}}>{eur(SALE_TRANSPORT_AMOUNT)}</strong>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Resumen de una ficha de operación guardada (solo lectura).
function SaleOperationSummary({ row }) {
  const years = row.warranty_years || 0;
  const rows = [
    { label: 'Forma de pago', text: row.pvp_type === 'financiado' ? 'Financiado' : 'Al contado' },
    { label: 'PVP', amount: row.pvp },
    years > 0
      ? { label: 'Garantía', sub: `${years} ${years === 1 ? 'año' : 'años'}${WARRANTY_TIER_LABELS[row.warranty_tier] ? ` · Tramo: ${WARRANTY_TIER_LABELS[row.warranty_tier]}` : ''}`, amount: row.warranty_amount }
      : { label: 'Garantía', text: 'Sin garantía' },
    row.courtesy
      ? { label: 'Cortesía comercial', sub: row.courtesy_authorized_by ? `Autorizada por ${row.courtesy_authorized_by}` : '', amount: row.courtesy_amount }
      : { label: 'Cortesía comercial', text: 'No' },
    { label: 'Cambio de nombre', amount: row.name_change_amount ?? SALE_NAME_CHANGE_AMOUNT },
    row.transport
      ? (row.transport_charged
        ? { label: 'Transporte', sub: `A ${row.transport_destination || '—'} · cobrado`, amount: row.transport_amount ?? SALE_TRANSPORT_AMOUNT }
        : { label: 'Transporte', sub: `A ${row.transport_destination || '—'}`, text: 'No cobrado' })
      : { label: 'Transporte', text: 'Sin transporte' }
  ];
  return (
    <div style={{border: '1px solid var(--l-374151)', borderRadius: '8px', overflow: 'hidden', marginBottom: '10px'}}>
      {rows.map((r, i) => (
        <div key={r.label} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', padding: '9px 12px',
          borderTop: i === 0 ? 'none' : '1px solid var(--l-374151)', backgroundColor: i % 2 ? 'transparent' : 'var(--b-1f2937)'}}>
          <div style={{minWidth: 0}}>
            <div style={{color: 'var(--t-cbd5e1)', fontSize: '0.85rem'}}>{r.label}</div>
            {r.sub && <div style={{color: 'var(--t-9ca3af)', fontSize: '0.75rem', marginTop: '2px', overflowWrap: 'anywhere'}}>{r.sub}</div>}
          </div>
          <strong style={{color: r.amount != null ? 'var(--t-f1f5f9)' : 'var(--t-9ca3af)', fontWeight: r.amount != null ? 700 : 500,
            fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', fontSize: '0.9rem'}}>
            {r.amount != null ? eur(r.amount) : r.text}
          </strong>
        </div>
      ))}
    </div>
  );
}

// Botón "🧾 Operación" de la ficha del coche: resumen de la ficha guardada y botón Editar para quien puede cambiarla.
function SaleOperationModal({ vehicle, sellerName, canEdit, isAdmin, onClose, onSaved }) {
  const [row, setRow] = useState(null);
  const [op, setOp] = useState(emptySaleOp());
  const [editing, setEditing] = useState(false);
  const [validated, setValidated] = useState(false);
  const [updatedByName, setUpdatedByName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [notice, setNotice] = useState('');

  const load = async () => {
    const [{ data }, { data: comm }] = await Promise.all([
      supabase.from('sale_operations').select('*').eq('vehicle_id', vehicle.id).is('cancelled_at', null).maybeSingle(),
      supabase.from('commission_operations').select('validated_at').eq('vehicle_id', vehicle.id).is('cancelled_at', null)
        .is('delivered_at', null).order('created_at', { ascending: false }).limit(1).maybeSingle()
    ]);
    setRow(data || null);
    setOp(saleOpFromRow(data));
    setValidated(!!comm?.validated_at);
    setEditing(!data);
    const who = data?.updated_by || data?.created_by;
    if (who) {
      const { data: prof } = await supabase.from('profiles').select('full_name').eq('id', who).maybeSingle();
      setUpdatedByName(prof?.full_name || '');
    } else setUpdatedByName('');
    setLoading(false);
  };
  useEffect(() => { load(); }, [vehicle.id]);

  // Cambiarla: comercial de la venta, responsable o creador (canEdit); con la venta validada, solo responsable o creador.
  const mayEdit = canEdit && (!validated || isAdmin);
  const error = saleOpError(op);
  const save = async () => {
    if (error) { setMessage(error); return; }
    setSaving(true);
    setMessage('');
    const { error: err } = await saveSaleOperation(vehicle.id, op);
    if (err) { setSaving(false); setMessage('Error: ' + err.message); return; }
    await load();
    setSaving(false);
    setNotice('Operación guardada.');
    onSaved();
  };
  const cancelEdit = () => {
    if (!row) { onClose(); return; }
    setOp(saleOpFromRow(row));
    setMessage('');
    setEditing(false);
  };
  const changedAt = row ? new Date(row.updated_at || row.created_at) : null;

  return (
    <div style={S.overlay}>
      <div style={{...S.modal, maxWidth: '500px'}}>
        <button onClick={onClose} style={S.closeBtn}>✕</button>
        <h3 style={S.title}>🧾 Operación{editing && row ? ' · Editar' : ''}</h3>
        <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem', margin: '8px 0 14px'}}>
          {vehicle.brand} {vehicle.model} ({vehicle.plate}) · Venta de {sellerName || 'Cruzada (otra tienda)'}
        </p>
        {message && (
          <div style={{backgroundColor: 'var(--b-7f1d1d)', color: 'var(--t-f1f5f9)', padding: '10px', borderRadius: '6px', marginBottom: '14px', fontSize: '0.875rem'}}>{message}</div>
        )}
        {loading ? (
          <p style={{color: 'var(--t-9ca3af)'}}>Cargando...</p>
        ) : !editing && row ? (
          <>
            {notice && <p role="status" style={{color: 'var(--t-86efac)', fontSize: '0.85rem', margin: '0 0 10px'}}>✅ {notice}</p>}
            <SaleOperationSummary row={row} />
            <p style={{color: 'var(--t-9ca3af)', fontSize: '0.75rem', margin: '0 0 6px'}}>
              El cambio de nombre y el transporte no computan en las comisiones.
            </p>
            {changedAt && (
              <p style={{color: 'var(--t-9ca3af)', fontSize: '0.75rem', margin: '0 0 14px'}}>
                Última modificación: {changedAt.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                {updatedByName ? ` · ${updatedByName}` : ''}
              </p>
            )}
            {canEdit && validated && !isAdmin && (
              <p style={{color: 'var(--t-fbbf24)', fontSize: '0.8rem', margin: '0 0 14px'}}>
                La venta ya está validada: solo el responsable o el creador pueden cambiar la ficha.
              </p>
            )}
            <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
              <button type="button" onClick={onClose} style={S.secondaryButton}>Cerrar</button>
              {mayEdit && (
                <button type="button" onClick={() => { setNotice(''); setEditing(true); }} style={S.button}>✏️ Editar</button>
              )}
            </div>
          </>
        ) : !mayEdit ? (
          <>
            <p style={{color: 'var(--t-fbbf24)', fontSize: '0.85rem', margin: '0 0 14px'}}>Aún no se ha rellenado la ficha de esta operación.</p>
            <div style={{display: 'flex', justifyContent: 'flex-end'}}>
              <button type="button" onClick={onClose} style={S.secondaryButton}>Cerrar</button>
            </div>
          </>
        ) : (
          <>
            {!row && (
              <p style={{color: 'var(--t-fbbf24)', fontSize: '0.85rem', margin: '0 0 12px'}}>Aún no se ha rellenado la ficha de esta operación.</p>
            )}
            <SaleOperationForm value={op} onChange={setOp} disabled={saving} idPrefix="sale-op-edit" />
            <p style={{color: 'var(--t-9ca3af)', fontSize: '0.75rem', margin: '4px 0 14px'}}>
              Se vuelca a las comisiones de {sellerName || 'la venta'}: PVP, financiado y garantía (el cambio de nombre y el transporte no computan). El importe financiado, la puntuación y el seguro se completan en 💶 Comisiones.
            </p>
            <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
              <button type="button" onClick={cancelEdit} disabled={saving} style={S.secondaryButton}>Cancelar</button>
              <button type="button" onClick={save} disabled={saving} style={{...S.button, opacity: saving ? 0.6 : 1}}>
                {saving ? 'Guardando...' : 'Guardar operación'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Flujo de venta: desde la reserva hasta la entrega. Cada paso se deduce del estado del coche.
function saleFlowSteps({ vehicle, columns, opFilled, prepSheet }) {
  const posOf = (pred) => columns.find(c => pred(c.label?.trim().toLowerCase()))?.position;
  const pos = columns.find(c => c.id === vehicle.column_id)?.position ?? -1;
  const resPos = posOf(l => l === RESERVED_LABEL);
  const prepPos = posOf(l => l === PREPARACION_LABEL);
  const limpPos = posOf(l => l === LIMPIEZA_ENTREGA_LABEL);
  const entPos = posOf(l => isDeliveryColumnLabel(l));
  const atOrAfter = (p) => p != null && pos >= p;
  const seller = vehicle.assigned_profile?.full_name || (vehicle.sale_cross_store ? 'el comercial de la otra tienda' : 'el comercial de la venta');
  const steps = [
    { key: 'operacion', label: 'Reserva y ficha de operación',
      done: opFilled === true || (opFilled == null && atOrAfter(prepPos)),
      skipped: opFilled === false && atOrAfter(limpPos), skippedText: 'no se rellenó',
      who: `${seller}, el responsable o el creador (botón 🧾 Operación)` },
    { key: 'preparacion', label: 'Cobro y paso a Preparación (cobrado, validación y hoja de preparación)',
      done: atOrAfter(prepPos), who: `${seller}, el encargado de preparación, el responsable o el creador` },
    { key: 'validacion', label: 'Venta subida a validación',
      done: !!vehicle.uploaded_to_validation, who: `${seller} o el creador` },
    { key: 'hoja', label: 'Preparación del coche (hoja de preparación)',
      done: atOrAfter(limpPos) || (prepSheet ? prepSheetComplete(prepSheet) : false), who: 'el encargado de preparación' },
    { key: 'limpieza', label: 'Traslado a Limpieza entrega',
      done: atOrAfter(limpPos), who: 'el encargado de preparación, el responsable o el creador' },
    { key: 'fecha', label: 'Fecha de entrega fijada',
      done: !!vehicle.delivery_scheduled_at, who: `${seller} (al pasar a Entrega)` },
    { key: 'entrega', label: 'Entrega al cliente',
      done: false, who: `${seller}, el responsable o el creador (🚚 Marcar como entregado)` }
  ];
  let currentFound = false;
  return steps.map(st => {
    let status = st.skipped ? 'skipped' : st.done ? 'done' : 'pending';
    if (status === 'pending' && !currentFound) {
      // "Entrega al cliente" solo es el paso actual con el coche ya en Entrega.
      if (st.key !== 'entrega' || atOrAfter(entPos)) { status = 'current'; currentFound = true; }
    }
    return { ...st, status };
  });
}

// Botón "Avanzar en el flujo de venta": la siguiente acción del flujo que este usuario puede hacer, con los
// mismos permisos que los botones y traslados de siempre. kind = operation | move | validation | prepSheet | taller | deliver.
function saleAdvanceAction({ vehicle, columns, role, currentUserId, opFilled, canSeeOperation, prepSheet, tallerTransfer, moveTargetColumns, canDeliver }) {
  const label = columns.find(c => c.id === vehicle.column_id)?.label?.trim().toLowerCase();
  const target = (pred) => (moveTargetColumns || []).find(c => pred(c.label?.trim().toLowerCase()));
  const isSeller = !!vehicle.assigned_to && vehicle.assigned_to === currentUserId;
  if (opFilled === false && canSeeOperation) {
    return { kind: 'operation', label: 'Rellenar la ficha de operación' };
  }
  if (label === RESERVED_LABEL) {
    const to = target(l => l === PREPARACION_LABEL);
    if (to && (isSeller || ['creador', 'encargado_preparacion', 'responsable'].includes(role))) {
      return { kind: 'move', to, label: 'Pasar a Preparación',
        note: 'Después se confirma el cobro, la subida a validación y la hoja de preparación.' };
    }
    return null;
  }
  if (vehicle.paid && !vehicle.uploaded_to_validation && (isSeller || role === 'creador')) {
    return { kind: 'validation', label: 'Confirmar que la venta está subida a validación' };
  }
  if (label === PREPARACION_LABEL) {
    if (prepSheet && !prepSheetComplete(prepSheet) && PREP_SHEET_EDIT_ROLES.includes(role)) {
      return { kind: 'prepSheet', label: 'Completar la hoja de preparación' };
    }
    const to = target(l => l === LIMPIEZA_ENTREGA_LABEL);
    const taller = TALLER_MOVE_ROLES.includes(role) ? target(l => l === TALLER_LABEL) : null;
    if (to) return { kind: 'move', to, alt: taller, label: 'Pasar a Limpieza entrega' };
    if (taller) return { kind: 'move', to: taller, label: 'Pasar a Taller' };
    return null;
  }
  if (label === TALLER_LABEL) {
    const state = tallerTransferState(tallerTransfer);
    if (!tallerTransferAuthorized(state, role)) return null;
    const direct = TALLER_PRIVILEGED_ROLES.includes(role);
    return { kind: 'taller', movesColumn: direct, toLabel: columns.find(c => c.label?.trim().toLowerCase() === LIMPIEZA_ENTREGA_LABEL)?.label || 'Limpieza entrega',
      label: state === 'form' ? (direct ? 'Pasar a Limpieza entrega' : 'Solicitar el traslado a Limpieza entrega') : 'Autorizar el traslado a Limpieza entrega' };
  }
  if (label === LIMPIEZA_ENTREGA_LABEL) {
    const to = target(l => isDeliveryColumnLabel(l));
    if (to) return { kind: 'move', to, label: vehicle.delivery_scheduled_at ? 'Pasar a Entrega' : 'Fijar la fecha de entrega y pasar a Entrega' };
    return null;
  }
  if (isDeliveryColumnLabel(label) && canDeliver) {
    return { kind: 'deliver', label: 'Marcar como entregado al cliente' };
  }
  return null;
}

// Flujo de compra: desde que entra el coche (Revisión F2) hasta que llega a Stock. Cada paso se deduce del estado
// del coche, así que sirve también para los coches que ya existían. Los de fuera tienen sus propios tres pasos.
const LIMPIEZA_FOTOS_LABEL = 'limpieza fotos';
const PENDIENTE_FOTOS_LABEL = 'pendiente fotos';
const PENDIENTE_PUBLICAR_LABEL = 'pendiente publicar';
function purchaseFlowPositions(columns) {
  const posOf = (l) => columns.find(c => c.label?.trim().toLowerCase() === l)?.position;
  return { f2: posOf(REVISION_F2_LABEL), limpieza: posOf(LIMPIEZA_FOTOS_LABEL), fotos: posOf(PENDIENTE_FOTOS_LABEL),
    publicar: posOf(PENDIENTE_PUBLICAR_LABEL), stock: posOf(STOCK_LABEL) };
}
function inPurchaseFlowRange(vehicle, columns) {
  const pos = columns.find(c => c.id === vehicle.column_id)?.position;
  const p = purchaseFlowPositions(columns);
  return pos != null && p.stock != null && pos <= p.stock;
}
function purchaseFlowSteps({ vehicle, columns, f2Check }) {
  const pos = columns.find(c => c.id === vehicle.column_id)?.position ?? -1;
  const p = purchaseFlowPositions(columns);
  const past = (x) => x != null && pos > x;
  const atOrPast = (x) => x != null && pos >= x;
  const nextLabel = (fromPos) => columns.filter(c => c.position > fromPos).sort((a, b) => a.position - b.position)[0]?.label || 'la siguiente columna';
  const mover = (fromPos) => `quien tenga permiso para pasarlo a ${nextLabel(fromPos)}`;
  let steps;
  if (isFuera(vehicle)) {
    const state = fueraState(vehicle, f2Check);
    const order = ['taller', 'compra', 'transporte', 'recogido'];
    const at = order.indexOf(state);
    const store = vehicle.fuera_store || 'la tienda de origen';
    steps = [
      { key: 'taller', label: 'Revisado en taller y en la tienda', done: at > 0, who: fueraPendingWhoText(vehicle, 'taller') },
      { key: 'compra', label: `${store} avisada y compra confirmada`, done: at > 1, who: vehicle.purchased_by ? fueraPendingWhoText(vehicle, 'compra') : 'el responsable o el creador (falta asignar la compra a un captador)' },
      { key: 'recogido', label: `Recogido por ${store}`, done: at > 2, who: fueraPendingWhoText(vehicle, 'transporte') }
    ];
  } else {
    const buyer = assignedPurchaseLabel(vehicle);
    const leftF2 = past(p.f2);
    const noRecord = leftF2 && !f2Check?.step1_done; // pasó la revisión antes de que existiera el proceso, o se añadió sin F2
    const skippedText = vehicle.sin_f2 ? 'añadido sin Revisión F2' : 'sin registro';
    const step2Done = !!f2Check?.step2_by;
    steps = [
      { key: 'compra', label: buyer ? `Compra asignada: ${buyer}` : 'Compra asignada (quién compra el coche)',
        done: !!buyer || atOrPast(p.stock), who: 'el creador (🔁 junto a "Compra asignada a")' },
      { key: 'taller', label: f2Check?.step1_done ? `Revisado en taller · ${f2Check.step1_location || ''}` : 'Revisado en taller y dónde está (Tienda o Parque)',
        done: !!f2Check?.step1_done || (leftF2 && !noRecord), skipped: noRecord, skippedText, who: f2PendingWhoText(vehicle, 'step1') },
      { key: 'confirmacion', label: `Confirmación de ${vehicle.purchase_company ? 'Compras empresa' : (buyer || 'quien compra')}: F2 revisada, F1 y F2 en el CRM y descuento`,
        done: step2Done || (leftF2 && !noRecord), skipped: noRecord, skippedText, who: f2PendingWhoText(vehicle, 'step2') },
      { key: 'autorizacion', label: 'Autorización del responsable o del creador (solo si no se llega al 60 %)',
        done: !!f2Check?.step3_authorized, skipped: noRecord || (step2Done && f2Check.step2_discount_60 !== false) || (leftF2 && !f2Check?.step3_authorized),
        skippedText: noRecord ? skippedText : 'no hace falta: se llegó al 60 %', who: f2PendingWhoText(vehicle, 'step3') },
      ...(vehicle.sin_f2 ? [{ key: 'sinf2', label: 'Revisión confirmada y F1 y F2 en el CRM (añadido sin F2)', done: false,
        who: 'el encargado de preparación, el responsable o el creador' }] : []),
      { key: 'limpieza', label: 'Limpieza para fotos', done: past(p.limpieza) || atOrPast(p.stock), who: mover(p.limpieza ?? pos) },
      { key: 'fotos', label: 'Fotos hechas', done: past(p.fotos) || atOrPast(p.stock), who: mover(p.fotos ?? pos) },
      { key: 'publicar', label: 'Anuncio publicado y en Stock', done: atOrPast(p.stock), who: mover(p.publicar ?? pos) }
    ];
  }
  let currentFound = false;
  return steps.map(st => {
    let status = st.skipped ? 'skipped' : st.done ? 'done' : 'pending';
    if (status === 'pending' && !currentFound) {
      // Los pasos de fotos solo son el paso actual con el coche ya fuera de Revisión F2.
      const waitsForColumn = ['limpieza', 'fotos', 'publicar'].includes(st.key) && !past(p.f2);
      if (!waitsForColumn) { status = 'current'; currentFound = true; }
    }
    return { ...st, status };
  });
}
// Siguiente acción del flujo de compra que este usuario puede hacer (mismos permisos de siempre).
function purchaseAdvanceAction({ vehicle, columns, role, currentUserId, f2Check, moveTargetColumns }) {
  const label = columns.find(c => c.id === vehicle.column_id)?.label?.trim().toLowerCase();
  const target = (l) => (moveTargetColumns || []).find(c => c.label?.trim().toLowerCase() === l);
  if (isFuera(vehicle)) {
    const state = fueraState(vehicle, f2Check);
    if (state === 'recogido') return null;
    if (state === 'compra' && !vehicle.purchased_by) {
      return BUYER_TYPE_ROLES.includes(role) ? { kind: 'assignPurchase', label: 'Asignar la compra al captador' } : null;
    }
    if (!fueraAuthorized(state, vehicle, role, currentUserId)) return null;
    const labels = { taller: 'Confirmar que está revisado en taller y en la tienda', compra: `Confirmar la compra de ${vehicle.fuera_store || 'la tienda de origen'}`,
      transporte: 'Confirmar que la tienda de origen lo ha recogido' };
    return { kind: 'f2', label: labels[state] };
  }
  if (!assignedPurchaseLabel(vehicle) && role === 'creador' && label !== STOCK_LABEL) {
    return { kind: 'assignPurchase', label: 'Asignar quién compra el coche' };
  }
  if (label === REVISION_F2_LABEL) {
    const state = f2StepState(f2Check);
    if (state !== 'done' && f2StepAuthorized(state, vehicle, role, currentUserId)) {
      if (state === 'step1') return { kind: 'f2', label: 'Confirmar la revisión en taller' };
      if (state === 'step2') return { kind: 'f2', label: 'Confirmar F2, CRM y descuento' };
      return { kind: 'f2', label: 'Autorizar la compra' };
    }
    return null;
  }
  if (vehicle.sin_f2 && SIN_F2_CONFIRM_ROLES.includes(role)) {
    return { kind: 'sinF2', label: 'Confirmar la revisión (añadido sin F2)' };
  }
  const nextByCol = { [LIMPIEZA_FOTOS_LABEL]: PENDIENTE_FOTOS_LABEL, [PENDIENTE_FOTOS_LABEL]: PENDIENTE_PUBLICAR_LABEL, [PENDIENTE_PUBLICAR_LABEL]: STOCK_LABEL };
  const to = nextByCol[label] ? target(nextByCol[label]) : null;
  if (to) return { kind: 'move', to, label: `Pasar a ${to.label}` };
  return null;
}

// Confirmación del botón "Avanzar": si el paso cambia el coche de columna, se pregunta antes de continuar.
function SaleAdvanceConfirmModal({ vehicle, fromLabel, toLabel, note, alt, saving, onConfirm, onAlt, onClose, title, text, confirmText }) {
  return (
    <div style={S.overlay}>
      <div style={{...S.modal, maxWidth: '440px'}}>
        <button onClick={onClose} style={S.closeBtn}>✕</button>
        <h3 style={S.title}>▶️ {title || 'Avanzar en el flujo de venta'}</h3>
        <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem', margin: '8px 0 14px'}}>{vehicle.brand} {vehicle.model} ({vehicle.plate})</p>
        {toLabel ? (
          <>
            <div style={{display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', backgroundColor: 'var(--b-1f2937)',
              border: '1px solid var(--l-374151)', borderRadius: '8px', padding: '10px 12px', marginBottom: '12px', fontSize: '0.9rem'}}>
              <span style={{color: 'var(--t-cbd5e1)'}}>{fromLabel}</span>
              <span aria-hidden="true" style={{color: 'var(--t-f59e0b)'}}>→</span>
              <strong style={{color: 'var(--t-f1f5f9)'}}>{toLabel}</strong>
            </div>
            <p style={{color: 'var(--t-e5e7eb)', fontSize: '0.875rem', margin: '0 0 8px'}}>
              Este paso pasa el coche a la siguiente columna. ¿Confirmas y continúas a <strong>{toLabel}</strong>?
            </p>
          </>
        ) : (
          <p style={{color: 'var(--t-e5e7eb)', fontSize: '0.875rem', margin: '0 0 8px'}}>{text}</p>
        )}
        {note && <p style={{color: 'var(--t-9ca3af)', fontSize: '0.8rem', margin: '0 0 8px'}}>{note}</p>}
        <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: '16px'}}>
          {alt && (
            <button type="button" onClick={onAlt} disabled={saving} style={{...S.secondaryButton, marginRight: 'auto'}}>🔧 Necesita taller: a {alt.label}</button>
          )}
          <button type="button" onClick={onClose} disabled={saving} style={S.secondaryButton}>Cancelar</button>
          <button type="button" onClick={onConfirm} disabled={saving} style={{...S.button, opacity: saving ? 0.6 : 1}}>
            {saving ? 'Guardando...' : (confirmText || (toLabel ? `Sí, continuar a ${toLabel}` : 'Sí, confirmar'))}
          </button>
        </div>
      </div>
    </div>
  );
}

function SaleFlow({ steps, advance, onAdvance, onAdvanceAlt, flowName = 'venta', doneText = 'Sin pasos pendientes' }) {
  const [open, setOpen] = useState(false);
  const idx = steps.findIndex(s => s.status === 'current');
  const current = idx >= 0 ? steps[idx] : null;
  return (
    <div style={{border: '1px solid var(--l-374151)', borderRadius: '8px', padding: '10px 12px', marginBottom: '16px', backgroundColor: 'var(--b-1f2937)'}}>
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}
        style={{background: 'none', border: 'none', padding: 0, cursor: 'pointer', width: '100%', textAlign: 'left', color: 'var(--t-f1f5f9)'}}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px'}}>
          <span style={{fontSize: '0.8rem', color: 'var(--t-9ca3af)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600}}>🧭 Flujo de {flowName}</span>
          <span style={{fontSize: '0.75rem', color: 'var(--t-f59e0b)'}}>{open ? 'Ocultar ▴' : 'Ver flujo completo ▾'}</span>
        </div>
        {current ? (
          <div style={{marginTop: '6px'}}>
            <div style={{fontSize: '0.9rem', fontWeight: 600}}>⏳ Paso {idx + 1} de {steps.length}: {current.label}</div>
            <div style={{fontSize: '0.78rem', color: 'var(--t-fbbf24)', marginTop: '2px'}}>Pendiente {/^el /.test(current.who) ? `del ${current.who.slice(3)}` : `de ${current.who}`}.</div>
          </div>
        ) : (
          <div style={{marginTop: '6px', fontSize: '0.9rem', color: 'var(--t-86efac)'}}>✅ {doneText}</div>
        )}
      </button>
      {open && (
        <div style={{marginTop: '12px'}}>
          <ProcessSteps steps={steps} />
        </div>
      )}
      {advance && (
        <div style={{marginTop: '10px', paddingTop: '10px', borderTop: '1px solid var(--l-374151)'}}>
          <button type="button" onClick={onAdvance} style={{...S.button, width: '100%', boxSizing: 'border-box', textAlign: 'left',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px'}}>
            <span>
              <span style={{display: 'block', fontSize: '0.9rem'}}>▶️ Avanzar en el flujo de {flowName}</span>
              <span style={{display: 'block', fontSize: '0.75rem', fontWeight: 500, opacity: 0.85, marginTop: '2px'}}>Siguiente: {advance.label}</span>
            </span>
            <span aria-hidden="true">→</span>
          </button>
          {advance.alt && onAdvanceAlt && (
            <button type="button" onClick={onAdvanceAlt} style={{...S.secondaryButton, width: '100%', boxSizing: 'border-box', marginTop: '8px', fontSize: '0.8rem'}}>
              🔧 Necesita taller: pasar a {advance.alt.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function AssignSaleModal({ vehicle, orgId, mode, toCol, onClose, onAssigned }) {
  const [assignees, setAssignees] = useState([]);
  const [loadingAssignees, setLoadingAssignees] = useState(true);
  const [selected, setSelected] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  // Al reservar (Stock -> Reservados) quien hace el traslado rellena la ficha de operación de la venta.
  const [saleOp, setSaleOp] = useState(emptySaleOp());

  useEffect(() => {
    supabase
      .from('profiles')
      .select('id, full_name, role')
      .eq('org_id', orgId)
      .in('role', SALE_ROLES)
      .order('full_name')
      .then(({ data }) => {
        setAssignees(data || []);
        setLoadingAssignees(false);
      });
  }, [orgId]);

  const isCrossStore = selected === CROSS_STORE_VALUE;
  const selectedAssignee = assignees.find(a => a.id === selected);
  const selectedLabel = isCrossStore
    ? 'Cruzada (comercial de otra tienda, sin usuario)'
    : (selectedAssignee ? `${selectedAssignee.full_name || 'Sin nombre'} (${roleLabel(selectedAssignee.role)})` : '');

  // Al reservar siempre hay que rellenar la ficha (también en las Cruzadas): la base de datos no deja pasar a Reservados sin ella.
  const withOperation = mode === 'move' && !!selected;
  const opError = withOperation ? saleOpError(saleOp) : null;

  const confirm = async () => {
    if (!selected) return;
    if (opError) { setMessage(opError); return; }
    setSaving(true);
    setMessage('');
    // Reservar: asignar la venta y guardar la ficha en un solo paso (si algo falla, no se reserva).
    const { error } = withOperation
      ? await supabase.rpc('reserve_vehicle', { p_vehicle_id: vehicle.id, p_assigned_to: isCrossStore ? null : selected,
          p_move_to_column_id: toCol.id, ...saleOperationParams(saleOp) })
      : await supabase.rpc('assign_vehicle_sale', {
          p_vehicle_id: vehicle.id,
          p_assigned_to: isCrossStore ? null : selected,
          p_move_to_column_id: mode === 'move' ? toCol.id : null
        });
    if (error) {
      setMessage((withOperation ? 'No se ha reservado: ' : 'Error al asignar la venta: ') + error.message);
      setSaving(false);
      return;
    }
    onAssigned();
  };

  return (
    <div style={S.overlay}>
      <div style={{...S.modal, maxWidth: withOperation ? '520px' : '420px'}}>
        <button onClick={onClose} style={S.closeBtn}>✕</button>
        <h3 style={S.title}>{mode === 'move' ? 'Mover a Reservados' : 'Reasignar venta'}</h3>
        <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem', marginBottom: '16px'}}>
          {vehicle.brand} {vehicle.model} ({vehicle.plate})
        </p>
        <p style={{color: 'var(--t-e5e7eb)', fontSize: '0.875rem', marginBottom: '12px'}}>
          {mode === 'move'
            ? '¿Qué usuario ha reservado este vehículo?'
            : '¿A qué usuario quieres reasignar esta venta?'}
        </p>

        {message && (
          <div style={{backgroundColor: 'var(--b-7f1d1d)', color: 'var(--t-f1f5f9)', padding: '10px', borderRadius: '6px', marginBottom: '16px', fontSize: '0.875rem'}}>
            {message}
          </div>
        )}

        {loadingAssignees ? (
          <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem', marginBottom: '16px'}}>Cargando usuarios...</p>
        ) : (
          <select
            style={{...S.select, marginBottom: '12px'}}
            value={selected}
            onChange={e => setSelected(e.target.value)}
            disabled={saving}
          >
            <option value="">Selecciona un usuario...</option>
            {assignees.map(a => (
              <option key={a.id} value={a.id}>{a.full_name || 'Sin nombre'} ({roleLabel(a.role)})</option>
            ))}
            <option value={CROSS_STORE_VALUE}>Cruzada (comercial de otra tienda, sin usuario)</option>
          </select>
        )}

        {withOperation && (
          <div style={{borderTop: '1px solid var(--l-374151)', paddingTop: '12px', marginBottom: '12px'}}>
            <p style={{color: 'var(--t-f1f5f9)', fontSize: '0.95rem', fontWeight: 600, margin: '0 0 10px'}}>🧾 Ficha de operación</p>
            <SaleOperationForm value={saleOp} onChange={setSaleOp} disabled={saving} idPrefix="sale-op-new" />
          </div>
        )}

        {selected && (
          <div style={{backgroundColor: 'var(--b-374151)', padding: '10px', borderRadius: '6px', marginBottom: '12px'}}>
            {opError && <p style={{color: 'var(--t-fbbf24)', fontSize: '0.8rem', margin: '0 0 8px'}}>{opError}</p>}
            <p style={{color: 'var(--t-e5e7eb)', fontSize: '0.8rem', margin: '0 0 8px'}}>
              ¿Confirmas asignar la venta a "{selectedLabel}"?
            </p>
            <div style={{display: 'flex', gap: '8px', justifyContent: 'flex-end'}}>
              <button type="button" onClick={() => setSelected('')} disabled={saving} style={S.secondaryButton}>Cancelar</button>
              <button type="button" onClick={confirm} disabled={saving || !!opError} style={{...S.button, opacity: (saving || opError) ? 0.6 : 1}}>
                {saving ? 'Guardando...' : (withOperation ? 'Reservar' : 'Confirmar')}
              </button>
            </div>
          </div>
        )}

        <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px'}}>
          <button type="button" onClick={onClose} style={S.secondaryButton}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// mode: 'assign' (justo al crear el vehículo) | 'reassign' (solo creador, más adelante)
const COMPANY_PURCHASE_VALUE = '__company__';

function AssignPurchaseModal({ vehicle, orgId, mode, onClose, onAssigned }) {
  // Vehículo de fuera: la compra la gestiona el captador (se propone por defecto).
  const fuera = isFuera(vehicle);
  const [assignees, setAssignees] = useState([]);
  const [loadingAssignees, setLoadingAssignees] = useState(true);
  const [selected, setSelected] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    supabase
      .from('profiles')
      .select('id, full_name, role')
      .eq('org_id', orgId)
      .in('role', fuera ? ['captador'] : SALE_ROLES)
      .order('full_name')
      .then(({ data }) => {
        setAssignees(data || []);
        if (fuera && data && data.length > 0 && !vehicle.purchased_by) setSelected(data[0].id);
        setLoadingAssignees(false);
      });
  }, [orgId, fuera, vehicle.purchased_by]);

  const isCompany = selected === COMPANY_PURCHASE_VALUE;
  const selectedAssignee = assignees.find(a => a.id === selected);
  const selectedLabel = isCompany
    ? 'Compras empresa (vehículo de campa, sin comercial asignado)'
    : (selectedAssignee ? `${selectedAssignee.full_name || 'Sin nombre'} (${roleLabel(selectedAssignee.role)})` : '');

  const confirm = async () => {
    if (!selected) return;
    setSaving(true);
    setMessage('');
    const { error } = await supabase.rpc('assign_vehicle_purchase', {
      p_vehicle_id: vehicle.id,
      p_purchased_by: isCompany ? null : selected,
      p_company: isCompany
    });
    if (error) {
      setMessage('Error al asignar la compra: ' + error.message);
      setSaving(false);
    } else {
      onAssigned();
    }
  };

  return (
    <div style={S.overlay}>
      <div style={{...S.modal, maxWidth: '420px'}}>
        {mode === 'reassign' && <button onClick={onClose} style={S.closeBtn}>✕</button>}
        <h3 style={S.title}>{mode === 'assign' ? '¿Quién compra este vehículo?' : (vehicle.purchased_by || vehicle.purchase_company ? 'Reasignar compra' : 'Asignar compra')}</h3>
        <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem', marginBottom: '16px'}}>
          {vehicle.brand} {vehicle.model} ({vehicle.plate})
        </p>

        {message && (
          <div style={{backgroundColor: 'var(--b-7f1d1d)', color: 'var(--t-f1f5f9)', padding: '10px', borderRadius: '6px', marginBottom: '16px', fontSize: '0.875rem'}}>
            {message}
          </div>
        )}

        {loadingAssignees ? (
          <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem', marginBottom: '16px'}}>Cargando usuarios...</p>
        ) : (
          <select
            style={{...S.select, marginBottom: '12px'}}
            value={selected}
            onChange={e => setSelected(e.target.value)}
            disabled={saving}
          >
            <option value="">Selecciona un usuario...</option>
            {assignees.map(a => (
              <option key={a.id} value={a.id}>{a.full_name || 'Sin nombre'} ({roleLabel(a.role)})</option>
            ))}
            {!fuera && <option value={COMPANY_PURCHASE_VALUE}>🏢 Compras empresa (vehículo de campa, sin comercial asignado)</option>}
          </select>
        )}
        {fuera && !loadingAssignees && assignees.length === 0 && (
          <p style={{color: 'var(--t-fbbf24)', fontSize: '0.8rem', margin: '0 0 12px'}}>No hay ningún captador dado de alta: la compra de un vehículo de fuera se asigna a un captador.</p>
        )}
        {fuera && (
          <p style={{color: 'var(--t-9ca3af)', fontSize: '0.8rem', margin: '0 0 12px'}}>Vehículo de fuera: la compra la gestiona el captador.</p>
        )}

        {selected && (
          <div style={{backgroundColor: 'var(--b-374151)', padding: '10px', borderRadius: '6px', marginBottom: '12px'}}>
            <p style={{color: 'var(--t-e5e7eb)', fontSize: '0.8rem', margin: '0 0 8px'}}>
              ¿Confirmas asignar la compra a "{selectedLabel}"?
            </p>
            <div style={{display: 'flex', gap: '8px', justifyContent: 'flex-end'}}>
              <button type="button" onClick={() => setSelected('')} disabled={saving} style={S.secondaryButton}>Cancelar</button>
              <button type="button" onClick={confirm} disabled={saving} style={{...S.button, opacity: saving ? 0.7 : 1}}>
                {saving ? 'Guardando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        )}

        {mode === 'reassign' && (
          <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px'}}>
            <button type="button" onClick={onClose} style={S.secondaryButton}>Cerrar</button>
          </div>
        )}
      </div>
    </div>
  );
}

// Editor de lista libre (Mecánica / Pintura / Observaciones): el usuario añade líneas de texto una a una.
function ItemListEditor({ items, setItems, placeholder }) {
  const [draft, setDraft] = useState('');

  const addItem = () => {
    if (!draft.trim()) return;
    setItems([...items, draft.trim()]);
    setDraft('');
  };

  return (
    <div>
      <div style={{display: 'flex', gap: '8px', marginBottom: '8px'}}>
        <input
          style={{...S.input, marginBottom: 0}}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder={placeholder}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addItem(); } }}
        />
        <button type="button" onClick={addItem} disabled={!draft.trim()} style={S.secondaryButton}>Añadir</button>
      </div>
      {items.length > 0 && (
        <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
          {items.map((text, i) => (
            <div key={i} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'var(--b-334155)', padding: '6px 10px', borderRadius: '6px'}}>
              <span style={{color: 'var(--t-e5e7eb)', fontSize: '0.8rem'}}>{text}</span>
              <button type="button" onClick={() => setItems(items.filter((_, idx) => idx !== i))} style={{background: 'none', border: 'none', color: 'var(--t-9ca3af)', cursor: 'pointer'}}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Flujo al mover un vehículo de Reservados a Preparación: checklist de cobro + validación,
// seguido del formulario de la hoja de preparación con borrador y doble confirmación.
function PrepChecklistModal({ vehicle, onClose, onDone, confirmColumnMove, fromLabel, toLabel }) {
  const moveConfirm = useColumnMoveConfirm(confirmColumnMove);
  const [step, setStep] = useState('paid'); // paid -> validation -> form -> draft
  const [paidConfirmed, setPaidConfirmed] = useState(false);
  const [uploadedToValidation, setUploadedToValidation] = useState(null); // true | false
  const [itvNeeded, setItvNeeded] = useState(null); // true | false
  const [mecanica, setMecanica] = useState([]);
  const [pintura, setPintura] = useState([]);
  const [observaciones, setObservaciones] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const formValid = itvNeeded !== null;
  const gestionVenta = gvPending(vehicle);
  const [gvCloser, setGvCloser] = useState(null); // nombre de quien compró el coche (responsable del cierre)

  // Al llegar a Stock se quita la asignación de compra del coche y queda registrada en vehicle_purchases:
  // de ahí sale quién lo compró originalmente.
  useEffect(() => {
    if (!gestionVenta) return;
    let cancelled = false;
    (async () => {
      let buyerId = vehicle.purchased_by || null;
      let company = !!vehicle.purchase_company;
      if (!buyerId && !company) {
        const { data } = await supabase
          .from('vehicle_purchases')
          .select('purchased_by, is_company')
          .eq('vehicle_id', vehicle.id)
          .order('purchased_at', { ascending: false })
          .limit(1);
        buyerId = data?.[0]?.purchased_by || null;
        company = !!data?.[0]?.is_company;
      }
      let name = company ? 'Compras empresa' : null;
      if (buyerId) {
        const { data } = await supabase.from('profiles').select('full_name').eq('id', buyerId).maybeSingle();
        name = data?.full_name || null;
      }
      if (!cancelled) setGvCloser(name || 'No consta quién lo compró');
    })();
    return () => { cancelled = true; };
  }, [gestionVenta, vehicle.id, vehicle.purchased_by, vehicle.purchase_company]);

  const confirmPublish = async () => {
    setSaving(true);
    setMessage('');
    const { error } = await supabase.rpc('move_to_preparacion_with_sheet', {
      p_vehicle_id: vehicle.id,
      p_uploaded_to_validation: uploadedToValidation,
      p_itv_needed: itvNeeded,
      p_mecanica: mecanica.map(text => ({ text })),
      p_pintura: pintura.map(text => ({ text })),
      p_observaciones: observaciones.map(text => ({ text }))
    });
    if (error) {
      setMessage('Error al confirmar: ' + error.message);
      setSaving(false);
    } else {
      onDone();
    }
  };

  return (
    <div style={S.overlay}>
      <div style={{...S.modal, maxWidth: '520px'}}>
        <button onClick={onClose} style={S.closeBtn}>✕</button>
        <h3 style={S.title}>Pasar a Preparación</h3>
        <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem', marginBottom: '16px'}}>
          {vehicle.brand} {vehicle.model} ({vehicle.plate})
        </p>

        {message && (
          <div style={{backgroundColor: 'var(--b-7f1d1d)', color: 'var(--t-f1f5f9)', padding: '10px', borderRadius: '6px', marginBottom: '16px', fontSize: '0.875rem'}}>
            {message}
          </div>
        )}

        {moveConfirm.finalAction && (
          <ColumnMoveConfirm fromLabel={fromLabel} toLabel={toLabel} lead="Al publicar la hoja de preparación" saving={saving}
            onBack={moveConfirm.back} onConfirm={moveConfirm.finalAction} />
        )}

        {!moveConfirm.finalAction && step === 'paid' && (
          <>
            {gestionVenta && (
              <div style={{backgroundColor: 'var(--b-052e16)', border: '1px solid var(--l-166534)', borderRadius: '8px', padding: '12px 14px', marginBottom: '12px'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px'}}>
                  <GvBadge />
                  <span style={{color: 'var(--t-bbf7d0)', fontSize: '0.9rem', fontWeight: 700}}>Gestión de Venta</span>
                </div>
                <p style={{color: 'var(--t-e5e7eb)', fontSize: '0.85rem', margin: '0 0 6px'}}>
                  Este vehículo es una gestión de venta: hay que hacer el cierre de la gestión antes de la validación.
                </p>
                <p style={{color: 'var(--t-d1d5db)', fontSize: '0.85rem', margin: 0}}>
                  Responsable del cierre de gestión: <strong style={{color: 'var(--t-f1f5f9)'}}>{gvCloser || 'Cargando...'}</strong>
                </p>
                <p style={{color: 'var(--t-9ca3af)', fontSize: '0.75rem', margin: '6px 0 0'}}>
                  {gvCloser && gvCloser !== 'Compras empresa' && gvCloser !== 'No consta quién lo compró' ? 'Al pasar a Preparación se le avisará por correo. ' : ''}Cuando esté subido, se confirma en la ficha del coche.
                </p>
              </div>
            )}
            <div
              onClick={() => setPaidConfirmed(c => !c)}
              style={{display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: 'var(--b-334155)', padding: '14px', borderRadius: '8px', cursor: 'pointer', marginBottom: '20px'}}
            >
              <span style={{fontSize: '1.25rem'}}>{paidConfirmed ? '✅' : '⬜'}</span>
              <span style={{color: 'var(--t-e5e7eb)', fontSize: '0.9rem'}}>Confirmo que el vehículo está cobrado</span>
            </div>
            <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
              <button type="button" onClick={onClose} style={S.secondaryButton}>Cancelar</button>
              <button type="button" onClick={() => setStep('validation')} disabled={!paidConfirmed} style={{...S.button, opacity: paidConfirmed ? 1 : 0.5}}>
                Continuar
              </button>
            </div>
          </>
        )}

        {!moveConfirm.finalAction && step === 'validation' && (
          <>
            <p style={{color: 'var(--t-e5e7eb)', fontSize: '0.875rem', marginBottom: '12px'}}>¿El vehículo está subido a validar?</p>
            <div style={{display: 'flex', gap: '8px', marginBottom: '20px'}}>
              <button
                type="button"
                onClick={() => setUploadedToValidation(true)}
                style={{...S.secondaryButton, flex: 1, backgroundColor: uploadedToValidation === true ? 'var(--b-334155)' : 'transparent'}}
              >
                Sí
              </button>
              <button
                type="button"
                onClick={() => setUploadedToValidation(false)}
                style={{...S.secondaryButton, flex: 1, backgroundColor: uploadedToValidation === false ? 'var(--b-334155)' : 'transparent'}}
              >
                No
              </button>
            </div>
            <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
              <button type="button" onClick={() => setStep('paid')} style={S.secondaryButton}>Atrás</button>
              <button type="button" onClick={() => setStep('form')} disabled={uploadedToValidation === null} style={{...S.button, opacity: uploadedToValidation === null ? 0.5 : 1}}>
                Continuar
              </button>
            </div>
          </>
        )}

        {!moveConfirm.finalAction && step === 'form' && (
          <>
            <h4 style={{color: 'var(--t-f1f5f9)', fontSize: '0.9rem', marginBottom: '8px'}}>1. ITV</h4>
            <div style={{display: 'flex', gap: '8px', marginBottom: '16px'}}>
              <button type="button" onClick={() => setItvNeeded(true)} style={{...S.secondaryButton, flex: 1, backgroundColor: itvNeeded === true ? 'var(--b-334155)' : 'transparent'}}>Sí</button>
              <button type="button" onClick={() => setItvNeeded(false)} style={{...S.secondaryButton, flex: 1, backgroundColor: itvNeeded === false ? 'var(--b-334155)' : 'transparent'}}>No</button>
            </div>

            <h4 style={{color: 'var(--t-f1f5f9)', fontSize: '0.9rem', marginBottom: '8px'}}>2. Mecánica</h4>
            <div style={{marginBottom: '16px'}}>
              <ItemListEditor items={mecanica} setItems={setMecanica} placeholder="Ej: Neumáticos delanteros" />
            </div>

            <h4 style={{color: 'var(--t-f1f5f9)', fontSize: '0.9rem', marginBottom: '8px'}}>3. Pintura</h4>
            <div style={{marginBottom: '16px'}}>
              <ItemListEditor items={pintura} setItems={setPintura} placeholder="Ej: Puerta delantera izquierda" />
            </div>

            <h4 style={{color: 'var(--t-f1f5f9)', fontSize: '0.9rem', marginBottom: '8px'}}>4. Observaciones</h4>
            <div style={{marginBottom: '20px'}}>
              <ItemListEditor items={observaciones} setItems={setObservaciones} placeholder="Añadir observación" />
            </div>

            <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
              <button type="button" onClick={() => setStep('validation')} style={S.secondaryButton}>Atrás</button>
              <button type="button" onClick={() => setStep('draft')} disabled={!formValid} style={{...S.button, opacity: formValid ? 1 : 0.5}}>
                Revisar borrador
              </button>
            </div>
          </>
        )}

        {!moveConfirm.finalAction && step === 'draft' && (
          <>
            <div style={{backgroundColor: 'var(--b-111827)', borderRadius: '8px', padding: '14px', marginBottom: '16px'}}>
              <div style={{marginBottom: '10px'}}>
                <strong style={{color: 'var(--t-f1f5f9)', fontSize: '0.85rem'}}>ITV:</strong>{' '}
                <span style={{color: 'var(--t-e5e7eb)', fontSize: '0.85rem'}}>{itvNeeded ? 'Sí' : 'No'}</span>
              </div>
              <div style={{marginBottom: '10px'}}>
                <strong style={{color: 'var(--t-f1f5f9)', fontSize: '0.85rem'}}>Mecánica:</strong>
                {mecanica.length === 0 ? <div style={{color: 'var(--t-6b7280)', fontSize: '0.8rem'}}>— sin elementos —</div> : mecanica.map((t, i) => <div key={i} style={{color: 'var(--t-e5e7eb)', fontSize: '0.8rem'}}>• {t}</div>)}
              </div>
              <div style={{marginBottom: '10px'}}>
                <strong style={{color: 'var(--t-f1f5f9)', fontSize: '0.85rem'}}>Pintura:</strong>
                {pintura.length === 0 ? <div style={{color: 'var(--t-6b7280)', fontSize: '0.8rem'}}>— sin elementos —</div> : pintura.map((t, i) => <div key={i} style={{color: 'var(--t-e5e7eb)', fontSize: '0.8rem'}}>• {t}</div>)}
              </div>
              <div>
                <strong style={{color: 'var(--t-f1f5f9)', fontSize: '0.85rem'}}>Observaciones:</strong>
                {observaciones.length === 0 ? <div style={{color: 'var(--t-6b7280)', fontSize: '0.8rem'}}>— sin elementos —</div> : observaciones.map((t, i) => <div key={i} style={{color: 'var(--t-e5e7eb)', fontSize: '0.8rem'}}>• {t}</div>)}
              </div>
            </div>
            <p style={{color: 'var(--t-fecaca)', fontSize: '0.8rem', marginBottom: '16px'}}>
              En cuanto confirmes, la hoja de preparación quedará publicada y ya no podrás modificarla: solo el encargado de preparación o el creador podrán hacerlo a partir de ahora.
            </p>
            <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
              <button type="button" onClick={() => setStep('form')} disabled={saving} style={S.secondaryButton}>Atrás</button>
              <button type="button" onClick={() => moveConfirm.finish(confirmPublish)} disabled={saving} style={{...S.button, opacity: saving ? 0.7 : 1}}>
                {saving ? 'Publicando...' : 'Confirmar y publicar'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Fecha/hora de entrega al mover a "Entrega" (o, opcional, a "Limpieza entrega"):
// lunes-sábado, 10:00-20:00 en tramos de 30 min; avisa si quedan menos de 24h.
function ScheduleDeliveryModal({ vehicle, toCol, optional, onClose, onDone, onMoveWithoutDate, confirmColumnMove, fromLabel }) {
  const moveConfirm = useColumnMoveConfirm(confirmColumnMove);
  const prev = vehicle.delivery_scheduled_at ? new Date(vehicle.delivery_scheduled_at) : null;
  const pad = (n) => String(n).padStart(2, '0');
  const [date, setDate] = useState(prev ? `${prev.getFullYear()}-${pad(prev.getMonth() + 1)}-${pad(prev.getDate())}` : '');
  const [time, setTime] = useState(prev ? `${pad(prev.getHours())}:${pad(prev.getMinutes())}` : '');
  const [step, setStep] = useState('form'); // form -> warn24h
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const scheduledDate = date && time ? new Date(`${date}T${time}:00`) : null;

  const doSchedule = async () => {
    setSaving(true);
    setMessage('');
    const { error } = await supabase.rpc('schedule_delivery_and_move', {
      p_vehicle_id: vehicle.id,
      p_move_to_column_id: toCol.id,
      p_scheduled_at: scheduledDate.toISOString()
    });
    if (error) {
      setMessage('Error al programar la entrega: ' + error.message);
      setSaving(false);
    } else {
      onDone();
    }
  };

  const handleContinue = (e) => {
    e.preventDefault();
    setMessage('');
    if (!scheduledDate) return;
    if (scheduledDate.getDay() === 0) {
      setMessage('La entrega debe programarse de lunes a sábado, no domingo.');
      return;
    }
    const hoursUntil = (scheduledDate - new Date()) / (1000 * 60 * 60);
    if (hoursUntil < 24) {
      setStep('warn24h');
    } else {
      moveConfirm.finish(doSchedule);
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div style={S.overlay}>
      <div style={{...S.modal, maxWidth: '420px'}}>
        <button onClick={onClose} style={S.closeBtn}>✕</button>
        <h3 style={S.title}>{optional ? `Mover a ${toCol.label}` : 'Programar entrega'}</h3>
        <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem', marginBottom: optional ? '8px' : '16px'}}>
          {vehicle.brand} {vehicle.model} ({vehicle.plate})
        </p>
        {optional && (
          <p style={{color: 'var(--t-9ca3af)', fontSize: '0.8rem', marginBottom: '16px'}}>
            Puedes fijar ya la fecha de entrega (opcional). Si lo haces, no se volverá a pedir al mover el coche a Entrega.
          </p>
        )}

        {message && (
          <div style={{backgroundColor: 'var(--b-7f1d1d)', color: 'var(--t-f1f5f9)', padding: '10px', borderRadius: '6px', marginBottom: '16px', fontSize: '0.875rem'}}>
            {message}
          </div>
        )}

        {moveConfirm.finalAction ? (
          <ColumnMoveConfirm fromLabel={fromLabel} toLabel={toCol.label} saving={saving} onBack={moveConfirm.back}
            onConfirm={async () => { setSaving(true); await moveConfirm.finalAction(); setSaving(false); }} />
        ) : step === 'form' ? (
          <form onSubmit={handleContinue}>
            <label style={S.label}>Fecha de entrega (lunes a sábado)</label>
            <input type="date" style={S.input} value={date} min={today} onChange={e => setDate(e.target.value)} required />

            <label style={S.label}>Hora de entrega</label>
            <select style={S.select} value={time} onChange={e => setTime(e.target.value)} required>
              <option value="">Selecciona una hora...</option>
              {DELIVERY_TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>

            <div style={{display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px', flexWrap: 'wrap'}}>
              <button type="button" onClick={onClose} style={S.secondaryButton}>Cancelar</button>
              {onMoveWithoutDate && (
                <button type="button" onClick={() => moveConfirm.finish(onMoveWithoutDate)} style={S.secondaryButton} title={`Se mueve a ${toCol.label} sin fecha; se podrá fijar después`}>
                  Mover sin fecha
                </button>
              )}
              <button type="submit" disabled={!date || !time} style={{...S.button, opacity: (!date || !time) ? 0.5 : 1}}>Continuar</button>
            </div>
          </form>
        ) : (
          <>
            <p style={{color: 'var(--t-fecaca)', fontSize: '0.875rem', marginBottom: '16px'}}>
              ⚠️ El proceso habitual es avisar con una antelación mínima de 24 horas. Has programado la entrega para el{' '}
              <strong>{scheduledDate?.toLocaleDateString('es-ES', {weekday: 'long', day: 'numeric', month: 'long'})} a las {time}</strong>,
              con menos preaviso. Es posible que no se pueda entregar el vehículo a tiempo.
            </p>
            <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
              <button type="button" onClick={() => setStep('form')} disabled={saving} style={S.secondaryButton}>Volver</button>
              <button type="button" onClick={() => moveConfirm.finish(doSchedule)} disabled={saving} style={{...S.button, opacity: saving ? 0.7 : 1}}>
                {saving ? 'Guardando...' : 'Programar igualmente'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function mondayOf(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function sameLocalDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

// Calendario semanal (lunes a sábado) de entregas programadas, navegable semana a semana.
const DELIVERY_EDIT_ROLES = ['encargado_preparacion', 'creador', 'responsable'];
const CAL_WEEKDAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

function startOfDay(d) { const n = new Date(d); n.setHours(0, 0, 0, 0); return n; }
function addDays(d, k) { const n = new Date(d); n.setDate(n.getDate() + k); return n; }
function dateInputValue(d) {
  const pad = (x) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function timeValue(d) { return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; }

// Calendario de entregas programadas: vista de mes (número de entregas por día), semana o día.
// Encargado de preparación, responsable y creador pueden cambiar la fecha arrastrando la entrega
// a otro día (ordenador) o pinchándola y eligiendo fecha y hora.
function DeliveryCalendarModal({ vehicles, columns, role, onRefresh, onClose }) {
  const canEdit = DELIVERY_EDIT_ROLES.includes(role);
  const [view, setView] = useState('month'); // 'month' | 'week' | 'day'
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));
  const [selectedDay, setSelectedDay] = useState(() => startOfDay(new Date()));
  const [editing, setEditing] = useState(null); // { id, date, time }
  const [dragId, setDragId] = useState(null);
  const [dropDay, setDropDay] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const colLabel = (v) => columns.find(c => c.id === v.column_id)?.label || '';
  const isTransporte = (v) => colLabel(v).trim().toLowerCase() === TRANSPORTE_LABEL;
  const scheduled = vehicles
    .filter(v => v.delivery_scheduled_at)
    .sort((x, y) => new Date(x.delivery_scheduled_at) - new Date(y.delivery_scheduled_at));
  const onDay = (day) => scheduled.filter(v => sameLocalDay(new Date(v.delivery_scheduled_at), day));
  const today = startOfDay(new Date());

  const reschedule = async (vehicle, newDate) => {
    if (newDate.getDay() === 0) { setMessage('Las entregas son de lunes a sábado: no se puede mover a domingo.'); return; }
    const before = new Date(vehicle.delivery_scheduled_at);
    if (before.getTime() === newDate.getTime()) return;
    setSaving(true);
    setMessage('');
    const { error } = await supabase.rpc('reschedule_delivery', { p_vehicle_id: vehicle.id, p_scheduled_at: newDate.toISOString() });
    setSaving(false);
    if (error) { setMessage('Error al cambiar la fecha: ' + error.message); return; }
    setEditing(null);
    await onRefresh();
  };

  const dropOn = (day) => {
    const v = scheduled.find(x => x.id === dragId);
    setDragId(null);
    setDropDay(null);
    if (!v) return;
    const prev = new Date(v.delivery_scheduled_at);
    const target = new Date(day);
    target.setHours(prev.getHours(), prev.getMinutes(), 0, 0);
    if (!window.confirm(`¿Mover la entrega de ${v.brand} ${v.model} (${v.plate}) al ${target.toLocaleDateString('es-ES', {weekday: 'long', day: 'numeric', month: 'long'})} a las ${timeValue(target)}?`)) return;
    reschedule(v, target);
  };

  const dropProps = (day) => (canEdit ? {
    onDragOver: (e) => { if (dragId) { e.preventDefault(); setDropDay(day.getTime()); } },
    onDragLeave: () => setDropDay(null),
    onDrop: (e) => { e.preventDefault(); dropOn(day); }
  } : {});
  const dropHighlight = (day) => (dropDay === day.getTime() ? '2px dashed var(--l-f59e0b)' : null);

  // Navegación
  const shift = (dir) => {
    if (view === 'month') setAnchor(a => new Date(a.getFullYear(), a.getMonth() + dir, 1));
    else if (view === 'week') setAnchor(a => addDays(a, 7 * dir));
    else setAnchor(a => { let n = addDays(a, dir); if (n.getDay() === 0) n = addDays(n, dir); return n; });
  };
  const weekStart = mondayOf(anchor);
  const monthStart = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const gridStart = mondayOf(monthStart);
  const monthCells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const monthWeeks = [0, 1, 2, 3, 4, 5].map(w => monthCells.slice(w * 7, w * 7 + 7)).filter(week => week.some(d => d.getMonth() === anchor.getMonth()));
  const weekDays = Array.from({ length: 6 }, (_, i) => addDays(weekStart, i));

  const title = view === 'month'
    ? `${MONTH_NAMES_ES[anchor.getMonth()]} ${anchor.getFullYear()}`
    : view === 'week'
      ? `${weekDays[0].toLocaleDateString('es-ES', {day: 'numeric', month: 'short'})} – ${weekDays[5].toLocaleDateString('es-ES', {day: 'numeric', month: 'short', year: 'numeric'})}`
      : anchor.toLocaleDateString('es-ES', {weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'});

  const entry = (v, compact) => {
    const d = new Date(v.delivery_scheduled_at);
    const transport = isTransporte(v);
    const otherCol = !transport && !isDeliveryColumnLabel(colLabel(v)) ? colLabel(v) : null;
    const isEditing = editing?.id === v.id;
    return (
      <div key={v.id}
        draggable={canEdit && !isEditing}
        onDragStart={(e) => { setDragId(v.id); e.dataTransfer.effectAllowed = 'move'; }}
        onDragEnd={() => { setDragId(null); setDropDay(null); }}
        onClick={() => { if (canEdit && !isEditing) setEditing({ id: v.id, date: dateInputValue(d), time: timeValue(d) }); }}
        title={canEdit ? 'Arrastra a otro día o pincha para cambiar la fecha' : undefined}
        style={{backgroundColor: transport ? 'var(--b-1e3a8a)' : 'var(--b-334155)', borderRadius: '6px', padding: compact ? '4px 6px' : '8px 10px', cursor: canEdit ? 'grab' : 'default', opacity: dragId === v.id ? 0.4 : 1, borderLeft: `3px solid ${transport ? '#60a5fa' : 'var(--l-f59e0b)'}`}}>
        <div style={{display: 'flex', justifyContent: 'space-between', gap: '6px', alignItems: 'baseline', flexWrap: 'wrap'}}>
          <span style={{color: 'var(--t-f59e0b)', fontSize: '0.72rem', fontWeight: 700}}>{timeValue(d)}</span>
          {transport && <span style={{color: 'var(--t-bfdbfe)', fontSize: '0.65rem', fontWeight: 700}}>🚚 Transporte</span>}
          {otherCol && <span style={{color: 'var(--t-cbd5e1)', fontSize: '0.65rem'}}>{otherCol}</span>}
        </div>
        <div style={{color: 'var(--t-e5e7eb)', fontSize: compact ? '0.72rem' : '0.82rem', fontWeight: 600}}>{v.brand} {v.model}</div>
        <div style={{color: 'var(--t-9ca3af)', fontSize: '0.7rem'}}>
          {v.plate}{!compact && v.assigned_profile?.full_name ? ` · 👤 ${v.assigned_profile.full_name}` : ''}{!compact && v.location ? ` · 📍 ${v.location}` : ''}
        </div>
        {isEditing && (
          <div onClick={e => e.stopPropagation()} style={{marginTop: '6px', display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center'}}>
            <input id={`cal-date-${v.id}`} type="date" value={editing.date} onChange={e => setEditing(x => ({...x, date: e.target.value}))}
              style={{...S.input, marginBottom: 0, padding: '4px 6px', fontSize: '0.75rem', width: 'auto'}} />
            <select id={`cal-time-${v.id}`} value={editing.time} onChange={e => setEditing(x => ({...x, time: e.target.value}))}
              style={{...S.select, marginBottom: 0, padding: '4px 6px', fontSize: '0.75rem', width: 'auto'}}>
              {DELIVERY_TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <button type="button" disabled={saving} onClick={() => reschedule(v, new Date(`${editing.date}T${editing.time}:00`))}
              style={{...S.button, padding: '4px 10px', fontSize: '0.75rem'}}>{saving ? '...' : 'Guardar'}</button>
            <button type="button" onClick={() => setEditing(null)} style={{...S.secondaryButton, padding: '4px 8px', fontSize: '0.75rem'}}>✕</button>
          </div>
        )}
      </div>
    );
  };

  const dayList = (day) => {
    const list = onDay(day);
    return list.length === 0
      ? <p style={{color: 'var(--t-6b7280)', fontSize: '0.8rem', fontStyle: 'italic', margin: 0}}>Sin entregas este día.</p>
      : <div style={{display: 'flex', flexDirection: 'column', gap: '6px'}}>{list.map(v => entry(v, false))}</div>;
  };

  const printView = () => {
    const esc = escapeHtml;
    const item = (v) => {
      const d = new Date(v.delivery_scheduled_at);
      return `<tr><td>${timeValue(d)}</td><td>${esc(`${v.brand || ''} ${v.model || ''}`)}</td><td>${esc(v.plate)}</td><td>${esc(v.assigned_profile?.full_name || '—')}</td><td>${esc(v.location || '—')}</td><td>${isTransporte(v) ? 'Transporte' : esc(colLabel(v))}</td></tr>`;
    };
    const dayTable = (day) => {
      const list = onDay(day);
      if (!list.length) return '';
      return `<h3>${esc(day.toLocaleDateString('es-ES', {weekday: 'long', day: 'numeric', month: 'long'}))} · ${list.length}</h3>
        <table><thead><tr><th>Hora</th><th>Vehículo</th><th>Matrícula</th><th>Comercial</th><th>Ubicación</th><th>Columna</th></tr></thead><tbody>${list.map(item).join('')}</tbody></table>`;
    };
    let body = '';
    if (view === 'month') {
      const grid = monthWeeks.map(week => `<tr>${week.map(d => {
        const n = onDay(d).length;
        const out = d.getMonth() !== anchor.getMonth();
        return `<td class="${out ? 'out' : ''}"><div class="dn">${d.getDate()}</div>${n ? `<div class="cnt">${n} ${n === 1 ? 'entrega' : 'entregas'}</div>` : ''}</td>`;
      }).join('')}</tr>`).join('');
      const days = monthCells.filter(d => d.getMonth() === anchor.getMonth()).map(dayTable).join('');
      body = `<table class="grid"><thead><tr>${CAL_WEEKDAYS.map(w => `<th>${w}</th>`).join('')}</tr></thead><tbody>${grid}</tbody></table>${days || '<p>Sin entregas este mes.</p>'}`;
    } else if (view === 'week') {
      body = weekDays.map(dayTable).join('') || '<p>Sin entregas esta semana.</p>';
    } else {
      body = dayTable(anchor) || '<p>Sin entregas este día.</p>';
    }
    const html = `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>Entregas - ${esc(title)}</title>
<style>
  body { font-family: Arial, sans-serif; color: #111; margin: 24px; font-size: 12px; }
  h1 { font-size: 18px; margin: 0 0 4px; text-transform: capitalize; } h3 { font-size: 13px; margin: 16px 0 4px; text-transform: capitalize; }
  table { width: 100%; border-collapse: collapse; } th, td { border-bottom: 1px solid #ddd; padding: 4px 6px; text-align: left; }
  th { background: #f3f4f6; }
  .grid td { border: 1px solid #ddd; height: 48px; vertical-align: top; width: 14.28%; } .grid .out { color: #bbb; }
  .dn { font-weight: bold; } .cnt { margin-top: 4px; background: #fef3c7; border-radius: 4px; padding: 2px 4px; display: inline-block; }
  @page { margin: 12mm; }
</style></head><body>
<h1>Entregas · ${esc(title)}</h1>
<p style="color:#555;margin:0 0 12px">Generado el ${new Date().toLocaleDateString('es-ES')}</p>
${body}
</body></html>`;
    const w = window.open('', '_blank');
    if (!w) { window.alert('El navegador ha bloqueado la ventana. Permite las ventanas emergentes para imprimir.'); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
    printWhenReady(w);
  };

  const tab = (key, label) => (
    <button key={key} type="button" onClick={() => { setView(key); setEditing(null); if (key === 'day') setAnchor(selectedDay); }}
      style={{...S.secondaryButton, padding: '4px 12px', backgroundColor: view === key ? 'var(--b-f59e0b)' : 'transparent', color: view === key ? '#111827' : S.secondaryButton.color, fontWeight: view === key ? 700 : 400}}>
      {label}
    </button>
  );

  return (
    <div style={S.overlay}>
      <div style={{...S.modal, maxWidth: '980px'}}>
        <button onClick={onClose} style={S.closeBtn}>✕</button>
        <h3 style={S.title}>📅 Calendario de entregas</h3>

        <div style={{display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px'}}>
          <div style={{display: 'flex', gap: '4px'}}>{tab('month', 'Mes')}{tab('week', 'Semana')}{tab('day', 'Día')}</div>
          <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
            <button type="button" onClick={() => shift(-1)} style={{...S.secondaryButton, padding: '4px 10px'}}>‹</button>
            <span style={{color: 'var(--t-f1f5f9)', fontWeight: 600, minWidth: '170px', textAlign: 'center', textTransform: 'capitalize'}}>{title}</span>
            <button type="button" onClick={() => shift(1)} style={{...S.secondaryButton, padding: '4px 10px'}}>›</button>
            <button type="button" onClick={() => { setAnchor(today); setSelectedDay(today); }} style={{...S.secondaryButton, padding: '4px 10px', fontSize: '0.8rem'}}>Hoy</button>
          </div>
          <button type="button" onClick={printView} style={{...S.secondaryButton, padding: '4px 12px', marginLeft: 'auto'}}>🖨️ Imprimir</button>
        </div>

        {canEdit ? (
          <p style={{color: 'var(--t-9ca3af)', fontSize: '0.75rem', margin: '0 0 10px'}}>
            Para cambiar una entrega de día: arrástrala a otro día (en ordenador) o pínchala y elige fecha y hora.
          </p>
        ) : (
          <p style={{color: 'var(--t-9ca3af)', fontSize: '0.75rem', margin: '0 0 10px'}}>
            Solo el encargado de preparación, el responsable o el creador pueden cambiar la fecha de una entrega.
          </p>
        )}

        {message && (
          <div style={{backgroundColor: 'var(--b-7f1d1d)', color: 'var(--t-f1f5f9)', padding: '8px 10px', borderRadius: '6px', marginBottom: '10px', fontSize: '0.8rem'}}>{message}</div>
        )}

        {view === 'month' && (
          <>
            <div style={{overflowX: 'auto'}}>
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(7, minmax(70px, 1fr))', gap: '4px', minWidth: '520px'}}>
                {CAL_WEEKDAYS.map(w => <div key={w} style={{color: 'var(--t-9ca3af)', fontSize: '0.7rem', fontWeight: 700, textAlign: 'center'}}>{w}</div>)}
                {monthWeeks.flat().map(d => {
                  const list = onDay(d);
                  const out = d.getMonth() !== anchor.getMonth();
                  const sunday = d.getDay() === 0;
                  const isSel = sameLocalDay(d, selectedDay);
                  const isToday = sameLocalDay(d, today);
                  const transports = list.filter(isTransporte).length;
                  return (
                    <div key={d.toISOString()} {...dropProps(d)}
                      onClick={() => setSelectedDay(d)}
                      style={{backgroundColor: sunday ? 'var(--b-0b1220)' : 'var(--b-111827)', borderRadius: '6px', padding: '6px', minHeight: '58px', cursor: 'pointer',
                        opacity: out ? 0.45 : 1, outline: dropHighlight(d) || (isSel ? '2px solid var(--l-f59e0b)' : 'none'), border: isToday ? '1px solid var(--l-f59e0b)' : '1px solid transparent'}}>
                      <div style={{color: isToday ? 'var(--t-f59e0b)' : 'var(--t-cbd5e1)', fontSize: '0.75rem', fontWeight: 700}}>{d.getDate()}</div>
                      {list.length > 0 && (
                        <div style={{marginTop: '4px', backgroundColor: 'var(--b-78350f)', color: 'var(--t-fde68a)', borderRadius: '10px', padding: '1px 6px', fontSize: '0.68rem', fontWeight: 700, display: 'inline-block'}}>
                          {list.length} {list.length === 1 ? 'entrega' : 'entregas'}
                        </div>
                      )}
                      {transports > 0 && <div style={{color: 'var(--t-93c5fd)', fontSize: '0.62rem', marginTop: '2px'}}>🚚 {transports}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
            <div style={{marginTop: '14px'}}>
              <p style={{color: 'var(--t-f1f5f9)', fontSize: '0.85rem', fontWeight: 700, margin: '0 0 8px', textTransform: 'capitalize'}}>
                {selectedDay.toLocaleDateString('es-ES', {weekday: 'long', day: 'numeric', month: 'long'})} · {onDay(selectedDay).length} {onDay(selectedDay).length === 1 ? 'entrega' : 'entregas'}
              </p>
              {dayList(selectedDay)}
            </div>
          </>
        )}

        {view === 'week' && (
          <div style={{overflowX: 'auto'}}>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(6, minmax(140px, 1fr))', gap: '8px', minWidth: '860px'}}>
              {weekDays.map(day => {
                const isToday = sameLocalDay(day, today);
                const list = onDay(day);
                return (
                  <div key={day.toISOString()} {...dropProps(day)}
                    style={{backgroundColor: 'var(--b-111827)', borderRadius: '8px', padding: '8px', minHeight: '160px', border: isToday ? '1px solid var(--l-f59e0b)' : '1px solid transparent', outline: dropHighlight(day) || 'none'}}>
                    <div onClick={() => { setSelectedDay(day); setAnchor(day); setView('day'); }}
                      style={{color: isToday ? 'var(--t-f59e0b)' : 'var(--t-9ca3af)', fontSize: '0.72rem', fontWeight: 700, textTransform: 'capitalize', marginBottom: '6px', cursor: 'pointer'}}>
                      {WEEKDAY_NAMES_ES[day.getDay()]} {day.getDate()} · {list.length}
                    </div>
                    <div style={{display: 'flex', flexDirection: 'column', gap: '6px'}}>
                      {list.length === 0 ? <div style={{color: '#4b5563', fontSize: '0.7rem', fontStyle: 'italic'}}>Sin entregas</div> : list.map(v => entry(v, true))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {view === 'day' && (
          <div {...dropProps(anchor)} style={{outline: dropHighlight(anchor) || 'none', borderRadius: '8px'}}>
            {anchor.getDay() === 0 && <p style={{color: 'var(--t-fca5a5)', fontSize: '0.8rem'}}>Domingo: no hay entregas.</p>}
            {dayList(anchor)}
          </div>
        )}

        <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: '16px'}}>
          <button onClick={onClose} style={S.secondaryButton}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

function AddVehicleModal({ columns, stockPosition, orgId, currentUserId, onClose, onSave }) {
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [color, setColor] = useState('');
  const [plate, setPlate] = useState('');
  const [location, setLocation] = useState('');
  const [buyerType, setBuyerType] = useState('tienda');
  const [fueraStore, setFueraStore] = useState('');
  const knownStores = useKnownFueraStores(buyerType === 'fuera');
  const [columnId, setColumnId] = useState(columns[0]?.id || '');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [createdVehicle, setCreatedVehicle] = useState(null);

  // Si cambia la columna elegida, las opciones de ubicación disponibles pueden cambiar
  // (p. ej. al elegir Taller), así que se reinicia para evitar un valor ya no válido.
  useEffect(() => { setLocation(''); }, [columnId]);
  const selectedColumnLabel = columns.find(c => c.id === columnId)?.label;
  const addBuyerTypeOptions = buyerTypeOptionsFor(selectedColumnLabel);
  // Al cambiar de columna, "Vehículo de fuera" deja de ser válido fuera de Revisión F2.
  if (!addBuyerTypeOptions.includes(buyerType)) setBuyerType('tienda');

  const finish = () => { onSave(); onClose(); };

  const save = async (e) => {
    e.preventDefault();
    if (buyerType === 'fuera' && fueraStoreError(fueraStore)) { setMessage(fueraStoreError(fueraStore)); return; }
    setLoading(true);
    setMessage('');

    const { data, error } = await supabase.from('vehicles').insert([{
      org_id: orgId,
      brand: brand.trim(),
      model: model.trim(),
      year: year ? Number(year) : null,
      color: color.trim() || null,
      plate: plate.trim(),
      location,
      buyer_type: buyerType,
      fuera_store: buyerType === 'fuera' ? fueraStore.trim() : null,
      column_id: columnId
    }]).select('id, brand, model, plate, buyer_type, purchased_by, fuera_store').single();

    if (error) {
      setMessage('Error al crear vehículo: ' + error.message);
      setLoading(false);
      return;
    }

    await supabase.from('vehicle_location_history').insert([{
      vehicle_id: data.id,
      location,
      changed_by: currentUserId
    }]);

    const selectedCol = columns.find(c => c.id === columnId);
    const requiresPurchase = selectedCol && stockPosition != null && selectedCol.position < stockPosition;
    if (requiresPurchase) {
      setCreatedVehicle(data);
      setLoading(false);
    } else {
      finish();
    }
  };

  if (createdVehicle) {
    return (
      <AssignPurchaseModal
        vehicle={createdVehicle}
        orgId={orgId}
        mode="assign"
        onAssigned={finish}
        onClose={finish}
      />
    );
  }

  return (
    <div style={S.overlay}>
      <div style={S.modal}>
        <button onClick={onClose} style={S.closeBtn}>✕</button>
        <h3 style={S.title}>Añadir Vehículo</h3>

        {message && (
          <div style={{backgroundColor: 'var(--b-7f1d1d)', color: 'var(--t-f1f5f9)', padding: '10px', borderRadius: '6px', marginBottom: '16px', fontSize: '0.875rem'}}>
            {message}
          </div>
        )}

        {columns.length === 0 ? (
          <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem'}}>No hay ninguna columna disponible para añadir vehículos con tu rol.</p>
        ) : (
        <form onSubmit={save}>
          <label style={S.label}>Marca</label>
          <input style={S.input} value={brand} onChange={e => setBrand(e.target.value)} required />

          <label style={S.label}>Modelo</label>
          <input style={S.input} value={model} onChange={e => setModel(e.target.value)} required />

          <div style={{display: 'flex', gap: '12px'}}>
            <div style={{flex: 1}}>
              <label style={S.label}>Año</label>
              <input style={S.input} type="number" value={year} onChange={e => setYear(e.target.value)} />
            </div>
            <div style={{flex: 1}}>
              <label style={S.label}>Color</label>
              <input style={S.input} value={color} onChange={e => setColor(e.target.value)} />
            </div>
          </div>

          <label style={S.label}>Matrícula</label>
          <input style={S.input} value={plate} onChange={e => setPlate(e.target.value)} required />

          <label style={S.label}>📍 Ubicación</label>
          <select style={S.select} value={location} onChange={e => setLocation(e.target.value)} required>
            <option value="" disabled>Selecciona una ubicación...</option>
            {locationOptionsForColumnLabel(columns.find(c => c.id === columnId)?.label).map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>

          <label style={S.label}>Columna</label>
          <select style={S.select} value={columnId} onChange={e => setColumnId(e.target.value)} required>
            {columns.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>

          <label style={S.label}>🏷️ Tipo de compra</label>
          <select style={S.select} value={buyerType} onChange={e => setBuyerType(e.target.value)} required>
            {addBuyerTypeOptions.map(t => <option key={t} value={t}>{BUYER_TYPE_LABELS[t]}</option>)}
          </select>
          {buyerType === 'fuera' && (
            <>
              <p style={{color: 'var(--t-9ca3af)', fontSize: '0.8rem', margin: '-4px 0 12px'}}>
                Coche de otra tienda Flexicar que viene solo a la revisión F2: se queda en esta columna, no pasa a nuestro stock y no cuenta en objetivos. La compra la gestiona el captador.
              </p>
              <label style={S.label} htmlFor="add-fuera-store">🏬 Tienda de origen</label>
              <FueraStoreInput id="add-fuera-store" value={fueraStore} onChange={setFueraStore} stores={knownStores} />
              <p style={{color: 'var(--t-9ca3af)', fontSize: '0.75rem', margin: '-6px 0 12px'}}>A esta tienda se le factura la revisión.</p>
            </>
          )}

          <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px'}}>
            <button type="button" onClick={onClose} style={S.secondaryButton}>Cancelar</button>
            <button type="submit" disabled={loading} style={{...S.button, opacity: loading ? 0.7 : 1}}>
              {loading ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}

// Ruta dentro del bucket de adjuntos a partir de la URL guardada (solo de nuestro propio Storage).
function attachmentPath(url) {
  const marker = '/storage/v1/object/public/comment-attachments/';
  if (typeof url !== 'string' || !url.startsWith('https://dabyiebqjiporyuxqlbf.supabase.co' + marker)) return null;
  try { return decodeURIComponent(url.slice(('https://dabyiebqjiporyuxqlbf.supabase.co' + marker).length).split('?')[0]); }
  catch (e) { return null; }
}

function VehicleDetailPanel({ vehicle, columns, role, currentUserId, currentUserName, linkedLeads = [], onOpenLeads, prepSheet, f2Check, tallerTransfer, moveTargetColumns, onRequestMove, onClose, onRefresh, onDeleted }) {
  const [editing, setEditing] = useState(false);
  const [confirmingSave, setConfirmingSave] = useState(false);
  const [brand, setBrand] = useState(vehicle.brand || '');
  const [model, setModel] = useState(vehicle.model || '');
  const [year, setYear] = useState(vehicle.year || '');
  const [color, setColor] = useState(vehicle.color || '');
  const [plate, setPlate] = useState(vehicle.plate || '');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);

  const [comments, setComments] = useState([]);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [sinF2Step, setSinF2Step] = useState(0); // 0 = cerrado, 1 = casillas, 2 = confirmación final
  const [gvConfirmOpen, setGvConfirmOpen] = useState(false);
  const [savingGv, setSavingGv] = useState(false);
  const [gvNames, setGvNames] = useState({});
  const [sinF2Reviewed, setSinF2Reviewed] = useState(false);
  const [sinF2Uploaded, setSinF2Uploaded] = useState(false);
  const [savingSinF2, setSavingSinF2] = useState(false);
  const [loadingComments, setLoadingComments] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [postingComment, setPostingComment] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [subLoading, setSubLoading] = useState(false);
  const [showDeliverModal, setShowDeliverModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [showReassignPurchaseModal, setShowReassignPurchaseModal] = useState(false);
  const [location, setLocation] = useState(vehicle.location || '');
  const [savingLocation, setSavingLocation] = useState(false);
  const [pendingComment, setPendingComment] = useState(false);
  const [savingValidation, setSavingValidation] = useState(false);
  const [showPrepSheet, setShowPrepSheet] = useState(false);
  const [showF2Process, setShowF2Process] = useState(false);
  const [showTallerGate, setShowTallerGate] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [pendingBuyerType, setPendingBuyerType] = useState(null);
  const [savingBuyerType, setSavingBuyerType] = useState(false);
  const [buyerTypeStore, setBuyerTypeStore] = useState(''); // tienda de origen al cambiar a "Vehículo de fuera"
  const [storeDraft, setStoreDraft] = useState(null); // null = sin editar la tienda de origen
  const [savingStore, setSavingStore] = useState(false);
  const knownFueraStores = useKnownFueraStores(pendingBuyerType === 'fuera' || storeDraft !== null);
  const [showProfDelivery, setShowProfDelivery] = useState(false);
  const [showCancelPurchase, setShowCancelPurchase] = useState(false);
  const [saleOp, setSaleOp] = useState(null);
  const [savingSaleValidation, setSavingSaleValidation] = useState(false);
  const [saleValidationDate, setSaleValidationDate] = useState('');
  const canValidateSale = ADMIN_TIER_ROLES.includes(role);

  const loadSaleOp = async () => {
    if (!canValidateSale) return;
    const { data } = await supabase.from('commission_operations')
      .select('id, validated_at, delivered_at, profile_id').eq('vehicle_id', vehicle.id).is('cancelled_at', null)
      .order('created_at', { ascending: false }).limit(1).maybeSingle();
    setSaleOp(data || null);
    if (data?.validated_at) {
      const d = new Date(data.validated_at);
      setSaleValidationDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    } else {
      setSaleValidationDate('');
    }
  };

  const saveSaleValidationDate = async () => {
    if (!saleOp || !saleValidationDate) return;
    if (!window.confirm(`¿Cambiar la fecha de validación al ${new Date(`${saleValidationDate}T12:00:00`).toLocaleDateString('es-ES')}?`)) return;
    setSavingSaleValidation(true);
    setMessage('');
    const { error } = await supabase.rpc('set_commission_validated_at', {
      p_id: saleOp.id, p_validated_at: new Date(`${saleValidationDate}T12:00:00`).toISOString()
    });
    setSavingSaleValidation(false);
    if (error) { setMessage('Error al cambiar la fecha de validación: ' + error.message); return; }
    loadSaleOp();
  };

  useEffect(() => { loadSaleOp(); }, [vehicle.id, vehicle.assigned_to]);

  const toggleSaleValidated = async () => {
    if (!saleOp) return;
    const validating = !saleOp.validated_at;
    if (!window.confirm(validating ? '¿Marcar este coche como validado hoy?' : '¿Quitar la validación de este coche?')) return;
    setSavingSaleValidation(true);
    setMessage('');
    const { error } = await supabase.rpc('set_commission_validated', { p_id: saleOp.id, p_validated: validating });
    setSavingSaleValidation(false);
    if (error) { setMessage('Error al cambiar la validación: ' + error.message); return; }
    loadSaleOp();
  };

  // Nombres del responsable del cierre de gestión y de quien lo confirmó.
  useEffect(() => {
    const ids = [vehicle.gv_closer_id, vehicle.gv_closed_by].filter(Boolean);
    if (!isGestionVenta(vehicle) || ids.length === 0) return;
    let cancelled = false;
    supabase.from('profiles').select('id, full_name').in('id', ids).then(({ data }) => {
      if (!cancelled) setGvNames(Object.fromEntries((data || []).map(p => [p.id, p.full_name])));
    });
    return () => { cancelled = true; };
  }, [vehicle.id, vehicle.buyer_type, vehicle.gv_closer_id, vehicle.gv_closed_by]);

  const confirmGvClosure = async () => {
    setSavingGv(true);
    setMessage('');
    const { error } = await supabase.rpc('confirm_gestion_venta_closure', { p_vehicle_id: vehicle.id });
    setSavingGv(false);
    if (error) { setMessage('Error: ' + error.message); return; }
    setGvConfirmOpen(false);
    loadComments();
    onRefresh();
  };

  const confirmSinF2 = async () => {
    setSavingSinF2(true);
    setMessage('');
    const { error } = await supabase.rpc('confirm_vehicle_f2_reviewed', {
      p_vehicle_id: vehicle.id, p_reviewed: sinF2Reviewed, p_uploaded_f1_f2: sinF2Uploaded
    });
    setSavingSinF2(false);
    if (error) { setMessage('Error: ' + error.message); return; }
    setSinF2Step(0);
    loadComments();
    onRefresh();
  };

  const confirmValidationUploaded = async (value) => {
    setSavingValidation(true);
    const { error } = await supabase.rpc('set_uploaded_to_validation', { p_vehicle_id: vehicle.id, p_value: value });
    if (!error) onRefresh();
    setSavingValidation(false);
  };

  const canEdit = ADMIN_TIER_ROLES.includes(role);
  const canDelete = role === 'creador';
  // Venta: flujo desde Reservados y botón 🧾 Operación hasta antes de Limpieza entrega.
  const colPos = (pred) => columns.find(c => pred(c.label?.trim().toLowerCase()))?.position;
  const vehicleColPos = columns.find(c => c.id === vehicle.column_id)?.position ?? -1;
  const reservadosPos = colPos(l => l === RESERVED_LABEL);
  const limpiezaEntregaPos = colPos(l => l === LIMPIEZA_ENTREGA_LABEL);
  const inSaleFlow = reservadosPos != null && vehicleColPos >= reservadosPos && !!vehicle.assigned_at;
  const canSeeOperation = inSaleFlow && (limpiezaEntregaPos == null || vehicleColPos < limpiezaEntregaPos)
    && (SALE_OP_ROLES.includes(role) || (!!vehicle.assigned_to && vehicle.assigned_to === currentUserId));
  const [showOperation, setShowOperation] = useState(false);
  const [advanceConfirm, setAdvanceConfirm] = useState(null); // acción del botón "Avanzar" pendiente de confirmar
  const [savingAdvance, setSavingAdvance] = useState(false);
  const [saleOpFilled, setSaleOpFilled] = useState(null); // null = no se sabe (sin permiso para verla)
  const loadSaleOpFilled = async () => {
    if (!(SALE_OP_ROLES.includes(role) || (!!vehicle.assigned_to && vehicle.assigned_to === currentUserId))) { setSaleOpFilled(null); return; }
    const { data } = await supabase.from('sale_operations').select('id').eq('vehicle_id', vehicle.id).is('cancelled_at', null).maybeSingle();
    setSaleOpFilled(!!data);
  };
  useEffect(() => { if (inSaleFlow) loadSaleOpFilled(); }, [vehicle.id, inSaleFlow, vehicle.column_id]);
  const isInRevisionF2 = columns.find(c => c.id === vehicle.column_id)?.label?.trim().toLowerCase() === REVISION_F2_LABEL;
  const isInTaller = columns.find(c => c.id === vehicle.column_id)?.label?.trim().toLowerCase() === TALLER_LABEL;
  const locationOptions = isInTaller ? TALLER_LOCATION_OPTIONS : LOCATION_OPTIONS;
  const currentColLabel = columns.find(c => c.id === vehicle.column_id)?.label?.trim().toLowerCase();
  const buyerType = vehicle.buyer_type || 'tienda';
  const isBuyerTypeManager = BUYER_TYPE_ROLES.includes(role);
  const canChangeBuyerType = isBuyerTypeManager && BUYER_TYPE_CHANGE_COLUMN_LABELS.includes(currentColLabel);
  // El tipo de compra (Tienda/Profesionales) solo se muestra antes de Stock: en Stock ya se ve por la vista
  // Tienda/Profesionales de la columna y después no aporta nada.
  const stockColPosition = columns.find(c => c.label?.trim().toLowerCase() === STOCK_LABEL)?.position;
  const currentColPosition = columns.find(c => c.id === vehicle.column_id)?.position;
  const showBuyerType = stockColPosition == null || currentColPosition == null || currentColPosition < stockColPosition;
  const lockedProfesionales = isLockedProfesionales(vehicle, columns);
  const canConfirmProfDelivery = lockedProfesionales && isBuyerTypeManager;
  const canCancelPurchase = isInRevisionF2 && (vehicle.purchased_by === currentUserId || isBuyerTypeManager)
    && !(isFuera(vehicle) && vehicle.fuera_confirmed_at); // en uno de fuera, solo hasta que la tienda de origen confirme la compra
  const fueraLocked = isFuera(vehicle) && !!vehicle.fuera_confirmed_at;

  const confirmBuyerTypeChange = async () => {
    if (pendingBuyerType === 'fuera' && fueraStoreError(buyerTypeStore)) { setMessage(fueraStoreError(buyerTypeStore)); return; }
    setSavingBuyerType(true);
    setMessage('');
    const { error } = await supabase.rpc('set_vehicle_buyer_type', {
      p_vehicle_id: vehicle.id, p_buyer_type: pendingBuyerType, p_fuera_store: pendingBuyerType === 'fuera' ? buyerTypeStore.trim() : null
    });
    if (error) {
      setMessage('Error al cambiar el tipo de compra: ' + error.message);
    } else {
      await supabase.from('vehicle_comments').insert([{
        vehicle_id: vehicle.id,
        user_id: currentUserId,
        author_name: currentUserName || null,
        content: `🏷️ Tipo de compra: ${BUYER_TYPE_LABELS[buyerType]} → ${BUYER_TYPE_LABELS[pendingBuyerType]}`
          + (pendingBuyerType === 'fuera' ? ` (🏬 ${buyerTypeStore.trim()})` : '')
      }]);
      loadComments();
      onRefresh();
    }
    setPendingBuyerType(null);
    setBuyerTypeStore('');
    setSavingBuyerType(false);
  };

  // Tienda de origen de un vehículo de fuera: la cambian el captador con la compra, el responsable o el creador.
  const canEditFueraStore = isFuera(vehicle) && (vehicle.purchased_by === currentUserId || ['creador', 'responsable'].includes(role));
  const saveFueraStore = async () => {
    const err = fueraStoreError(storeDraft);
    if (err) { setMessage(err); return; }
    setSavingStore(true);
    setMessage('');
    const { error } = await supabase.rpc('set_vehicle_fuera_store', { p_vehicle_id: vehicle.id, p_store: storeDraft.trim() });
    setSavingStore(false);
    if (error) { setMessage('Error al cambiar la tienda de origen: ' + error.message); return; }
    setStoreDraft(null);
    loadComments();
    onRefresh();
  };

  useEffect(() => {
    loadComments();
    loadSubscription();
  }, [vehicle.id]);

  // Comentarios en vivo: si alguien comenta (o se borra un comentario) en este coche, se recargan sin parpadeo.
  useEffect(() => {
    const onChange = (e) => {
      const p = e.detail || {};
      const affects = p.vehicle_id === vehicle.id;
      if (affects) loadComments({ silent: true });
    };
    window.addEventListener('enmarcha:comment-change', onChange);
    return () => window.removeEventListener('enmarcha:comment-change', onChange);
  }, [vehicle.id]);

  const loadSubscription = async () => {
    const { data } = await supabase
      .from('vehicle_subscriptions')
      .select('id')
      .eq('vehicle_id', vehicle.id)
      .eq('profile_id', currentUserId)
      .maybeSingle();
    setSubscribed(!!data);
  };

  const toggleSubscription = async () => {
    setSubLoading(true);
    if (subscribed) {
      const { error } = await supabase.from('vehicle_subscriptions')
        .delete().eq('vehicle_id', vehicle.id).eq('profile_id', currentUserId);
      if (!error) { setSubscribed(false); onRefresh(); }
    } else {
      const { error } = await supabase.from('vehicle_subscriptions')
        .insert([{ vehicle_id: vehicle.id, profile_id: currentUserId }]);
      if (!error) { setSubscribed(true); onRefresh(); }
    }
    setSubLoading(false);
  };

  const loadComments = async (opts) => {
    if (!opts?.silent) setLoadingComments(true);
    const { data } = await supabase
      .from('vehicle_comments')
      .select('*')
      .eq('vehicle_id', vehicle.id)
      .order('created_at', { ascending: false });
    // Los adjuntos no son públicos: se piden enlaces firmados temporales (1 h) a partir de su ruta.
    const rows = data || [];
    const paths = [...new Set(rows.flatMap(c => (c.attachments || []).map(a => attachmentPath(a.url)).filter(Boolean)))];
    let signed = {};
    if (paths.length > 0) {
      const { data: urls } = await supabase.storage.from('comment-attachments').createSignedUrls(paths, 3600);
      (urls || []).forEach(u => { if (u.signedUrl) signed[u.path] = u.signedUrl; });
    }
    setComments(rows.map(c => ({
      ...c,
      attachments: (c.attachments || []).map(a => ({ ...a, signedUrl: signed[attachmentPath(a.url)] || null }))
    })));
    setLoadingComments(false);
  };

  const confirmLocationChange = async () => {
    const newLocation = location || null;
    setSavingLocation(true);
    setMessage('');
    const { error } = await supabase.from('vehicles').update({ location: newLocation }).eq('id', vehicle.id);
    if (error) {
      setLocation(vehicle.location || '');
      setMessage('Error al actualizar la ubicación: ' + error.message);
    } else {
      await supabase.from('vehicle_location_history').insert([{
        vehicle_id: vehicle.id,
        location: newLocation,
        changed_by: currentUserId
      }]);
      onRefresh();
    }
    setSavingLocation(false);
  };

  const cancelLocationChange = () => setLocation(vehicle.location || '');

  const saveChanges = async () => {
    setConfirmingSave(false);
    setSaving(true);
    setMessage('');

    const newYear = year ? Number(year) : null;
    const newColor = color.trim() || null;

    const changes = [];
    if (brand.trim() !== (vehicle.brand || '')) changes.push(`Marca: "${vehicle.brand || '—'}" → "${brand.trim()}"`);
    if (model.trim() !== (vehicle.model || '')) changes.push(`Modelo: "${vehicle.model || '—'}" → "${model.trim()}"`);
    if (newYear !== (vehicle.year ?? null)) changes.push(`Año: ${vehicle.year ?? '—'} → ${newYear ?? '—'}`);
    if (newColor !== (vehicle.color ?? null)) changes.push(`Color: ${vehicle.color || '—'} → ${newColor || '—'}`);
    if (plate.trim() !== (vehicle.plate || '')) changes.push(`Matrícula: ${vehicle.plate || '—'} → ${plate.trim()}`);

    const { error } = await supabase.from('vehicles').update({
      brand: brand.trim(),
      model: model.trim(),
      year: newYear,
      color: newColor,
      plate: plate.trim()
    }).eq('id', vehicle.id);

    if (error) {
      setMessage('Error al guardar: ' + error.message);
    } else {
      if (changes.length > 0) {
        await supabase.from('vehicle_comments').insert([{
          vehicle_id: vehicle.id,
          user_id: currentUserId,
          author_name: currentUserName || null,
          content: `✏️ Editado: ${changes.join(' · ')}`,
          is_system: true // la edición ya avisa por su cuenta (tipo "Ediciones")
        }]);
        loadComments();
      }
      setEditing(false);
      onRefresh();
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Eliminar este vehículo? Esta acción no se puede deshacer.')) return;
    const { error } = await supabase.from('vehicles').delete().eq('id', vehicle.id);
    if (!error) {
      onRefresh();
      onDeleted();
    } else {
      setMessage('Error al eliminar: ' + error.message);
    }
  };

  const requestPostComment = (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setPendingComment(true);
  };

  const postComment = async () => {
    setPendingComment(false);
    setPostingComment(true);
    const { error } = await supabase.from('vehicle_comments').insert([{
      vehicle_id: vehicle.id,
      user_id: currentUserId,
      author_name: currentUserName || null,
      content: newComment.trim()
    }]);
    if (!error) {
      setNewComment('');
      loadComments();
    }
    setPostingComment(false);
  };

  const deleteComment = async (commentId) => {
    if (role !== 'creador') return;
    if (!window.confirm('¿Eliminar este comentario?')) return;
    const { error } = await supabase.from('vehicle_comments').delete().eq('id', commentId);
    if (!error) loadComments();
  };

  const handleFileSelected = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setMessage('Solo se permiten imágenes o archivos PDF.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setMessage('El archivo no puede superar los 10 MB.');
      return;
    }

    setMessage('');
    setPendingFile(file);
  };

  const cancelUpload = () => setPendingFile(null);

  const confirmUpload = async () => {
    const file = pendingFile;
    if (!file) return;
    setUploading(true);
    setMessage('');
    const safeName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
    const path = `${vehicle.id}/${Date.now()}_${safeName}`;

    const { error: uploadError } = await supabase.storage.from('comment-attachments').upload(path, file);
    if (uploadError) {
      setMessage('Error al subir el archivo: ' + uploadError.message);
      setUploading(false);
      return;
    }

    const { data: urlData } = supabase.storage.from('comment-attachments').getPublicUrl(path);

    const { error: insertError } = await supabase.from('vehicle_comments').insert([{
      vehicle_id: vehicle.id,
      user_id: currentUserId,
      author_name: currentUserName || null,
      content: `📎 Archivo adjunto: ${file.name}`,
      attachments: [{ name: file.name, url: urlData.publicUrl, type: file.type }]
    }]);
    if (insertError) {
      setMessage('Error al registrar el archivo: ' + insertError.message);
    } else {
      loadComments();
      setPendingFile(null);
    }
    setUploading(false);
  };

  const currentCol = columns.find(c => c.id === vehicle.column_id);
  const canDeliver = DELIVER_ROLES.includes(role) && isDeliveryColumnLabel(currentCol?.label);

  // Botón "Avanzar en el flujo de venta" (solo si este usuario puede hacer el siguiente paso).
  const saleAdvance = inSaleFlow && !isLockedProfesionales(vehicle, columns)
    ? saleAdvanceAction({ vehicle, columns, role, currentUserId, opFilled: saleOpFilled, canSeeOperation, prepSheet, tallerTransfer,
        moveTargetColumns, canDeliver })
    : null;
  // Flujo de compra: desde Revisión F2 hasta Stock (incluido).
  const inPurchaseFlow = inPurchaseFlowRange(vehicle, columns);
  const purchaseAdvance = inPurchaseFlow
    ? purchaseAdvanceAction({ vehicle, columns, role, currentUserId, f2Check, moveTargetColumns })
    : null;
  const runAdvance = (action) => {
    if (action.kind === 'operation') setShowOperation(true);
    else if (action.kind === 'assignPurchase') setShowReassignPurchaseModal(true);
    else if (action.kind === 'f2' && !action.movesColumn) setShowF2Process(true);
    else if (action.kind === 'sinF2') {
      setSinF2Step(1); setSinF2Reviewed(false); setSinF2Uploaded(false);
      setTimeout(() => document.getElementById('sin-f2-block')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
    }
    else if (action.kind === 'prepSheet') setShowPrepSheet(true);
    else if (action.kind === 'deliver') setShowDeliverModal(true);
    else if (action.kind === 'taller') setShowTallerGate(true);
    else if (action.kind === 'move') onRequestMove(action.to.id, { advance: inPurchaseFlow ? 'compra' : 'venta' });
    else setAdvanceConfirm(action); // confirmación de la subida a validación (no cambia de columna)
  };
  const confirmAdvance = async () => {
    const action = advanceConfirm;
    if (!action) return;
    if (action.kind === 'validation') {
      setSavingAdvance(true);
      await confirmValidationUploaded(true);
      setSavingAdvance(false);
      setAdvanceConfirm(null);
    }
  };

  return (
    <>
    <div style={S.overlay}>
      <div style={{...S.modal, maxWidth: '560px'}}>
        <button onClick={onClose} style={S.closeBtn}>✕</button>
        <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginRight: '32px'}}>
          <h3 style={{...S.title, marginBottom: 0}}>{brand} {model}</h3>
          {gvPending(vehicle) && <GvBadge />}
          {isFuera(vehicle) && <FueraBadge store={vehicle.fuera_store} />}
          <button
            onClick={toggleSubscription}
            disabled={subLoading}
            title={subscribed ? 'Dejar de seguir este vehículo' : 'Seguir este vehículo (recibir notificaciones)'}
            style={{
              background: 'none', border: 'none', cursor: subLoading ? 'wait' : 'pointer',
              fontSize: '1.25rem', padding: '2px', opacity: subLoading ? 0.5 : 1,
              filter: subscribed ? 'none' : 'grayscale(1) opacity(0.6)'
            }}
          >
            🔔
          </button>
        </div>
        <div style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem', marginBottom: '16px', marginTop: '4px'}}>
          {plate} · {currentCol?.label || 'Sin columna'} · {timeInColumnLabel(vehicle)} en columna
        </div>

        {message && (
          <div style={{backgroundColor: 'var(--b-7f1d1d)', color: 'var(--t-f1f5f9)', padding: '10px', borderRadius: '6px', marginBottom: '16px', fontSize: '0.875rem'}}>
            {message}
          </div>
        )}

        {editing ? (
          confirmingSave ? (
            <div style={{backgroundColor: 'var(--b-7f1d1d)', padding: '14px', borderRadius: '8px', marginBottom: '12px'}}>
              <p style={{color: 'var(--t-fecaca)', fontSize: '0.875rem', marginBottom: '12px'}}>
                ¿Seguro que quieres guardar los cambios? Los datos anteriores de este vehículo se perderán.
              </p>
              <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
                <button type="button" onClick={() => setConfirmingSave(false)} style={S.secondaryButton}>Cancelar</button>
                <button type="button" onClick={saveChanges} disabled={saving} style={{...S.button, opacity: saving ? 0.7 : 1}}>
                  {saving ? 'Guardando...' : 'Sí, guardar cambios'}
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); setConfirmingSave(true); }}>
              <label style={S.label}>Marca</label>
              <input style={S.input} value={brand} onChange={e => setBrand(e.target.value)} required />
              <label style={S.label}>Modelo</label>
              <input style={S.input} value={model} onChange={e => setModel(e.target.value)} required />
              <div style={{display: 'flex', gap: '12px'}}>
                <div style={{flex: 1}}>
                  <label style={S.label}>Año</label>
                  <input style={S.input} type="number" value={year} onChange={e => setYear(e.target.value)} />
                </div>
                <div style={{flex: 1}}>
                  <label style={S.label}>Color</label>
                  <input style={S.input} value={color} onChange={e => setColor(e.target.value)} />
                </div>
              </div>
              <label style={S.label}>Matrícula</label>
              <input style={S.input} value={plate} onChange={e => setPlate(e.target.value)} required />

              <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '12px'}}>
                <button type="button" onClick={() => setEditing(false)} style={S.secondaryButton}>Cancelar</button>
                <button type="submit" style={S.button}>Guardar cambios</button>
              </div>
            </form>
          )
        ) : (
          <>
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '0.875rem', color: 'var(--t-e5e7eb)', marginBottom: '16px'}}>
              <div>Año: <strong>{year || '—'}</strong></div>
              <div>Color: <strong>{color || '—'}</strong></div>
            </div>

            {/* Leads (src/leads/) de este coche que el usuario puede ver. Se dejan de mostrar cuando el coche
                ya está en Preparación (o más adelante) y cobrado: la venta está hecha. */}
            {(() => {
              if (!onOpenLeads || linkedLeads.length === 0) return null;
              const prepCol = columns.find(c => c.label?.trim().toLowerCase() === PREPARACION_LABEL);
              const curCol = columns.find(c => c.id === vehicle.column_id);
              if (vehicle.paid && prepCol && curCol && curCol.position >= prepCol.position) return null;
              const open = linkedLeads.filter(l => !['vendido', 'perdido'].includes(l.status)).length;
              return (
                <div style={{marginBottom: '16px'}}>
                  <button type="button" onClick={onOpenLeads} style={S.secondaryButton} title="Ver los leads de este coche">
                    📇 Leads de este coche ({linkedLeads.length}{open !== linkedLeads.length ? `, ${open} abiertos` : ''})
                  </button>
                </div>
              );
            })()}

            <div style={{display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px'}}>
              <div style={{flex: 1, minWidth: '160px'}}>
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px'}}>
                  <label style={S.label}>📍 Ubicación</label>
                  <button type="button" onClick={() => setShowHistory(true)} title="Ver historial de movimientos" style={{background: 'none', border: 'none', color: 'var(--t-9ca3af)', cursor: 'pointer', fontSize: '0.75rem', padding: 0}}>
                    🕘 Historial
                  </button>
                </div>
                <select style={S.select} value={location} onChange={e => setLocation(e.target.value)} disabled={savingLocation || (lockedProfesionales && !isBuyerTypeManager)}>
                  <option value="">Sin especificar</option>
                  {locationOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
                {location !== (vehicle.location || '') && (
                  <div style={{backgroundColor: 'var(--b-374151)', padding: '8px 10px', borderRadius: '6px', marginTop: '8px'}}>
                    <p style={{color: 'var(--t-e5e7eb)', fontSize: '0.8rem', margin: '0 0 8px'}}>
                      ¿Confirmas cambiar la ubicación a "{location || 'Sin especificar'}"?
                    </p>
                    <div style={{display: 'flex', gap: '8px', justifyContent: 'flex-end'}}>
                      <button type="button" onClick={cancelLocationChange} disabled={savingLocation} style={S.secondaryButton}>Cancelar</button>
                      <button type="button" onClick={confirmLocationChange} disabled={savingLocation} style={{...S.button, opacity: savingLocation ? 0.7 : 1}}>
                        {savingLocation ? 'Guardando...' : 'Confirmar'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {assignedSaleLabel(vehicle) && (
                <div style={{flex: 1, minWidth: '160px'}}>
                  <label style={S.label}>🧑‍💼 Venta asignada a</label>
                  <div style={{display: 'flex', alignItems: 'center', gap: '8px', height: '38px'}}>
                    <span style={{color: 'var(--t-f1f5f9)', fontSize: '0.875rem'}}>{assignedSaleLabel(vehicle)}</span>
                    {role === 'creador' && (
                      <button onClick={() => setShowReassignModal(true)} title="Reasignar venta" style={{background: 'none', border: 'none', color: 'var(--t-9ca3af)', cursor: 'pointer', fontSize: '0.9rem'}}>
                        🔁
                      </button>
                    )}
                  </div>
                </div>
              )}
              {assignedPurchaseLabel(vehicle) && (
                <div style={{flex: 1, minWidth: '160px'}}>
                  <label style={S.label}>🛒 Compra asignada a</label>
                  <div style={{display: 'flex', alignItems: 'center', gap: '8px', height: '38px'}}>
                    <span style={{color: 'var(--t-f1f5f9)', fontSize: '0.875rem'}}>{assignedPurchaseLabel(vehicle)}</span>
                    {role === 'creador' && (
                      <button onClick={() => setShowReassignPurchaseModal(true)} title="Reasignar compra" style={{background: 'none', border: 'none', color: 'var(--t-9ca3af)', cursor: 'pointer', fontSize: '0.9rem'}}>
                        🔁
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {showBuyerType && (
            <div style={{marginBottom: '16px'}}>
              <label style={S.label}>🏷️ Tipo de compra</label>
              {canChangeBuyerType && !fueraLocked ? (
                <select
                  style={S.select}
                  value={pendingBuyerType || buyerType}
                  onChange={e => setPendingBuyerType(e.target.value === buyerType ? null : e.target.value)}
                  disabled={savingBuyerType}
                >
                  {buyerTypeOptionsFor(currentColLabel).map(t => <option key={t} value={t}>{BUYER_TYPE_LABELS[t]}</option>)}
                </select>
              ) : (
                <div style={{color: 'var(--t-f1f5f9)', fontSize: '0.875rem'}}>{BUYER_TYPE_LABELS[buyerType]}</div>
              )}
              {pendingBuyerType && (
                <div style={{backgroundColor: 'var(--b-374151)', padding: '8px 10px', borderRadius: '6px', marginTop: '8px'}}>
                  <p style={{color: 'var(--t-e5e7eb)', fontSize: '0.8rem', margin: '0 0 8px'}}>
                    ¿Confirmas cambiar el tipo de compra a "{BUYER_TYPE_LABELS[pendingBuyerType]}"?
                    {pendingBuyerType === 'profesionales' && currentColLabel === STOCK_LABEL && ' Dejará de verse en Stock (vista Tienda) y no se podrá mover a otras columnas.'}
                    {pendingBuyerType === 'gestion_venta' && ' Llevará siempre la viñeta "GV" y al pasar a Preparación se avisará de hacer el cierre de la gestión antes de la validación.'}
                    {pendingBuyerType === 'fuera' && ' Se quedará en Revisión F2, no pasará a nuestro stock y no contará en objetivos. La compra tiene que estar asignada a un captador.'}
                  </p>
                  {pendingBuyerType === 'fuera' && (
                    <>
                      <label style={S.label} htmlFor="buyer-fuera-store">🏬 Tienda de origen (se le factura la revisión)</label>
                      <FueraStoreInput id="buyer-fuera-store" value={buyerTypeStore} onChange={setBuyerTypeStore} disabled={savingBuyerType} stores={knownFueraStores} />
                    </>
                  )}
                  <div style={{display: 'flex', gap: '8px', justifyContent: 'flex-end'}}>
                    <button type="button" onClick={() => { setPendingBuyerType(null); setBuyerTypeStore(''); }} disabled={savingBuyerType} style={S.secondaryButton}>Cancelar</button>
                    <button type="button" onClick={confirmBuyerTypeChange}
                      disabled={savingBuyerType || (pendingBuyerType === 'fuera' && !!fueraStoreError(buyerTypeStore))}
                      style={{...S.button, opacity: (savingBuyerType || (pendingBuyerType === 'fuera' && !!fueraStoreError(buyerTypeStore))) ? 0.6 : 1}}>
                      {savingBuyerType ? 'Guardando...' : 'Confirmar'}
                    </button>
                  </div>
                </div>
              )}
            </div>
            )}

            {isFuera(vehicle) && (
              <div style={{marginBottom: '16px'}}>
                <label style={S.label} htmlFor={storeDraft !== null ? 'detail-fuera-store' : undefined}>🏬 Tienda de origen</label>
                {storeDraft === null ? (
                  <div style={{display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap'}}>
                    <span style={{color: vehicle.fuera_store ? 'var(--t-f1f5f9)' : 'var(--t-fbbf24)', fontSize: '0.875rem'}}>
                      {vehicle.fuera_store || 'Sin indicar'}
                    </span>
                    <span style={{color: 'var(--t-9ca3af)', fontSize: '0.75rem'}}>Se le factura la revisión.</span>
                    {canEditFueraStore && (
                      <button type="button" onClick={() => setStoreDraft(vehicle.fuera_store || '')} style={{...S.secondaryButton, padding: '4px 10px', fontSize: '0.75rem'}}>
                        Cambiar
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    <FueraStoreInput id="detail-fuera-store" value={storeDraft} onChange={setStoreDraft} disabled={savingStore} stores={knownFueraStores} />
                    <div style={{display: 'flex', gap: '8px', justifyContent: 'flex-end'}}>
                      <button type="button" onClick={() => setStoreDraft(null)} disabled={savingStore} style={S.secondaryButton}>Cancelar</button>
                      <button type="button" onClick={saveFueraStore} disabled={savingStore || !!fueraStoreError(storeDraft) || storeDraft.trim() === (vehicle.fuera_store || '')}
                        style={{...S.button, opacity: (savingStore || !!fueraStoreError(storeDraft)) ? 0.6 : 1}}>
                        {savingStore ? 'Guardando...' : 'Guardar tienda'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {lockedProfesionales && (
              <div style={{backgroundColor: 'var(--b-2e1065)', border: '1px solid var(--l-6d28d9)', borderRadius: '6px', padding: '10px', marginBottom: '16px'}}>
                <p style={{color: 'var(--t-ddd6fe)', fontSize: '0.8rem', margin: canConfirmProfDelivery ? '0 0 10px' : 0}}>
                  🔒 Vehículo de Profesionales en Stock: no se puede mover a otras columnas mientras mantenga esta etiqueta. Solo el creador o el responsable pueden marcarlo como entregado.
                </p>
                {canConfirmProfDelivery && (
                  <button onClick={() => setShowProfDelivery(true)} style={{...S.secondaryButton, color: 'var(--t-4ade80)', borderColor: 'var(--l-166534)'}}>
                    ✅ Confirmar entregado
                  </button>
                )}
              </div>
            )}

            {canValidateSale && saleOp && (
              <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', flexWrap: 'wrap'}}>
                <label style={{...S.label, marginBottom: 0}}>🧾 Venta validada</label>
                {saleOp.validated_at ? (
                  <span style={{color: 'var(--t-4ade80)', fontSize: '0.8rem'}}>✅ Sí, el {new Date(saleOp.validated_at).toLocaleDateString('es-ES')}</span>
                ) : (
                  <span style={{color: 'var(--t-fca5a5)', fontSize: '0.8rem'}}>Pendiente</span>
                )}
                {!(saleOp.validated_at && saleOp.delivered_at) && (
                  <button onClick={toggleSaleValidated} disabled={savingSaleValidation} style={{...S.secondaryButton, padding: '4px 10px', fontSize: '0.75rem', color: saleOp.validated_at ? 'var(--t-fca5a5)' : 'var(--t-86efac)'}}>
                    {savingSaleValidation ? 'Guardando...' : (saleOp.validated_at ? 'Quitar validación' : '✓ Marcar como validado')}
                  </button>
                )}
                {saleOp.validated_at && (
                  <span style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                    <input id="sale-validation-date" type="date" style={{...S.input, marginBottom: 0, width: 'auto', padding: '4px 8px'}}
                      value={saleValidationDate} onChange={e => setSaleValidationDate(e.target.value)} disabled={savingSaleValidation} />
                    <button onClick={saveSaleValidationDate}
                      disabled={savingSaleValidation || !saleValidationDate || new Date(saleOp.validated_at).toDateString() === new Date(`${saleValidationDate}T12:00:00`).toDateString()}
                      style={{...S.secondaryButton, padding: '4px 10px', fontSize: '0.75rem'}}>
                      Cambiar fecha
                    </button>
                  </span>
                )}
              </div>
            )}

            {vehicle.paid && (
              <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px'}}>
                <label style={{...S.label, marginBottom: 0}}>📤 Validación</label>
                {vehicle.uploaded_to_validation ? (
                  <span style={{color: 'var(--t-4ade80)', fontSize: '0.8rem'}}>✅ Subida</span>
                ) : (
                  <>
                    <span style={{color: 'var(--t-fca5a5)', fontSize: '0.8rem'}}>⚠️ Pendiente de subir</span>
                    {(vehicle.assigned_to === currentUserId || role === 'creador') && saleAdvance?.kind !== 'validation' && (
                      <button onClick={() => confirmValidationUploaded(true)} disabled={savingValidation} style={{...S.secondaryButton, padding: '4px 10px', fontSize: '0.75rem'}}>
                        {savingValidation ? 'Guardando...' : 'Confirmar que está subida'}
                      </button>
                    )}
                  </>
                )}
              </div>
            )}

            {/* La hoja de preparación se ve siempre en la ficha (también sirve para consultarla). */}
            {prepSheet && (
              <div style={{marginBottom: '16px'}}>
                <button onClick={() => setShowPrepSheet(true)} style={S.secondaryButton}>
                  📋 Hoja de preparación {prepSheetComplete(prepSheet) ? '(completa)' : '(pendiente)'}
                </button>
              </div>
            )}

            {isGestionVenta(vehicle) && vehicle.gv_requested_at && !vehicle.gv_closed_at && (
              <div style={{backgroundColor: 'var(--b-052e16)', border: '1px solid var(--l-166534)', borderRadius: '6px', padding: '10px', marginBottom: '16px'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px'}}>
                  <GvBadge />
                  <span style={{color: 'var(--t-bbf7d0)', fontSize: '0.85rem', fontWeight: 600}}>Cierre de gestión pendiente</span>
                </div>
                <p style={{color: 'var(--t-d1d5db)', fontSize: '0.8rem', margin: '0 0 10px'}}>
                  Hay que hacer el cierre de la gestión antes de la validación.
                </p>
                <ProcessSteps steps={[
                  { label: `En Preparación: cierre asignado a ${vehicle.gv_closer_id ? (gvNames[vehicle.gv_closer_id] || '...') : 'nadie (no consta quién compró el coche)'}`, status: 'done' },
                  { label: 'Cierre de gestión subido', status: 'current' }
                ]} />
                {!(GV_CONFIRM_ROLES.includes(role) || currentUserId === vehicle.gv_closer_id) && (
                  <ProcessPending who={vehicle.gv_closer_id ? `${gvNames[vehicle.gv_closer_id] || 'quien compró el coche'}, el responsable o el creador` : 'el responsable o el creador'} />
                )}
                {(GV_CONFIRM_ROLES.includes(role) || currentUserId === vehicle.gv_closer_id) && (
                  !gvConfirmOpen ? (
                    <button type="button" onClick={() => setGvConfirmOpen(true)} style={S.secondaryButton}>
                      ✅ Confirmar cierre de gestión subido
                    </button>
                  ) : (
                    <div style={{backgroundColor: 'var(--b-1f2937)', borderRadius: '6px', padding: '10px'}}>
                      <p style={{color: 'var(--t-e5e7eb)', fontSize: '0.8rem', margin: '0 0 10px'}}>
                        ¿Confirmas que el cierre de gestión de <strong>{vehicle.brand} {vehicle.model}</strong> ({vehicle.plate}) está subido? Se registrará en comentarios y desaparecerá la viñeta GV.
                      </p>
                      <div style={{display: 'flex', gap: '8px', justifyContent: 'flex-end'}}>
                        <button type="button" onClick={() => setGvConfirmOpen(false)} disabled={savingGv} style={S.secondaryButton}>Cancelar</button>
                        <button type="button" onClick={confirmGvClosure} disabled={savingGv} style={{...S.button, opacity: savingGv ? 0.7 : 1}}>
                          {savingGv ? 'Guardando...' : 'Sí, confirmar'}
                        </button>
                      </div>
                    </div>
                  )
                )}
              </div>
            )}

            {isGestionVenta(vehicle) && vehicle.gv_closed_at && (
              <div style={{color: 'var(--t-86efac)', fontSize: '0.8rem', marginBottom: '16px'}}>
                ✅ Cierre de gestión subido ({vehicle.gv_closed_by && gvNames[vehicle.gv_closed_by] ? `confirmado por ${gvNames[vehicle.gv_closed_by]} ` : ''}el {new Date(vehicle.gv_closed_at).toLocaleDateString('es-ES')})
              </div>
            )}

            {vehicle.sin_f2 && (
              <div id="sin-f2-block" style={{backgroundColor: 'var(--b-450a0a)', border: '1px solid var(--l-7f1d1d)', borderRadius: '6px', padding: '10px', marginBottom: '16px'}}>
                <p style={{color: 'var(--t-fecaca)', fontSize: '0.85rem', fontWeight: 600, margin: '0 0 4px'}}>🚫 Sin F2</p>
                <p style={{color: 'var(--t-fca5a5)', fontSize: '0.8rem', margin: SIN_F2_CONFIRM_ROLES.includes(role) ? '0 0 10px' : 0}}>
                  Este vehículo se añadió sin pasar por Revisión F2: aún no consta como revisado.
                </p>
                {SIN_F2_CONFIRM_ROLES.includes(role) && !(inPurchaseFlow && sinF2Step === 0) && (
                  sinF2Step === 0 ? (
                    <button type="button" onClick={() => { setSinF2Step(1); setSinF2Reviewed(false); setSinF2Uploaded(false); }} style={S.secondaryButton}>
                      Confirmar revisión
                    </button>
                  ) : (
                    <div style={{backgroundColor: 'var(--b-1f2937)', borderRadius: '6px', padding: '10px'}}>
                      {sinF2Step === 1 ? (
                        <>
                          <label style={{display: 'flex', gap: '8px', alignItems: 'flex-start', color: 'var(--t-e5e7eb)', fontSize: '0.8rem', marginBottom: '8px', cursor: 'pointer'}}>
                            <input type="checkbox" checked={sinF2Reviewed} onChange={e => setSinF2Reviewed(e.target.checked)} />
                            El vehículo ha sido revisado
                          </label>
                          <label style={{display: 'flex', gap: '8px', alignItems: 'flex-start', color: 'var(--t-e5e7eb)', fontSize: '0.8rem', marginBottom: '10px', cursor: 'pointer'}}>
                            <input type="checkbox" checked={sinF2Uploaded} onChange={e => setSinF2Uploaded(e.target.checked)} />
                            La F1 y la F2 están subidas al CRM
                          </label>
                          <div style={{display: 'flex', gap: '8px', justifyContent: 'flex-end'}}>
                            <button type="button" onClick={() => setSinF2Step(0)} style={S.secondaryButton}>Cancelar</button>
                            <button type="button" onClick={() => setSinF2Step(2)} disabled={!sinF2Reviewed || !sinF2Uploaded}
                              style={{...S.button, opacity: (!sinF2Reviewed || !sinF2Uploaded) ? 0.5 : 1}}>Continuar</button>
                          </div>
                        </>
                      ) : (
                        <>
                          <p style={{color: 'var(--t-e5e7eb)', fontSize: '0.8rem', margin: '0 0 10px'}}>
                            ¿Confirmas que <strong>{vehicle.brand} {vehicle.model}</strong> ({vehicle.plate}) está revisado y que la F1 y la F2 están subidas al CRM? Se registrará en comentarios y desaparecerá el aviso.
                          </p>
                          <div style={{display: 'flex', gap: '8px', justifyContent: 'flex-end'}}>
                            <button type="button" onClick={() => setSinF2Step(1)} disabled={savingSinF2} style={S.secondaryButton}>Volver</button>
                            <button type="button" onClick={confirmSinF2} disabled={savingSinF2} style={{...S.button, opacity: savingSinF2 ? 0.7 : 1}}>
                              {savingSinF2 ? 'Guardando...' : 'Sí, confirmar'}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )
                )}
              </div>
            )}

            {inPurchaseFlow && (
              <SaleFlow flowName="compra" doneText={isFuera(vehicle) ? 'Recogido por la tienda de origen' : 'Compra completada: el coche está en Stock'}
                steps={purchaseFlowSteps({ vehicle, columns, f2Check })}
                advance={purchaseAdvance} onAdvance={() => runAdvance(purchaseAdvance)} />
            )}
            {inSaleFlow && (
              <SaleFlow steps={saleFlowSteps({ vehicle, columns, opFilled: saleOpFilled, prepSheet })}
                advance={saleAdvance} onAdvance={() => runAdvance(saleAdvance)}
                onAdvanceAlt={saleAdvance?.alt ? () => onRequestMove(saleAdvance.alt.id, { advance: 'venta' }) : null} />
            )}
            {/* Los botones de cada paso se ocultan cuando "Avanzar" hace lo mismo. */}
            {canSeeOperation && saleAdvance?.kind !== 'operation' && (
              <div style={{marginBottom: '16px'}}>
                <button onClick={() => setShowOperation(true)} style={S.secondaryButton}>
                  🧾 Operación{saleOpFilled === false ? ' (pendiente de rellenar)' : ''}
                </button>
              </div>
            )}
            {/* Los pasos de la Revisión F2 (y de los vehículos de fuera) se hacen desde "Avanzar en el flujo de compra". */}

            {canCancelPurchase && (
              <div style={{marginBottom: '16px'}}>
                <button onClick={() => setShowCancelPurchase(true)} style={{...S.secondaryButton, color: 'var(--t-fca5a5)', borderColor: 'var(--l-7f1d1d)'}}>
                  ❌ No se compra
                </button>
              </div>
            )}

            {isInTaller && saleAdvance?.kind !== 'taller' && (
              <div style={{marginBottom: '16px'}}>
                <button onClick={() => setShowTallerGate(true)} style={S.secondaryButton}>
                  🚦 Traslado a Limpieza entrega {tallerPendingBadge(tallerTransfer) ? `(${tallerPendingBadge(tallerTransfer)})` : ''}
                </button>
              </div>
            )}

            {moveTargetColumns && moveTargetColumns.length > 0 && (
              <div style={{marginBottom: '16px'}}>
                <label style={S.label}>↔️ Mover a columna</label>
                <select style={S.select} value="" onChange={e => { if (e.target.value) onRequestMove(e.target.value); }}>
                  <option value="">Selecciona una columna...</option>
                  {moveTargetColumns.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
            )}

            <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px'}}>
              {canEdit && (
                <button onClick={() => setEditing(true)} style={S.secondaryButton}>✏️ Editar</button>
              )}
              {canDelete && (
                <button onClick={handleDelete} style={{...S.secondaryButton, color: 'var(--t-fca5a5)', borderColor: 'var(--l-7f1d1d)'}}>🗑️ Eliminar</button>
              )}
              {canDeliver && saleAdvance?.kind !== 'deliver' && (
                <button onClick={() => setShowDeliverModal(true)} style={{...S.secondaryButton, color: 'var(--t-4ade80)', borderColor: 'var(--l-166534)'}}>🚚 Marcar como entregado</button>
              )}
            </div>
          </>
        )}

        <div style={{borderTop: '1px solid var(--l-374151)', paddingTop: '16px', marginTop: '4px'}}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: commentsOpen ? '12px' : 0}}>
            <button type="button" onClick={() => setCommentsOpen(o => !o)} aria-expanded={commentsOpen}
              style={{background: 'none', border: 'none', padding: '4px 0', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--t-f1f5f9)', fontSize: '0.95rem', fontWeight: 600}}>
              <span style={{display: 'inline-block', width: '12px', color: 'var(--t-9ca3af)'}}>{commentsOpen ? '▾' : '▸'}</span>
              💬 Comentarios {!loadingComments && <span style={{color: 'var(--t-9ca3af)', fontWeight: 400}}>({comments.length})</span>}
            </button>
            {commentsOpen && (
              <label style={{...S.secondaryButton, display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: uploading ? 'wait' : 'pointer', opacity: uploading ? 0.7 : 1, margin: 0}}>
                📎 Añadir archivo
                <input type="file" accept="image/*,application/pdf" onChange={handleFileSelected} disabled={uploading} style={{display: 'none'}} />
              </label>
            )}
          </div>

          {commentsOpen && (<>

          {pendingFile && (
            <div style={{backgroundColor: 'var(--b-374151)', padding: '10px', borderRadius: '6px', marginBottom: '12px'}}>
              <p style={{color: 'var(--t-e5e7eb)', fontSize: '0.8rem', margin: '0 0 8px'}}>
                ¿Confirmas subir el archivo "{pendingFile.name}"?
              </p>
              <div style={{display: 'flex', gap: '8px', justifyContent: 'flex-end'}}>
                <button type="button" onClick={cancelUpload} disabled={uploading} style={S.secondaryButton}>Cancelar</button>
                <button type="button" onClick={confirmUpload} disabled={uploading} style={{...S.button, opacity: uploading ? 0.7 : 1}}>
                  {uploading ? 'Subiendo...' : 'Confirmar'}
                </button>
              </div>
            </div>
          )}

          {loadingComments ? (
            <div style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem'}}>Cargando comentarios...</div>
          ) : (
            <div style={{maxHeight: '220px', overflowY: 'auto', marginBottom: '12px'}}>
              {comments.length === 0 ? (
                <p style={{color: 'var(--t-9ca3af)', fontStyle: 'italic', fontSize: '0.875rem'}}>Sin comentarios todavía</p>
              ) : (
                comments.map(c => (
                  <div key={c.id} style={{backgroundColor: 'var(--b-334155)', padding: '10px', borderRadius: '6px', marginBottom: '8px'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                      <div style={{color: 'var(--t-f1f5f9)', fontSize: '0.8rem', fontWeight: 600}}>
                        {c.author_name || 'Usuario'}
                        {c.column_label && <span style={{color: 'var(--t-9ca3af)', fontWeight: 400}}> · {c.column_label}</span>}
                        {c.created_at && (
                          <span style={{color: 'var(--t-6b7280)', fontWeight: 400}}> · {new Date(c.created_at).toLocaleString('es-ES', {dateStyle: 'short', timeStyle: 'short'})}</span>
                        )}
                      </div>
                      {role === 'creador' && (
                        <button onClick={() => deleteComment(c.id)} style={{background: 'none', border: 'none', color: 'var(--t-9ca3af)', cursor: 'pointer', fontSize: '0.75rem'}}>✕</button>
                      )}
                    </div>
                    <div style={{color: 'var(--t-e5e7eb)', fontSize: '0.875rem', marginTop: '4px', whiteSpace: 'pre-wrap'}}>{c.content}</div>
                    {c.attachments && c.attachments.length > 0 && (
                      <div style={{marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px'}}>
                        {c.attachments.map((att, i) => (
                          att.type?.startsWith('image/') ? (
                            !att.signedUrl ? <span key={i} style={{color: 'var(--t-9ca3af)', fontSize: '0.75rem'}}>📎 {att.name} (no disponible)</span> :
                            <a key={i} href={att.signedUrl} target="_blank" rel="noopener noreferrer">
                              <img src={att.signedUrl} alt={att.name} style={{width: '64px', height: '64px', objectFit: 'cover', borderRadius: '4px', border: '1px solid var(--l-4b5563)'}} />
                            </a>
                          ) : (
                            !att.signedUrl ? <span key={i} style={{color: 'var(--t-9ca3af)', fontSize: '0.75rem'}}>📄 {att.name} (no disponible)</span> :
                            <a key={i} href={att.signedUrl} target="_blank" rel="noopener noreferrer" style={{color: 'var(--t-f59e0b)', fontSize: '0.75rem', textDecoration: 'underline'}}>
                              📄 {att.name}
                            </a>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {pendingComment ? (
            <div style={{backgroundColor: 'var(--b-374151)', padding: '10px', borderRadius: '6px'}}>
              <p style={{color: 'var(--t-e5e7eb)', fontSize: '0.8rem', margin: '0 0 8px'}}>¿Confirmas publicar este comentario?</p>
              <p style={{color: 'var(--t-9ca3af)', fontSize: '0.8rem', fontStyle: 'italic', margin: '0 0 10px', whiteSpace: 'pre-wrap'}}>"{newComment}"</p>
              <div style={{display: 'flex', gap: '8px', justifyContent: 'flex-end'}}>
                <button type="button" onClick={() => setPendingComment(false)} disabled={postingComment} style={S.secondaryButton}>Cancelar</button>
                <button type="button" onClick={postComment} disabled={postingComment} style={{...S.button, opacity: postingComment ? 0.7 : 1}}>
                  {postingComment ? 'Publicando...' : 'Confirmar'}
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={requestPostComment} style={{display: 'flex', gap: '8px'}}>
              <input
                style={{...S.input, marginBottom: 0}}
                value={newComment}
                onChange={e => setNewComment(e.target.value)}
                placeholder="Escribe un comentario..."
              />
              <button type="submit" disabled={!newComment.trim()} style={S.button}>
                Enviar
              </button>
            </form>
          )}
          </>)}
        </div>
      </div>
    </div>
    {showDeliverModal && (
      <DeliverVehicleModal
        vehicle={vehicle}
        orgId={vehicle.org_id}
        currentUserId={currentUserId}
        onClose={() => setShowDeliverModal(false)}
        onDelivered={() => { onRefresh(); onDeleted(); }}
      />
    )}
    {showReassignModal && (
      <AssignSaleModal
        vehicle={vehicle}
        orgId={vehicle.org_id}
        mode="reassign"
        onClose={() => setShowReassignModal(false)}
        onAssigned={() => { setShowReassignModal(false); onRefresh(); }}
      />
    )}
    {showReassignPurchaseModal && (
      <AssignPurchaseModal
        vehicle={vehicle}
        orgId={vehicle.org_id}
        mode="reassign"
        onClose={() => setShowReassignPurchaseModal(false)}
        onAssigned={() => { setShowReassignPurchaseModal(false); onRefresh(); }}
      />
    )}
    {showPrepSheet && prepSheet && (
      <PrepSheetModal
        vehicle={vehicle}
        sheet={prepSheet}
        role={role}
        onClose={() => setShowPrepSheet(false)}
        onRefresh={onRefresh}
      />
    )}
    {showHistory && (
      <VehicleHistoryModal
        vehicle={vehicle}
        onClose={() => setShowHistory(false)}
      />
    )}
    {showF2Process && (
      <F2ProcessGateModal
        vehicle={vehicle}
        check={f2Check}
        role={role}
        currentUserId={currentUserId}
        confirmColumnMove
        nextLabel={columns.find(c => c.label?.trim().toLowerCase() === LIMPIEZA_FOTOS_LABEL)?.label || 'Limpieza fotos'}
        onDone={async (result) => { setShowF2Process(false); await onRefresh(); if (result === 'recogido') onDeleted(); }}
        onClose={() => setShowF2Process(false)}
      />
    )}
    {showTallerGate && (
      <TallerDepartureGateModal
        vehicle={vehicle}
        transfer={tallerTransfer}
        role={role}
        confirmColumnMove
        fromLabel={currentCol?.label}
        toLabel={columns.find(c => c.label?.trim().toLowerCase() === LIMPIEZA_ENTREGA_LABEL)?.label || 'Limpieza entrega'}
        currentUserId={currentUserId}
        onDone={async () => { setShowTallerGate(false); await onRefresh(); }}
        onClose={() => setShowTallerGate(false)}
      />
    )}
    {showProfDelivery && (
      <ProfesionalesDeliveryModal
        vehicle={vehicle}
        onClose={() => setShowProfDelivery(false)}
        onDone={() => { onRefresh(); onDeleted(); }}
      />
    )}
    {advanceConfirm && (
      <SaleAdvanceConfirmModal
        vehicle={vehicle}
        fromLabel={currentCol?.label}
        title={inPurchaseFlow ? 'Avanzar en el flujo de compra' : 'Avanzar en el flujo de venta'}
        text="¿Confirmas que la venta ya está subida a validación?"
        saving={savingAdvance}
        onConfirm={confirmAdvance}
        onClose={() => setAdvanceConfirm(null)}
      />
    )}
    {showOperation && (
      <SaleOperationModal
        vehicle={vehicle}
        sellerName={vehicle.assigned_profile?.full_name}
        canEdit={canSeeOperation}
        isAdmin={SALE_OP_ROLES.includes(role)}
        onClose={() => setShowOperation(false)}
        onSaved={() => { loadSaleOpFilled(); loadComments(); onRefresh(); }}
      />
    )}
    {showCancelPurchase && (
      <CancelPurchaseModal
        vehicle={vehicle}
        onClose={() => setShowCancelPurchase(false)}
        onDone={() => { onRefresh(); onDeleted(); }}
      />
    )}
    </>
  );
}

function VehicleHistoryModal({ vehicle, onClose }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [authors, setAuthors] = useState({});

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from('vehicle_location_history')
        .select('id, location, changed_by, changed_at')
        .eq('vehicle_id', vehicle.id)
        .order('changed_at', { ascending: false });
      const history = data || [];
      setRows(history);

      const authorIds = [...new Set(history.map(r => r.changed_by).filter(Boolean))];
      if (authorIds.length > 0) {
        const { data: profs } = await supabase.from('profiles').select('id, full_name').in('id', authorIds);
        const map = {};
        (profs || []).forEach(p => { map[p.id] = p.full_name; });
        setAuthors(map);
      }
      setLoading(false);
    })();
  }, [vehicle.id]);

  return (
    <div style={S.overlay}>
      <div style={{...S.modal, maxWidth: '480px'}}>
        <button onClick={onClose} style={S.closeBtn}>✕</button>
        <h3 style={S.title}>🕘 Historial de ubicaciones</h3>
        <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem', marginBottom: '16px'}}>
          {vehicle.brand} {vehicle.model} ({vehicle.plate})
        </p>

        {loading ? (
          <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem'}}>Cargando historial...</p>
        ) : rows.length === 0 ? (
          <p style={{color: 'var(--t-9ca3af)', fontStyle: 'italic', fontSize: '0.875rem'}}>Sin cambios de ubicación registrados todavía</p>
        ) : (
          <div style={{maxHeight: '360px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px'}}>
            {rows.map(r => (
              <div key={r.id} style={{backgroundColor: 'var(--b-334155)', padding: '10px', borderRadius: '6px'}}>
                <div style={{color: 'var(--t-f1f5f9)', fontSize: '0.85rem'}}>
                  📍 {r.location || 'Sin especificar'}
                </div>
                <div style={{color: 'var(--t-9ca3af)', fontSize: '0.75rem', marginTop: '4px'}}>
                  {r.changed_by ? (authors[r.changed_by] || 'Usuario') : 'Sistema'}
                  {r.changed_at && (
                    <> · {new Date(r.changed_at).toLocaleString('es-ES', {dateStyle: 'short', timeStyle: 'short'})}</>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const DELIVERY_CHECKLIST_ITEMS = [
  { key: 'contrato', label: 'Contrato de venta firmado' },
  { key: 'preparacion', label: 'Preparación hecha' },
  { key: 'crm', label: 'CRM firmado completo (entrega y prueba dinámica)' },
  { key: 'confirmacion', label: 'Confirmación de entrega firmada' },
  { key: 'stock', label: 'Vehículo borrado de stock' }
];

// Entrega directa desde Stock de un vehículo de Profesionales. Queda en el histórico como
// "Entrega profesionales" y no cuenta para los objetivos de entregas.
function ProfesionalesDeliveryModal({ vehicle, onClose, onDone }) {
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const confirm = async () => {
    setSaving(true);
    setMessage('');
    const { error } = await supabase.rpc('mark_vehicle_delivered_profesionales', { p_vehicle_id: vehicle.id });
    if (error) { setMessage('Error: ' + error.message); setSaving(false); return; }
    onDone();
  };

  return (
    <div style={S.overlay}>
      <div style={{...S.modal, maxWidth: '420px'}}>
        <button onClick={onClose} style={S.closeBtn}>✕</button>
        <h3 style={S.title}>✅ Confirmar entregado</h3>
        <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem', marginBottom: '16px'}}>
          {vehicle.brand} {vehicle.model} ({vehicle.plate}) · Profesionales
        </p>
        {message && (
          <div style={{backgroundColor: 'var(--b-7f1d1d)', color: 'var(--t-f1f5f9)', padding: '10px', borderRadius: '6px', marginBottom: '16px', fontSize: '0.875rem'}}>
            {message}
          </div>
        )}
        <p style={{color: 'var(--t-e5e7eb)', fontSize: '0.875rem', marginBottom: '20px'}}>
          El vehículo saldrá del tablero y quedará registrado en el histórico como entrega de Profesionales.
          No cuenta para los objetivos de entregas de la tienda ni de ningún comercial.
        </p>
        <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
          <button type="button" onClick={onClose} disabled={saving} style={S.secondaryButton}>Cancelar</button>
          <button type="button" onClick={confirm} disabled={saving} style={{...S.button, opacity: saving ? 0.7 : 1}}>
            {saving ? 'Guardando...' : 'Confirmar entregado'}
          </button>
        </div>
      </div>
    </div>
  );
}

// "No se compra" desde Revisión F2: doble confirmación y borrado definitivo del vehículo.
function CancelPurchaseModal({ vehicle, onClose, onDone }) {
  const [step, setStep] = useState('form'); // form -> confirm
  const [notifiedCustomer, setNotifiedCustomer] = useState(false);
  const [uploadedCrm, setUploadedCrm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const canContinue = notifiedCustomer && uploadedCrm;

  const doCancel = async () => {
    setSaving(true);
    setMessage('');
    const { error } = await supabase.rpc('cancel_vehicle_purchase', {
      p_vehicle_id: vehicle.id,
      p_notified_customer: notifiedCustomer,
      p_uploaded_f2_crm: uploadedCrm
    });
    if (error) { setMessage('Error: ' + error.message); setSaving(false); return; }
    onDone();
  };

  const checkboxRow = {display: 'flex', gap: '8px', alignItems: 'flex-start', color: 'var(--t-e5e7eb)', fontSize: '0.875rem', cursor: 'pointer'};

  return (
    <div style={S.overlay}>
      <div style={{...S.modal, maxWidth: '440px'}}>
        <button onClick={onClose} style={S.closeBtn}>✕</button>
        <h3 style={S.title}>❌ No se compra</h3>
        <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem', marginBottom: '16px'}}>
          {vehicle.brand} {vehicle.model} ({vehicle.plate})
        </p>
        {message && (
          <div style={{backgroundColor: 'var(--b-7f1d1d)', color: 'var(--t-f1f5f9)', padding: '10px', borderRadius: '6px', marginBottom: '16px', fontSize: '0.875rem'}}>
            {message}
          </div>
        )}

        {step === 'form' ? (
          <>
            <p style={{color: 'var(--t-fecaca)', fontSize: '0.875rem', marginBottom: '16px'}}>
              ⚠️ Al confirmar, el vehículo se eliminará definitivamente junto con todos sus datos (comentarios, historial, revisión F2…).
              No se podrá recuperar: si hiciera falta, habría que crearlo de nuevo.
            </p>
            <div style={{display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px'}}>
              <label style={checkboxRow}>
                <input id="cancel-notified-customer" type="checkbox" checked={notifiedCustomer} onChange={e => setNotifiedCustomer(e.target.checked)} style={{marginTop: '3px'}} />
                {isFuera(vehicle) ? 'Se ha avisado a la tienda de origen de que no se compra.' : 'Se ha avisado al cliente de que pase a recoger el vehículo.'}
              </label>
              <label style={checkboxRow}>
                <input id="cancel-uploaded-crm" type="checkbox" checked={uploadedCrm} onChange={e => setUploadedCrm(e.target.checked)} style={{marginTop: '3px'}} />
                Se ha subido la F2 al CRM.
              </label>
            </div>
            <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
              <button type="button" onClick={onClose} style={S.secondaryButton}>Cancelar</button>
              <button type="button" onClick={() => setStep('confirm')} disabled={!canContinue} style={{...S.button, backgroundColor: 'var(--b-991b1b)', opacity: canContinue ? 1 : 0.5}}>
                Continuar
              </button>
            </div>
          </>
        ) : (
          <>
            <p style={{color: 'var(--t-fecaca)', fontSize: '0.9rem', fontWeight: 600, marginBottom: '20px'}}>
              ¿Seguro? Esta es la confirmación definitiva: el vehículo y todos sus datos se borrarán para siempre.
            </p>
            <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
              <button type="button" onClick={() => setStep('form')} disabled={saving} style={S.secondaryButton}>Volver</button>
              <button type="button" onClick={doCancel} disabled={saving} style={{...S.button, backgroundColor: 'var(--b-991b1b)', opacity: saving ? 0.7 : 1}}>
                {saving ? 'Eliminando...' : 'Sí, eliminar definitivamente'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function DeliverVehicleModal({ vehicle, orgId, currentUserId, onClose, onDelivered }) {
  const [step, setStep] = useState('checklist'); // 'checklist' | 'operation'
  const [checked, setChecked] = useState({});
  const [operationMode, setOperationMode] = useState(null); // 'mine' | 'colleague' | 'cross'
  const [colleagues, setColleagues] = useState([]);
  const [loadingColleagues, setLoadingColleagues] = useState(false);
  const [colleagueId, setColleagueId] = useState('');
  const [insurance, setInsurance] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const allChecked = DELIVERY_CHECKLIST_ITEMS.every(item => checked[item.key]);

  const loadColleagues = async () => {
    setLoadingColleagues(true);
    const { data } = await supabase
      .from('profiles')
      .select('id, full_name, role')
      .eq('org_id', orgId)
      .in('role', ['comercial', 'captador'])
      .neq('id', currentUserId)
      .order('full_name');
    setColleagues(data || []);
    setLoadingColleagues(false);
  };

  const goToOperationStep = () => {
    setStep('operation');
    loadColleagues();
  };

  const confirmDelivery = async () => {
    const deliveredBy = operationMode === 'mine' ? currentUserId : operationMode === 'colleague' ? colleagueId : null;
    if (operationMode !== 'cross' && !deliveredBy) return;
    if (insurance === null) return;
    setSaving(true);
    setMessage('');
    const { error: insuranceError } = await supabase.rpc('set_commission_insurance_for_vehicle', {
      p_vehicle_id: vehicle.id,
      p_insurance: insurance
    });
    if (insuranceError) {
      setMessage('Error al guardar el seguro: ' + insuranceError.message);
      setSaving(false);
      return;
    }
    const { error } = await supabase.rpc('mark_vehicle_delivered', {
      p_vehicle_id: vehicle.id,
      p_delivered_by: deliveredBy
    });
    if (error) {
      setMessage('Error al confirmar la entrega: ' + error.message);
      setSaving(false);
    } else {
      onDelivered();
    }
  };

  return (
    <div style={S.overlay}>
      <div style={{...S.modal, maxWidth: '480px'}}>
        <button onClick={onClose} style={S.closeBtn}>✕</button>
        <h3 style={S.title}>Confirmar entrega</h3>
        <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem', marginBottom: '16px'}}>
          {vehicle.brand} {vehicle.model} ({vehicle.plate})
        </p>

        {message && (
          <div style={{backgroundColor: 'var(--b-7f1d1d)', color: 'var(--t-f1f5f9)', padding: '10px', borderRadius: '6px', marginBottom: '16px', fontSize: '0.875rem'}}>
            {message}
          </div>
        )}

        {step === 'checklist' ? (
          <>
            <div style={{display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px'}}>
              {DELIVERY_CHECKLIST_ITEMS.map(item => (
                <div
                  key={item.key}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    backgroundColor: 'var(--b-334155)', padding: '10px 12px', borderRadius: '6px'
                  }}
                >
                  <span style={{color: 'var(--t-e5e7eb)', fontSize: '0.875rem'}}>{item.label}</span>
                  <button
                    type="button"
                    onClick={() => setChecked(c => ({...c, [item.key]: !c[item.key]}))}
                    style={{
                      backgroundColor: checked[item.key] ? 'var(--b-166534)' : 'transparent',
                      color: checked[item.key] ? 'var(--t-4ade80)' : 'var(--t-9ca3af)',
                      border: `1px solid ${checked[item.key] ? 'var(--l-166534)' : 'var(--l-4b5563)'}`,
                      borderRadius: '6px', padding: '4px 10px', cursor: 'pointer', fontSize: '0.8rem', flexShrink: 0
                    }}
                  >
                    {checked[item.key] ? '✓ Hecho' : 'Marcar'}
                  </button>
                </div>
              ))}
            </div>
            <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
              <button type="button" onClick={onClose} style={S.secondaryButton}>Cancelar</button>
              <button type="button" onClick={goToOperationStep} disabled={!allChecked} style={{...S.button, opacity: allChecked ? 1 : 0.5}}>
                Continuar
              </button>
            </div>
          </>
        ) : (
          <>
            <p style={{color: 'var(--t-e5e7eb)', fontSize: '0.875rem', marginBottom: '12px'}}>
              ¿Esta entrega es tuya o estás ayudando a un compañero al que se le computa la operación?
            </p>
            <div style={{display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap'}}>
              <button
                type="button"
                onClick={() => setOperationMode('mine')}
                style={{...S.secondaryButton, flex: 1, minWidth: '120px', backgroundColor: operationMode === 'mine' ? 'var(--b-334155)' : 'transparent'}}
              >
                Es mía
              </button>
              <button
                type="button"
                onClick={() => setOperationMode('colleague')}
                style={{...S.secondaryButton, flex: 1, minWidth: '120px', backgroundColor: operationMode === 'colleague' ? 'var(--b-334155)' : 'transparent'}}
              >
                Es de un compañero
              </button>
              <button
                type="button"
                onClick={() => setOperationMode('cross')}
                style={{...S.secondaryButton, flex: 1, minWidth: '120px', backgroundColor: operationMode === 'cross' ? 'var(--b-334155)' : 'transparent'}}
              >
                Cruzada (otra tienda)
              </button>
            </div>

            {operationMode === 'colleague' && (
              <div style={{marginBottom: '16px'}}>
                <label style={S.label}>Compañero que recibe la operación</label>
                {loadingColleagues ? (
                  <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem'}}>Cargando usuarios...</p>
                ) : colleagues.length === 0 ? (
                  <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem'}}>No hay otros usuarios comercial/captador en la organización.</p>
                ) : (
                  <select style={S.select} value={colleagueId} onChange={e => setColleagueId(e.target.value)}>
                    <option value="">Selecciona un usuario...</option>
                    {colleagues.map(c => (
                      <option key={c.id} value={c.id}>{c.full_name || 'Sin nombre'} ({roleLabel(c.role)})</option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {operationMode === 'cross' && (
              <p style={{color: 'var(--t-9ca3af)', fontSize: '0.8rem', marginBottom: '16px'}}>
                Esta entrega no se computará a ningún usuario de la app.
              </p>
            )}

            <label style={S.label}>🛡️ ¿El cliente ha contratado seguro?</label>
            <div style={{display: 'flex', gap: '8px', marginBottom: '16px'}}>
              {[{ v: true, l: 'Sí' }, { v: false, l: 'No' }].map(o => (
                <button
                  key={o.l}
                  type="button"
                  onClick={() => setInsurance(o.v)}
                  style={{...S.secondaryButton, flex: 1, backgroundColor: insurance === o.v ? 'var(--b-334155)' : 'transparent'}}
                >
                  {o.l}
                </button>
              ))}
            </div>

            <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
              <button type="button" onClick={() => setStep('checklist')} style={S.secondaryButton}>Atrás</button>
              <button
                type="button"
                onClick={confirmDelivery}
                disabled={saving || !operationMode || (operationMode === 'colleague' && !colleagueId) || insurance === null}
                style={{...S.button, opacity: saving ? 0.7 : 1}}
              >
                {saving ? 'Confirmando...' : 'Confirmar entrega'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const METRICS_TABS = [
  { key: 'tiempos', label: '⏱️ Tiempos' },
  { key: 'ventas', label: '💰 Ventas' }
];

const MONTH_NAMES_ES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const MONTH_SHORT_ES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

function monthKeyLabel(key) {
  const [y, m] = key.split('-');
  return `${MONTH_NAMES_ES[Number(m) - 1]} ${y}`;
}

function monthKeyShortLabel(key) {
  const [y, m] = key.split('-');
  return `${MONTH_SHORT_ES[Number(m) - 1]} ${y.slice(2)}`;
}

// Barra horizontal simple (sin dependencias externas) para las secciones de métricas.
function HorizontalBar({ label, sublabel, value, max, color }) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 2;
  return (
    <div style={{marginBottom: '10px'}}>
      <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: '3px'}}>
        <span style={{color: 'var(--t-e5e7eb)'}}>{label}</span>
        <span style={{color: 'var(--t-9ca3af)'}}>{sublabel}</span>
      </div>
      <div style={{backgroundColor: 'var(--b-374151)', borderRadius: '4px', height: '10px', overflow: 'hidden'}}>
        <div style={{width: `${pct}%`, height: '100%', backgroundColor: color || '#3b82f6', borderRadius: '4px', transition: 'width 0.2s'}}></div>
      </div>
    </div>
  );
}

// Gráfico de barras verticales por mes, clicable para ver el detalle de un mes concreto.
function MonthlyBarChart({ monthKeysAsc, byMonth, selectedMonth, onSelectMonth }) {
  const maxCount = Math.max(1, ...monthKeysAsc.map(k => byMonth[k].total));
  return (
    <div style={{display: 'flex', alignItems: 'flex-end', gap: '10px', height: '140px', marginBottom: '8px', overflowX: 'auto', paddingBottom: '4px'}}>
      {monthKeysAsc.map(key => {
        const h = Math.max(6, Math.round((byMonth[key].total / maxCount) * 110));
        const isSelected = key === selectedMonth;
        return (
          <div
            key={key}
            onClick={() => onSelectMonth(key)}
            title={`${monthKeyLabel(key)}: ${byMonth[key].total} entregas`}
            style={{display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', minWidth: '36px', flexShrink: 0}}
          >
            <div style={{fontSize: '0.7rem', color: isSelected ? 'var(--t-f59e0b)' : 'var(--t-f1f5f9)', fontWeight: isSelected ? 700 : 400, marginBottom: '3px'}}>
              {byMonth[key].total}
            </div>
            <div style={{
              width: '26px', height: `${h}px`,
              backgroundColor: isSelected ? 'var(--b-f59e0b)' : '#3b82f6',
              borderRadius: '4px 4px 0 0', transition: 'background-color 0.15s'
            }}></div>
            <div style={{fontSize: '0.65rem', color: isSelected ? 'var(--t-f59e0b)' : 'var(--t-9ca3af)', marginTop: '4px', textAlign: 'center'}}>
              {monthKeyShortLabel(key)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PrepSheetModal({ vehicle, sheet, role, onClose, onRefresh }) {
  const [expanded, setExpanded] = useState(() => new Set(PREP_SECTIONS.map(s => s.key)));
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [newItemText, setNewItemText] = useState({ mecanica: '', pintura: '', observaciones: '' });
  const canEditSheet = PREP_SHEET_EDIT_ROLES.includes(role);

  const toggleExpanded = (key) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleTask = async (section, index, done) => {
    setSaving(true);
    const { error } = await supabase.rpc('toggle_prep_task', {
      p_vehicle_id: vehicle.id, p_section: section, p_index: index, p_done: done
    });
    if (!error) onRefresh();
    setSaving(false);
  };

  const addItem = async (section) => {
    const text = (newItemText[section] || '').trim();
    if (!text) return;
    setSaving(true);
    const { error } = await supabase.rpc('add_prep_sheet_item', {
      p_vehicle_id: vehicle.id, p_section: section, p_text: text
    });
    if (!error) {
      setNewItemText(t => ({...t, [section]: ''}));
      onRefresh();
    }
    setSaving(false);
  };

  const markItvNeeded = async () => {
    setSaving(true);
    const { error } = await supabase.rpc('set_prep_sheet_itv_needed', {
      p_vehicle_id: vehicle.id, p_needed: true
    });
    if (!error) onRefresh();
    setSaving(false);
  };

  const textStyle = (done) => ({
    color: done ? 'var(--t-6b7280)' : 'var(--t-e5e7eb)',
    textDecoration: done ? 'line-through' : 'none',
    fontSize: '0.85rem'
  });

  return (
    <div style={S.overlay}>
      <div style={{...S.modal, maxWidth: '520px'}}>
        <button onClick={onClose} style={S.closeBtn}>✕</button>
        <h3 style={S.title}>Hoja de preparación</h3>
        <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem', marginBottom: '16px'}}>
          {vehicle.brand} {vehicle.model} ({vehicle.plate})
        </p>
        {!canEditSheet && (
          <p style={{color: 'var(--t-6b7280)', fontSize: '0.75rem', marginBottom: '16px'}}>
            Solo el encargado de preparación, el responsable o el creador pueden marcar tareas como hechas.
          </p>
        )}

        {PREP_SECTIONS.map(({ key, label }) => {
          const complete = prepSheetSectionComplete(sheet, key);
          const isOpen = expanded.has(key);
          return (
            <div key={key} style={{backgroundColor: 'var(--b-111827)', borderRadius: '8px', marginBottom: '10px', overflow: 'hidden'}}>
              <div
                onClick={() => toggleExpanded(key)}
                style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', cursor: 'pointer'}}
              >
                <span style={{...textStyle(complete), fontWeight: 700, fontSize: '0.9rem'}}>
                  {complete ? '✅' : '⬜'} {label}
                </span>
                <span style={{color: 'var(--t-6b7280)', fontSize: '0.75rem'}}>{isOpen ? '▲' : '▼'}</span>
              </div>

              {isOpen && (
                <div style={{padding: '0 14px 14px'}}>
                  {key === 'itv' && (
                    !sheet.itv_needed ? (
                      editMode && canEditSheet ? (
                        <button type="button" onClick={markItvNeeded} disabled={saving} style={{...S.secondaryButton, padding: '4px 10px', fontSize: '0.75rem'}}>
                          Marcar ITV como necesaria
                        </button>
                      ) : (
                        <p style={{color: 'var(--t-6b7280)', fontSize: '0.8rem', margin: 0}}>No aplica</p>
                      )
                    ) : (
                      <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: canEditSheet ? 'pointer' : 'default'}}>
                        <input type="checkbox" checked={sheet.itv_done} disabled={!canEditSheet || saving} onChange={e => toggleTask('itv', null, e.target.checked)} />
                        <span style={textStyle(sheet.itv_done)}>Pasar ITV</span>
                      </label>
                    )
                  )}

                  {key === 'mecanica' && (
                    <>
                      {(sheet.mecanica || []).length === 0 ? (
                        <p style={{color: 'var(--t-6b7280)', fontSize: '0.8rem', margin: 0}}>Sin tareas</p>
                      ) : (
                        <div style={{display: 'flex', flexDirection: 'column', gap: '6px'}}>
                          {sheet.mecanica.map((item, i) => (
                            <label key={i} style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: canEditSheet ? 'pointer' : 'default'}}>
                              <input type="checkbox" checked={!!item.done} disabled={!canEditSheet || saving} onChange={e => toggleTask('mecanica', i, e.target.checked)} />
                              <span style={textStyle(item.done)}>{item.text}</span>
                            </label>
                          ))}
                        </div>
                      )}
                      {editMode && canEditSheet && (
                        <div style={{display: 'flex', gap: '6px', marginTop: '10px', borderTop: '1px solid var(--l-374151)', paddingTop: '10px'}}>
                          <input
                            type="text"
                            style={{...S.input, marginBottom: 0, flex: 1}}
                            placeholder="Nueva tarea de mecánica"
                            value={newItemText.mecanica}
                            onChange={e => setNewItemText(t => ({...t, mecanica: e.target.value}))}
                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addItem('mecanica'); } }}
                          />
                          <button type="button" onClick={() => addItem('mecanica')} disabled={saving || !newItemText.mecanica.trim()} style={{...S.secondaryButton, padding: '4px 10px', fontSize: '0.75rem'}}>
                            Añadir
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  {key === 'pintura' && (
                    <>
                      {(sheet.pintura || []).length === 0 ? (
                        <p style={{color: 'var(--t-6b7280)', fontSize: '0.8rem', margin: 0}}>Sin elementos</p>
                      ) : (
                        <>
                          <div style={{display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '10px'}}>
                            {sheet.pintura.map((item, i) => (
                              <div key={i} style={textStyle(sheet.pintura_done)}>• {item.text}</div>
                            ))}
                          </div>
                          <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: canEditSheet ? 'pointer' : 'default', borderTop: '1px solid var(--l-374151)', paddingTop: '10px'}}>
                            <input type="checkbox" checked={sheet.pintura_done} disabled={!canEditSheet || saving} onChange={e => toggleTask('pintura', null, e.target.checked)} />
                            <span style={textStyle(sheet.pintura_done)}>Pintura completada</span>
                          </label>
                        </>
                      )}
                      {editMode && canEditSheet && (
                        <div style={{display: 'flex', gap: '6px', marginTop: '10px', borderTop: (sheet.pintura || []).length === 0 ? 'none' : '1px solid var(--l-374151)', paddingTop: (sheet.pintura || []).length === 0 ? 0 : '10px'}}>
                          <input
                            type="text"
                            style={{...S.input, marginBottom: 0, flex: 1}}
                            placeholder="Nuevo elemento de pintura"
                            value={newItemText.pintura}
                            onChange={e => setNewItemText(t => ({...t, pintura: e.target.value}))}
                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addItem('pintura'); } }}
                          />
                          <button type="button" onClick={() => addItem('pintura')} disabled={saving || !newItemText.pintura.trim()} style={{...S.secondaryButton, padding: '4px 10px', fontSize: '0.75rem'}}>
                            Añadir
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  {key === 'observaciones' && (
                    <>
                      {(sheet.observaciones || []).length === 0 ? (
                        <p style={{color: 'var(--t-6b7280)', fontSize: '0.8rem', margin: 0}}>Sin observaciones</p>
                      ) : (
                        <div style={{display: 'flex', flexDirection: 'column', gap: '6px'}}>
                          {sheet.observaciones.map((item, i) => (
                            <label key={i} style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: canEditSheet ? 'pointer' : 'default'}}>
                              <input type="checkbox" checked={!!item.done} disabled={!canEditSheet || saving} onChange={e => toggleTask('observaciones', i, e.target.checked)} />
                              <span style={textStyle(item.done)}>{item.text}</span>
                            </label>
                          ))}
                        </div>
                      )}
                      {editMode && canEditSheet && (
                        <div style={{display: 'flex', gap: '6px', marginTop: '10px', borderTop: (sheet.observaciones || []).length === 0 ? 'none' : '1px solid var(--l-374151)', paddingTop: (sheet.observaciones || []).length === 0 ? 0 : '10px'}}>
                          <input
                            type="text"
                            style={{...S.input, marginBottom: 0, flex: 1}}
                            placeholder="Nueva observación"
                            value={newItemText.observaciones}
                            onChange={e => setNewItemText(t => ({...t, observaciones: e.target.value}))}
                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addItem('observaciones'); } }}
                          />
                          <button type="button" onClick={() => addItem('observaciones')} disabled={saving || !newItemText.observaciones.trim()} style={{...S.secondaryButton, padding: '4px 10px', fontSize: '0.75rem'}}>
                            Añadir
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}

        <div style={{display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px'}}>
          {canEditSheet && (
            <button onClick={() => setEditMode(e => !e)} style={{...S.secondaryButton, backgroundColor: editMode ? 'var(--b-334155)' : 'transparent'}}>
              {editMode ? '✓ Terminar edición' : '✏️ Editar'}
            </button>
          )}
          <button onClick={onClose} style={S.secondaryButton}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

function MetricsPanel({ vehicles, columns, orgId, onClose }) {
  const [tab, setTab] = useState('tiempos');
  const [deliveries, setDeliveries] = useState([]);
  const [loadingDeliveries, setLoadingDeliveries] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(null);

  useEffect(() => {
    supabase
      .from('vehicle_deliveries')
      .select('id, brand, model, plate, delivered_at, delivered_by, is_professional_sale, profiles:delivered_by(full_name)')
      .eq('org_id', orgId)
      .order('delivered_at', { ascending: false })
      .then(({ data }) => {
        setDeliveries(data || []);
        setLoadingDeliveries(false);
      });
  }, [orgId]);

  const avgDays = vehicles.length ? Math.round(vehicles.reduce((sum, v) => sum + daysInColumn(v), 0) / vehicles.length) : 0;
  const maxColCount = Math.max(1, ...columns.map(col => vehicles.filter(v => v.column_id === col.id).length));

  const perColumn = columns.map(col => {
    const colVehicles = vehicles.filter(v => v.column_id === col.id);
    const avg = colVehicles.length ? Math.round(colVehicles.reduce((s, v) => s + daysInColumn(v), 0) / colVehicles.length) : 0;
    return { col, count: colVehicles.length, avgDays: avg };
  });

  const oldest = [...vehicles].sort((a, b) => daysInColumn(b) - daysInColumn(a)).slice(0, 5);
  const maxOldestDays = Math.max(1, ...oldest.map(daysInColumn));

  const byMonth = {};
  deliveries.forEach(d => {
    const key = (d.delivered_at || '').slice(0, 7);
    if (!key) return;
    if (!byMonth[key]) byMonth[key] = { total: 0, byUser: {} };
    byMonth[key].total += 1;
    const name = deliveryAuthorLabel(d);
    byMonth[key].byUser[name] = (byMonth[key].byUser[name] || 0) + 1;
  });
  const monthKeysAsc = Object.keys(byMonth).sort();

  useEffect(() => {
    if (!selectedMonth && monthKeysAsc.length > 0) setSelectedMonth(monthKeysAsc[monthKeysAsc.length - 1]);
  }, [deliveries]);

  const monthDetail = selectedMonth ? byMonth[selectedMonth] : null;
  const monthDeliveries = selectedMonth
    ? deliveries.filter(d => (d.delivered_at || '').slice(0, 7) === selectedMonth)
    : [];

  return (
    <div style={S.overlay}>
      <div style={{...S.modal, maxWidth: '640px'}}>
        <button onClick={onClose} style={S.closeBtn}>✕</button>
        <h3 style={S.title}>Métricas</h3>

        <div style={{display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: '1px solid var(--l-374151)'}}>
          {METRICS_TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: '8px 4px',
                color: tab === t.key ? 'var(--t-f59e0b)' : 'var(--t-9ca3af)',
                fontWeight: tab === t.key ? 700 : 400, fontSize: '0.9rem',
                borderBottom: tab === t.key ? '2px solid var(--l-f59e0b)' : '2px solid transparent',
                marginBottom: '-1px'
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'tiempos' ? (
          <>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '24px'}}>
              <div style={{backgroundColor: 'var(--b-374151)', padding: '12px', borderRadius: '8px', textAlign: 'center'}}>
                <div style={{color: 'var(--t-9ca3af)', fontSize: '0.75rem'}}>Vehículos</div>
                <div style={{color: 'var(--t-f1f5f9)', fontSize: '1.25rem', fontWeight: 700}}>{vehicles.length}</div>
              </div>
              <div style={{backgroundColor: 'var(--b-374151)', padding: '12px', borderRadius: '8px', textAlign: 'center'}}>
                <div style={{color: 'var(--t-9ca3af)', fontSize: '0.75rem'}}>Días medios en columna</div>
                <div style={{color: 'var(--t-f1f5f9)', fontSize: '1.25rem', fontWeight: 700}}>{avgDays}</div>
              </div>
            </div>

            <h4 style={{color: 'var(--t-f1f5f9)', fontSize: '0.95rem', marginBottom: '10px'}}>Por columna</h4>
            <div style={{marginBottom: '20px'}}>
              {perColumn.map(({ col, count, avgDays }) => (
                <HorizontalBar
                  key={col.id}
                  label={col.label}
                  sublabel={`${count} veh. · ${avgDays}d media`}
                  value={count}
                  max={maxColCount}
                  color={col.color}
                />
              ))}
            </div>

            <h4 style={{color: 'var(--t-f1f5f9)', fontSize: '0.95rem', marginBottom: '10px'}}>Más tiempo en columna</h4>
            <div>
              {oldest.length === 0 ? (
                <p style={{color: 'var(--t-9ca3af)', fontStyle: 'italic', fontSize: '0.875rem'}}>Sin vehículos</p>
              ) : (
                oldest.map(v => (
                  <HorizontalBar
                    key={v.id}
                    label={`${v.brand} ${v.model} (${v.plate})`}
                    sublabel={timeInColumnLabel(v)}
                    value={daysInColumn(v)}
                    max={maxOldestDays}
                    color={daysInColumn(v) > 30 ? '#ef4444' : '#3b82f6'}
                  />
                ))
              )}
            </div>
          </>
        ) : (
          <>
            {loadingDeliveries ? (
              <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem'}}>Cargando entregas...</p>
            ) : monthKeysAsc.length === 0 ? (
              <p style={{color: 'var(--t-9ca3af)', fontStyle: 'italic', fontSize: '0.875rem'}}>Todavía no hay entregas registradas</p>
            ) : (
              <>
                <h4 style={{color: 'var(--t-f1f5f9)', fontSize: '0.95rem', marginBottom: '8px'}}>Entregas por mes</h4>
                <p style={{color: 'var(--t-6b7280)', fontSize: '0.75rem', marginTop: 0, marginBottom: '8px'}}>Pincha en un mes para ver el detalle</p>
                <MonthlyBarChart monthKeysAsc={monthKeysAsc} byMonth={byMonth} selectedMonth={selectedMonth} onSelectMonth={setSelectedMonth} />

                {monthDetail && (
                  <div style={{backgroundColor: 'var(--b-111827)', borderRadius: '8px', padding: '14px', marginTop: '12px', marginBottom: '8px'}}>
                    <h5 style={{margin: '0 0 10px', color: 'var(--t-f1f5f9)', fontSize: '0.9rem', textTransform: 'capitalize'}}>
                      {monthKeyLabel(selectedMonth)} · {monthDetail.total} entrega{monthDetail.total === 1 ? '' : 's'}
                    </h5>

                    <div style={{marginBottom: '12px'}}>
                      {Object.entries(monthDetail.byUser).sort((a, b) => b[1] - a[1]).map(([name, count]) => (
                        <div key={name} style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', padding: '3px 0'}}>
                          <span style={{color: 'var(--t-e5e7eb)'}}>{name}</span>
                          <span style={{color: 'var(--t-9ca3af)'}}>{count} ({Math.round((count / monthDetail.total) * 100)}%)</span>
                        </div>
                      ))}
                    </div>

                    <div style={{borderTop: '1px solid var(--l-374151)', paddingTop: '10px', display: 'flex', flexDirection: 'column', gap: '4px'}}>
                      {monthDeliveries.map(d => (
                        <div key={d.id} style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem'}}>
                          <span style={{color: 'var(--t-e5e7eb)'}}>{d.brand} {d.model} ({d.plate})</span>
                          <span style={{color: 'var(--t-9ca3af)'}}>{deliveryAuthorLabel(d)} · {new Date(d.delivered_at).toLocaleDateString('es-ES')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ManageKanbanCols({ columns, onClose, orgId, onRefresh }) {
  const [newLabel, setNewLabel] = useState('');
  const [newColor, setNewColor] = useState('#6B7280');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const addColumn = async (e) => {
    e.preventDefault();
    if (!newLabel.trim()) return;
    setSaving(true);
    setMessage('');
    const { error } = await supabase.from('columns').insert([{
      org_id: orgId,
      label: newLabel.trim(),
      color: newColor,
      position: columns.length
    }]);
    if (error) {
      setMessage('Error al crear columna: ' + error.message);
    } else {
      setNewLabel('');
      onRefresh();
    }
    setSaving(false);
  };

  const updateColumn = async (col, changes) => {
    const { error } = await supabase.from('columns').update(changes).eq('id', col.id);
    if (!error) onRefresh();
  };

  const deleteColumn = async (col) => {
    if (!window.confirm(`¿Eliminar la columna "${col.label}"? Los vehículos que tenga deben moverse antes.`)) return;
    const { error } = await supabase.from('columns').delete().eq('id', col.id);
    if (error) {
      setMessage('Error al eliminar: ' + error.message);
    } else {
      onRefresh();
    }
  };

  const move = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= columns.length) return;
    const a = columns[index];
    const b = columns[target];
    await Promise.all([
      supabase.from('columns').update({ position: b.position }).eq('id', a.id),
      supabase.from('columns').update({ position: a.position }).eq('id', b.id)
    ]);
    onRefresh();
  };

  return (
    <div style={S.overlay}>
      <div style={{...S.modal, maxWidth: '520px'}}>
        <button onClick={onClose} style={S.closeBtn}>✕</button>
        <h3 style={S.title}>Gestionar Columnas</h3>

        {message && (
          <div style={{backgroundColor: 'var(--b-7f1d1d)', color: 'var(--t-f1f5f9)', padding: '10px', borderRadius: '6px', marginBottom: '16px', fontSize: '0.875rem'}}>
            {message}
          </div>
        )}

        <div style={{marginBottom: '20px'}}>
          {columns.map((col, index) => (
            <div key={col.id} style={{backgroundColor: 'var(--b-374151)', padding: '10px', borderRadius: '6px', marginBottom: '8px'}}>
              <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px'}}>
                <input type="color" value={col.color || '#6B7280'} onChange={(e) => updateColumn(col, { color: e.target.value })} style={{width: '28px', height: '28px', border: 'none', background: 'none', cursor: 'pointer'}} />
                <input
                  defaultValue={col.label}
                  onBlur={(e) => { if (e.target.value.trim() && e.target.value !== col.label) updateColumn(col, { label: e.target.value.trim() }); }}
                  style={{...S.input, marginBottom: 0, flex: 1}}
                />
                <button onClick={() => move(index, -1)} disabled={index === 0} style={{...S.secondaryButton, padding: '6px 10px', opacity: index === 0 ? 0.4 : 1}}>↑</button>
                <button onClick={() => move(index, 1)} disabled={index === columns.length - 1} style={{...S.secondaryButton, padding: '6px 10px', opacity: index === columns.length - 1 ? 0.4 : 1}}>↓</button>
                <button onClick={() => deleteColumn(col)} style={{backgroundColor: 'var(--b-7f1d1d)', color: '#fff', border: 'none', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer'}}>🗑️</button>
              </div>
              <div style={{display: 'flex', alignItems: 'center', gap: '8px', paddingLeft: '36px'}}>
                <label style={{color: 'var(--t-9ca3af)', fontSize: '0.75rem', whiteSpace: 'nowrap'}}>⚠️ Alertar a partir de</label>
                <input
                  type="number"
                  min="1"
                  defaultValue={col.alert_days ?? ''}
                  placeholder="sin límite"
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    const parsed = v ? Number(v) : null;
                    if (parsed !== (col.alert_days ?? null)) updateColumn(col, { alert_days: parsed });
                  }}
                  style={{...S.input, marginBottom: 0, width: '80px', padding: '6px 8px'}}
                />
                <span style={{color: 'var(--t-9ca3af)', fontSize: '0.75rem'}}>días</span>
              </div>
            </div>
          ))}
        </div>

        <form onSubmit={addColumn} style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
          <input type="color" value={newColor} onChange={(e) => setNewColor(e.target.value)} style={{width: '28px', height: '28px', border: 'none', background: 'none', cursor: 'pointer'}} />
          <input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="Nueva columna..."
            style={{...S.input, marginBottom: 0, flex: 1}}
          />
          <button type="submit" disabled={saving || !newLabel.trim()} style={{...S.button, opacity: saving ? 0.7 : 1}}>+ Añadir</button>
        </form>
      </div>
    </div>
  );
}

// Tipos de aviso por email que cada usuario puede activar o desactivar (tabla notification_settings;
// sin fila = todos activados). Invitaciones y cambios de rol se envían siempre.
const NOTIFICATION_TYPES = [
  { key: 'movimientos', label: '🚗 Movimientos entre columnas', help: 'Un aviso por movimiento: de qué columna sale y a cuál entra. Cuando un coche entra en una de tus columnas asignadas, el aviso te llega siempre.' },
  { key: 'comentarios', label: '💬 Comentarios', help: 'Comentarios nuevos escritos por otros usuarios.' },
  { key: 'alertas', label: '⚠️ Alertas de tiempo', help: 'Sección del resumen diario de las 10:00 con los coches en alerta que sigues o de tus columnas.' },
  { key: 'ediciones', label: '✏️ Ediciones de vehículos', help: 'Cambios en los datos de un vehículo (marca, precio, km...).' },
  { key: 'ventas', label: '🧑‍💼 Asignación de ventas', help: 'Cuando se te asigna o se te quita una venta.' },
  { key: 'compras', label: '🛒 Asignación de compras', help: 'Cuando se te asigna o se te quita una compra.' },
  { key: 'preparacion', label: '🔧 Preparación', help: 'Secciones de la hoja de preparación completadas y coche listo para recoger.' },
  { key: 'entregas', label: '🗓️ Entregas', help: 'Sección del resumen diario de las 10:00: entregas de hoy y del día siguiente, y coches sin fecha (los tuyos si tienes la venta asignada).',
    roles: ['logistica', 'encargado_preparacion', 'comercial', 'captador'] },
  { key: 'leads', label: '📇 Leads', help: 'Leads nuevos en la Bandeja, leads que te asignan y, si supervisas, quién coge cada lead. Solo si tienes el panel de leads activado.',
    roles: ['creador', 'responsable', 'comercial', 'captador'] }
];

// Código de seguridad de los correos: aparece arriba en cada correo de EnMarcha (antiphishing).
function securityCodeError(code) {
  if (!code) return 'El código de seguridad es obligatorio.';
  if (code.length < 4 || code.length > 40 || /[<>&"']/.test(code)) return 'Usa entre 4 y 40 caracteres, sin < > & " \'.';
  return null;
}

// Pantalla obligatoria la primera vez que alguien entra sin código de seguridad: no se puede seguir sin elegirlo.
function SecurityCodeRequiredModal({ currentUserId, onSaved, onSignOut }) {
  const [code, setCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async (e) => {
    e.preventDefault();
    const value = code.trim();
    const invalid = securityCodeError(value);
    if (invalid) { setError(invalid); return; }
    setSaving(true);
    setError('');
    const { error: err } = await supabase.from('notification_settings')
      .upsert({ profile_id: currentUserId, antiphishing_code: value, updated_at: new Date().toISOString() }, { onConflict: 'profile_id' });
    setSaving(false);
    if (err) { setError('Error al guardar: ' + err.message); return; }
    onSaved();
  };

  return (
    <div style={{...S.overlay, backgroundColor: 'var(--b-0f172a)'}}>
      <div style={{...S.modal, maxWidth: '480px'}}>
        <h3 style={S.title}>🔐 Elige tu código de seguridad</h3>
        <p style={{color: 'var(--t-d1d5db)', fontSize: '0.875rem', lineHeight: 1.5, margin: '0 0 10px'}}>
          Para protegerte de correos falsos que se hacen pasar por EnMarcha para robar contraseñas (<em>phishing</em>),
          todos los correos que te enviemos llevarán arriba un código que solo conoces tú.
        </p>
        <p style={{color: 'var(--t-d1d5db)', fontSize: '0.875rem', lineHeight: 1.5, margin: '0 0 14px'}}>
          Si un correo dice ser de EnMarcha y no muestra tu código, no es nuestro: no pulses sus enlaces.
          Es obligatorio elegirlo para continuar.
        </p>
        <form onSubmit={save}>
          <label style={S.label} htmlFor="required-security-code">Tu código de seguridad</label>
          <input id="required-security-code" style={S.input} value={code} maxLength={40} autoFocus autoComplete="off"
            onChange={e => setCode(e.target.value)} placeholder="Ej.: girasol azul 27" />
          <p style={{color: 'var(--t-9ca3af)', fontSize: '0.75rem', margin: '-4px 0 12px'}}>
            Una palabra o frase de 4 a 40 caracteres. No uses tu contraseña. Podrás cambiarlo en ⚙️ Mis notificaciones.
          </p>
          {error && <p style={{color: 'var(--t-fca5a5)', fontSize: '0.8rem', margin: '0 0 12px'}}>{error}</p>}
          <div style={{display: 'flex', gap: '12px', justifyContent: 'space-between', alignItems: 'center'}}>
            <button type="button" onClick={onSignOut} style={S.secondaryButton}>Cerrar sesión</button>
            <button type="submit" disabled={saving || !code.trim()} style={{...S.button, opacity: (saving || !code.trim()) ? 0.6 : 1}}>
              {saving ? 'Guardando...' : 'Guardar y continuar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function NotificationPreferencesModal({ columns, currentUserId, role, followFilter, followCount, onToggleFollowFilter, onClose }) {
  // Las columnas de avisos las asignan el responsable y el creador; el resto de usuarios solo las ve.
  const canManageColumns = ADMIN_TIER_ROLES.includes(role);
  const [typeSettings, setTypeSettings] = useState(null); // null = cargando
  const [securityCode, setSecurityCode] = useState('');
  const [savedSecurityCode, setSavedSecurityCode] = useState('');
  const [savingCode, setSavingCode] = useState(false);
  const [codeMessage, setCodeMessage] = useState('');
  const [savingType, setSavingType] = useState(null);
  const [enabledCols, setEnabledCols] = useState(new Set());
  const [loadingPrefs, setLoadingPrefs] = useState(true);
  const [savingCol, setSavingCol] = useState(null);
  const [savingAll, setSavingAll] = useState(false);
  const [message, setMessage] = useState('');

  const loadPrefs = async () => {
    setLoadingPrefs(true);
    if (canManageColumns) {
      const { data } = await supabase.from('column_emails').select('column_id').eq('profile_id', currentUserId);
      setEnabledCols(new Set((data || []).map(r => r.column_id)));
    } else {
      const { data } = await supabase.rpc('get_my_notification_columns');
      setEnabledCols(new Set((data || []).map(r => (typeof r === 'string' ? r : r.get_my_notification_columns))));
    }
    setLoadingPrefs(false);
  };

  const loadTypeSettings = async () => {
    const { data } = await supabase.from('notification_settings').select('*').eq('profile_id', currentUserId).maybeSingle();
    const next = {};
    NOTIFICATION_TYPES.forEach(t => { next[t.key] = data ? data[t.key] !== false : true; });
    setTypeSettings(next);
    setSecurityCode(data?.antiphishing_code || '');
    setSavedSecurityCode(data?.antiphishing_code || '');
  };

  const saveSecurityCode = async () => {
    const code = securityCode.trim();
    setCodeMessage('');
    const invalid = securityCodeError(code);
    if (invalid) {
      setCodeMessage(invalid);
      return;
    }
    setSavingCode(true);
    const { error } = await supabase.from('notification_settings')
      .upsert({ profile_id: currentUserId, antiphishing_code: code || null, updated_at: new Date().toISOString() }, { onConflict: 'profile_id' });
    setSavingCode(false);
    if (error) { setCodeMessage('Error: ' + error.message); return; }
    setSavedSecurityCode(code);
    setCodeMessage('Guardado: aparecerá en todos tus correos de EnMarcha.');
  };

  useEffect(() => { loadPrefs(); loadTypeSettings(); }, [currentUserId]);

  const toggleType = async (key) => {
    const updated = { ...typeSettings, [key]: !typeSettings[key] };
    setSavingType(key);
    setMessage('');
    const { error } = await supabase.from('notification_settings')
      .upsert({ profile_id: currentUserId, ...updated, updated_at: new Date().toISOString() }, { onConflict: 'profile_id' });
    if (error) setMessage('Error: ' + error.message);
    else setTypeSettings(updated);
    setSavingType(null);
  };

  const sectionTitle = { color: 'var(--t-f1f5f9)', fontSize: '0.95rem', fontWeight: 600, margin: '0 0 8px' };
  const toggleStyle = (enabled) => ({
    backgroundColor: enabled ? 'var(--b-166534)' : 'transparent',
    color: enabled ? 'var(--t-4ade80)' : 'var(--t-9ca3af)',
    border: `1px solid ${enabled ? 'var(--l-166534)' : 'var(--l-4b5563)'}`,
    borderRadius: '6px', padding: '4px 10px', cursor: 'pointer', fontSize: '0.8rem', whiteSpace: 'nowrap'
  });

  const toggleColumn = async (columnId, enabled) => {
    setSavingCol(columnId);
    setMessage('');
    const { error } = await supabase.rpc('set_column_notification', {
      p_column_id: columnId, p_profile_id: currentUserId, p_enabled: enabled
    });
    if (error) {
      setMessage('Error: ' + error.message);
    } else {
      setEnabledCols(prev => {
        const next = new Set(prev);
        if (enabled) next.add(columnId); else next.delete(columnId);
        return next;
      });
    }
    setSavingCol(null);
  };

  const setAll = async (enabled) => {
    setSavingAll(true);
    setMessage('');
    const { error } = await supabase.rpc('set_all_column_notifications', { p_enabled: enabled });
    if (error) {
      setMessage('Error: ' + error.message);
      setSavingAll(false);
    } else {
      await loadPrefs();
      setSavingAll(false);
    }
  };

  return (
    <div style={S.overlay}>
      <div style={{...S.modal, maxWidth: '480px'}}>
        <button onClick={onClose} style={S.closeBtn}>✕</button>
        <h3 style={S.title}>Mis notificaciones</h3>

        {message && (
          <div style={{backgroundColor: 'var(--b-7f1d1d)', color: 'var(--t-f1f5f9)', padding: '10px', borderRadius: '6px', marginBottom: '16px', fontSize: '0.875rem'}}>
            {message}
          </div>
        )}

        <div style={{marginBottom: '20px'}}>
          <p style={sectionTitle}>🔔 Seguimientos</p>
          <p style={{color: 'var(--t-9ca3af)', fontSize: '0.8rem', margin: '0 0 8px'}}>
            Sigues {followCount} {followCount === 1 ? 'vehículo' : 'vehículos'} (campanita 🔔 en la ficha del coche). Recibes sus avisos aunque no tengas su columna activada.
          </p>
          <button type="button" onClick={onToggleFollowFilter}
            style={{...(followFilter ? S.button : S.secondaryButton), width: '100%'}}>
            {followFilter ? 'Ver todos los vehículos' : 'Ver solo los vehículos que sigo'}
          </button>
        </div>

        <div style={{marginBottom: '20px'}}>
          <p style={sectionTitle}>🔐 Código de seguridad de tus correos</p>
          <p style={{color: 'var(--t-9ca3af)', fontSize: '0.8rem', margin: '0 0 8px'}}>
            Elige una palabra o frase que solo tú conozcas. Aparecerá arriba en cada correo de EnMarcha: si un correo no la muestra, no es nuestro y no debes pulsar nada.
            Nunca te pediremos la contraseña por correo.
          </p>
          <div style={{display: 'flex', gap: '8px'}}>
            <input id="security-code" style={{...S.input, marginBottom: 0}} value={securityCode} maxLength={40}
              onChange={e => setSecurityCode(e.target.value)} placeholder="Ej.: girasol azul 27" autoComplete="off" />
            <button type="button" onClick={saveSecurityCode} disabled={savingCode || securityCode.trim() === savedSecurityCode}
              style={{...S.button, whiteSpace: 'nowrap', opacity: (savingCode || securityCode.trim() === savedSecurityCode) ? 0.5 : 1}}>
              {savingCode ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
          {codeMessage && <p style={{color: codeMessage.startsWith('Guardado') ? 'var(--t-86efac)' : 'var(--t-fca5a5)', fontSize: '0.75rem', margin: '6px 0 0'}}>{codeMessage}</p>}
        </div>

        <div style={{marginBottom: '20px'}}>
          <p style={sectionTitle}>📬 Qué avisos quiero recibir</p>
          <p style={{color: 'var(--t-9ca3af)', fontSize: '0.75rem', margin: '0 0 8px'}}>Nunca recibes avisos de las acciones que haces tú.</p>
          {typeSettings === null ? (
            <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem'}}>Cargando...</p>
          ) : (
            <div style={{display: 'flex', flexDirection: 'column', gap: '6px'}}>
              {NOTIFICATION_TYPES.filter(t => !t.roles || t.roles.includes(role)).map(t => (
                <div key={t.key} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', backgroundColor: 'var(--b-334155)', padding: '8px 12px', borderRadius: '6px'}}>
                  <div style={{minWidth: 0}}>
                    <div style={{color: 'var(--t-e5e7eb)', fontSize: '0.85rem'}}>{t.label}</div>
                    <div style={{color: 'var(--t-9ca3af)', fontSize: '0.72rem'}}>{t.help}</div>
                  </div>
                  <button type="button" onClick={() => toggleType(t.key)} disabled={savingType === t.key} style={toggleStyle(typeSettings[t.key])}>
                    {typeSettings[t.key] ? '🔔 Sí' : '🔕 No'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <p style={sectionTitle}>🗂️ Columnas asignadas</p>
        {!canManageColumns ? (
          <>
            <p style={{color: 'var(--t-9ca3af)', fontSize: '0.8rem', margin: '0 0 8px'}}>
              Recibes los avisos de todos los vehículos de estas columnas. Las asigna el responsable o el creador.
              Cuando un coche entra en una de ellas, el aviso te llega siempre.
            </p>
            {loadingPrefs ? (
              <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem'}}>Cargando...</p>
            ) : columns.filter(col => enabledCols.has(col.id)).length === 0 ? (
              <p style={{color: 'var(--t-9ca3af)', fontSize: '0.85rem', fontStyle: 'italic'}}>No tienes ninguna columna asignada.</p>
            ) : (
              <div style={{display: 'flex', flexWrap: 'wrap', gap: '6px'}}>
                {columns.filter(col => enabledCols.has(col.id)).map(col => (
                  <span key={col.id} style={{display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: 'var(--b-334155)', color: 'var(--t-e5e7eb)', padding: '6px 10px', borderRadius: '6px', fontSize: '0.85rem'}}>
                    <span style={{display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: col.color || 'var(--b-374151)'}}></span>
                    {col.label}
                  </span>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
        <p style={{color: 'var(--t-9ca3af)', fontSize: '0.8rem', margin: '0 0 8px'}}>
          Tus columnas: recibe los avisos de todos los vehículos de las columnas que actives. Las de los demás usuarios se asignan en 👥 Usuarios → 🔔 Notificaciones.
        </p>        <div style={{display: 'flex', gap: '8px', marginBottom: '16px'}}>
          <button type="button" onClick={() => setAll(true)} disabled={savingAll} style={{...S.secondaryButton, flex: 1}}>Activar todas</button>
          <button type="button" onClick={() => setAll(false)} disabled={savingAll} style={{...S.secondaryButton, flex: 1}}>Desactivar todas</button>
        </div>

        {loadingPrefs ? (
          <p style={{color: 'var(--t-9ca3af)', fontSize: '0.875rem'}}>Cargando preferencias...</p>
        ) : (
          <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
            {columns.map(col => {
              const enabled = enabledCols.has(col.id);
              return (
                <div key={col.id} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'var(--b-334155)', padding: '10px 12px', borderRadius: '6px'}}>
                  <span style={{color: 'var(--t-e5e7eb)', fontSize: '0.875rem'}}>
                    <span style={{display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: col.color || 'var(--b-374151)', marginRight: '8px'}}></span>
                    {col.label}
                  </span>
                  <button
                    type="button"
                    onClick={() => toggleColumn(col.id, !enabled)}
                    disabled={savingCol === col.id || savingAll}
                    style={toggleStyle(enabled)}
                  >
                    {enabled ? '🔔 Activadas' : '🔕 Desactivadas'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
}

function UsersPanel({ role, currentUserId, columns, orgId, getAvailableRoles, onClose }) {
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [permsByUser, setPermsByUser] = useState({});
  const [notifsByUser, setNotifsByUser] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // { userId, section: 'perms' | 'notifs' }
  const [message, setMessage] = useState('');
  const [notice, setNotice] = useState('');

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState(() => getAvailableRoles()[0] || 'comercial');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteMessage, setInviteMessage] = useState('');

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    const [usersRes, invitesRes, permsRes, notifsRes] = await Promise.all([
      supabase.rpc('get_org_users'),
      supabase.from('invitations').select('id, email, role, created_at, expires_at')
        .is('accepted_at', null).gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false }),
      supabase.from('column_permissions').select('profile_id, column_id'),
      supabase.from('column_emails').select('profile_id, column_id').not('profile_id', 'is', null)
    ]);

    setUsers(usersRes.data || []);
    setInvites(invitesRes.data || []);

    const permsMap = {};
    (permsRes.data || []).forEach(p => {
      if (!permsMap[p.profile_id]) permsMap[p.profile_id] = new Set();
      permsMap[p.profile_id].add(p.column_id);
    });
    setPermsByUser(permsMap);

    const notifsMap = {};
    (notifsRes.data || []).forEach(n => {
      if (!notifsMap[n.profile_id]) notifsMap[n.profile_id] = new Set();
      notifsMap[n.profile_id].add(n.column_id);
    });
    setNotifsByUser(notifsMap);

    setLoading(false);
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    setInviteLoading(true);
    setInviteMessage('');

    const { error } = await supabase.from('invitations').insert([{
      email: inviteEmail.trim(),
      role: inviteRole,
      org_id: orgId
    }]);

    if (error) {
      setInviteMessage('Error al enviar invitación: ' + error.message);
    } else {
      setInviteMessage('Invitación enviada');
      setInviteEmail('');
      loadAll();
    }
    setInviteLoading(false);
  };

  const deleteUser = async (userId) => {
    if (!MANAGE_USERS_ROLES.includes(role)) return;
    if (!window.confirm('¿Estás seguro de que quieres eliminar este usuario?')) return;
    const { error } = await supabase.from('profiles').delete().eq('id', userId);
    if (!error) {
      loadAll();
    } else {
      setMessage('Error al eliminar usuario: ' + error.message);
    }
  };

  const cancelInvite = async (inviteId) => {
    if (!window.confirm('¿Cancelar esta invitación?')) return;
    const { error } = await supabase.from('invitations').delete().eq('id', inviteId);
    if (!error) {
      loadAll();
    } else {
      setMessage('Error al cancelar la invitación: ' + error.message);
    }
  };

  // Cambio de rol (solo el creador; la base de datos lo impone con protect_profile_fields).
  const canChangeRoles = role === 'creador';
  const [roleDraft, setRoleDraft] = useState('');
  const [roleOpenLeads, setRoleOpenLeads] = useState(0);
  const [roleSaving, setRoleSaving] = useState(false);

  const openRoleEditor = async (u) => {
    setMessage('');
    setNotice('');
    setRoleDraft(u.role);
    setRoleOpenLeads(0);
    setExpanded({ userId: u.id, section: 'role' });
    const { count } = await supabase.from('leads').select('id', { count: 'exact', head: true })
      .eq('assigned_to', u.id).not('status', 'in', '(vendido,perdido,descartado)');
    setRoleOpenLeads(count || 0);
  };

  // Avisos de lo que cambia para el usuario con el rol nuevo.
  const roleChangeNotes = (u, next) => {
    const leadRoles = ['comercial', 'captador'];
    const notes = [];
    if (next === 'creador') notes.push('Tendrá control total, como tú: también podrá cambiar roles y no se le podrá eliminar desde la app.');
    if (next === 'responsable') notes.push('Tendrá los mismos permisos que el creador (salvo cambiar roles) y, al entrar, la app le pedirá fijar los objetivos del mes.');
    if (leadRoles.includes(next) && !leadRoles.includes(u.role)) {
      notes.push('Para que use Leads, actívale el panel en 📇 Leads → Supervisión → Equipo y accesos.');
    }
    if (roleOpenLeads > 0 && !leadRoles.includes(next) && !ADMIN_TIER_ROLES.includes(next)) {
      notes.push(`Tiene ${roleOpenLeads} lead${roleOpenLeads === 1 ? '' : 's'} abierto${roleOpenLeads === 1 ? '' : 's'} a su nombre: seguirán asignados, pero con el rol nuevo no podrá verlos. Reasígnalos en 📇 Leads → Supervisión.`);
    }
    if (ADMIN_TIER_ROLES.includes(next) && !ADMIN_TIER_ROLES.includes(u.role)) notes.push('Podrá mover coches a cualquier columna: los permisos por columna dejan de aplicarse.');
    if (!ADMIN_TIER_ROLES.includes(next) && ADMIN_TIER_ROLES.includes(u.role)) notes.push('Solo podrá mover coches a las columnas que tenga marcadas en ⚙️ Permisos.');
    return notes;
  };

  const saveRole = async (u) => {
    if (!canChangeRoles || u.id === currentUserId || !roleDraft || roleDraft === u.role) return;
    const name = (u.full_name || '').trim() || 'este usuario';
    const extra = roleDraft === 'creador' ? '\n\nOjo: tendrá control total de la empresa, igual que tú.' : '';
    if (!window.confirm(`¿Cambiar el rol de ${name} de ${roleLabel(u.role)} a ${roleLabel(roleDraft)}?${extra}`)) return;
    setRoleSaving(true);
    setMessage('');
    const { data, error } = await supabase.from('profiles').update({ role: roleDraft }).eq('id', u.id).select('id');
    setRoleSaving(false);
    if (error || !data || data.length === 0) {
      setMessage('Error al cambiar el rol: ' + (error?.message || 'no se ha podido guardar.'));
      return;
    }
    setExpanded(null);
    setNotice(`Rol de ${name} cambiado a ${roleLabel(roleDraft)}. Le llega un correo y lo verá al abrir o recargar la app.`);
    loadAll();
  };

  const toggleColumnPermission = async (profileId, columnId, checked) => {
    setMessage('');
    if (checked) {
      const { error } = await supabase.from('column_permissions').insert([{ profile_id: profileId, column_id: columnId }]);
      if (error) { setMessage('Error: ' + error.message); return; }
    } else {
      const { error } = await supabase.from('column_permissions').delete().eq('profile_id', profileId).eq('column_id', columnId);
      if (error) { setMessage('Error: ' + error.message); return; }
    }
    loadAll();
  };

  const toggleColumnNotification = async (profileId, columnId, checked) => {
    setMessage('');
    const { error } = await supabase.rpc('set_column_notification', {
      p_column_id: columnId,
      p_profile_id: profileId,
      p_enabled: checked
    });
    if (error) { setMessage('Error: ' + error.message); return; }
    loadAll();
  };

  return (
    <div style={S.overlay}>
      <div style={{...S.modal, maxWidth: '640px'}}>
        <button onClick={onClose} style={S.closeBtn}>✕</button>
        <h3 style={S.title}>Gestionar Usuarios</h3>

        {message && (
          <div style={{backgroundColor: 'var(--b-7f1d1d)', color: 'var(--t-f1f5f9)', padding: '10px', borderRadius: '6px', marginBottom: '16px', fontSize: '0.875rem'}}>
            {message}
          </div>
        )}
        {notice && !message && (
          <div role="status" style={{backgroundColor: 'var(--b-065f46)', color: 'var(--t-f1f5f9)', padding: '10px', borderRadius: '6px', marginBottom: '16px', fontSize: '0.875rem'}}>
            {notice}
          </div>
        )}

        <div style={{backgroundColor: 'var(--b-374151)', borderRadius: '8px', padding: '12px', marginBottom: '20px'}}>
          <p style={{color: 'var(--t-f1f5f9)', fontSize: '0.875rem', fontWeight: 600, margin: '0 0 10px'}}>+ Nueva invitación</p>
          {inviteMessage && (
            <div style={{
              backgroundColor: inviteMessage.includes('Error') ? 'var(--b-7f1d1d)' : 'var(--b-065f46)',
              color: 'var(--t-f1f5f9)', padding: '8px 10px', borderRadius: '6px', marginBottom: '10px', fontSize: '0.8rem'
            }}>
              {inviteMessage}
            </div>
          )}
          <form onSubmit={sendInvite} style={{display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-start'}}>
            <input
              style={{...S.input, marginBottom: 0, flex: '1 1 200px'}}
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="usuario@ejemplo.com"
              required
            />
            <select
              style={{...S.select, marginBottom: 0, flex: '0 1 180px'}}
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
              required
            >
              {getAvailableRoles().map(r => (
                <option key={r} value={r}>{roleLabel(r)}</option>
              ))}
            </select>
            <button type="submit" disabled={inviteLoading} style={{...S.button, opacity: inviteLoading ? 0.7 : 1}}>
              {inviteLoading ? 'Enviando...' : 'Invitar'}
            </button>
          </form>
        </div>

        {loading ? (
          <div style={{textAlign: 'center', color: 'var(--t-9ca3af)'}}>Cargando...</div>
        ) : (
          <>
            <div style={{maxHeight: '420px', overflowY: 'auto', marginBottom: '20px'}}>
              {users.length === 0 ? (
                <p style={{color: 'var(--t-9ca3af)', fontStyle: 'italic', textAlign: 'center'}}>No hay usuarios</p>
              ) : (
                users.map(u => {
                  const isPermsOpen = expanded?.userId === u.id && expanded.section === 'perms';
                  const isNotifsOpen = expanded?.userId === u.id && expanded.section === 'notifs';
                  const isRoleOpen = expanded?.userId === u.id && expanded.section === 'role';
                  const isAdminTier = u.role === 'creador' || ADMIN_TIER_ROLES.includes(u.role);
                  // Las columnas de avisos las asignan creador y responsable a cualquiera (también entre ellos),
                  // salvo que el responsable no puede tocar las de un creador.
                  const canEditNotifs = role === 'creador' || u.role !== 'creador';
                  const userPerms = permsByUser[u.id] || new Set();
                  const userNotifs = notifsByUser[u.id] || new Set();
                  return (
                    <div key={u.id} style={{backgroundColor: 'var(--b-374151)', borderRadius: '6px', marginBottom: '8px', overflow: 'hidden'}}>
                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', flexWrap: 'wrap', gap: '8px'}}>
                        <div>
                          <p style={{color: 'var(--t-f1f5f9)', margin: 0, fontSize: '0.875rem'}}>
                            {u.full_name || '(sin nombre)'}{u.id === currentUserId ? ' (tú)' : ''}
                          </p>
                          <p style={{color: 'var(--t-9ca3af)', margin: 0, fontSize: '0.75rem'}}>
                            {roleLabel(u.role)}
                          </p>
                          {MANAGE_USERS_ROLES.includes(role) && u.email && (
                            <p style={{color: 'var(--t-9ca3af)', margin: '2px 0 0', fontSize: '0.75rem'}}>
                              ✉️ {u.email}
                            </p>
                          )}
                        </div>
                        <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                          {!isAdminTier && (
                            <button
                              onClick={() => setExpanded(isPermsOpen ? null : { userId: u.id, section: 'perms' })}
                              style={{...S.secondaryButton, padding: '4px 10px', fontSize: '0.75rem'}}
                            >
                              {isPermsOpen ? 'Ocultar' : '⚙️ Permisos'}
                            </button>
                          )}
                          {canEditNotifs && (
                            <button
                              onClick={() => setExpanded(isNotifsOpen ? null : { userId: u.id, section: 'notifs' })}
                              style={{...S.secondaryButton, padding: '4px 10px', fontSize: '0.75rem'}}
                            >
                              {isNotifsOpen ? 'Ocultar' : '🔔 Notificaciones'}
                            </button>
                          )}
                          {canChangeRoles && u.id !== currentUserId && (
                            <button
                              onClick={() => (isRoleOpen ? setExpanded(null) : openRoleEditor(u))}
                              style={{...S.secondaryButton, padding: '4px 10px', fontSize: '0.75rem'}}
                            >
                              {isRoleOpen ? 'Ocultar' : '🔁 Cambiar rol'}
                            </button>
                          )}
                          {MANAGE_USERS_ROLES.includes(role) && u.role !== 'creador' && u.id !== currentUserId && (
                            <button
                              onClick={() => deleteUser(u.id)}
                              style={{backgroundColor: '#ef4444', color: '#fff', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem'}}
                            >
                              Eliminar
                            </button>
                          )}
                        </div>
                      </div>

                      {isRoleOpen && canChangeRoles && (
                        <div style={{padding: '0 12px 12px', borderTop: '1px solid var(--l-4b5563)'}}>
                          <label htmlFor={`role-${u.id}`} style={{display: 'block', color: 'var(--t-9ca3af)', fontSize: '0.75rem', margin: '10px 0 6px'}}>
                            Rol nuevo (ahora: {roleLabel(u.role)})
                          </label>
                          <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center'}}>
                            <select
                              id={`role-${u.id}`}
                              style={{...S.select, marginBottom: 0, flex: '0 1 220px'}}
                              value={roleDraft}
                              onChange={(e) => setRoleDraft(e.target.value)}
                            >
                              {!getAvailableRoles().includes(u.role) && <option value={u.role}>{roleLabel(u.role)}</option>}
                              {getAvailableRoles().map(r => (
                                <option key={r} value={r}>{roleLabel(r)}</option>
                              ))}
                            </select>
                            <button
                              onClick={() => saveRole(u)}
                              disabled={roleSaving || !roleDraft || roleDraft === u.role}
                              style={{...S.button, padding: '6px 12px', fontSize: '0.8rem', opacity: (roleSaving || roleDraft === u.role) ? 0.6 : 1}}
                            >
                              {roleSaving ? 'Guardando...' : 'Guardar rol'}
                            </button>
                          </div>
                          {roleDraft && roleDraft !== u.role && roleChangeNotes(u, roleDraft).length > 0 && (
                            <ul style={{color: 'var(--t-fbbf24)', fontSize: '0.75rem', margin: '10px 0 0', paddingLeft: '18px'}}>
                              {roleChangeNotes(u, roleDraft).map((n, i) => <li key={i} style={{marginBottom: '4px'}}>{n}</li>)}
                            </ul>
                          )}
                          <p style={{color: 'var(--t-6b7280)', fontSize: '0.7rem', marginTop: '8px'}}>
                            Le llegará un correo avisándole del rol nuevo, que se aplica la próxima vez que abra o recargue la app. Sus ventas, compras, comisiones y leads se conservan.
                          </p>
                        </div>
                      )}

                      {isPermsOpen && !isAdminTier && (
                        <div style={{padding: '0 12px 12px', borderTop: '1px solid var(--l-4b5563)'}}>
                          <p style={{color: 'var(--t-9ca3af)', fontSize: '0.75rem', margin: '10px 0 6px'}}>Puede mover vehículos a:</p>
                          <div style={{display: 'flex', flexWrap: 'wrap', gap: '10px'}}>
                            {columns.map(col => (
                              <label key={col.id} style={{display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--t-e5e7eb)', fontSize: '0.8rem', cursor: 'pointer'}}>
                                <input
                                  type="checkbox"
                                  checked={userPerms.has(col.id)}
                                  onChange={(e) => toggleColumnPermission(u.id, col.id, e.target.checked)}
                                />
                                {col.label}
                              </label>
                            ))}
                          </div>
                          <p style={{color: 'var(--t-6b7280)', fontSize: '0.7rem', marginTop: '8px'}}>
                            Sin ninguna columna marcada, este usuario no podrá mover vehículos entre columnas.
                          </p>
                        </div>
                      )}

                      {isNotifsOpen && canEditNotifs && (
                        <div style={{padding: '0 12px 12px', borderTop: '1px solid var(--l-4b5563)'}}>
                          <p style={{color: 'var(--t-9ca3af)', fontSize: '0.75rem', margin: '10px 0 6px'}}>Recibe notificaciones por email de:</p>
                          <div style={{display: 'flex', flexWrap: 'wrap', gap: '10px'}}>
                            {columns.map(col => (
                              <label key={col.id} style={{display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--t-e5e7eb)', fontSize: '0.8rem', cursor: 'pointer'}}>
                                <input
                                  type="checkbox"
                                  checked={userNotifs.has(col.id)}
                                  onChange={(e) => toggleColumnNotification(u.id, col.id, e.target.checked)}
                                />
                                {col.label}
                              </label>
                            ))}
                          </div>
                          <p style={{color: 'var(--t-6b7280)', fontSize: '0.7rem', marginTop: '8px'}}>
                            Avisa cuando un coche entra o sale de la columna, se edita, o recibe un comentario (nunca por sus propias acciones).
                            Cuando un coche entra en una de estas columnas, el aviso le llega siempre, aunque haya desactivado "Movimientos" en sus ajustes.
                            Solo el responsable y el creador pueden cambiar estas columnas.
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {invites.length > 0 && (
              <>
                <h4 style={{color: 'var(--t-f1f5f9)', fontSize: '0.875rem', marginBottom: '10px'}}>Invitaciones pendientes</h4>
                <div>
                  {invites.map(inv => (
                    <div key={inv.id} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'var(--b-3f3320)', padding: '10px 12px', borderRadius: '6px', marginBottom: '8px'}}>
                      <div>
                        <p style={{color: 'var(--t-fbbf24)', margin: 0, fontSize: '0.8rem'}}>{inv.email}</p>
                        <p style={{color: 'var(--t-9ca3af)', margin: 0, fontSize: '0.7rem'}}>
                          {roleLabel(inv.role)} · enviada el {new Date(inv.created_at).toLocaleDateString('es-ES')}
                        </p>
                      </div>
                      {role === 'creador' && (
                        <button
                          onClick={() => cancelInvite(inv.id)}
                          style={{backgroundColor: 'transparent', color: 'var(--t-f87171)', border: '1px solid var(--l-7f1d1d)', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem'}}
                        >
                          Cancelar
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function AcceptInvite({ token, goTo }) {
  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    const { data: rows } = await supabase.rpc('get_invitation_by_token', { p_token: token });
    const data = Array.isArray(rows) ? rows[0] : null;

    if (data) {
      // Verificar manualmente si no ha expirado
      const now = new Date();
      const expiresAt = new Date(data.expires_at);
      
      if (expiresAt > now) {
        setInvitation(data);
      } else {
        setError('Invitación expirada');
      }
    } else {
      setError('Invitación inválida o expirada');
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <BrandLoader text="Validando invitación" />
    );
  }

  if (error) {
    return (
      <div style={{minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--b-0f172a)'}}>
        <div style={{backgroundColor: 'var(--b-1e293b)', padding: '2rem', borderRadius: '0.5rem', textAlign: 'center'}}>
          <div style={{color: '#ef4444', marginBottom: '1rem'}}>{error}</div>
          <button 
            onClick={() => goTo('login')}
            style={{
              backgroundColor: 'var(--b-f59e0b)',
              color: '#1f2937',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '0.375rem',
              cursor: 'pointer'
            }}
          >
            Ir al Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--b-0f172a)'}}>
      <div style={{backgroundColor: 'var(--b-1e293b)', padding: '2rem', borderRadius: '0.5rem', textAlign: 'center'}}>
        <BrandLockup />
        <h2 style={{color: 'var(--t-f1f5f9)', marginBottom: '1rem'}}>Invitación a EnMarcha</h2>
        <p style={{color: 'var(--t-e5e7eb)', marginBottom: '1.5rem'}}>
          Has sido invitado a unirte a <strong>{invitation.org_name}</strong> como <strong>{roleLabel(invitation.role)}</strong>
        </p>
        <button 
          onClick={() => goTo('register')}
          style={{
            backgroundColor: 'var(--b-f59e0b)',
            color: '#1f2937',
            border: 'none',
            padding: '0.75rem 1.5rem',
            borderRadius: '0.375rem',
            cursor: 'pointer'
          }}
        >
          Aceptar Invitación
        </button>
      </div>
    </div>
  );
}

function AppRouter({ page, goTo }) {
  const { user, loading, recovery, finishRecovery, pinRequired, pinVerified } = useAuth();
  const urlParams = new URLSearchParams(window.location.search);
  const inviteToken = urlParams.get('invite');
  // ?recuperar=1: botón "No he sido yo" del correo de aviso de inicio de sesión.
  const [changeFromAlert, setChangeFromAlert] = useState(() => urlParams.has('recuperar'));

  if (inviteToken && page !== 'register') return <AcceptInvite token={inviteToken} goTo={goTo} />;

  if (loading) {
    return (
      <BrandLoader />
    );
  }

  if (user && pinRequired === null) return <BrandLoader />;
  if (user && pinRequired) return <LoginPinScreen email={user.email} onVerified={pinVerified} />;
  if (user && (recovery || changeFromAlert)) {
    return <SetNewPassword fromAlert={!recovery} onDone={() => { finishRecovery(); setChangeFromAlert(false); }} />;
  }
  if (user) return <Dashboard />;
  if (page === 'register') return <Register goTo={goTo} />;
  if (page === 'forgot') return <ForgotPassword goTo={goTo} initialMessage={RECOVERY_LINK_ERROR} />;
  return <Login goTo={goTo} />;
}

export default function App() {
  const [page, setPage] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return (params.has('recuperar') || RECOVERY_LINK_ERROR) ? 'forgot' : 'login';
  });
  return <AuthProvider><AppRouter page={page} goTo={setPage} /></AuthProvider>;
}

const S = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)' },
  modal: { backgroundColor: 'var(--b-1e293b)', borderRadius: '12px', padding: '24px', width: '90vw', maxWidth: '500px', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', border: '1px solid var(--l-374151)' },
  closeBtn: { position: 'absolute', top: '16px', right: '16px', background: 'transparent', border: 'none', color: 'var(--t-9ca3af)', fontSize: '24px', cursor: 'pointer', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px' },
  title: { color: 'var(--t-f1f5f9)', fontSize: '20px', fontWeight: '600', marginBottom: '20px', margin: 0 },
  input: { width: '100%', padding: '10px', backgroundColor: 'var(--b-374151)', border: '1px solid var(--l-4b5563)', borderRadius: '6px', color: 'var(--t-f1f5f9)', fontSize: '14px', marginBottom: '12px' },
  textarea: { width: '100%', padding: '10px', backgroundColor: 'var(--b-374151)', border: '1px solid var(--l-4b5563)', borderRadius: '6px', color: 'var(--t-f1f5f9)', fontSize: '14px', minHeight: '80px', resize: 'vertical', fontFamily: 'inherit' },
  select: { width: '100%', padding: '10px', backgroundColor: 'var(--b-374151)', border: '1px solid var(--l-4b5563)', borderRadius: '6px', color: 'var(--t-f1f5f9)', fontSize: '14px', marginBottom: '12px' },
  button: { backgroundColor: 'var(--b-f59e0b)', color: '#1f2937', border: 'none', padding: '10px 20px', borderRadius: '6px', fontWeight: '500', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' },
  secondaryButton: { backgroundColor: 'var(--b-374151)', color: 'var(--t-f1f5f9)', border: '1px solid var(--l-4b5563)', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' },
  label: { color: 'var(--t-e5e7eb)', fontSize: '14px', fontWeight: '500', marginBottom: '6px', display: 'block' }
}